import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AdminTableCard, AdminTable, AdminTableHead, AdminTh, AdminTr, AdminTd,
  AdminBadge, AdminTableEmpty, AdminTableLoading, AdminIconBtn,
} from './admin/AdminTable';
import {
  faChartBar, faUsers, faCog, faBars, faMoon, faSun, faShieldAlt,
  faUserShield, faCheckCircle, faNairaSign, faEye, faFilter, faSearch,
  faUser, faTimes, faBell, faExclamationTriangle, faDollarSign, faGavel,
  faServer, faFileAlt, faUserCog, faBan, faEnvelope, faPhone, faLock,
  faChartLine, faCreditCard, faDatabase,
  faMicrochip, faEdit, faTrash, faPlus, faSave,
  faDownload, faUpload, faSync, faUserTie, faUserCheck, faUserTimes,
  faFlag, faComment, faImage, faVideo, faStar, faExchangeAlt, faSignOutAlt, faArrowRight,
  faUserCircle, faQuestionCircle, faBox, faShoppingCart, faWallet, faCrown, faTruck,
  faClipboardList, faTags, faTag, faFire, faSpinner, faPaperPlane, faHistory, faBullhorn, faBolt,
  faEnvelopeOpenText, faUniversity, faPercentage, faCheck, faCertificate, faGift,
  faChevronLeft, faChevronRight, faInfoCircle, faCalendarPlus, faTimesCircle, faClock, faCoins, faBuilding,
  faEllipsisVertical, faUserSlash, faIdBadge, faIdCard, faMobileAlt, faPalette
} from '@fortawesome/free-solid-svg-icons';
import { format, subDays, subHours } from 'date-fns';
import { adminAPI, supportAPI, logisticsAPI, swapAPI, uploadAPI, disputesAPI, notificationsAPI, escrowAPI } from '../services/api';
import { getStoredDarkMode, setStoredDarkMode } from '../utils/theme';
import { uploadMultipleImages, validateImageFile, uploadImage } from '../services/imageUploadService';
import EscrowAnalytics from './EscrowAnalytics';
import EscrowSettingsPanel from './EscrowSettingsPanel';
import AdminIMEIViewer from './AdminIMEIViewer';
import DeviceColorManager from './admin/DeviceColorManager';
import ProductReviewModal from './admin/ProductReviewModal';
import TwoFactorAuth from './TwoFactorAuth';
import lightLogo from '../assets/autoboy_logo2.png';
import darkLogo from '../assets/autoboy_logo3.png';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isDarkMode, setIsDarkMode] = useState(() => getStoredDarkMode());
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // State for dynamic data
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeListings: 0,
    pendingReports: 0,
    todayRevenue: 0,
    totalTransactions: 0,
    activeSessions: 0,
    systemHealth: 0,
    storageUsed: 0
  });
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [escrows, setEscrows] = useState([]);
  const [escrowSearch, setEscrowSearch] = useState('');
  const [escrowStatusFilter, setEscrowStatusFilter] = useState('all');
  const [showEscrowActionModal, setShowEscrowActionModal] = useState(false);
  const [escrowActionType, setEscrowActionType] = useState(null);
  const [escrowActionTarget, setEscrowActionTarget] = useState(null);
  const [escrowActionNote, setEscrowActionNote] = useState('');
  const [showEscrowDetailModal, setShowEscrowDetailModal] = useState(false);
  const [selectedEscrowDetails, setSelectedEscrowDetails] = useState(null);
  const [escrowDetailsLoading, setEscrowDetailsLoading] = useState(false);
  // Wallet details modal
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [selectedWalletDetails, setSelectedWalletDetails] = useState(null);
  const [walletDetailsLoading, setWalletDetailsLoading] = useState(false);
  // Adjust balance panel (inline inside wallet modal)
  const [showAdjustPanel, setShowAdjustPanel] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustType, setAdjustType] = useState('credit');
  // Freeze modal
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [freezeReason, setFreezeReason] = useState('');
  const [disputes, setDisputes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [premiumUsers, setPremiumUsers] = useState([]);
  const [premiumSubTab, setPremiumSubTab] = useState('active'); // 'active', 'analytics', 'settings'
  const [premiumAnalytics, setPremiumAnalytics] = useState(null);
  const [premiumSettings, setPremiumSettings] = useState(null);
  const [selectedPremiumUser, setSelectedPremiumUser] = useState(null);
  const [showPremiumExtendModal, setShowPremiumExtendModal] = useState(false);
  const [showPremiumRevokeModal, setShowPremiumRevokeModal] = useState(false);
  const [showPremiumGiftModal, setShowPremiumGiftModal] = useState(false);
  const [premiumExtensionDays, setPremiumExtensionDays] = useState(30);
  const [premiumRevocationReason, setPremiumRevocationReason] = useState('');
  const [premiumRefundPartial, setPremiumRefundPartial] = useState(false);
  const [premiumActionLoading, setPremiumActionLoading] = useState(false);
  const [editingPremiumSettings, setEditingPremiumSettings] = useState(null);
  const [showGrantPremiumModal, setShowGrantPremiumModal] = useState(false);
  const [grantPremiumSearch, setGrantPremiumSearch] = useState('');
  const [grantPremiumSearchResults, setGrantPremiumSearchResults] = useState([]);
  const [grantPremiumSearching, setGrantPremiumSearching] = useState(false);
  const [grantPremiumSelectedUser, setGrantPremiumSelectedUser] = useState(null);
  const [grantPremiumPlanId, setGrantPremiumPlanId] = useState('');
  const [grantPremiumDurationDays, setGrantPremiumDurationDays] = useState(30);
  const [grantPremiumReason, setGrantPremiumReason] = useState('');
  const [categories, setCategories] = useState([]);
  // Listing being reviewed in the approval queue. Read-only view, kept
  // separate from editingProduct so opening a review can never start an edit.
  const [reviewingProduct, setReviewingProduct] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [kycRequests, setKycRequests] = useState([]);
  const [kycStatusFilter, setKycStatusFilter] = useState('all');
  const [kycMainTab, setKycMainTab] = useState('kyc');
  const [kybRequests, setKybRequests] = useState([]);
  const [showRejectKYCModal, setShowRejectKYCModal] = useState(false);
  const [rejectKYCTargetUser, setRejectKYCTargetUser] = useState(null);
  const [rejectKYCReason, setRejectKYCReason] = useState('');
  const [reports, setReports] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [ticketReplyMessage, setTicketReplyMessage] = useState('');
  const [systemLogs, setSystemLogs] = useState([]);
  // Promo state removed � backend has no promo admin endpoints yet
  const [systemMetrics, setSystemMetrics] = useState({
    cpu: 0, memory: 0, storage: 0, bandwidth: 0, apiLatency: 0, uptime: 0,
    activeConnections: 0, queuedJobs: 0,
    dbConnected: false, dbCollections: {}, environment: {}, lastChecked: null
  });
  const [analyticsData, setAnalyticsData] = useState({
    totalUsers: 0, totalOrders: 0, totalProducts: 0,
    newUsers: { today: 0, week: 0, month: 0 },
    revenue: { today: 0, week: 0, month: 0 },
    transactions: { today: 0, week: 0, month: 0 },
    topCategories: [],
    topSellers: []
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productFormData, setProductFormData] = useState({
    title: '',
    description: '',
    price: '',
    seller_price: '',
    currency: 'NGN',
    category_id: '',
    condition: 'new',
    quantity: 1,
    brand: '',
    model: '',
    specifications: {},
    location: { city: '', state: '', country: 'Nigeria' },
    images: [],
    is_featured: false,
    is_trending: false,
    is_hot: false,
    is_good_deal: false
  });
  const [productUploadingImages, setProductUploadingImages] = useState(false);
  const [productUploadProgress, setProductUploadProgress] = useState({ current: 0, total: 0, fileName: '' });
  const [productImageUrlInput, setProductImageUrlInput] = useState('');
  const [productSpecInput, setProductSpecInput] = useState({ key: '', value: '' });
  const [productStatusFilter, setProductStatusFilter] = useState('all');
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [boostTargetId, setBoostTargetId] = useState(null);
  const [boostLevel, setBoostLevel] = useState('1');
  const [boostDays, setBoostDays] = useState('7');
  const [showRejectProductModal, setShowRejectProductModal] = useState(false);
  const [rejectProductTargetId, setRejectProductTargetId] = useState(null);
  const [rejectProductReason, setRejectProductReason] = useState('');
  const [showFlashDealModal, setShowFlashDealModal] = useState(false);
  const [flashDealTarget, setFlashDealTarget] = useState(null);
  const [flashDealForm, setFlashDealForm] = useState({ price: '', end_time: '', early_access_hours: '24' });

  // Category CRUD state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', description: '', icon: '' });

  // Admin CRUD state
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [adminFormData, setAdminFormData] = useState({ email: '', first_name: '', last_name: '', admin_role: 'moderator' });
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [tempAdminPassword, setTempAdminPassword] = useState(null);

  // Settings state
  const [settingsData, setSettingsData] = useState({
    general: { platform_name: '', support_email: '', support_phone: '', maintenance_mode: false, maintenance_message: '' },
    payment: { commission_rate: 10, escrow_fee: 3, minimum_transaction: 1000 },
    security: { two_fa_required: false, session_timeout: 30, ip_whitelist: '' },
    logistics: { fabstech_api_key: '', fabstech_base_url: '', base_shipping_rate: 0, price_per_km: 0 },
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Export state (System Health page)
  const [exportingType, setExportingType] = useState(null);

  // Dispute state
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeNoteText, setDisputeNoteText] = useState('');
  const [disputeFilter, setDisputeFilter] = useState('all');

  // Dispute resolution
  const [resolutionPayload, setResolutionPayload] = useState({ resolution: '', amount: '', reason: '' });
  const [resolutionLoading, setResolutionLoading] = useState(false);
  const [showPartialInput, setShowPartialInput] = useState(false);

  // Dispute modal UX
  const [addingNote, setAddingNote] = useState(false);
  const [evidenceIndex, setEvidenceIndex] = useState(0);

  // Service Charges (commission tiers + VAT) state
  const [commissionTiers, setCommissionTiers] = useState([]);
  const [vatPercentage, setVatPercentage] = useState(7.5);
  const [vatInput, setVatInput] = useState('7.5');
  const [loadingCommission, setLoadingCommission] = useState(false);
  const [savingVat, setSavingVat] = useState(false);
  const [showTierModal, setShowTierModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null); // null = creating new
  const [tierForm, setTierForm] = useState({ min_price: '', max_price: '', percentage: '', active: true });
  const [savingTier, setSavingTier] = useState(false);

  const [moderationSubTab, setModerationSubTab] = useState('support');
  const [marketingSubTab, setMarketingSubTab] = useState('banners');
  const [communicationSubTab, setCommunicationSubTab] = useState('logs');
  const [walletSubTab, setWalletSubTab] = useState('wallets');

  // Marketing State
  const [banners, setBanners] = useState([]);
  const [flashSales, setFlashSales] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  // Marketing modal forms
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [bannerForm, setBannerForm] = useState({ type: 'hero', title: '', subtitle: '', cta_text: '', cta_link: '', image: '', is_active: true });
  const [bannerUploading, setBannerUploading] = useState(false);
  const [showFlashModal, setShowFlashModal] = useState(false);
  const [flashForm, setFlashForm] = useState({ title: '', description: '', discount_percentage: '', start_time: '', end_time: '', early_access_hours: '24', image: '', is_active: true });
  const [flashUploading, setFlashUploading] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoForm, setPromoForm] = useState({ code: '', type: 'percentage', discount_value: '', min_order_amount: '', max_uses: '', max_uses_per_user: '', start_date: '', end_date: '', is_active: true });
  const [marketingSaving, setMarketingSaving] = useState(false);

  // Communication State
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [notificationTemplates, setNotificationTemplates] = useState([]);
  const [notificationSchedules, setNotificationSchedules] = useState([]);
  const [notificationStats, setNotificationStats] = useState({ sent: 0, delivered: 0, read: 0, failed: 0 });

  // Swaps State
  const [swapSubTab, setSwapSubTab] = useState('pending_approval');
  const [swaps, setSwaps] = useState([]);
  const [pendingSwaps, setPendingSwaps] = useState([]);
  const [swapEndpointReady, setSwapEndpointReady] = useState(true);

  // Swap Reject Modal
  const [showRejectSwapModal, setShowRejectSwapModal] = useState(false);
  const [rejectSwapTargetId, setRejectSwapTargetId] = useState(null);
  const [rejectSwapReason, setRejectSwapReason] = useState('');

  // Swap Inspection Modal
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [selectedSwapForInspection, setSelectedSwapForInspection] = useState(null);
  const [inspectionStep, setInspectionStep] = useState(1);
  const [inspectionData, setInspectionData] = useState({
    battery_percent: '', face_id_ok: false, storage_confirmed: false,
    screen_ok: false, battery_health_ok: false, connectivity_ok: false,
    buttons_ok: false, technician_notes: '', final_decision: 'pass'
  });

  // Swap Details Modal
  const [showSwapDetailsModal, setShowSwapDetailsModal] = useState(false);
  const [selectedSwapForDetails, setSelectedSwapForDetails] = useState(null);
  const [swapLogisticsEvents, setSwapLogisticsEvents] = useState([]);
  const [loadingSwapLogistics, setLoadingSwapLogistics] = useState(false);

  // Swap image lightbox — { images: string[], index: number } or null
  const [swapImageViewer, setSwapImageViewer] = useState(null);
  const openSwapImageViewer = (images, index = 0) => {
    const list = (Array.isArray(images) ? images : [images]).filter(Boolean);
    if (list.length === 0) return;
    setSwapImageViewer({ images: list, index: Math.min(index, list.length - 1) });
  };

  // Search query state for each tab
  const [userSearch, setUserSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  // User management pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsersCount, setTotalUsersCount] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [userSort, setUserSort] = useState('newest');
  // Orders/Escrows/Disputes/Swaps each fetched a hardcoded page 1 with no
  // way to reach older records — fine on a small dataset, but the same
  // pagination-hides-data bug already fixed once for KYC/Users. Each tab
  // gets its own page/total state since they're independent lists.
  // IMEI inspection panel target — { type: 'order' | 'swap', id }. These
  // values never appear in normal order/swap responses; the viewer hits the
  // dedicated admin-only endpoints.
  const [imeiViewerTarget, setIMEIViewerTarget] = useState(null);
  const [orderPage, setOrderPage] = useState(1);
  const [orderTotalPages, setOrderTotalPages] = useState(1);
  const [escrowPage, setEscrowPage] = useState(1);
  const [escrowTotalPages, setEscrowTotalPages] = useState(1);
  const [disputePage, setDisputePage] = useState(1);
  const [disputeTotalPages, setDisputeTotalPages] = useState(1);
  const [swapPage, setSwapPage] = useState(1);
  const [swapTotalPages, setSwapTotalPages] = useState(1);
  // Which user row's action menu is open (users table redesign)
  const [openUserActionMenu, setOpenUserActionMenu] = useState(null);
  // User action modals
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendTargetUser, setSuspendTargetUser] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [showPerksModal, setShowPerksModal] = useState(false);
  const [perksTargetUser, setPerksTargetUser] = useState(null);
  const [perksInput, setPerksInput] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [showOrderStatusModal, setShowOrderStatusModal] = useState(false);
  const [orderStatusTargetId, setOrderStatusTargetId] = useState(null);
  const [orderStatusValue, setOrderStatusValue] = useState('pending');
  const [orderStatusNote, setOrderStatusNote] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [walletSearch, setWalletSearch] = useState('');
  const [reviewSearch, setReviewSearch] = useState('');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [selectedReviewForResponse, setSelectedReviewForResponse] = useState(null);
  const [showReviewResponseModal, setShowReviewResponseModal] = useState(false);
  const [reviewResponseType, setReviewResponseType] = useState('approve'); // 'approve', 'reject', 'flag'
  const [reviewRejectReason, setReviewRejectReason] = useState('');
  const [reviewResponseLoading, setReviewResponseLoading] = useState(false);
  const [prohibitedItems, setProhibitedItems] = useState([]);
  const [prohibitedItemsText, setProhibitedItemsText] = useState('');
  const [initializingSystem, setInitializingSystem] = useState(false);

  // Fetch dashboard data on mount
  useEffect(() => {
    fetchDashboardData();
    fetchCategories(); // Ensure categories are loaded for product creation/editing
  }, []);

  // Fetch data when section changes
  useEffect(() => {
    switch (activeSection) {
      case 'dashboard':
        fetchDashboardData();
        break;
      case 'users':
        fetchUsers(1, '');
        break;
      case 'kyc-verification':
        fetchKYCRequests();
        fetchKYBRequests();
        break;
      case 'products':
        fetchProducts(productStatusFilter, productSearch);
        break;
      case 'orders':
        fetchOrders(orderStatusFilter, orderSearch);
        break;
      case 'escrow':
        fetchEscrows(escrowStatusFilter, escrowSearch);
        break;
      case 'wallets':
        fetchWallets();
        fetchTransactions();
        break;
      case 'swaps':
        fetchSwapData();
        break;
      case 'disputes':
        fetchDisputes();
        break;
      case 'reviews':
        fetchReviews();
        break;
      case 'premium':
        fetchPremiumData();
        break;
      case 'moderation':
        fetchReports();
        fetchSupportTickets();
        fetchProhibitedItems();
        break;
      case 'categories':
        fetchCategories();
        break;
      case 'analytics':
        fetchAnalytics();
        break;
      case 'system-health':
        fetchSystemMetrics();
        break;
      case 'admins':
        fetchAdmins();
        break;
      case 'settings':
        fetchSettings();
        break;
      case 'marketing':
        fetchMarketingData();
        break;
      case 'communication':
        fetchCommunicationData();
        break;
      case 'service-charges':
        fetchCommissionData();
        break;
      case 'logs':
        fetchSystemActivity();
        break;
      default:
        break;
    }
  }, [activeSection]);

  useEffect(() => {
    document.body.classList.toggle('AutoboyExpress-dash-dark-mode', isDarkMode);
    // Tailwind's compiled `:where(...)` selector for our custom darkMode config
    // only expands `.tw-dark-mode` to also match descendants (`.tw-dark-mode *`);
    // `.tw-AutoboyExpress-dash-dark-mode` compiles as a self-match-only term, so
    // toggling it on <body> never reaches nested `dark:` utilities (see AdminTable.jsx).
    // `tw-dark-mode` is the class that actually cascades — ThemeSync strips it on
    // dashboard routes to avoid storefront bleed, so we re-assert it here.
    document.body.classList.toggle('tw-dark-mode', isDarkMode);
    setStoredDarkMode(isDarkMode);
  }, [isDarkMode]);

  // Close the users-table row action menu on outside click / Escape
  useEffect(() => {
    if (!openUserActionMenu) return;
    const closeMenu = (e) => {
      if (!e.target.closest?.('.aex-user-action-menu-wrap')) setOpenUserActionMenu(null);
    };
    const closeOnEscape = (e) => { if (e.key === 'Escape') setOpenUserActionMenu(null); };
    // The menu is positioned fixed at click-time coordinates, so any scroll
    // (window or the table's own scroll container) would leave it misaligned — close it instead.
    const closeOnScroll = () => setOpenUserActionMenu(null);
    document.addEventListener('mousedown', closeMenu);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => {
      document.removeEventListener('mousedown', closeMenu);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [openUserActionMenu]);

  // Load the admin's own notifications into the header bell (and keep it fresh).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await notificationsAPI.getNotifications();
        const p = res?.data ?? res;
        const list = Array.isArray(p) ? p : (p?.notifications || p?.items || []);
        if (!cancelled) setNotifications(Array.isArray(list) ? list : []);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Open a notification's full detail, marking it read.
  const handleOpenNotif = async (n) => {
    setSelectedNotif(n);
    setShowNotifications(false);
    const nid = n._id || n.id;
    if (nid && !(n.is_read || n.read)) {
      try {
        await notificationsAPI.markAsRead(nid);
        setNotifications(prev => prev.map(x => ((x._id || x.id) === nid ? { ...x, is_read: true, read: true } : x)));
      } catch { /* ignore */ }
    }
  };

  // Live polling for Reports & Support — surface new tickets/reports and incoming
  // chat messages without a manual refresh.
  const prevTicketCountRef = useRef(0);
  const prevReportCountRef = useRef(0);
  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    if (activeSection !== 'moderation') return;
    let cancelled = false;
    prevTicketCountRef.current = supportTickets.length;
    prevReportCountRef.current = reports.length;
    const poll = async () => {
      try {
        const [tRes, rRes] = await Promise.all([
          adminAPI.getAdminTickets().catch(() => null),
          adminAPI.getReports({ limit: 50 }).catch(() => null),
        ]);
        if (cancelled) return;
        if (tRes) {
          const tp = tRes?.data || tRes;
          const list = Array.isArray(tp) ? tp : (tp.tickets || []);
          if (prevTicketCountRef.current && list.length > prevTicketCountRef.current) {
            showToast(`${list.length - prevTicketCountRef.current} new support ticket(s)`, 'info');
          }
          prevTicketCountRef.current = list.length;
          setSupportTickets(list);
        }
        if (rRes) {
          const rp = rRes?.data || rRes;
          const rlist = Array.isArray(rp) ? rp : (rp.reports || []);
          if (prevReportCountRef.current && rlist.length > prevReportCountRef.current) {
            showToast(`${rlist.length - prevReportCountRef.current} new report(s) submitted`, 'info');
          }
          prevReportCountRef.current = rlist.length;
          setReports(rlist);
        }
      } catch { /* ignore poll errors */ }
    };
    const id = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'moderation' || !selectedTicket) return;
    let cancelled = false;
    prevMsgCountRef.current = ticketMessages.length;
    const poll = async () => {
      try {
        const res = await adminAPI.getAdminTicket(selectedTicket.id);
        if (cancelled || !res) return;
        const tp = res?.data || res;
        const responses = tp.responses || [];
        if (prevMsgCountRef.current && responses.length > prevMsgCountRef.current) {
          showToast('New message in this ticket', 'info');
        }
        prevMsgCountRef.current = responses.length;
        setTicketMessages(responses.map(resp => ({
          id: resp.id,
          message: resp.message,
          sender: resp.is_from_user ? 'user' : 'support',
          created_at: resp.created_at,
        })));
      } catch { /* ignore poll errors */ }
    };
    const id = setInterval(poll, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeSection, selectedTicket]);

  // ===== API FETCH FUNCTIONS =====

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch from confirmed-working endpoints in parallel
      const [analyticsRes, sysRes, ordersRes, activityRes] = await Promise.all([
        adminAPI.getAnalytics().catch(() => null),
        adminAPI.getSystemStatus().catch(() => null),
        adminAPI.getAllOrders({ limit: 5 }).catch(() => null),
        adminAPI.getRecentActivity({ limit: 10 }).catch(() => null),
      ]);

      const analytics = analyticsRes?.data || analyticsRes || {};
      const sys = sysRes?.data || sysRes || {};

      setStats({
        totalUsers: analytics.total_users || sys.database?.collections?.users || 0,
        activeListings: analytics.total_products || sys.database?.collections?.products || 0,
        totalTransactions: analytics.total_orders || sys.database?.collections?.orders || 0,
        pendingReports: 0,
        todayRevenue: 0,
        activeSessions: 0,
        systemHealth: sys.database?.connected ? 100 : 0,
        storageUsed: 0,
        dbConnected: sys.database?.connected ?? false,
        dbCollections: sys.database?.collections || {},
        environment: sys.environment || {},
      });

      if (ordersRes) {
        const op = ordersRes?.data || ordersRes;
        const list = Array.isArray(op) ? op : (op?.orders || []);
        if (list.length > 0) {
          // Resolve buyer_id → buyer_name so the preview shows real customer names.
          const enriched = await enrichItemsWithUserData(list, ['buyer_id', 'seller_id']);
          setOrders(enriched);
        }
      }

      if (activityRes) {
        const acts = Array.isArray(activityRes?.data || activityRes)
          ? (activityRes?.data || activityRes) : [];
        const logs = acts.map(a => ({
          type: a.action?.includes('delete') || a.action?.includes('reject') ? 'warn' : 'info',
          timestamp: a.created_at,
          message: `${(a.action || '').replace(/_/g, ' ')}`,
          admin_id: a.admin_id,
          target_user_id: a.target_user_id,
          details: a.details,
        }));
        if (logs.length > 0) setSystemLogs(logs);
      }
    } catch (error) {
      showToast('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (page = 1, search = '', role = 'all', sort = 'newest') => {
    setLoading(true);
    try {
      const params = { limit: 20, page, sort };
      if (search) params.search = search;
      if (role !== 'all') params.user_type = role;
      // Do NOT add a status filter — we want ALL users including
      // Google OAuth users who may have different status values
      const response = await adminAPI.getUsers(params);
      if (response) {
        // apiClient returns raw JSON body directly (no .data wrapper)
        // Backend envelope: { "data": { "users": [...], "pagination": {...} } }
        const payload = response?.data || response;
        const raw = payload?.users || (Array.isArray(payload) ? payload : []);
        setUsers(Array.isArray(raw) ? raw : []);
        const pagination = payload?.pagination || {};
        const total = pagination?.total_pages
          || (pagination?.total && Math.ceil(pagination.total / 20))
          || payload?.total_pages
          || 1;
        setTotalPages(total);
        setTotalUsersCount(typeof pagination?.total === 'number' ? pagination.total : null);
        setCurrentPage(page);
      }
    } catch (error) {
      showToast('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchKYCRequests = async (statusFilter = 'all') => {
    setLoading(true);
    try {
      // GetUsers paginates on `page`, not `skip` — a `skip`-based loop here
      // silently re-fetches the same first page forever (the backend has no
      // `skip` query param, so `page` always defaulted to 1), which is why
      // this table only ever reflected the newest ~25 users.
      const PAGE_SIZE = 50;
      let page = 1;
      let total = null;
      let allUsers = [];
      while (total === null || allUsers.length < total) {
        const res = await adminAPI.getUsers({ page, limit: PAGE_SIZE });
        const batchPayload = res?.data || res;
        if (total === null) total = batchPayload?.pagination?.total || 0;
        const batch = Array.isArray(batchPayload?.users) ? batchPayload.users : [];
        if (batch.length === 0) break;
        allUsers = [...allUsers, ...batch];
        page += 1;
      }
      const userList = allUsers;

      // Client-side filter by verification_status since backend may ignore the param
      const filtered = statusFilter === 'all'
        ? userList
        : userList.filter(u => {
            const vs = u.verification_status || u.profile?.verification_status || 'not_started';
            return vs === statusFilter;
          });

      const normalized = filtered.map(u => ({
        ...u,
        _id: u._id || u.id,
        full_name: u.full_name ||
          (u.profile?.first_name ? `${u.profile.first_name} ${u.profile.last_name || ''}`.trim() : null) ||
          u.username || u.email || 'Unknown',
        email: u.email || 'N/A',
        profile: u.profile || null,
        verification_status: u.verification_status || u.profile?.verification_status || 'not_started',
        email_verified: u.is_email_verified || u.email_verified || false,
        phone_verified: u.is_phone_verified || u.phone_verified || false,
        user_type: u.user_type || u.role || 'buyer',
      }));

      setKycRequests(normalized);
    } catch (error) {
      showToast('Failed to load verification data', 'error');
      setKycRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchKYBRequests = async () => {
    try {
      const response = await adminAPI.getKYBRequests();
      const payload = response?.data || response;
      setKybRequests(Array.isArray(payload?.requests) ? payload.requests : []);
    } catch {
      setKybRequests([]);
    }
  };

  const fetchProducts = async (statusFilter = 'all', search = '') => {
    setLoading(true);
    try {
      // GET /admin/products returns ALL statuses — no server-side status filter supported
      // Filtering is done client-side in the render
      const params = { limit: 100 };
      if (search) params.search = search;
      const response = await adminAPI.getAllProducts(params);
      // apiClient returns raw JSON body — no .data wrapper
      // GET /admin/products returns an array directly per API docs
      const payload = response?.data || response;
      const raw = Array.isArray(payload)
        ? payload
        : payload?.products || (Array.isArray(payload?.data) ? payload.data : payload?.data?.products || []);
      setProducts(Array.isArray(raw) ? raw : []);
    } catch (error) {
      showToast('Failed to load products', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Cache of user IDs that returned 404 — skip repeated network calls for deleted users
  const _failedUserIds = useRef(new Set());

  // Shared helper: enrich items with user data from getUserDetails
  // Resolves names for rows that only carry user ids.
  //
  // This used to fire one request per id per row, all at once — a page of
  // 100 orders with a buyer and a seller each meant up to 200 simultaneous
  // calls, which on its own exceeded the server's 200-requests-per-minute
  // limit. Everything from that IP then 429'd for the rest of the window,
  // and the orders fetch itself failed with "Failed to load orders".
  //
  // Now: resolve the distinct set of ids, reuse anything already looked up
  // in this session, and cap how many are in flight at once. A hundred
  // orders from a handful of sellers costs a handful of requests. Endpoints
  // that return *_name server-side (admin orders now does) skip it entirely
  // via the guard in the mapping loop below.
  const _userCache = useRef(new Map());

  const enrichItemsWithUserData = async (items, userIdFields = ['user_id']) => {
    const needed = new Set();
    for (const item of items) {
      for (const field of userIdFields) {
        const userId = item[field];
        const prefix = field.replace('_id', '');
        if (!userId || item[`${prefix}_name`]) continue;
        if (_userCache.current.has(userId) || _failedUserIds.current.has(userId)) continue;
        needed.add(userId);
      }
    }

    // Small fixed concurrency — enough to stay fast, low enough never to
    // burst the rate limiter no matter how many rows are on screen.
    const ids = [...needed];
    const CONCURRENCY = 5;
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      await Promise.all(ids.slice(i, i + CONCURRENCY).map(async (userId) => {
        try {
          const res = await adminAPI.getUserDetails(userId);
          const u = res?.data || res;
          if (u) {
            // A user can have a profile object with empty names — fall back to
            // full_name / username / email rather than landing on "Unknown".
            const fullName = u.profile
              ? `${u.profile.first_name || ''} ${u.profile.last_name || ''}`.trim()
              : '';
            _userCache.current.set(userId, {
              name: fullName || u.full_name || u.name || u.username || u.email || 'Unknown',
              email: u.email || '',
              avatar: u.profile?.avatar || '',
              status: u.status || 'active',
            });
          }
        } catch {
          _failedUserIds.current.add(userId);
        }
      }));
    }

    return items.map((item) => {
      const enriched = { ...item };
      for (const field of userIdFields) {
        const userId = item[field];
        const prefix = field.replace('_id', '');
        if (!userId || item[`${prefix}_name`]) continue;
        const u = _userCache.current.get(userId);
        enriched[`${prefix}_name`] = u?.name || 'Unknown';
        enriched[`${prefix}_email`] = u?.email || '';
        enriched[`${prefix}_avatar`] = u?.avatar || '';
        if (field === 'user_id' && u) enriched.user_status = u.status;
      }
      return enriched;
    });
  };

  // Build a readable list of item names from an order (e.g. "iPhone 16, AirPods").
  const getOrderItemsLabel = (order) => {
    const items = order?.items || order?.products || [];
    if (!Array.isArray(items) || items.length === 0) return '';
    const names = items
      .map(it => it.product_title || it.product_snapshot?.title || it.title || it.name || it.product_name)
      .filter(Boolean);
    if (names.length === 0) return '';
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
  };

  // Best available customer name for an order.
  const getOrderCustomerName = (order) =>
    order?.buyer_name || order?.buyer?.name || order?.customer_name || order?.buyer_email ||
    (order?.buyer_id ? `Customer #${String(order.buyer_id).slice(-6).toUpperCase()}` : 'Customer');

  const fetchOrders = async (statusFilter = 'all', search = '', page = 1) => {
    setLoading(true);
    try {
      const params = { limit: 100, page };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const response = await adminAPI.getAllOrders(params);
      const payload = response?.data || response;
      const raw = payload?.orders || (Array.isArray(payload) ? payload : []);
      const orderList = Array.isArray(raw) ? raw : [];
      const enrichedOrders = await enrichItemsWithUserData(orderList, ['buyer_id', 'seller_id']);
      setOrders(enrichedOrders);
      setOrderPage(page);
      const total = payload?.pagination?.total ?? 0;
      setOrderTotalPages(Math.max(1, Math.ceil(total / 100)));
    } catch (error) {
      // A 429 here isn't a data problem and shouldn't read like one — say
      // what actually happened so it's clear waiting will fix it.
      showToast(
        error?.status === 429
          ? 'Too many requests — please wait a moment and refresh'
          : 'Failed to load orders',
        'error'
      );
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Shared debounce for the orders search box.
  const _ordersSearchTimer = useRef(null);
  const debouncedFetchOrders = (statusFilter, search) => {
    if (_ordersSearchTimer.current) clearTimeout(_ordersSearchTimer.current);
    _ordersSearchTimer.current = setTimeout(() => fetchOrders(statusFilter, search), 400);
  };

  const fetchEscrows = async (statusFilter = 'all', search = '', page = 1) => {
    setLoading(true);
    try {
      const params = { limit: 100, page };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const response = await escrowAPI.getAll(params);
      const payload = response?.data || response;
      const raw = payload?.escrows || (Array.isArray(payload) ? payload : []);
      const escrowList = Array.isArray(raw) ? raw : [];
      const enriched = await enrichItemsWithUserData(escrowList, ['buyer_id', 'seller_id']);
      setEscrows(enriched);
      setEscrowPage(page);
      const total = payload?.pagination?.total ?? 0;
      setEscrowTotalPages(Math.max(1, Math.ceil(total / 100)));
    } catch (error) {
      showToast('Failed to load escrows', 'error');
      setEscrows([]);
    } finally {
      setLoading(false);
    }
  };


  const fetchWallets = async (page = 1) => {
    setLoading(true);
    try {
      const response = await adminAPI.getUserWallets({ page, limit: 20 });
      const payload = response?.data || response;
      setWallets(Array.isArray(payload?.wallets) ? payload.wallets : (Array.isArray(payload) ? payload : []));
    } catch (error) {
      showToast('Failed to load wallets', 'error');
      setWallets([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (page = 1) => {
    try {
      const response = await adminAPI.getAllTransactions({ page, limit: 20 });
      const payload = response?.data || response;
      setTransactions(Array.isArray(payload?.transactions) ? payload.transactions : (Array.isArray(payload) ? payload : []));
    } catch {
      setTransactions([]);
    }
  };

  // Pull a list out of any of the response shapes the backend might use:
  // [...] | { data: [...] } | { data: { <key>: [...] } } | { <key>: [...] }
  const extractList = (response, key) => {
    const p = response?.data ?? response;
    if (Array.isArray(p)) return p;
    if (Array.isArray(p?.[key])) return p[key];
    if (Array.isArray(p?.data)) return p.data;
    if (Array.isArray(p?.data?.[key])) return p.data[key];
    return [];
  };

  const fetchDisputes = async (page = 1) => {
    setLoading(true);
    try {
      const response = await adminAPI.getAllDisputes({ page, limit: 20 });
      setDisputes(extractList(response, 'disputes'));
      setDisputePage(page);
      const total = response?.data?.pagination?.total ?? response?.pagination?.total ?? 0;
      setDisputeTotalPages(Math.max(1, Math.ceil(total / 20)));
    } catch (error) {
      // Fallback to the alternate admin disputes route before giving up
      try {
        const alt = await disputesAPI.getAllDisputes();
        setDisputes(extractList(alt, 'disputes'));
      } catch {
        showToast('Failed to load disputes', 'error');
        setDisputes([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async (page = 1) => {
    setLoading(true);
    try {
      const response = await adminAPI.getAllReviews({ page, limit: 20 });
      setReviews(extractList(response, 'reviews'));
    } catch (error) {
      showToast('Failed to load reviews', 'error');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPremiumData = async () => {
    setLoading(true);
    try {
      const [premiumRes, analyticsRes, settingsRes] = await Promise.all([
        adminAPI.getPremiumUsers().catch(() => null),
        adminAPI.getPremiumAnalytics().catch(() => null),
        adminAPI.getPremiumSettings().catch(() => null),
      ]);

      let premiumList = [];
      if (premiumRes) {
        const pp = premiumRes?.data || premiumRes;
        premiumList = Array.isArray(pp) ? pp : (pp?.users || pp?.premium_users || pp?.premiumUsers || []);
      }

      // Fallback: derive from the users list only if the dedicated endpoint
      // returned nothing at all (e.g. genuinely unreachable) — normally
      // GetAllPremiumUsers above already returns real, correctly-shaped data.
      if (premiumList.length === 0 && !premiumRes) {
        const PAGE_SIZE = 25;
        let skip = 0, total = null, allUsers = [];
        while (total === null || skip < total) {
          const batchLimit = total !== null ? Math.min(PAGE_SIZE, total - skip) : PAGE_SIZE;
          if (batchLimit <= 0) break;
          const res = await adminAPI.getUsers({ skip, limit: batchLimit }).catch(() => null);
          const bp = res?.data || res;
          if (!bp) break;
          if (total === null) total = bp?.pagination?.total || 0;
          const batch = Array.isArray(bp?.users) ? bp.users : [];
          if (batch.length === 0) break;
          allUsers = [...allUsers, ...batch];
          skip += batch.length;
        }
        premiumList = allUsers
          .filter(u => u.isPremium || u.is_premium)
          .map(u => ({
            user_id: u.id || u._id,
            user_name: `${u.profile?.first_name || ''} ${u.profile?.last_name || ''}`.trim() || u.username,
            user_email: u.email,
            user_type: u.user_type,
          }));
      }
      setPremiumUsers(premiumList);

      // Prefer backend analytics; otherwise derive from the list we loaded
      const aData = analyticsRes?.data || analyticsRes || null;
      const now = Date.now();
      const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
      const expiringSoon = premiumList.filter(u => {
        const exp = u.end_date;
        if (!exp) return false;
        const expMs = new Date(exp).getTime();
        return expMs > now && expMs - now < thirtyDaysMs;
      });
      setPremiumAnalytics({
        active_count: aData?.active_premium ?? premiumList.length,
        total_revenue: aData?.total_revenue ?? 0,
        avg_duration_days: aData?.avg_duration_days ?? 0,
        expiring_soon: expiringSoon.length,
      });

      const sData = settingsRes?.data || settingsRes || null;
      const realSettings = sData && Array.isArray(sData.plans) && sData.plans.length > 0 ? sData : {
        plans: [
          { id: 'buyer_monthly', name: 'Premium Buyer Monthly', price: 15000, duration_months: 1, currency: 'NGN', user_type: 'buyer', features: [], is_active: true },
          { id: 'seller_monthly', name: 'Premium Seller Monthly', price: 35000, duration_months: 1, currency: 'NGN', user_type: 'seller', features: [], is_active: true },
        ],
        vat_percentage: 7.5,
        currency: 'NGN',
      };
      setPremiumSettings(realSettings);
      setEditingPremiumSettings(JSON.parse(JSON.stringify(realSettings)));
    } catch (error) {
      showToast('Failed to load premium data', 'error');
      setPremiumUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getReports({ limit: 50 });
      const rptPayload = response?.data || response;
      if (rptPayload) {
        const reportsList = Array.isArray(rptPayload) ? rptPayload : (rptPayload.reports || []);
        setReports(reportsList);
      }
    } catch (error) {
      showToast('Failed to load reports', 'error');
      setReports([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      // Fetch categories and products in parallel, then compute per-category product counts
      const [catRes, prodRes] = await Promise.all([
        adminAPI.getCategories(),
        adminAPI.getAllProducts({ limit: 500 }).catch(() => null),
      ]);
      const catPayload = catRes?.data || catRes;
      const categoriesList = Array.isArray(catPayload) ? catPayload : (catPayload?.categories || []);

      // Build a count map from products' category_id field
      const countMap = {};
      if (prodRes) {
        const prodPayload = prodRes?.data || prodRes;
        const prods = Array.isArray(prodPayload) ? prodPayload : (prodPayload?.products || []);
        prods.forEach(p => {
          const cid = p.category_id || p.category?.id;
          if (cid) countMap[cid] = (countMap[cid] || 0) + 1;
        });
      }

      // Merge computed counts into categories (override backend's product_count:0)
      const enriched = categoriesList.map(cat => ({
        ...cat,
        product_count: countMap[cat.id || cat._id] ?? cat.product_count ?? 0,
      }));
      setCategories(enriched);
    } catch (error) {
      showToast('Failed to load categories', 'error');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportTickets = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAdminTickets();
      if (response) {
        const payload = response?.data || response;
        const ticketsList = Array.isArray(payload) ? payload :
          (payload.tickets || []);
        setSupportTickets(ticketsList);
      }
    } catch (error) {
      showToast('Failed to load support tickets', 'error');
      setSupportTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketMessages = async (ticketId) => {
    try {
      const response = await adminAPI.getAdminTicket(ticketId);
      const tmPayload = response?.data || response;
      if (tmPayload) {
        const ticketData = tmPayload;
        const responses = ticketData.responses || [];
        const formattedMessages = responses.map(resp => ({
          id: resp.id,
          message: resp.message,
          sender: resp.is_from_user ? 'user' : 'support',
          created_at: resp.created_at
        }));
        setTicketMessages(formattedMessages);
      }
    } catch (error) {
      showToast('Failed to load ticket messages', 'error');
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // /admin/analytics gives top-line counts; revenue/users trends and the
      // top-categories / top-sellers leaderboards live on dedicated endpoints.
      const [analyticsRes, revenueRes, usersRes, catsRes, sellersRes] = await Promise.all([
        adminAPI.getAnalytics().catch(() => null),
        adminAPI.getRevenueAnalytics().catch(() => null),
        adminAPI.getUserAnalytics().catch(() => null),
        adminAPI.getTopCategories().catch(() => null),
        adminAPI.getTopSellers().catch(() => null),
      ]);

      const d = analyticsRes?.data || analyticsRes || {};
      const revD = revenueRes?.data || revenueRes || {};
      const usrD = usersRes?.data || usersRes || {};

      // top-categories: [{ _id: category_name, sales, revenue }]
      const rawCats = catsRes?.data || catsRes || [];
      const topCategories = (Array.isArray(rawCats) ? rawCats : (rawCats.categories || [])).map((c) => ({
        name: c.name || c.category_name || (c._id ? c._id : 'Uncategorized'),
        count: c.sales ?? c.count ?? 0,
        revenue: c.revenue ?? 0,
      }));

      // top-sellers: [{ _id: seller_id, sales, revenue }] — resolve seller names
      const rawSellers = sellersRes?.data || sellersRes || [];
      const sellerList = (Array.isArray(rawSellers) ? rawSellers : (rawSellers.sellers || []))
        .map((s) => ({ ...s, seller_id: s.seller_id || s._id }));
      const enrichedSellers = await enrichItemsWithUserData(sellerList, ['seller_id']);
      const topSellers = enrichedSellers.map((s) => ({
        name: s.seller_name && s.seller_name !== 'Unknown' ? s.seller_name : (s.name || `Seller ${String(s.seller_id || '').slice(-6)}`),
        revenue: s.revenue ?? 0,
        sales: s.sales ?? 0,
      }));

      setAnalyticsData({
        totalUsers: d.total_users || d.totalUsers || 0,
        totalOrders: d.total_orders || d.totalOrders || 0,
        totalProducts: d.total_products || d.totalProducts || 0,
        newUsers: usrD.new_users || d.new_users || { today: 0, week: 0, month: 0 },
        revenue: revD.revenue || d.revenue || { today: 0, week: 0, month: 0 },
        transactions: d.transactions || { today: 0, week: 0, month: 0 },
        topCategories,
        topSellers,
      });
    } catch (error) {
      showToast('Failed to load analytics', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemMetrics = async () => {
    setLoading(true);
    try {
      const [statusRes, metricsRes, apiStatsRes] = await Promise.all([
        adminAPI.getSystemStatus().catch(() => null),
        adminAPI.getSystemMetricsDetailed().catch(() => null),
        adminAPI.getAPIStats().catch(() => null),
      ]);
      // getSystemStatus: { database: { collections: {...}, connected }, environment: {...}, timestamp }
      // getSystemMetricsDetailed: { cpu: { usage_percent, cores }, memory: { usage_percent, ... }, storage: { usage_percent, ... } }
      // getAPIStats: { active_connections, queued_jobs, uptime_seconds, db_latency_ms, db_reachable }
      const d = statusRes?.data || statusRes || {};
      const m = metricsRes?.data || metricsRes || {};
      const a = apiStatsRes?.data || apiStatsRes || {};
      setSystemMetrics({
        cpu: m.cpu?.usage_percent ?? 0,
        cpuCores: m.cpu?.cores ?? 0,
        memory: m.memory?.usage_percent ?? 0,
        storage: m.storage?.usage_percent ?? 0,
        apiLatency: a.db_latency_ms ?? 0,
        uptime: a.uptime_seconds ?? 0,
        activeConnections: a.active_connections ?? 0,
        queuedJobs: a.queued_jobs ?? 0,
        dbReachable: a.db_reachable ?? true,
        dbConnected: d.database?.connected ?? false,
        dbCollections: d.database?.collections || {},
        environment: d.environment || {},
        lastChecked: d.timestamp || new Date().toISOString(),
      });
    } catch (error) {
      showToast('Failed to load system metrics', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAdmins();
      const payload = res?.data || res;
      setAdmins(Array.isArray(payload) ? payload : []);
    } catch (error) {
      showToast('Failed to load admins', 'error');
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemActivity = async () => {
    setLoading(true);
    try {
      // /admin/system/logs returns null. /admin/system/activity has real audit trail data.
      const res = await adminAPI.getRecentActivity({ limit: 100 });
      const payload = res?.data || res;
      const activities = Array.isArray(payload) ? payload : [];
      // Map activity entries to the log shape the render function expects
      const logs = activities.map(a => ({
        type: a.action?.includes('delete') ? 'warn' : a.action?.includes('reject') ? 'error' : 'info',
        timestamp: a.created_at,
        message: `[${a.action?.replace(/_/g, ' ').toUpperCase()}] Admin ${a.admin_id?.substring(0, 8)}... → user ${a.target_user_id?.substring(0, 8) || 'N/A'}... ${a.details ? JSON.stringify(a.details) : ''}`,
        action: a.action,
        admin_id: a.admin_id,
        target_user_id: a.target_user_id,
        details: a.details,
        raw: a,
      }));
      setSystemLogs(logs);
    } catch (error) {
      setSystemLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketingData = async () => {
    setLoading(true);
    try {
      const [bannersRes, flashSalesRes, promosRes] = await Promise.all([
        adminAPI.getAllBanners().catch(() => ({ data: [] })),
        adminAPI.getAllFlashSales().catch(() => ({ data: [] })),
        adminAPI.getAllPromoCodes().catch(() => ({ data: [] }))
      ]);
      const bPayload = bannersRes?.data || bannersRes || [];
      const fsPayload = flashSalesRes?.data || flashSalesRes || [];
      const pcPayload = promosRes?.data || promosRes || [];
      setBanners(Array.isArray(bPayload) ? bPayload : (bPayload.banners || []));
      setFlashSales(Array.isArray(fsPayload) ? fsPayload : (fsPayload.flash_sales || []));
      setPromoCodes(Array.isArray(pcPayload) ? pcPayload : (pcPayload.promo_codes || []));
    } catch (error) {
      showToast('Failed to load marketing data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommunicationData = async () => {
    setLoading(true);
    try {
      const [templatesRes, statsRes] = await Promise.all([
        adminAPI.getNotificationTemplates().catch(() => null),
        adminAPI.getNotificationStats().catch(() => null),
      ]);
      const tmplP = templatesRes?.data || templatesRes || [];
      // Backend fields: total_sent, total_read, read_rate, total_unread
      const rawStats = statsRes?.data || statsRes || {};
      setNotificationLogs([]);
      setNotificationTemplates(Array.isArray(tmplP) ? tmplP : (tmplP.templates || []));
      setNotificationSchedules([]);
      setNotificationStats({
        sent: rawStats.total_sent ?? rawStats.sent ?? 0,
        delivered: rawStats.total_sent ?? rawStats.delivered ?? 0,
        read: rawStats.total_read ?? rawStats.read ?? 0,
        failed: rawStats.total_unread ?? rawStats.failed ?? 0,
        read_rate: rawStats.read_rate ?? 0,
      });
    } catch (error) {
      showToast('Failed to load communication data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSwapData = async (page = 1) => {
    setLoading(true);
    try {
      // Only the admin-wide endpoints can return OTHER users' swaps. We deliberately
      // do NOT fall back to /swap/offers — that endpoint is scoped to the logged-in
      // admin, so it would show the admin's own swaps as if they were everyone's.
      const [adminAllRes, pendingRes] = await Promise.all([
        adminAPI.getAllSwaps({ limit: 100, page }).catch(() => null),
        adminAPI.getPendingSwaps().catch(() => null),
      ]);
      setSwapPage(page);
      const swapTotal = adminAllRes?.data?.pagination?.total ?? adminAllRes?.pagination?.total ?? 0;
      setSwapTotalPages(Math.max(1, Math.ceil(swapTotal / 100)));

      const parseList = (res, keys) => {
        if (!res) return [];
        const p = res?.data || res;
        if (Array.isArray(p)) return p;
        for (const k of keys) if (Array.isArray(p?.[k])) return p[k];
        return [];
      };

      // adminAllRes is null when /admin/swaps is unavailable (currently 501).
      setSwapEndpointReady(!!adminAllRes || !!pendingRes);

      const allSwapsList = parseList(adminAllRes, ['swaps', 'swap_offers', 'deals']);
      let pendingSwapsList = parseList(pendingRes, ['swaps', 'swap_offers', 'pending']);
      if (pendingSwapsList.length === 0) {
        pendingSwapsList = allSwapsList.filter(swap =>
          ['pending_approval', 'pending_inspection', 'pending'].includes(swap?.status)
        );
      }

      setSwaps(allSwapsList);
      setPendingSwaps(pendingSwapsList);
    } catch (error) {
      showToast('Failed to load swap data', 'error');
      setSwaps([]);
      setPendingSwaps([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProhibitedItems = async () => {
    try {
      const response = await adminAPI.getProhibitedItems();
      const piPayload = response?.data || response;
      const piItems = piPayload?.items || [];
      setProhibitedItems(piItems);
      setProhibitedItemsText(piItems.join(', '));
    } catch (error) {
      console.error('Failed to fetch prohibited items:', error);
    }
  };

  const handleUpdateProhibitedItems = async () => {
    try {
      const items = prohibitedItemsText.split(',').map(i => i.trim()).filter(i => i);
      await adminAPI.updateProhibitedItems({ items });
      showToast('Prohibited items updated', 'success');
      fetchProhibitedItems();
    } catch (error) {
      showToast('Failed to update prohibited items', 'error');
    }
  };

  const handleInitDatabase = async () => {
    if (!window.confirm('WARNING: This will initialize the database. Are you sure?')) return;
    setInitializingSystem(true);
    try {
      await adminAPI.initializeDatabase();
      showToast('Database initialized successfully', 'success');
    } catch (error) {
      showToast('Failed to initialize database', 'error');
    } finally {
      setInitializingSystem(false);
    }
  };


  // ===== ACTION HANDLERS =====

  const handleApproveUser = async (userId) => {
    try {
      await adminAPI.updateUserStatus(userId, 'active');
      showToast('User approved successfully', 'success');
      fetchUsers(currentPage, userSearch, roleFilter, userSort);
    } catch (error) {
      showToast('Failed to approve user', 'error');
    }
  };

  const handleSuspendUser = (userId) => {
    setSuspendTargetUser(userId);
    setSuspendReason('');
    setShowSuspendModal(true);
  };

  const submitSuspendUser = async () => {
    if (!suspendReason.trim()) { showToast('Please enter a suspension reason', 'error'); return; }
    try {
      await adminAPI.updateUserStatus(suspendTargetUser, 'suspended', suspendReason.trim());
      showToast('User suspended successfully', 'success');
      setShowSuspendModal(false);
      fetchUsers(currentPage, userSearch, roleFilter, userSort);
    } catch (error) {
      showToast('Failed to suspend user', 'error');
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      await adminAPI.updateUserStatus(userId, 'active', 'Reactivated by admin');
      showToast('User reactivated successfully', 'success');
      fetchUsers(currentPage, userSearch, roleFilter, userSort);
    } catch (error) {
      showToast('Failed to reactivate user', 'error');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    // AdminDeleteUser only sets status: "inactive" — a soft delete, data is
    // retained. The confirm dialog used to claim permanence it doesn't have.
    if (!window.confirm(`Deactivate user ${userEmail || userId}? Their account will be suspended and data retained — this is not a permanent deletion.`)) return;
    try {
      await adminAPI.deleteUser(userId);
      showToast('User deactivated', 'success');
      fetchUsers(currentPage, userSearch, roleFilter, userSort);
    } catch (error) {
      showToast('Failed to delete user', 'error');
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;

    try {
      await adminAPI.updateUserStatus(userId, 'inactive', 'Deactivated by admin');
      showToast('User deactivated successfully', 'success');
      fetchUsers(currentPage, userSearch, roleFilter, userSort);
    } catch (error) {
      showToast('Failed to deactivate user', 'error');
    }
  };

  const handleVerifyBadge = async (userId, currentStatus) => {
    const verified = !currentStatus;
    const reason = verified ? prompt('Reason for verification:') : prompt('Reason for unverifying:');
    if (verified && !reason) return;
    try {
      await adminAPI.verifyUserBadge(userId, verified, reason || '');
      showToast(verified ? 'User badge verified' : 'User badge removed', 'success');
      fetchUsers(currentPage, userSearch, roleFilter, userSort);
    } catch (error) {
      showToast('Failed to update badge status', 'error');
    }
  };

  const handleUpdateUserPerks = (userId, user) => {
    setPerksTargetUser(userId);
    // UpdateUserPerks replaces the whole perks list server-side (by design —
    // it's a "set" endpoint, not "add one"), but this modal used to always
    // open blank with no way to see what a user already had. An admin adding
    // one perk to an existing three would submit just that one and silently
    // wipe the other three. Pre-filling with the user's current perks makes
    // "add a perk" actually mean add, not blind overwrite.
    const currentPerks = user?.profile?.perks || user?.perks || [];
    setPerksInput(currentPerks.join(', '));
    setShowPerksModal(true);
  };

  const submitUpdateUserPerks = async () => {
    try {
      const perks = perksInput.split(',').map(p => p.trim()).filter(Boolean);
      await adminAPI.updateUserPerks(perksTargetUser, perks);
      showToast('User perks updated', 'success');
      setShowPerksModal(false);
      fetchUsers(currentPage, userSearch, roleFilter, userSort);
    } catch (error) {
      showToast('Failed to update perks', 'error');
    }
  };

  const handleViewUser = async (user) => {
    try {
      // Fetch detailed user information
      const response = await adminAPI.getUserDetails(user._id || user.id);
      const vuPayload = response?.data || response;
      if (vuPayload) {
        setSelectedUser(vuPayload);
        setShowUserModal(true);
      } else {
        setSelectedUser(user);
        setShowUserModal(true);
      }
    } catch (error) {
      // Fallback to existing user data
      setSelectedUser(user);
      setShowUserModal(true);
    }
  };

  const handleApproveProduct = async (productId) => {
    try {
      await adminAPI.approveProduct(productId);
      showToast('Product approved successfully', 'success');
      fetchProducts(productStatusFilter, productSearch);
    } catch (error) {
      showToast('Failed to approve product', 'error');
    }
  };

  const handleRejectProduct = (productId) => {
    setRejectProductTargetId(productId);
    setRejectProductReason('');
    setShowRejectProductModal(true);
  };

  const submitRejectProduct = async () => {
    if (!rejectProductReason.trim()) { showToast('Please enter a rejection reason', 'error'); return; }
    try {
      await adminAPI.rejectProduct(rejectProductTargetId, rejectProductReason.trim());
      showToast('Product rejected successfully', 'success');
      setShowRejectProductModal(false);
      fetchProducts(productStatusFilter, productSearch);
    } catch (error) {
      showToast('Failed to reject product', 'error');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      return;
    }
    try {
      await adminAPI.deleteProduct(productId);
      showToast('Product deleted successfully', 'success');
      fetchProducts(productStatusFilter, productSearch);
    } catch (error) {
      showToast('Failed to delete product', 'error');
    }
  };

  const handleFeatureProduct = async (productId, featured) => {
    try {
      // Use updateProductFlags for more comprehensive control
      await adminAPI.updateProductFlags(productId, {
        is_featured: featured
      });
      showToast(`Product ${featured ? 'featured' : 'unfeatured'} successfully`, 'success');
      fetchProducts(productStatusFilter, productSearch);
    } catch (error) {
      showToast('Failed to update product', 'error');
    }
  };

  const handleUpdateProductFlags = async (productId, flags) => {
    try {
      await adminAPI.updateProductFlags(productId, flags);
      showToast('Product flags updated successfully', 'success');
      fetchProducts(productStatusFilter, productSearch);
    } catch (error) {
      showToast('Failed to update product flags', 'error');
    }
  };

  const handleToggleProductTrending = async (productId, isTrending) => {
    try {
      await adminAPI.updateProductFlags(productId, {
        is_trending: !isTrending
      });
      showToast(`Product ${!isTrending ? 'marked as trending' : 'removed from trending'}`, 'success');
      fetchProducts(productStatusFilter, productSearch);
    } catch (error) {
      showToast('Failed to update trending status', 'error');
    }
  };

  const handleToggleProductHot = async (productId, isHot) => {
    try {
      await adminAPI.updateProductFlags(productId, {
        is_hot: !isHot
      });
      showToast(`Product ${!isHot ? 'marked as hot' : 'removed from hot deals'}`, 'success');
      fetchProducts(productStatusFilter, productSearch);
    } catch (error) {
      showToast('Failed to update hot status', 'error');
    }
  };

  const handleToggleProductGoodDeal = async (productId, isGoodDeal) => {
    try {
      // The Homepage "Good Deals" section is fed by getFeatured (?featured=true),
      // so "Good Deal" is backed by the real is_featured flag.
      await adminAPI.updateProductFlags(productId, { is_featured: !isGoodDeal });
      showToast(`Product ${!isGoodDeal ? 'marked as Good Deal' : 'removed from Good Deals'}`, 'success');
      fetchProducts(productStatusFilter, productSearch);
    } catch (error) {
      showToast('Failed to update good deal status', 'error');
    }
  };

  const handleBoostProduct = (productId) => {
    setBoostTargetId(productId);
    setBoostLevel('1');
    setBoostDays('7');
    setShowBoostModal(true);
  };

  const submitBoostProduct = async () => {
    const level = parseInt(boostLevel);
    const duration = parseInt(boostDays);
    if (isNaN(level) || level < 1 || level > 5) { showToast('Boost level must be 1–5', 'error'); return; }
    if (isNaN(duration) || duration < 1) { showToast('Duration must be at least 1 day', 'error'); return; }
    try {
      const boostExpiresAt = new Date();
      boostExpiresAt.setDate(boostExpiresAt.getDate() + duration);
      await adminAPI.updateProductFlags(boostTargetId, {
        boost_level: level,
        boost_expires_at: boostExpiresAt.toISOString(),
        is_premium_listing: true
      });
      showToast(`Product boosted to level ${level} for ${duration} days`, 'success');
      setShowBoostModal(false);
      fetchProducts(productStatusFilter, productSearch);
    } catch (error) {
      showToast('Failed to boost product', 'error');
    }
  };

  // Flash Deal — a real product marked down for a limited time (Product.is_flash_deal).
  // This is what actually shows as a product card on the homepage/shop; it is
  // NOT the same thing as a "Sales Campaign" under Marketing, which is a
  // promotional banner with no product attached.
  const handleOpenFlashDeal = (product) => {
    setFlashDealTarget(product);
    setFlashDealForm({ price: '', end_time: '', early_access_hours: '24' });
    setShowFlashDealModal(true);
  };

  const submitFlashDeal = async () => {
    const pid = flashDealTarget?._id || flashDealTarget?.id;
    const price = parseFloat(flashDealForm.price);
    if (isNaN(price) || price <= 0) return showToast('Enter a valid flash deal price', 'error');
    if (flashDealTarget?.price && price >= flashDealTarget.price) {
      return showToast('Flash deal price should be lower than the regular price', 'error');
    }
    if (!flashDealForm.end_time) return showToast('End date is required', 'error');
    try {
      await adminAPI.updateProductFlags(pid, {
        is_flash_deal: true,
        flash_deal_price: price,
        flash_deal_ends_at: new Date(flashDealForm.end_time).toISOString(),
        flash_deal_early_access_hours: parseInt(flashDealForm.early_access_hours) || 0,
      });
      showToast('Flash deal set — live on the homepage and shop', 'success');
      setShowFlashDealModal(false);
      fetchProducts(productStatusFilter, productSearch);
    } catch (error) {
      showToast('Failed to set flash deal', 'error');
    }
  };

  const handleRemoveFlashDeal = async (productId) => {
    if (!window.confirm('Remove this flash deal? The product reverts to its regular price.')) return;
    try {
      await adminAPI.updateProductFlags(productId, { is_flash_deal: false });
      showToast('Flash deal removed', 'success');
      fetchProducts(productStatusFilter, productSearch);
    } catch (error) {
      showToast('Failed to remove flash deal', 'error');
    }
  };

  const handleViewProduct = async (product) => {
    try {
      const response = await adminAPI.getProductDetails(product._id || product.id);
      const detailed = response?.data || product;
      setEditingProduct(detailed);
      setProductFormData({
        title: detailed.title || '',
        description: detailed.description || '',
        price: detailed.price || '',
        category: detailed.category || '',
        condition: detailed.condition || 'new',
        quantity: detailed.quantity || 1,
        images: detailed.images || []
      });
      setShowProductModal(true);
    } catch (error) {
      showToast('Failed to load product details', 'error');
    }
  };

  const handleCreateProduct = () => {
    setEditingProduct(null);
    setProductFormData({
      title: '',
      description: '',
      price: '',
      seller_price: '',
      currency: 'NGN',
      category_id: '',
      condition: 'new',
      quantity: 1,
      brand: '',
      model: '',
      specifications: {},
      location: { city: '', state: '', country: 'Nigeria' },
      images: []
    });
    setProductImageUrlInput('');
    setProductSpecInput({ key: '', value: '' });
    setShowProductModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductFormData({
      title: product.title || '',
      description: product.description || '',
      price: product.price || '',
      seller_price: product.seller_price || '',
      currency: product.currency || 'NGN',
      category_id: product.category_id || '',
      condition: product.condition || 'new',
      quantity: product.quantity || 1,
      brand: product.brand || '',
      model: product.model || '',
      specifications: product.specifications || {},
      location: {
        city: product.location?.city || '',
        state: product.location?.state || '',
        country: product.location?.country || 'Nigeria',
      },
      images: product.images || []
    });
    setProductImageUrlInput('');
    setProductSpecInput({ key: '', value: '' });
    setShowProductModal(true);
  };

  const handleProductImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const currentImages = productFormData.images || [];
    if (currentImages.length + files.length > 10) {
      showToast(`Max 10 images. Currently have ${currentImages.length}.`, 'error');
      return;
    }
    const validFiles = files.filter(f => {
      const v = validateImageFile(f, { maxSize: 10 * 1024 * 1024, allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'] });
      if (!v.valid) showToast(`${f.name}: ${v.error}`, 'error');
      return v.valid;
    });
    if (!validFiles.length) return;
    setProductUploadingImages(true);
    setProductUploadProgress({ current: 0, total: validFiles.length, fileName: '' });
    try {
      const urls = await uploadMultipleImages(validFiles, (p) => {
        setProductUploadProgress({ current: p.index + 1, total: p.total, fileName: p.fileName, status: p.status });
        if (p.status === 'error') showToast(`Failed to upload ${p.fileName}`, 'error');
      });
      const ok = urls.filter(Boolean);
      if (ok.length) {
        setProductFormData(prev => ({ ...prev, images: [...(prev.images || []), ...ok].slice(0, 10) }));
        showToast(`Uploaded ${ok.length} image(s)`, 'success');
      }
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error');
    } finally {
      setProductUploadingImages(false);
      setProductUploadProgress({ current: 0, total: 0, fileName: '' });
      e.target.value = '';
    }
  };

  const handleAddProductImageUrl = () => {
    const url = productImageUrlInput.trim();
    if (!url) return;
    const currentImages = productFormData.images || [];
    if (currentImages.length >= 10) { showToast('Max 10 images', 'error'); return; }
    setProductFormData(prev => ({ ...prev, images: [...(prev.images || []), url] }));
    setProductImageUrlInput('');
  };

  const removeProductImage = (idx) => {
    setProductFormData(prev => ({ ...prev, images: (prev.images || []).filter((_, i) => i !== idx) }));
  };

  const addProductSpec = () => {
    const k = productSpecInput.key.trim();
    const v = productSpecInput.value.trim();
    if (!k || !v) return;
    setProductFormData(prev => ({ ...prev, specifications: { ...prev.specifications, [k]: v } }));
    setProductSpecInput({ key: '', value: '' });
  };

  const removeProductSpec = (key) => {
    setProductFormData(prev => {
      const specs = { ...prev.specifications };
      delete specs[key];
      return { ...prev, specifications: specs };
    });
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();

    if (!productFormData.title || !productFormData.price || !productFormData.category_id ||
        !productFormData.location?.city || !productFormData.location?.state || !productFormData.location?.country) {
      showToast('Please fill in all required fields (title, price, category, location)', 'error');
      return;
    }

    try {
      setLoading(true);

      // Build payload with both snake_case and PascalCase keys — the /admin/products
      // Go struct uses PascalCase field names without json tags, so we send both forms
      // to cover whichever the backend is bound to.
      const loc = productFormData.location || {};
      const payload = {
        // Common fields (snake_case — matched case-insensitively by Go JSON decoder)
        title: productFormData.title,
        description: productFormData.description,
        condition: productFormData.condition,
        brand: productFormData.brand || '',
        model: productFormData.model || '',
        specifications: productFormData.specifications || {},
        swap_available: productFormData.swap_available || false,
        tags: productFormData.tags || [],
        status: productFormData.status || 'active',
        images: (productFormData.images || []).filter(img => !String(img).startsWith('blob:')),
        // Numeric fields — must be numbers, not strings
        price: parseFloat(productFormData.price) || 0,
        quantity: parseInt(productFormData.quantity, 10) || 1,
        // PascalCase variants for admin endpoint struct (no json tags)
        Currency: productFormData.currency || 'NGN',
        CategoryID: productFormData.category_id || '',
        Location: {
          City: loc.city || '',
          State: loc.state || '',
          Country: loc.country || 'Nigeria',
        },
        // snake_case variants as fallback (Go JSON is case-insensitive for single words)
        currency: productFormData.currency || 'NGN',
        category_id: productFormData.category_id || '',
        location: {
          city: loc.city || '',
          state: loc.state || '',
          country: loc.country || 'Nigeria',
        },
      };
      if (productFormData.seller_price) {
        payload.seller_price = parseFloat(productFormData.seller_price);
        payload.SellerPrice = parseFloat(productFormData.seller_price);
      }

      if (editingProduct) {
        await adminAPI.updateProduct(editingProduct._id || editingProduct.id, payload);
        showToast('Product updated successfully', 'success');
      } else {
        await adminAPI.createProduct(payload);
        showToast('Product created successfully', 'success');
      }

      setShowProductModal(false);
      fetchProducts(productStatusFilter, productSearch);
    } catch (error) {
      showToast(`Failed to ${editingProduct ? 'update' : 'create'} product`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeProductStatus = async (productId, newStatus) => {
    try {
      await adminAPI.updateProductStatus(productId, newStatus);
      showToast(`Product status changed to ${newStatus}`, 'success');
      fetchProducts(productStatusFilter, productSearch);
      setShowProductModal(false);
      setEditingProduct(null);
    } catch (error) {
      showToast('Failed to change product status', 'error');
    }
  };

  const handleViewOrder = async (order) => {
    try {
      const response = await adminAPI.getOrderDetails(order._id || order.id);
      const payload = response?.data || response;
      const detail = payload?.order || payload || order;
      setSelectedOrder({ ...order, ...detail });
      setShowOrderDetailModal(true);
    } catch (error) {
      setSelectedOrder(order);
      setShowOrderDetailModal(true);
    }
  };

  const handleUpdateOrderStatus = (orderId, currentStatus) => {
    setOrderStatusTargetId(orderId);
    setOrderStatusValue(currentStatus || 'pending');
    setOrderStatusNote('');
    setShowOrderStatusModal(true);
  };

  const submitUpdateOrderStatus = async () => {
    if (!orderStatusValue) { showToast('Please select a status', 'error'); return; }
    try {
      await adminAPI.updateOrderStatus(orderStatusTargetId, orderStatusValue);
      showToast('Order status updated successfully', 'success');
      setShowOrderStatusModal(false);
      fetchOrders(orderStatusFilter, orderSearch);
    } catch (error) {
      showToast('Failed to update order status', 'error');
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await adminAPI.cancelOrder(orderId, 'Cancelled by admin');
      showToast('Order cancelled successfully', 'success');
      fetchOrders(orderStatusFilter, orderSearch);
    } catch (error) {
      showToast('Failed to cancel order', 'error');
    }
  };

  const handleReleaseEscrow = (escrow) => {
    setEscrowActionTarget(escrow);
    setEscrowActionType('release');
    setEscrowActionNote('');
    setShowEscrowActionModal(true);
  };

  const handleRefundEscrow = (escrow) => {
    setEscrowActionTarget(escrow);
    setEscrowActionType('refund');
    setEscrowActionNote('');
    setShowEscrowActionModal(true);
  };

  const submitEscrowAction = async () => {
    if (!escrowActionTarget) return;
    const eid = escrowActionTarget._id || escrowActionTarget.id;
    if (escrowActionType === 'refund' && !escrowActionNote.trim()) {
      showToast('A refund reason is required', 'error');
      return;
    }
    try {
      if (escrowActionType === 'release') {
        await escrowAPI.releaseEscrow(eid, escrowActionNote.trim());
        showToast('Funds released to seller', 'success');
      } else {
        await escrowAPI.disputeEscrow(eid, escrowActionNote.trim());
        showToast('Funds refunded to buyer', 'success');
      }
      setShowEscrowActionModal(false);
      setEscrowActionNote('');
      setEscrowActionTarget(null);
      fetchEscrows(escrowStatusFilter, escrowSearch);
    } catch (error) {
      // Pandascrow-backed escrows reject manual release/refund by design (release
      // needs the buyer's OTP; refund only happens via Pandascrow's dispute flow).
      // Surface the backend's explanation instead of a generic failure message.
      showToast(error?.message || `Failed to ${escrowActionType} escrow`, 'error');
    }
  };

  const handleViewEscrowDetails = async (escrowId) => {
    setShowEscrowDetailModal(true);
    setEscrowDetailsLoading(true);
    setSelectedEscrowDetails(null);
    try {
      const res = await escrowAPI.getDetails(escrowId);
      const raw = res?.data || res;
      const escrowObj = raw?.escrow || raw || {};
      const [enriched] = await enrichItemsWithUserData([escrowObj], ['buyer_id', 'seller_id', 'payer_id', 'payee_id']);
      setSelectedEscrowDetails(enriched);
    } catch (error) {
      showToast(error?.message || 'Failed to load escrow details', 'error');
      setShowEscrowDetailModal(false);
    } finally {
      setEscrowDetailsLoading(false);
    }
  };

  const handleApproveKYC = async (userId) => {
    try {
      await adminAPI.approveKYC(userId, 'Verified by admin');
      showToast('KYC approved successfully', 'success');
      fetchKYCRequests(kycStatusFilter);
    } catch (error) {
      showToast('Failed to approve KYC', 'error');
    }
  };

  const handleRejectKYC = (userId) => {
    setRejectKYCTargetUser(userId);
    setRejectKYCReason('');
    setShowRejectKYCModal(true);
  };

  const submitRejectKYC = async () => {
    if (!rejectKYCReason.trim()) { showToast('Please enter a rejection reason', 'error'); return; }
    try {
      await adminAPI.rejectKYC(rejectKYCTargetUser, rejectKYCReason.trim());
      showToast('KYC rejected', 'success');
      setShowRejectKYCModal(false);
      fetchKYCRequests(kycStatusFilter);
    } catch (error) {
      showToast('Failed to reject KYC', 'error');
    }
  };

  // Support Ticket Handlers
  const handleSelectTicket = async (ticket) => {
    setSelectedTicket(ticket);
    await fetchTicketMessages(ticket.id);
  };

  const handleReplyToTicket = async (e) => {
    e.preventDefault();
    if (!ticketReplyMessage.trim() || !selectedTicket) return;

    try {
      await adminAPI.replyAdminTicket(selectedTicket.id, ticketReplyMessage);
      showToast('Reply sent successfully', 'success');
      setTicketReplyMessage('');
      await fetchTicketMessages(selectedTicket.id);
    } catch (error) {
      showToast('Failed to send reply', 'error');
    }
  };

  // Valid ticket statuses are open, in_progress, resolved and closed — the
  // backend rejects anything else.
  const handleSetTicketStatus = async (ticketId, status) => {
    const labels = { in_progress: 'in progress', resolved: 'resolved', closed: 'closed' };
    if (status === 'closed' && !window.confirm('Close this ticket? The user will no longer be able to reply.')) return;

    try {
      await adminAPI.updateAdminTicketStatus(ticketId, status);
      showToast(`Ticket marked ${labels[status] || status}`, 'success');
      if (status === 'closed') {
        setSelectedTicket(null);
        setTicketMessages([]);
      } else {
        setSelectedTicket(prev => prev && prev.id === ticketId ? { ...prev, status } : prev);
      }
      fetchSupportTickets();
    } catch (error) {
      showToast(error?.message || `Failed to mark ticket ${labels[status] || status}`, 'error');
    }
  };

  // Review Handlers
  const handleOpenReviewResponse = (review, type) => {
    setSelectedReviewForResponse(review);
    setReviewResponseType(type);
    setReviewRejectReason('');
    setShowReviewResponseModal(true);
  };

  const handleApproveReview = async () => {
    if (!selectedReviewForResponse) return;
    setReviewResponseLoading(true);
    try {
      await adminAPI.approveReview(selectedReviewForResponse._id || selectedReviewForResponse.id);
      showToast('Review approved successfully', 'success');
      setShowReviewResponseModal(false);
      fetchReviews();
    } catch (error) {
      showToast('Failed to approve review', 'error');
    } finally {
      setReviewResponseLoading(false);
    }
  };

  const handleRejectReview = async () => {
    if (!selectedReviewForResponse || !reviewRejectReason.trim()) {
      showToast('Please enter a rejection reason', 'error');
      return;
    }
    setReviewResponseLoading(true);
    try {
      await adminAPI.rejectReview(selectedReviewForResponse._id || selectedReviewForResponse.id, reviewRejectReason);
      showToast('Review rejected successfully', 'success');
      setShowReviewResponseModal(false);
      fetchReviews();
    } catch (error) {
      showToast('Failed to reject review', 'error');
    } finally {
      setReviewResponseLoading(false);
    }
  };

  const handleFlagReview = async () => {
    if (!selectedReviewForResponse) return;
    setReviewResponseLoading(true);
    try {
      await adminAPI.flagReview(selectedReviewForResponse._id || selectedReviewForResponse.id);
      showToast('Review flagged for review', 'success');
      setShowReviewResponseModal(false);
      fetchReviews();
    } catch (error) {
      showToast('Failed to flag review', 'error');
    } finally {
      setReviewResponseLoading(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!selectedReviewForResponse) return;
    setReviewResponseLoading(true);
    try {
      await adminAPI.deleteReview(selectedReviewForResponse._id || selectedReviewForResponse.id);
      showToast('Review deleted successfully', 'success');
      setShowReviewResponseModal(false);
      fetchReviews();
    } catch (error) {
      showToast('Failed to delete review', 'error');
    } finally {
      setReviewResponseLoading(false);
    }
  };

  // ===== CATEGORY CRUD HANDLERS =====

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({ name: '', description: '', icon: '' });
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name || '',
      description: category.description || '',
      icon: category.icon || ''
    });
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryFormData.name.trim()) {
      showToast('Category name is required', 'error');
      return;
    }
    try {
      if (editingCategory) {
        await adminAPI.updateCategory(editingCategory._id || editingCategory.id, categoryFormData);
        showToast('Category updated successfully', 'success');
      } else {
        await adminAPI.createCategory(categoryFormData);
        showToast('Category created successfully', 'success');
      }
      setShowCategoryModal(false);
      fetchCategories();
    } catch (error) {
      showToast('Failed to save category', 'error');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category? Products in it will be uncategorized.')) return;
    try {
      await adminAPI.deleteCategory(categoryId);
      showToast('Category deleted successfully', 'success');
      fetchCategories();
    } catch (error) {
      showToast('Failed to delete category', 'error');
    }
  };

  // ===== ADMIN MANAGEMENT HANDLERS =====

  const handleCreateAdmin = () => {
    setEditingAdmin(null);
    setAdminFormData({ email: '', first_name: '', last_name: '', admin_role: 'moderator' });
    setShowAdminModal(true);
  };

  const handleEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setAdminFormData({
      email: admin.email || '',
      first_name: admin.profile?.first_name || '',
      last_name: admin.profile?.last_name || '',
      admin_role: admin.admin_role || 'moderator'
    });
    setShowAdminModal(true);
  };

  const handleSaveAdmin = async () => {
    setSavingAdmin(true);
    try {
      if (editingAdmin) {
        await adminAPI.updateAdminRole(editingAdmin._id || editingAdmin.id, adminFormData.admin_role);
        showToast('Admin role updated successfully', 'success');
        setShowAdminModal(false);
      } else {
        if (!adminFormData.email.trim() || !adminFormData.first_name.trim() || !adminFormData.last_name.trim()) {
          showToast('Email, first name, and last name are required', 'error');
          setSavingAdmin(false);
          return;
        }
        const res = await adminAPI.createAdmin({
          email: adminFormData.email.trim(),
          first_name: adminFormData.first_name.trim(),
          last_name: adminFormData.last_name.trim(),
          role: adminFormData.admin_role,
        });
        const payload = res?.data || res;
        showToast('Admin created successfully', 'success');
        setShowAdminModal(false);
        if (payload?.temp_password) {
          setTempAdminPassword({ email: adminFormData.email.trim(), password: payload.temp_password });
        }
      }
      fetchAdmins();
    } catch (error) {
      showToast(error?.message || 'Failed to save admin', 'error');
    } finally {
      setSavingAdmin(false);
    }
  };

  const handleDeactivateAdmin = async (adminId) => {
    if (!window.confirm('Deactivate this admin account?')) return;
    try {
      await adminAPI.deactivateAdmin(adminId);
      showToast('Admin deactivated successfully', 'success');
      fetchAdmins();
    } catch (error) {
      showToast('Failed to deactivate admin', 'error');
    }
  };


  // ===== SWAP ACTION HANDLERS =====

  const handleApproveSwap = async (swapId) => {
    try {
      await adminAPI.approveSwap(swapId);
      showToast('Swap approved successfully', 'success');
      fetchSwapData();
    } catch (error) {
      showToast('Failed to approve swap', 'error');
    }
  };

  const handleRejectSwap = async () => {
    if (!rejectSwapReason.trim()) {
      showToast('Please enter a rejection reason', 'error');
      return;
    }
    try {
      await adminAPI.rejectSwap(rejectSwapTargetId, rejectSwapReason.trim());
      showToast('Swap rejected', 'success');
      setShowRejectSwapModal(false);
      setRejectSwapTargetId(null);
      setRejectSwapReason('');
      fetchSwapData();
    } catch (error) {
      showToast('Failed to reject swap', 'error');
    }
  };

  const handleInspectSwap = async () => {
    const swapId = selectedSwapForInspection?.id || selectedSwapForInspection?._id;
    if (!swapId) return;
    try {
      await adminAPI.inspectSwapDevice(swapId, {
        technician_notes: inspectionData.technician_notes,
        final_decision: inspectionData.final_decision,
        verification_checks: {
          battery_percent: inspectionData.battery_percent,
          face_id_ok: inspectionData.face_id_ok,
          storage_confirmed: inspectionData.storage_confirmed,
        },
        functionality_checks: {
          screen_ok: inspectionData.screen_ok,
          battery_health_ok: inspectionData.battery_health_ok,
          connectivity_ok: inspectionData.connectivity_ok,
          buttons_ok: inspectionData.buttons_ok,
        }
      });
      showToast('Inspection recorded successfully', 'success');
      setShowInspectionModal(false);
      setSelectedSwapForInspection(null);
      setInspectionStep(1);
      fetchSwapData();
    } catch (error) {
      showToast('Failed to record inspection', 'error');
    }
  };

  const handleFetchSwapLogistics = async (trackingId) => {
    if (!trackingId) return;
    setLoadingSwapLogistics(true);
    try {
      const res = await logisticsAPI.trackDelivery(trackingId);
      setSwapLogisticsEvents(res?.data?.events || res?.events || []);
    } catch {
      setSwapLogisticsEvents([]);
    } finally {
      setLoadingSwapLogistics(false);
    }
  };

  // ===== SETTINGS HANDLERS =====

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await adminAPI.getSettings();
      if (response) {
        // Backend model: models.SystemSettings (site_name, support_email, support_phone,
        // maintenance_mode, maintenance_message, transaction_fee, minimum_transaction,
        // escrow_fee, require_2fa, session_timeout, ip_whitelist, logistics_config.*)
        const d = response?.data || response;
        setSettingsData({
          general: {
            platform_name: d.site_name || '',
            support_email: d.support_email || '',
            support_phone: d.support_phone || '',
            maintenance_mode: d.maintenance_mode ?? false,
            maintenance_message: d.maintenance_message || '',
          },
          payment: {
            commission_rate: d.transaction_fee ?? 5,
            minimum_transaction: d.minimum_transaction ?? 1000,
            escrow_fee: d.escrow_fee ?? 3,
          },
          security: {
            two_fa_required: d.require_2fa ?? false,
            session_timeout: d.session_timeout ?? 30,
            ip_whitelist: Array.isArray(d.ip_whitelist) ? d.ip_whitelist.join(', ') : '',
          },
          logistics: {
            fabstech_api_key: d.logistics_config?.fabstech_api_key || '',
            fabstech_base_url: d.logistics_config?.fabstech_base_url || '',
            base_shipping_rate: d.logistics_config?.base_shipping_rate ?? 0,
            price_per_km: d.logistics_config?.price_per_km ?? 0,
          },
        });
      }
    } catch (error) {
      showToast('Failed to load settings', 'error');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSettingsLoading(true);
      const results = await Promise.allSettled([
        adminAPI.updateGeneralSettings({
          site_name: settingsData.general.platform_name,
          support_email: settingsData.general.support_email,
          support_phone: settingsData.general.support_phone,
        }),
        adminAPI.updatePaymentSettings({
          transaction_fee: Number(settingsData.payment.commission_rate) || 0,
          minimum_transaction: Number(settingsData.payment.minimum_transaction) || 0,
          escrow_fee: Number(settingsData.payment.escrow_fee) || 0,
        }),
        adminAPI.updateSecuritySettings({
          require_2fa: !!settingsData.security.two_fa_required,
          session_timeout: Number(settingsData.security.session_timeout) || 30,
          ip_whitelist: settingsData.security.ip_whitelist
            ? settingsData.security.ip_whitelist.split(',').map(ip => ip.trim()).filter(Boolean)
            : [],
        }),
        adminAPI.toggleMaintenanceMode(!!settingsData.general.maintenance_mode),
        adminAPI.updateLogisticsSettings({
          fabstech_api_key: settingsData.logistics.fabstech_api_key,
          fabstech_base_url: settingsData.logistics.fabstech_base_url,
          base_shipping_rate: Number(settingsData.logistics.base_shipping_rate) || 0,
          price_per_km: Number(settingsData.logistics.price_per_km) || 0,
        }),
      ]);

      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed === results.length) {
        showToast('Failed to save settings', 'error');
      } else if (failed > 0) {
        showToast('Some settings could not be saved', 'warning');
      } else {
        showToast('Settings saved successfully', 'success');
      }
      fetchSettings();
    } catch (error) {
      showToast('Failed to save settings', 'error');
    } finally {
      setSettingsLoading(false);
    }
  };

  // ===== DISPUTE ACTION HANDLERS =====

  const handleReviewDispute = async (dispute) => {
    try {
      const response = await adminAPI.getDisputeDetails(dispute._id || dispute.id);
      setSelectedDispute(response?.data || dispute);
      setShowDisputeModal(true);
      setEvidenceIndex(0);
      setShowPartialInput(false);
      setResolutionPayload({ resolution: '', amount: '', reason: '' });
    } catch (error) {
      showToast('Failed to load dispute details', 'error');
    }
  };

  const handleUpdateDisputeStatus = async (disputeId, newStatus) => {
    try {
      await adminAPI.updateDisputeStatus(disputeId, newStatus);
      showToast(`Dispute status updated to ${newStatus}`, 'success');
      handleReviewDispute({ _id: disputeId });
      fetchDisputes();
    } catch (error) {
      showToast('Failed to update dispute status', 'error');
    }
  };

  const handleAddDisputeNote = async (disputeId) => {
    if (!disputeNoteText.trim()) return;
    setAddingNote(true);
    try {
      await adminAPI.addDisputeNote(disputeId, disputeNoteText);
      showToast('Note added successfully', 'success');
      setDisputeNoteText('');
      handleReviewDispute({ _id: disputeId });
    } catch (error) {
      showToast('Failed to add note', 'error');
    } finally {
      setAddingNote(false);
    }
  };

  const handleResolveDispute = async () => {
    const disputeId = selectedDispute?._id || selectedDispute?.id;
    if (!disputeId) return;
    const { resolution, reason, amount } = resolutionPayload;
    if (!reason.trim()) {
      showToast('Please provide a resolution reason', 'error');
      return;
    }
    if (resolution === 'partial_refund' && (!amount || parseFloat(amount) <= 0)) {
      showToast('Please enter a valid refund amount', 'error');
      return;
    }
    setResolutionLoading(true);
    try {
      const payload = { resolution, reason: reason.trim() };
      if (resolution === 'partial_refund') payload.amount = parseFloat(amount);
      await adminAPI.resolveDispute(disputeId, payload);
      showToast('Dispute resolved successfully', 'success');
      setShowDisputeModal(false);
      setResolutionPayload({ resolution: '', amount: '', reason: '' });
      fetchDisputes();
    } catch (error) {
      showToast('Failed to resolve dispute', 'error');
    } finally {
      setResolutionLoading(false);
    }
  };

  // ===== SERVICE CHARGES (commission tiers + VAT) HANDLERS =====

  const fetchCommissionData = async () => {
    setLoadingCommission(true);
    try {
      const [tiersRes, settingsRes] = await Promise.all([
        adminAPI.getCommissionTiers(),
        adminAPI.getCommissionSettings(),
      ]);
      const tiers = tiersRes?.data || [];
      setCommissionTiers(Array.isArray(tiers) ? [...tiers].sort((a, b) => a.min_price - b.min_price) : []);
      const vat = settingsRes?.data?.vat_percentage ?? 7.5;
      setVatPercentage(vat);
      setVatInput(String(vat));
    } catch (error) {
      showToast('Failed to load service charge settings', 'error');
    } finally {
      setLoadingCommission(false);
    }
  };

  const handleSaveVat = async () => {
    const parsed = parseFloat(vatInput);
    if (isNaN(parsed) || parsed < 0 || parsed > 100) {
      showToast('VAT must be a number between 0 and 100', 'error');
      return;
    }
    setSavingVat(true);
    try {
      await adminAPI.updateCommissionSettings({ vat_percentage: parsed });
      setVatPercentage(parsed);
      showToast('VAT rate updated', 'success');
    } catch (error) {
      showToast('Failed to update VAT rate', 'error');
    } finally {
      setSavingVat(false);
    }
  };

  const openTierModal = (tier = null) => {
    setEditingTier(tier);
    setTierForm(tier
      ? { min_price: tier.min_price, max_price: tier.max_price, percentage: (tier.percentage * 100).toString(), active: tier.active !== false }
      : { min_price: '', max_price: '', percentage: '', active: true }
    );
    setShowTierModal(true);
  };

  const handleSaveTier = async () => {
    const minPrice = parseFloat(tierForm.min_price);
    const maxPrice = parseFloat(tierForm.max_price);
    const basePercent = parseFloat(tierForm.percentage);
    if (isNaN(minPrice) || minPrice < 0) { showToast('Enter a valid minimum amount', 'error'); return; }
    if (tierForm.max_price !== '' && tierForm.max_price !== '-1' && (isNaN(maxPrice) || maxPrice <= minPrice)) {
      showToast('Maximum amount must be greater than the minimum (leave blank or -1 for "and above")', 'error');
      return;
    }
    if (isNaN(basePercent) || basePercent < 0 || basePercent > 100) { showToast('Enter a valid base service charge percentage', 'error'); return; }

    const payload = {
      min_price: minPrice,
      max_price: tierForm.max_price === '' ? -1 : maxPrice,
      percentage: basePercent / 100,
      active: tierForm.active,
    };

    setSavingTier(true);
    try {
      if (editingTier) {
        await adminAPI.updateCommissionTier(editingTier.id || editingTier._id, payload);
        showToast('Tier updated', 'success');
      } else {
        await adminAPI.createCommissionTier(payload);
        showToast('Tier created', 'success');
      }
      setShowTierModal(false);
      fetchCommissionData();
    } catch (error) {
      showToast('Failed to save tier', 'error');
    } finally {
      setSavingTier(false);
    }
  };

  const handleDeleteTier = async (tier) => {
    if (!window.confirm(`Delete the ${formatCurrency(tier.min_price)} – ${tier.max_price < 0 ? 'and above' : formatCurrency(tier.max_price)} tier?`)) return;
    try {
      await adminAPI.deleteCommissionTier(tier.id || tier._id);
      showToast('Tier deleted', 'success');
      fetchCommissionData();
    } catch (error) {
      showToast('Failed to delete tier', 'error');
    }
  };

  const handleToggleTierActive = async (tier) => {
    try {
      await adminAPI.updateCommissionTier(tier.id || tier._id, { ...tier, active: !tier.active });
      fetchCommissionData();
    } catch (error) {
      showToast('Failed to update tier', 'error');
    }
  };

  // ===== REPORT ACTION HANDLERS =====

  const handleUpdateReportStatus = async (reportId, status) => {
    try {
      await adminAPI.updateReportStatus(reportId, status);
      showToast(`Report marked as ${status}`, 'success');
      setReports(prev => prev.map(r => (r._id || r.id) === reportId ? { ...r, status } : r));
    } catch (error) {
      showToast(error?.message || 'Failed to update report status', 'error');
    }
  };

  // ban_user / remove_content / warn_user all require a moderator's reason —
  // it is written to the user's warning or suspension record, so it can't be
  // an empty string.
  const handleTakeReportAction = async (reportId, action) => {
    const labels = {
      warn_user: 'warn this user',
      ban_user: 'suspend this user',
      remove_content: 'remove the reported content',
    };
    const reason = window.prompt(
      `Reason to ${labels[action] || action} (shown in the moderation record):`
    );
    if (reason === null) return;
    if (!reason.trim()) {
      showToast('A reason is required to take this action', 'error');
      return;
    }
    try {
      await adminAPI.takeReportAction(reportId, action, reason.trim());
      showToast(`Action "${action.replace(/_/g, ' ')}" applied`, 'success');
      setReports(prev => prev.map(r => (r._id || r.id) === reportId ? { ...r, status: 'resolved' } : r));
    } catch (error) {
      showToast(error?.message || 'Failed to apply action', 'error');
    }
  };

  // ===== STUB BUTTON HANDLERS (Wired to real APIs) =====

  const handleOpenExtendPremium = (premium) => {
    setSelectedPremiumUser(premium);
    setPremiumExtensionDays(30);
    setShowPremiumExtendModal(true);
  };

  const handleOpenRevokePremium = (premium) => {
    setSelectedPremiumUser(premium);
    setPremiumRevocationReason('');
    setPremiumRefundPartial(false);
    setShowPremiumRevokeModal(true);
  };

  const handleOpenGiftPremium = (premium) => {
    setSelectedPremiumUser(premium);
    setShowPremiumGiftModal(true);
  };

  const handleExtendPremium = async () => {
    if (!selectedPremiumUser || !premiumExtensionDays) {
      showToast('Please select extension duration', 'error');
      return;
    }
    setPremiumActionLoading(true);
    try {
      const res = await adminAPI.extendPremium(selectedPremiumUser.user_id || selectedPremiumUser.id, premiumExtensionDays);
      const newExpiry = res?.data?.new_expiry || res?.new_expiry;
      showToast(
        newExpiry
          ? `Premium extended to ${format(new Date(newExpiry), 'MMM dd, yyyy')}`
          : `Premium extended by ${premiumExtensionDays} days`,
        'success'
      );
      setShowPremiumExtendModal(false);
      fetchPremiumData();
    } catch (error) {
      showToast('Failed to extend premium', 'error');
    } finally {
      setPremiumActionLoading(false);
    }
  };

  const handleRevokePremium = async () => {
    if (!selectedPremiumUser || !premiumRevocationReason.trim()) {
      showToast('Please provide a revocation reason', 'error');
      return;
    }
    setPremiumActionLoading(true);
    try {
      const payload = {
        reason: premiumRevocationReason,
        refund_partial: premiumRefundPartial
      };
      await adminAPI.revokePremium(selectedPremiumUser.user_id || selectedPremiumUser.id, payload.reason);
      showToast('Premium status revoked', 'success');
      setShowPremiumRevokeModal(false);
      fetchPremiumData();
    } catch (error) {
      showToast('Failed to revoke premium', 'error');
    } finally {
      setPremiumActionLoading(false);
    }
  };

  const handleGiftPremium = async () => {
    if (!selectedPremiumUser) return;
    setPremiumActionLoading(true);
    try {
      await adminAPI.extendPremium(selectedPremiumUser.user_id || selectedPremiumUser.id, 30);
      showToast('30-day premium gift sent', 'success');
      setShowPremiumGiftModal(false);
      fetchPremiumData();
    } catch (error) {
      showToast('Failed to gift premium', 'error');
    } finally {
      setPremiumActionLoading(false);
    }
  };

  const handleSavePremiumSettings = async () => {
    if (!editingPremiumSettings) return;
    setPremiumActionLoading(true);
    try {
      await adminAPI.updatePremiumSettings(editingPremiumSettings);
      showToast('Premium settings updated', 'success');
      setPremiumSettings(editingPremiumSettings);
    } catch (error) {
      showToast('Failed to save premium settings', 'error');
    } finally {
      setPremiumActionLoading(false);
    }
  };

  const handleOpenGrantPremium = () => {
    setGrantPremiumSearch('');
    setGrantPremiumSearchResults([]);
    setGrantPremiumSelectedUser(null);
    setGrantPremiumPlanId((premiumSettings?.plans || [])[0]?.id || '');
    setGrantPremiumDurationDays(30);
    setGrantPremiumReason('');
    setShowGrantPremiumModal(true);
  };

  // Debounced search — mirrors the fix already applied to escrow search:
  // only hit the server after the admin pauses typing, not on every keystroke.
  useEffect(() => {
    if (!showGrantPremiumModal || grantPremiumSelectedUser) return;
    const query = grantPremiumSearch.trim();
    if (query.length < 2) {
      setGrantPremiumSearchResults([]);
      return;
    }
    setGrantPremiumSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await adminAPI.getUsers({ search: query, limit: 8 });
        const payload = res?.data || res;
        setGrantPremiumSearchResults(Array.isArray(payload?.users) ? payload.users : []);
      } catch (_) {
        setGrantPremiumSearchResults([]);
      } finally {
        setGrantPremiumSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [grantPremiumSearch, showGrantPremiumModal, grantPremiumSelectedUser]);

  const handleGrantPremium = async () => {
    if (!grantPremiumSelectedUser || !grantPremiumPlanId || !grantPremiumDurationDays) {
      showToast('Select a user, plan, and duration first', 'error');
      return;
    }
    setPremiumActionLoading(true);
    try {
      const userId = grantPremiumSelectedUser.id || grantPremiumSelectedUser._id;
      await adminAPI.grantPremium(userId, grantPremiumPlanId, Number(grantPremiumDurationDays), grantPremiumReason);
      showToast('Premium granted', 'success');
      setShowGrantPremiumModal(false);
      fetchPremiumData();
    } catch (error) {
      showToast(error?.message || 'Failed to grant premium', 'error');
    } finally {
      setPremiumActionLoading(false);
    }
  };

  const handleViewWalletDetail = async (wallet) => {
    setShowWalletModal(true);
    setWalletDetailsLoading(true);
    setSelectedWalletDetails(null);
    setShowAdjustPanel(false);
    try {
      const res = await adminAPI.getWalletDetails(wallet._id || wallet.id || wallet.user_id);
      const payload = res?.data || res;
      setSelectedWalletDetails({
        ...wallet,
        ...(payload?.wallet || payload),
        recent_transactions: payload?.recent_transactions || [],
      });
    } catch {
      showToast('Failed to load wallet details', 'error');
      setShowWalletModal(false);
    } finally {
      setWalletDetailsLoading(false);
    }
  };

  const submitAdjustWallet = async () => {
    if (!adjustAmount || isNaN(parseFloat(adjustAmount))) { showToast('Enter a valid amount', 'error'); return; }
    if (!adjustReason.trim()) { showToast('A reason is required', 'error'); return; }
    const uid = selectedWalletDetails?._id || selectedWalletDetails?.id || selectedWalletDetails?.user_id;
    try {
      await adminAPI.adjustWallet(uid, parseFloat(adjustAmount), adjustReason.trim(), adjustType);
      showToast(`Wallet ${adjustType === 'credit' ? 'credited' : 'debited'} successfully`, 'success');
      setShowAdjustPanel(false);
      setAdjustAmount('');
      setAdjustReason('');
      handleViewWalletDetail(selectedWalletDetails);
      fetchWallets();
    } catch (error) {
      showToast('Failed to adjust wallet', 'error');
    }
  };

  const handleFreezeWallet = () => {
    setFreezeReason('');
    setShowFreezeModal(true);
  };

  const submitFreezeWallet = async () => {
    if (!freezeReason.trim()) { showToast('A reason is required', 'error'); return; }
    const uid = selectedWalletDetails?._id || selectedWalletDetails?.id || selectedWalletDetails?.user_id;
    try {
      await adminAPI.freezeWallet(uid, freezeReason.trim());
      showToast('Wallet frozen', 'success');
      setShowFreezeModal(false);
      handleViewWalletDetail(selectedWalletDetails);
      fetchWallets();
    } catch (error) {
      showToast('Failed to freeze wallet', 'error');
    }
  };

  const handleUnfreezeWallet = async () => {
    const uid = selectedWalletDetails?._id || selectedWalletDetails?.id || selectedWalletDetails?.user_id;
    try {
      await adminAPI.unfreezeWallet(uid);
      showToast('Wallet unfrozen', 'success');
      handleViewWalletDetail(selectedWalletDetails);
      fetchWallets();
    } catch (error) {
      showToast('Failed to unfreeze wallet', 'error');
    }
  };

  // ===== UTILITY FUNCTIONS =====

  const toggleTheme = () => setIsDarkMode(!isDarkMode);
  const toggleMobileSidebar = () => setIsMobileSidebarOpen(!isMobileSidebarOpen);
  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString()}`;
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Export whatever is currently on screen as CSV. Built from the rows the
  // admin is already looking at rather than re-fetching, so what downloads is
  // exactly what they see (and no extra fields leak into the file).
  const exportRowsToCSV = (rows, filename) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      showToast('Nothing to export yet', 'warning');
      return;
    }

    const columns = [...new Set(rows.flatMap(r => Object.keys(r)))];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
      // Quote when the value contains a delimiter, quote, or newline.
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const csv = [
      columns.join(','),
      ...rows.map(r => columns.map(c => escape(r[c])).join(',')),
    ].join('\r\n');

    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast(`Exported ${rows.length} row${rows.length === 1 ? '' : 's'}`, 'success');
  };

  const getStatusColor = (status) => {
    const colors = {
      active: '#22c55e', inactive: '#6b7280', suspended: '#ef4444',
      completed: '#22c55e', processing: '#f59e0b', failed: '#ef4444',
      pending: '#f59e0b', reviewing: '#22c55e', resolved: '#22c55e',
      open: '#ef4444', investigating: '#f59e0b', approved: '#22c55e',
      rejected: '#ef4444', verified: '#22c55e'
    };
    return colors[status?.toLowerCase()] || '#6b7280';
  };

  const getUserRole = (user) => {
    // API returns user_type: "buyer" | "seller" | "admin"
    // Also check legacy `role` field as fallback
    const type = user.user_type || user.role || '';
    if (type === 'seller') return 'seller';
    if (type === 'admin') return 'admin';
    if (type === 'buyer') return 'buyer';
    // Secondary detection from flags
    if (user.is_admin === true) return 'admin';
    if (user.is_seller === true) return 'seller';
    return 'buyer';
  };

  const getPriorityColor = (priority) => {
    const colors = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e' };
    return colors[priority?.toLowerCase()] || '#6b7280';
  };

  const getLogTypeColor = (type) => {
    const colors = { info: '#22c55e', warning: '#f59e0b', error: '#ef4444', success: '#22c55e' };
    return colors[type?.toLowerCase()] || '#6b7280';
  };

  // Payment Status Helper Functions (matching mobile app and seller dashboards)
  const getPaymentStatusColor = (paymentStatus) => {
    const colors = {
      pending: '#f59e0b',
      paid: '#22c55e',
      completed: '#22c55e',
      escrow: '#22c55e',
      failed: '#ef4444',
      refunded: '#6b7280',
      cancelled: '#9ca3af'
    };
    return colors[paymentStatus?.toLowerCase()] || '#6b7280';
  };

  // `completed` covers two very different situations: a plain card/transfer
  // order that simply finished, and an escrow order whose funds the buyer
  // has now released to the seller. Sellers were told the second case still
  // read "In Escrow" (the release path never updated payment_status — fixed
  // backend-side), and once it does update, "Completed" alone doesn't say
  // the money actually moved. Pass the order and escrow-released orders read
  // "Released".
  const getPaymentStatusText = (paymentStatus, order) => {
    const texts = {
      pending: 'Pending',
      paid: 'Paid',
      completed: 'Completed',
      escrow: 'In Escrow',
      failed: 'Failed',
      refunded: 'Refunded',
      cancelled: 'Cancelled'
    };
    const key = String(paymentStatus || '').toLowerCase();
    if (key === 'completed' && (order?.use_escrow || order?.useEscrow)) return 'Released';
    return texts[key] || paymentStatus;
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      navigate('/auth');
    }
  };

  // ===== RENDER FUNCTIONS =====

  const renderUsersContent = () => {
    // Single-accent-per-meaning tinted badge (flat, not gradient) — role/status/bank badges
    const badgeTone = (tone) => {
      const tones = {
        green: { bg: isDarkMode ? 'rgba(34,197,94,0.16)' : 'rgba(22,163,74,0.1)', fg: isDarkMode ? '#4ade80' : '#16a34a' },
        red: { bg: isDarkMode ? 'rgba(239,68,68,0.16)' : 'rgba(220,38,38,0.1)', fg: isDarkMode ? '#f87171' : '#dc2626' },
        amber: { bg: isDarkMode ? 'rgba(245,158,11,0.16)' : 'rgba(217,119,6,0.1)', fg: isDarkMode ? '#fbbf24' : '#b45309' },
        neutral: { bg: isDarkMode ? 'rgba(148,163,184,0.14)' : 'rgba(100,116,139,0.1)', fg: isDarkMode ? '#94a3b8' : '#475569' },
      };
      return tones[tone] || tones.neutral;
    };
    const Badge = ({ tone, icon, children }) => {
      const t = badgeTone(tone);
      return (
        <span style={{
          padding: '0.4rem 0.85rem', borderRadius: '999px', fontSize: '0.78rem', fontWeight: '700',
          letterSpacing: '0.2px', background: t.bg, color: t.fg,
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap'
        }}>
          {icon && <FontAwesomeIcon icon={icon} style={{ fontSize: '0.75rem' }} />}
          {children}
        </span>
      );
    };

    const roleTone = (u) => getUserRole(u) === 'seller' ? 'green' : getUserRole(u) === 'admin' ? 'red' : 'neutral';
    const roleIcon = (u) => getUserRole(u) === 'seller' ? faUserTie : getUserRole(u) === 'admin' ? faUserShield : faUser;
    const statusTone = (u) => (u.status || 'active') === 'active' ? 'green' : u.status === 'suspended' ? 'red' : 'amber';
    const statusIcon = (u) => (u.status || 'active') === 'active' ? faCheckCircle : u.status === 'suspended' ? faBan : faExclamationTriangle;
    const bankAccountsOf = (u) => u.profile?.bank_accounts || u.bank_accounts || [];
    const userDisplayName = (u) => u.full_name ||
      (u.profile?.first_name && u.profile?.last_name
        ? `${u.profile.first_name} ${u.profile.last_name}`.trim()
        : u.profile?.first_name || u.profile?.last_name || null) ||
      u.username || u.email || 'N/A';

    const menuBtnBase = {
      width: '100%', display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.6rem 0.75rem',
      borderRadius: '8px', border: 'none', background: 'transparent', cursor: 'pointer',
      fontSize: '0.85rem', fontWeight: '600', textAlign: 'left', transition: 'background 0.15s ease',
      color: isDarkMode ? '#e5e7eb' : '#1f2937'
    };
    const MenuItem = ({ icon, label, onClick, danger }) => (
      <button
        style={{ ...menuBtnBase, color: danger ? (isDarkMode ? '#f87171' : '#dc2626') : menuBtnBase.color }}
        onClick={onClick}
        onMouseEnter={(e) => { e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <FontAwesomeIcon icon={icon} style={{ width: '14px' }} />
        {label}
      </button>
    );

    const closeMenu = () => setOpenUserActionMenu(null);

    const ActionsCell = ({ user }) => {
      const userId = user._id || user.id;
      const isOpen = openUserActionMenu?.id === userId;
      const isPendingLike = user.status === 'inactive' || user.status === 'pending';
      return (
        <div className="aex-user-action-menu-wrap" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            title="View Details"
            onClick={() => handleViewUser(user)}
            style={{
              background: isDarkMode ? 'rgba(34,197,94,0.14)' : 'rgba(22,163,74,0.1)',
              border: 'none', color: isDarkMode ? '#4ade80' : '#16a34a',
              width: '36px', height: '36px', borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'all 0.2s ease', flexShrink: 0
            }}
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
          <button
            title="More actions"
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              const menuWidth = 220;
              setOpenUserActionMenu(isOpen ? null : {
                id: userId,
                top: rect.bottom + 6,
                left: Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8))
              });
            }}
            style={{
              background: isOpen ? (isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)') : (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
              border: 'none', color: 'inherit',
              width: '36px', height: '36px', borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'all 0.2s ease', flexShrink: 0
            }}
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </button>
          {isOpen && openUserActionMenu && createPortal(
            <div
              className="aex-user-action-menu-wrap"
              style={{
                position: 'fixed', top: openUserActionMenu.top, left: openUserActionMenu.left, width: '220px',
                background: isDarkMode ? '#17171f' : '#ffffff',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
                borderRadius: '12px', boxShadow: '0 16px 40px rgba(0,0,0,0.3)', padding: '0.4rem', zIndex: 1000
              }}
            >
              <MenuItem
                icon={faIdBadge}
                label={user.verified_badge ? 'Remove Verified Badge' : 'Grant Verified Badge'}
                onClick={() => { closeMenu(); handleVerifyBadge(userId, user.verified_badge); }}
              />
              <MenuItem
                icon={faGift}
                label="Update Perks"
                onClick={() => { closeMenu(); handleUpdateUserPerks(userId, user); }}
              />
              {isPendingLike ? (
                <MenuItem icon={faCheckCircle} label="Approve Account" onClick={() => { closeMenu(); handleApproveUser(userId); }} />
              ) : user.status === 'suspended' ? (
                <MenuItem icon={faUserCheck} label="Reactivate User" onClick={() => { closeMenu(); handleReactivateUser(userId); }} />
              ) : (
                <MenuItem icon={faUserSlash} label="Suspend User" onClick={() => { closeMenu(); handleSuspendUser(userId); }} />
              )}
              <div style={{ height: '1px', background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', margin: '0.35rem 0.2rem' }} />
              <MenuItem
                icon={faTrash}
                label="Delete User"
                danger
                onClick={() => { closeMenu(); handleDeleteUser(userId, user.email); }}
              />
            </div>,
            document.body
          )}
        </div>
      );
    };

    return (
    <div className="AutoboyExpress-dash-content-wrapper">
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#22C55E' }} />
          <p style={{ marginTop: '1rem', fontSize: '1.1rem', fontWeight: '500' }}>Loading users...</p>
        </div>
      ) : (
        <>
          <div className="AutoboyExpress-dash-section-header" style={{
            marginBottom: '2rem',
            padding: '1.5rem',
            background: isDarkMode ? 'linear-gradient(135deg, rgba(10, 10, 15, 0.97) 0%, rgba(20, 20, 25, 0.97) 100%)' : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 253, 244, 0.95) 100%)',
            borderRadius: '16px',
            backdropFilter: 'blur(10px)',
            border: isDarkMode ? '1px solid rgba(34, 197, 94, 0.2)' : '1px solid rgba(22, 163, 74, 0.15)',
            boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(34, 197, 94, 0.12)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h2 style={{
                  fontSize: '1.75rem',
                  fontWeight: '700',
                  margin: '0 0 0.5rem 0',
                  background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  <FontAwesomeIcon icon={faUsers} style={{ marginRight: '0.75rem' }} />
                  User Management
                </h2>
                <p style={{ margin: 0, opacity: 0.7, fontSize: '0.95rem' }}>
                  {totalUsersCount != null ? `${totalUsersCount.toLocaleString()} total users` : 'Manage and monitor all platform users'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div className="AutoboyExpress-dash-search-bar" style={{ minWidth: '250px', flex: '1 1 250px' }}>
                  <FontAwesomeIcon icon={faSearch} className="AutoboyExpress-dash-search-icon" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setUserSearch(val);
                      clearTimeout(window._userSearchTimer);
                      window._userSearchTimer = setTimeout(() => {
                        setCurrentPage(1);
                        fetchUsers(1, val, roleFilter, userSort);
                      }, 400);
                    }}
                  />
                </div>
                <select
                  value={roleFilter}
                  onChange={(e) => {
                    setRoleFilter(e.target.value);
                    setCurrentPage(1);
                    fetchUsers(1, userSearch, e.target.value, userSort);
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    borderRadius: '12px',
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    color: 'inherit',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                    outline: 'none',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    minWidth: '140px'
                  }}
                >
                  <option value="all">All Roles</option>
                  <option value="buyer">Buyers</option>
                  <option value="seller">Sellers</option>
                  <option value="admin">Admins</option>
                </select>
                <select
                  value={userSort}
                  onChange={(e) => {
                    setUserSort(e.target.value);
                    setCurrentPage(1);
                    fetchUsers(1, userSearch, roleFilter, e.target.value);
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    borderRadius: '12px',
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    color: 'inherit',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                    outline: 'none',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    minWidth: '150px'
                  }}
                >
                  <option value="newest">Recently added</option>
                  <option value="oldest">Oldest first</option>
                  <option value="name_asc">Name A-Z</option>
                  <option value="name_desc">Name Z-A</option>
                </select>
              </div>
            </div>
          </div>

          {users.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '4rem 2rem', borderRadius: '16px',
              background: isDarkMode ? 'rgba(15, 15, 20, 0.6)' : 'rgba(255, 255, 255, 0.9)',
              border: isDarkMode ? '1px solid rgba(34, 197, 94, 0.15)' : '1px solid rgba(22, 163, 74, 0.08)',
            }}>
              <FontAwesomeIcon icon={faUsers} style={{ fontSize: '2.5rem', opacity: 0.35, marginBottom: '1rem' }} />
              <p style={{ fontSize: '1.05rem', fontWeight: '700', margin: '0 0 0.35rem 0' }}>No users found</p>
              <p style={{ margin: 0, opacity: 0.6, fontSize: '0.9rem' }}>
                {userSearch || roleFilter !== 'all' ? 'Try adjusting your search or role filter.' : 'New registrations will appear here.'}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop table (>=768px) */}
              <div className="aex-desktop-table-wrap">
                <AdminTableCard>
                  <AdminTable
                    minWidth="760px"
                    columns={[
                      { width: '30%' }, { width: '13%' }, { width: '13%' },
                      { width: '17%' }, { width: '15%' }, { width: '12%' },
                    ]}
                  >
                    <AdminTableHead>
                      <AdminTh>User</AdminTh>
                      <AdminTh>Role</AdminTh>
                      <AdminTh>Status</AdminTh>
                      <AdminTh>Bank Verify</AdminTh>
                      <AdminTh>Joined</AdminTh>
                      <AdminTh align="right">Actions</AdminTh>
                    </AdminTableHead>
                    <tbody>
                      {users.map((user) => {
                        const accounts = bankAccountsOf(user);
                        const verifiedCount = accounts.filter(acc => acc.is_verified).length;
                        return (
                          <AdminTr key={user._id || user.id}>
                            <AdminTd>
                              <div className="tw-flex tw-items-center tw-gap-3">
                                <div className="tw-w-10 tw-h-10 tw-rounded-xl tw-flex-shrink-0 tw-bg-gradient-to-br tw-from-green-500 tw-to-green-600 tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-shadow-[0_3px_10px_rgba(34,197,94,0.3)]">
                                  {user.profile?.avatar ? (
                                    <img src={user.profile.avatar} alt="" className="tw-w-full tw-h-full tw-rounded-xl tw-object-cover" />
                                  ) : (
                                    userDisplayName(user).charAt(0).toUpperCase()
                                  )}
                                </div>
                                <div className="tw-min-w-0">
                                  <div className="tw-font-bold tw-text-[0.95rem] tw-truncate">{userDisplayName(user)}</div>
                                  <div className="tw-text-[0.8rem] tw-opacity-65 tw-truncate">{user.email || 'N/A'}</div>
                                  <div className="tw-text-[0.78rem] tw-opacity-50">{user.phone || 'No phone'}</div>
                                </div>
                              </div>
                            </AdminTd>
                            <AdminTd>
                              <AdminBadge tone={roleTone(user)} icon={roleIcon(user)}>{getUserRole(user)}</AdminBadge>
                            </AdminTd>
                            <AdminTd>
                              <AdminBadge tone={statusTone(user)} icon={statusIcon(user)}>{user.status || 'active'}</AdminBadge>
                            </AdminTd>
                            <AdminTd>
                              {accounts.length > 0 ? (
                                <AdminBadge tone={verifiedCount > 0 ? 'green' : 'amber'} icon={verifiedCount > 0 ? faCheckCircle : faExclamationTriangle}>
                                  {verifiedCount}/{accounts.length} Verified
                                </AdminBadge>
                              ) : (
                                <AdminBadge tone="neutral" icon={faWallet}>No accounts</AdminBadge>
                              )}
                            </AdminTd>
                            <AdminTd className="tw-opacity-80">
                              {user.created_at ? format(new Date(user.created_at), 'MMM dd, yyyy') : 'N/A'}
                            </AdminTd>
                            <AdminTd align="right" className="tw-overflow-visible">
                              <ActionsCell user={user} />
                            </AdminTd>
                          </AdminTr>
                        );
                      })}
                    </tbody>
                  </AdminTable>
                </AdminTableCard>
              </div>

              {/* Mobile cards (<768px) */}
              <div className="aex-mobile-user-cards">
                {users.map((user) => {
                  const accounts = bankAccountsOf(user);
                  const verifiedCount = accounts.filter(acc => acc.is_verified).length;
                  return (
                    <div key={user._id || user.id} className="aex-user-card" style={{
                      background: isDarkMode ? 'rgba(15, 15, 20, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                      border: isDarkMode ? '1px solid rgba(34, 197, 94, 0.15)' : '1px solid rgba(22, 163, 74, 0.08)',
                      borderRadius: '14px', padding: '1rem', marginBottom: '0.85rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '0.85rem' }}>
                        <div style={{
                          width: '46px', height: '46px', borderRadius: '12px', flexShrink: 0,
                          background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '1.1rem', color: 'white', fontWeight: '700',
                          boxShadow: '0 3px 10px rgba(34, 197, 94, 0.3)'
                        }}>
                          {user.profile?.avatar ? (
                            <img src={user.profile.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '12px', objectFit: 'cover' }} />
                          ) : (
                            userDisplayName(user).charAt(0).toUpperCase()
                          )}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: '700', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {userDisplayName(user)}
                          </div>
                          <div style={{ fontSize: '0.8rem', opacity: 0.65, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {user.email || 'N/A'}
                          </div>
                        </div>
                        <ActionsCell user={user} />
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.6rem' }}>
                        <Badge tone={roleTone(user)} icon={roleIcon(user)}>{getUserRole(user)}</Badge>
                        <Badge tone={statusTone(user)} icon={statusIcon(user)}>{user.status || 'active'}</Badge>
                        {accounts.length > 0 ? (
                          <Badge tone={verifiedCount > 0 ? 'green' : 'amber'} icon={verifiedCount > 0 ? faCheckCircle : faExclamationTriangle}>
                            {verifiedCount}/{accounts.length} Verified
                          </Badge>
                        ) : (
                          <Badge tone="neutral" icon={faWallet}>No accounts</Badge>
                        )}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', opacity: 0.6, paddingTop: '0.5rem', borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' }}>
                        <span>{user.phone || 'No phone'}</span>
                        <span>{user.created_at ? format(new Date(user.created_at), 'MMM dd, yyyy') : 'N/A'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                onClick={() => { const p = currentPage - 1; fetchUsers(p, userSearch, roleFilter, userSort); }}
                disabled={currentPage <= 1}
                style={{
                  padding: '0.6rem 1.25rem', borderRadius: '10px', border: 'none', fontWeight: 600,
                  cursor: currentPage <= 1 ? 'not-allowed' : 'pointer', opacity: currentPage <= 1 ? 0.4 : 1,
                  background: isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(22,163,74,0.1)',
                  color: isDarkMode ? '#4ade80' : '#16a34a', transition: 'all 0.2s',
                }}
              >
                ← Previous
              </button>
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => { const p = currentPage + 1; fetchUsers(p, userSearch, roleFilter, userSort); }}
                disabled={currentPage >= totalPages}
                style={{
                  padding: '0.6rem 1.25rem', borderRadius: '10px', border: 'none', fontWeight: 600,
                  cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages ? 0.4 : 1,
                  background: isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(22,163,74,0.1)',
                  color: isDarkMode ? '#4ade80' : '#16a34a', transition: 'all 0.2s',
                }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
    );
  };

  const renderKYCVerificationContent = () => {
    const dark = isDarkMode;
    const card = { background: dark ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)', border: `1px solid ${dark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)'}`, borderRadius: '20px', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: dark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(34,197,94,0.08)' };
    const verifiedCount = kycRequests.filter(k => k.verification_status === 'verified').length;
    const pendingCount = kycRequests.filter(k => k.verification_status === 'pending').length;
    const rejectedCount = kycRequests.filter(k => k.verification_status === 'rejected').length;

    return (
  <div style={{ padding: '0 0 2rem 0', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
    {loading ? (
      <div style={{ textAlign: 'center', padding: '5rem', color: '#22C55E' }}>
        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2.5rem' }} />
        <p style={{ marginTop: '1rem', color: dark ? '#9ca3af' : '#6b7280' }}>Loading verification data…</p>
      </div>
    ) : (
      <>
        {/* Header */}
        <div style={{ ...card, padding: '1.75rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, background: 'linear-gradient(135deg, #22C55E, #16A34A)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              <FontAwesomeIcon icon={faUserCheck} style={{ WebkitTextFillColor: '#22C55E', marginRight: '0.6rem' }} />Identity Verification
            </h2>
            <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: dark ? '#6b7280' : '#9ca3af' }}>
              {kycRequests.length} users · {verifiedCount} verified · {pendingCount} pending · {rejectedCount} rejected
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={() => fetchKYCRequests(kycStatusFilter)} style={{ padding: '0.6rem 1.2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <FontAwesomeIcon icon={faSync} /> Refresh
            </button>
          </div>
        </div>

        {/* KYC / KYB Tab switcher */}
        <div style={{ display: 'flex', gap: '0.5rem', background: dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', borderRadius: '14px', padding: '0.35rem', width: 'fit-content' }}>
          {[{ k: 'kyc', label: 'KYC (Individual)' }, { k: 'kyb', label: 'KYB (Business)' }].map(t => (
            <button key={t.k} onClick={() => setKycMainTab(t.k)} style={{ padding: '0.55rem 1.2rem', borderRadius: '10px', border: 'none', fontWeight: 600, fontSize: '0.83rem', cursor: 'pointer', transition: 'all 0.2s', background: kycMainTab === t.k ? 'linear-gradient(135deg, #22C55E, #16A34A)' : 'transparent', color: kycMainTab === t.k ? '#fff' : (dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'), boxShadow: kycMainTab === t.k ? '0 4px 12px rgba(34,197,94,0.3)' : 'none' }}>
              {t.label}
            </button>
          ))}
        </div>

        {kycMainTab === 'kyb' ? (
          <>
            <p style={{ margin: 0, fontSize: '0.85rem', color: dark ? '#6b7280' : '#9ca3af' }}>
              {kybRequests.length} business verification{kybRequests.length === 1 ? '' : 's'} — one per seller who has uploaded a CAC business document.
            </p>

            {/* KYB Table */}
            <AdminTableCard>
              <AdminTable
                minWidth="900px"
                columns={[{ width: '24%' }, { width: '14%' }, { width: '13%' }, { width: '13%' }, { width: '12%' }, { width: '10%' }, { width: '14%' }]}
              >
                <AdminTableHead>
                  <AdminTh>Business</AdminTh>
                  <AdminTh>Type</AdminTh>
                  <AdminTh>Reg. Number</AdminTh>
                  <AdminTh>Tax ID</AdminTh>
                  <AdminTh>Status</AdminTh>
                  <AdminTh>Submitted</AdminTh>
                  <AdminTh>Actions</AdminTh>
                </AdminTableHead>
                <tbody>
                  {kybRequests.length === 0 ? (
                    <AdminTableEmpty colSpan={7} message="No business verifications found — KYB submissions from sellers will appear here once submitted via the seller dashboard." />
                  ) : (
                    kybRequests.map((kyb, index) => {
                      const status = kyb.verification_status || 'pending';
                      const statusTone = status === 'verified' ? 'green' : status === 'rejected' ? 'red' : 'amber';
                      return (
                        <AdminTr key={kyb.user_id || index}>
                          <AdminTd>
                            <div className="tw-flex tw-items-center tw-gap-3">
                              <div className="tw-w-10 tw-h-10 tw-rounded-xl tw-flex-shrink-0 tw-bg-gradient-to-br tw-from-green-500 tw-to-green-600 tw-flex tw-items-center tw-justify-center">
                                {kyb.document_url ? (
                                  <a href={kyb.document_url} target="_blank" rel="noreferrer" className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
                                    <FontAwesomeIcon icon={faBuilding} className="tw-text-white" />
                                  </a>
                                ) : (
                                  <FontAwesomeIcon icon={faBuilding} className="tw-text-white" />
                                )}
                              </div>
                              <div className="tw-min-w-0">
                                <div className="tw-font-bold tw-text-[0.9rem] tw-truncate">{kyb.business_name || 'Unnamed Business'}</div>
                                <div className="tw-text-[0.78rem] tw-opacity-60 tw-truncate">{kyb.user_name || kyb.user_email || '—'}</div>
                              </div>
                            </div>
                          </AdminTd>
                          <AdminTd className="tw-capitalize">{kyb.business_type || '—'}</AdminTd>
                          <AdminTd className="tw-font-mono">{kyb.registration_number || '—'}</AdminTd>
                          <AdminTd className="tw-font-mono">{kyb.tax_id || '—'}</AdminTd>
                          <AdminTd><AdminBadge tone={statusTone}>{status}</AdminBadge></AdminTd>
                          <AdminTd className="tw-opacity-70">
                            {kyb.uploaded_at ? (() => { try { return format(new Date(kyb.uploaded_at), 'MMM d, yyyy'); } catch { return '—'; } })() : '—'}
                          </AdminTd>
                          <AdminTd>
                            {status === 'pending' ? (
                              <div className="tw-flex tw-gap-2">
                                <button
                                  onClick={async () => {
                                    try {
                                      await adminAPI.approveKYC(kyb.user_id, 'Business verification approved', 'cac');
                                      showToast('Business verification approved', 'success');
                                      fetchKYBRequests();
                                    } catch { showToast('Failed to approve', 'error'); }
                                  }}
                                  className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-border-0 tw-bg-gradient-to-br tw-from-green-500 tw-to-green-600 tw-text-white tw-font-semibold tw-text-xs tw-cursor-pointer"
                                >
                                  <FontAwesomeIcon icon={faCheckCircle} className="tw-mr-1" />Approve
                                </button>
                                <button
                                  onClick={async () => {
                                    const reason = window.prompt('Rejection reason:');
                                    if (!reason) return;
                                    try {
                                      await adminAPI.rejectKYC(kyb.user_id, reason, 'cac');
                                      showToast('Business verification rejected', 'success');
                                      fetchKYBRequests();
                                    } catch { showToast('Failed to reject', 'error'); }
                                  }}
                                  className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-red-500/30 tw-bg-transparent tw-text-red-500 tw-font-semibold tw-text-xs tw-cursor-pointer"
                                >
                                  <FontAwesomeIcon icon={faTimes} className="tw-mr-1" />Reject
                                </button>
                              </div>
                            ) : (
                              <span className="tw-text-[0.8rem] tw-opacity-50 tw-italic">
                                {status === 'verified' ? 'Approved' : 'Rejected'}
                              </span>
                            )}
                          </AdminTd>
                        </AdminTr>
                      );
                    })
                  )}
                </tbody>
              </AdminTable>
            </AdminTableCard>
          </>
        ) : (
          <>
            {/* Status Filter Tabs */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', borderRadius: '12px', padding: '0.35rem', width: 'fit-content' }}>
              {['all', 'pending', 'verified', 'rejected', 'not_started'].map(tab => (
                <button key={tab} onClick={() => { setKycStatusFilter(tab); fetchKYCRequests(tab); }} style={{ padding: '0.55rem 1rem', borderRadius: '9px', border: 'none', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', transition: 'all 0.2s', background: kycStatusFilter === tab ? 'linear-gradient(135deg, #22C55E, #16A34A)' : 'transparent', color: kycStatusFilter === tab ? '#fff' : (dark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)'), boxShadow: kycStatusFilter === tab ? '0 4px 12px rgba(34,197,94,0.35)' : 'none' }}>
                  {tab === 'not_started' ? 'Not Started' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

        {/* ── KYC Table ── */}
        <AdminTableCard>
          <AdminTable
            minWidth="960px"
            columns={[{ width: '24%' }, { width: '11%' }, { width: '13%' }, { width: '13%' }, { width: '15%' }, { width: '10%' }, { width: '14%' }]}
          >
            <AdminTableHead>
              <AdminTh>User</AdminTh>
              <AdminTh>User Type</AdminTh>
              <AdminTh>Email Verified</AdminTh>
              <AdminTh>Phone Verified</AdminTh>
              <AdminTh>Verification Status</AdminTh>
              <AdminTh>Joined</AdminTh>
              <AdminTh>Actions</AdminTh>
            </AdminTableHead>
            <tbody>
              {kycRequests.length === 0 ? (
                <AdminTableEmpty
                  colSpan={7}
                  message={`No KYC submissions found with status "${kycStatusFilter}". Try switching to "all" to see everything.`}
                />
              ) : (
                kycRequests.map((kyc) => {
                  const role = getUserRole(kyc);
                  const roleTone = role === 'seller' ? 'green' : role === 'admin' ? 'red' : 'neutral';
                  const vs = kyc.verification_status || 'not_started';
                  const vsTone = vs === 'verified' ? 'green' : vs === 'pending' ? 'amber' : vs === 'rejected' ? 'red' : 'neutral';
                  const vsIcon = vs === 'verified' ? faCheckCircle : vs === 'pending' ? faSpinner : vs === 'rejected' ? faBan : faTimes;
                  return (
                    <AdminTr key={kyc._id || kyc.id}>
                      <AdminTd>
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-flex-shrink-0 tw-overflow-hidden tw-bg-gradient-to-br tw-from-green-500 tw-to-green-600 tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-shadow-[0_4px_15px_rgba(34,197,94,0.35)]">
                            {kyc.profile?.avatar
                              ? <img src={kyc.profile.avatar} alt="" className="tw-w-full tw-h-full tw-object-cover" />
                              : (kyc.full_name || kyc.profile?.first_name || kyc.username || kyc.email || 'U').charAt(0).toUpperCase()
                            }
                          </div>
                          <div className="tw-min-w-0">
                            <div className="tw-font-bold tw-truncate">{kyc.full_name || kyc.username || 'N/A'}</div>
                            <div className="tw-text-[0.82rem] tw-opacity-60 tw-truncate">
                              <FontAwesomeIcon icon={faEnvelope} className="tw-mr-1.5" />
                              {kyc.email || 'No email'}
                            </div>
                          </div>
                        </div>
                      </AdminTd>
                      <AdminTd><AdminBadge tone={roleTone}>{role}</AdminBadge></AdminTd>
                      <AdminTd>
                        <AdminBadge tone={kyc.email_verified ? 'green' : 'red'} icon={kyc.email_verified ? faCheckCircle : faTimes}>
                          {kyc.email_verified ? 'Yes' : 'No'}
                        </AdminBadge>
                      </AdminTd>
                      <AdminTd>
                        <AdminBadge tone={kyc.phone_verified ? 'green' : 'neutral'} icon={kyc.phone_verified ? faCheckCircle : faTimes}>
                          {kyc.phone_verified ? 'Yes' : 'No'}
                        </AdminBadge>
                      </AdminTd>
                      <AdminTd>
                        <AdminBadge tone={vsTone} icon={vsIcon}>{vs.replace('_', ' ')}</AdminBadge>
                      </AdminTd>
                      <AdminTd className="tw-opacity-80">
                        {(() => {
                          try { return kyc.created_at ? format(new Date(kyc.created_at), 'MMM dd, yyyy') : '—'; } catch { return '—'; }
                        })()}
                      </AdminTd>
                      <AdminTd>
                        {vs === 'pending' ? (
                          <div className="tw-flex tw-gap-2">
                            <button
                              onClick={() => handleApproveKYC(kyc._id || kyc.id)}
                              className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-border-0 tw-bg-gradient-to-br tw-from-green-500 tw-to-green-600 tw-text-white tw-font-semibold tw-text-xs tw-cursor-pointer"
                            >
                              <FontAwesomeIcon icon={faCheckCircle} className="tw-mr-1" />Approve
                            </button>
                            <button
                              onClick={() => handleRejectKYC(kyc._id || kyc.id)}
                              className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-red-500/30 tw-bg-transparent tw-text-red-500 tw-font-semibold tw-text-xs tw-cursor-pointer"
                            >
                              <FontAwesomeIcon icon={faTimes} className="tw-mr-1" />Reject
                            </button>
                          </div>
                        ) : (
                          <span className="tw-text-[0.8rem] tw-opacity-50 tw-italic">—</span>
                        )}
                      </AdminTd>
                    </AdminTr>
                  );
                })
              )}
            </tbody>
          </AdminTable>
        </AdminTableCard>
          </>
        )}
      </>
    )}

    {/* Reject KYC Modal */}
    {showRejectKYCModal && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
      }} onClick={() => setShowRejectKYCModal(false)}>
        <div style={{
          background: dark ? 'rgba(15,15,20,0.98)' : '#fff',
          borderRadius: '24px', overflow: 'hidden', maxWidth: '440px', width: '90%',
          boxShadow: '0 30px 80px rgba(0,0,0,0.4)',
          border: `1px solid ${dark ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.2)'}`
        }} onClick={(e) => e.stopPropagation()}>
          <div style={{ height: '4px', background: 'linear-gradient(90deg, #ef4444, #dc2626)' }} />
          <div style={{ padding: '2rem' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '800', color: dark ? '#F3F4F6' : '#1F2937' }}>
              <FontAwesomeIcon icon={faTimes} style={{ color: '#ef4444', marginRight: '0.6rem' }} />
              Reject KYC Verification
            </h2>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: dark ? '#9CA3AF' : '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Reason *
            </label>
            <textarea
              value={rejectKYCReason}
              onChange={(e) => setRejectKYCReason(e.target.value)}
              rows={4}
              placeholder="Explain why this verification is being rejected..."
              style={{
                width: '100%', padding: '0.75rem 1rem', borderRadius: '12px',
                border: `1px solid ${dark ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.2)'}`,
                background: dark ? 'rgba(0,0,0,0.3)' : '#f9fafb', color: dark ? '#F3F4F6' : '#1F2937',
                fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit'
              }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRejectKYCModal(false)}
                style={{ padding: '0.7rem 1.5rem', borderRadius: '12px', border: `1px solid ${dark ? 'rgba(239,68,68,0.3)' : 'rgba(239,68,68,0.25)'}`, background: 'transparent', color: '#ef4444', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={submitRejectKYC}
                style={{ padding: '0.7rem 1.5rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff', fontWeight: '700', cursor: 'pointer' }}
              >
                <FontAwesomeIcon icon={faTimes} style={{ marginRight: '0.5rem' }} />
                Reject
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
    );
  };

  const renderProductsContent = () => {
    const filteredProducts = products.filter(p => {
      const q = productSearch.toLowerCase();
      const matchesSearch = !q || (p.title || '').toLowerCase().includes(q) || (p.seller_name || p.seller_email || '').toLowerCase().includes(q);
      const matchesStatus = productStatusFilter === 'all' || (p.status || 'pending') === productStatusFilter;
      return matchesSearch && matchesStatus;
    });
    const STATUS_TABS = ['all', 'pending', 'draft', 'active', 'rejected'];
    const btnBase = { border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 0.75rem', transition: 'all 0.2s ease' };

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#22C55E' }} />
            <p style={{ marginTop: '1rem', fontSize: '1.1rem', fontWeight: '500' }}>Loading products...</p>
          </div>
        ) : (
          <>
            {/* ── Header ── */}
            <div style={{
              marginBottom: '1.75rem', padding: '1.5rem',
              background: isDarkMode ? 'linear-gradient(135deg, rgba(30,30,45,0.95) 0%, rgba(45,45,60,0.95) 100%)' : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,253,244,0.95) 100%)',
              borderRadius: '16px', backdropFilter: 'blur(10px)',
              border: isDarkMode ? '1px solid rgba(34,197,94,0.2)' : '1px solid rgba(22,163,74,0.15)',
              boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(34,197,94,0.12)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <h2 style={{
                    fontSize: '1.75rem', fontWeight: '700', margin: '0 0 0.4rem',
                    background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
                  }}>
                    <FontAwesomeIcon icon={faBox} style={{ marginRight: '0.75rem' }} />
                    Product Management
                  </h2>
                  <p style={{ margin: 0, opacity: 0.7, fontSize: '0.95rem' }}>
                    Approve listings, manage flags, and maintain marketplace quality — {products.length} total
                  </p>
                </div>
                <button
                  onClick={handleCreateProduct}
                  style={{ ...btnBase, padding: '0.75rem 1.25rem', background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: 'white', boxShadow: '0 4px 15px rgba(34,197,94,0.35)', fontSize: '0.9rem' }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Create Product
                </button>
              </div>

              {/* ── Search + Status Tabs ── */}
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{
                  flex: '1 1 260px', display: 'flex', alignItems: 'center', gap: '0.6rem',
                  background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  borderRadius: '10px', padding: '0.6rem 1rem',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)'
                }}>
                  <FontAwesomeIcon icon={faSearch} style={{ opacity: 0.4 }} />
                  <input
                    type="text"
                    placeholder="Search by title or seller..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '0.9rem', flex: 1, color: 'inherit' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', borderRadius: '10px', padding: '0.3rem' }}>
                  {STATUS_TABS.map(tab => (
                    <button
                      key={tab}
                      onClick={() => { setProductStatusFilter(tab); fetchProducts(tab, productSearch); }}
                      style={{
                        ...btnBase, padding: '0.5rem 0.9rem', textTransform: 'capitalize',
                        background: productStatusFilter === tab ? 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' : 'transparent',
                        color: productStatusFilter === tab ? 'white' : (isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)'),
                        boxShadow: productStatusFilter === tab ? '0 4px 12px rgba(34,197,94,0.3)' : 'none'
                      }}
                    >{tab}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Product Grid ── */}
            {filteredProducts.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '4rem',
                background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(240,253,244,0.5)',
                borderRadius: '16px', border: `2px dashed ${isDarkMode ? 'rgba(34,197,94,0.2)' : 'rgba(22,163,74,0.2)'}`
              }}>
                <FontAwesomeIcon icon={faBox} size="3x" style={{ color: '#22C55E', opacity: 0.3, marginBottom: '1rem', display: 'block' }} />
                <p style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.4rem' }}>No products found</p>
                <p style={{ opacity: 0.6, fontSize: '0.9rem' }}>Try a different status filter or search term</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
                {filteredProducts.map(product => {
                  const pid = product._id || product.id;
                  const imgSrc = product.images?.length
                    ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url)
                    : product.image || null;
                  const status = product.status || 'pending';
                  const statusColors = { active: '#22C55E', pending: '#F59E0B', draft: '#94A3B8', rejected: '#EF4444', deleted: '#6B7280' };
                  const sc = statusColors[status] || '#94A3B8';

                  return (
                    <div
                      key={pid}
                      style={{
                        background: isDarkMode ? 'rgba(20,20,35,0.85)' : '#fff',
                        border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.12)' : 'rgba(22,163,74,0.12)'}`,
                        borderRadius: '20px', overflow: 'hidden',
                        boxShadow: isDarkMode ? '0 4px 24px rgba(0,0,0,0.35)' : '0 4px 24px rgba(34,197,94,0.07)',
                        transition: 'all 0.3s ease', display: 'flex', flexDirection: 'column'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = isDarkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(34,197,94,0.15)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = isDarkMode ? '0 4px 24px rgba(0,0,0,0.35)' : '0 4px 24px rgba(34,197,94,0.07)'; }}
                    >
                      {/* ── Cinematic Image ── */}
                      <div style={{ position: 'relative', height: '220px', overflow: 'hidden', flexShrink: 0, background: isDarkMode ? '#0a0a0a' : '#f0fdf4' }}>
                        {imgSrc && (
                          <img
                            src={imgSrc}
                            alt={product.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        )}
                        {!imgSrc && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <FontAwesomeIcon icon={faImage} size="3x" style={{ color: '#22C55E', opacity: 0.2 }} />
                          </div>
                        )}
                        {/* Cinematic gradient overlay */}
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.12) 55%, transparent 100%)' }} />

                        {/* Status badge — top right */}
                        <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', zIndex: 2 }}>
                          <span style={{
                            padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700',
                            textTransform: 'uppercase', letterSpacing: '0.5px',
                            background: `${sc}28`, color: sc,
                            border: `1px solid ${sc}60`, backdropFilter: 'blur(8px)'
                          }}>{status}</span>
                        </div>

                        {/* Flag badges — top left, stacked */}
                        <div style={{ position: 'absolute', top: '0.75rem', left: '0.75rem', zIndex: 2, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                          {product.is_featured && <span style={{ padding: '0.2rem 0.55rem', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', background: 'linear-gradient(135deg,#22C55E,#16A34A)', color: 'white', boxShadow: '0 2px 8px rgba(34,197,94,0.45)' }}><FontAwesomeIcon icon={faStar} style={{ marginRight: '0.25rem' }} />Featured</span>}
                          {product.is_trending && <span style={{ padding: '0.2rem 0.55rem', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', background: 'linear-gradient(135deg,#F59E0B,#D97706)', color: 'white', boxShadow: '0 2px 8px rgba(245,158,11,0.45)' }}><FontAwesomeIcon icon={faChartLine} style={{ marginRight: '0.25rem' }} />Trending</span>}
                          {product.is_hot && <span style={{ padding: '0.2rem 0.55rem', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', background: 'linear-gradient(135deg,#EF4444,#DC2626)', color: 'white', boxShadow: '0 2px 8px rgba(239,68,68,0.45)' }}><FontAwesomeIcon icon={faFlag} style={{ marginRight: '0.25rem' }} />Hot</span>}
                          {product.boost_level > 0 && <span style={{ padding: '0.2rem 0.55rem', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', background: 'linear-gradient(135deg,#22C55E,#16A34A)', color: 'white', boxShadow: '0 2px 8px rgba(34,197,94,0.45)' }}><FontAwesomeIcon icon={faCrown} style={{ marginRight: '0.25rem' }} />×{product.boost_level}</span>}
                          {product.is_featured && <span style={{ padding: '0.2rem 0.55rem', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', background: 'linear-gradient(135deg,#10B981,#059669)', color: 'white', boxShadow: '0 2px 8px rgba(16,185,129,0.45)' }}><FontAwesomeIcon icon={faTag} style={{ marginRight: '0.25rem' }} />Good Deal</span>}
                        </div>

                        {/* Title + price overlay at image bottom */}
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '0.85rem 1rem', zIndex: 2 }}>
                          <div style={{ fontWeight: '700', fontSize: '1rem', color: 'white', lineHeight: 1.3, marginBottom: '0.2rem', textShadow: '0 1px 4px rgba(0,0,0,0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {product.title || 'Untitled Product'}
                          </div>
                          <div style={{ fontSize: '1.15rem', fontWeight: '800', color: '#4ade80', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
                            {formatCurrency(product.price || 0)}
                          </div>
                        </div>
                      </div>

                      {/* ── Seller / Meta Row ── */}
                      <div style={{ padding: '0.75rem 1rem 0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)' }}>
                        <span style={{ fontSize: '0.82rem', opacity: 0.65, display: 'flex', alignItems: 'center', gap: '0.4rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
                          <FontAwesomeIcon icon={faUserTie} style={{ color: '#22C55E', flexShrink: 0 }} />
                          {product.seller_name || 'Unknown Seller'}
                        </span>
                        <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                          {product.condition && <span style={{ padding: '0.18rem 0.5rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '600', background: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', textTransform: 'capitalize' }}>{product.condition}</span>}
                          {product.quantity != null && <span style={{ padding: '0.18rem 0.5rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '600', background: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)' }}>Qty {product.quantity}</span>}
                        </div>
                      </div>

                      {/* ── Action Buttons ── */}
                      <div style={{ padding: '0.75rem 1rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: 'auto' }}>
                        {/* Full details — the approval decision needs the
                            description, specs and every image, which used to
                            be reachable only by opening the Edit form. */}
                        <button
                          onClick={() => setReviewingProduct(product)}
                          style={{
                            ...btnBase, justifyContent: 'center', padding: '0.6rem', fontSize: '0.85rem',
                            background: (status === 'pending' || status === 'draft')
                              ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                              : (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                            color: (status === 'pending' || status === 'draft') ? 'white' : 'inherit',
                            border: (status === 'pending' || status === 'draft') ? 'none' : `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                            boxShadow: (status === 'pending' || status === 'draft') ? '0 3px 10px rgba(59,130,246,0.35)' : 'none',
                          }}
                        >
                          <FontAwesomeIcon icon={faEye} /> View Full Details
                        </button>

                        {/* Approve / Reject — pending & draft only */}
                        {(status === 'pending' || status === 'draft') && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                            <button onClick={() => handleApproveProduct(pid)} style={{ ...btnBase, justifyContent: 'center', background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: 'white', boxShadow: '0 3px 10px rgba(34,197,94,0.35)', padding: '0.6rem', fontSize: '0.85rem' }}>
                              <FontAwesomeIcon icon={faCheckCircle} /> Approve
                            </button>
                            <button onClick={() => handleRejectProduct(pid)} style={{ ...btnBase, justifyContent: 'center', background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', color: 'white', boxShadow: '0 3px 10px rgba(239,68,68,0.3)', padding: '0.6rem', fontSize: '0.85rem' }}>
                              <FontAwesomeIcon icon={faTimes} /> Reject
                            </button>
                          </div>
                        )}

                        {/* Flags row — active only */}
                        {status === 'active' && (
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <button onClick={() => handleToggleProductGoodDeal(pid, product.is_featured)} style={{ ...btnBase, background: product.is_featured ? 'rgba(16,185,129,0.18)' : (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'), color: product.is_featured ? '#10B981' : 'inherit' }}>
                              <FontAwesomeIcon icon={faTag} /> {product.is_featured ? 'Ungood Deal' : 'Good Deal'}
                            </button>
                            <button onClick={() => handleToggleProductTrending(pid, product.is_trending)} style={{ ...btnBase, background: product.is_trending ? 'rgba(245,158,11,0.18)' : (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'), color: product.is_trending ? '#F59E0B' : 'inherit' }}>
                              <FontAwesomeIcon icon={faChartLine} /> {product.is_trending ? 'Untrend' : 'Trending'}
                            </button>
                            <button onClick={() => handleToggleProductHot(pid, product.is_hot)} style={{ ...btnBase, background: product.is_hot ? 'rgba(239,68,68,0.15)' : (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'), color: product.is_hot ? '#EF4444' : 'inherit' }}>
                              <FontAwesomeIcon icon={faFlag} /> {product.is_hot ? 'Unhot' : 'Hot Deal'}
                            </button>
                            <button onClick={() => handleBoostProduct(pid)} style={{ ...btnBase, background: isDarkMode ? 'rgba(124,58,237,0.16)' : 'rgba(124,58,237,0.09)', color: '#7C3AED' }}>
                              <FontAwesomeIcon icon={faCrown} /> Boost
                            </button>
                            {product.is_flash_deal ? (
                              <button onClick={() => handleRemoveFlashDeal(pid)} style={{ ...btnBase, background: 'rgba(234,88,12,0.18)', color: '#EA580C' }} title="Remove flash deal">
                                <FontAwesomeIcon icon={faBolt} /> Flash Deal Active
                              </button>
                            ) : (
                              <button onClick={() => handleOpenFlashDeal(product)} style={{ ...btnBase, background: isDarkMode ? 'rgba(234,88,12,0.14)' : 'rgba(234,88,12,0.08)', color: '#EA580C' }}>
                                <FontAwesomeIcon icon={faBolt} /> Flash Deal
                              </button>
                            )}
                          </div>
                        )}

                        {/* Edit + Delete — always shown */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem' }}>
                          <button onClick={() => handleEditProduct(product)} style={{ ...btnBase, justifyContent: 'center', background: isDarkMode ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.08)', color: '#16A34A', border: '1px solid rgba(34,197,94,0.2)', padding: '0.55rem' }}>
                            <FontAwesomeIcon icon={faEdit} /> Edit Details
                          </button>
                          <button onClick={() => handleDeleteProduct(pid)} title="Delete" style={{ ...btnBase, background: isDarkMode ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)', padding: '0.55rem 0.9rem' }}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Shared Previous/Next control — mirrors the Users tab's own pagination
  // styling exactly. Orders/Escrows/Disputes/Swaps each used to fetch a
  // single hardcoded page with no way to reach older records once a tab
  // held more than one page's worth — same bug class as the KYC pagination
  // fix, recurring in four more places.
  const AdminPagination = ({ page, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
      <div className="pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem' }}>
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          style={{
            padding: '0.6rem 1.25rem', borderRadius: '10px', border: 'none', fontWeight: 600,
            cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.4 : 1,
            background: isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(22,163,74,0.1)',
            color: isDarkMode ? '#4ade80' : '#16a34a', transition: 'all 0.2s',
          }}
        >
          ← Previous
        </button>
        <span style={{ fontSize: '0.9rem', fontWeight: 600, color: isDarkMode ? '#94a3b8' : '#64748b' }}>
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          style={{
            padding: '0.6rem 1.25rem', borderRadius: '10px', border: 'none', fontWeight: 600,
            cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.4 : 1,
            background: isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(22,163,74,0.1)',
            color: isDarkMode ? '#4ade80' : '#16a34a', transition: 'all 0.2s',
          }}
        >
          Next →
        </button>
      </div>
    );
  };

  const renderOrdersContent = () => {
    const ORDER_STATUSES = ['all', 'pending', 'confirmed', 'paid', 'processing', 'shipped', 'delivered', 'completed', 'disputed', 'cancelled', 'refunded'];

    const filteredOrders = orders.filter(o => {
      const q = orderSearch.toLowerCase();
      const matchesSearch = !q ||
        (o._id || o.id || '').toLowerCase().includes(q) ||
        (o.order_number || '').toLowerCase().includes(q) ||
        (o.buyer_name || o.buyer_email || '').toLowerCase().includes(q) ||
        (o.seller_name || o.seller_email || '').toLowerCase().includes(q);
      const matchesStatus = orderStatusFilter === 'all' || (o.status || 'pending') === orderStatusFilter;
      return matchesSearch && matchesStatus;
    });

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#22C55E' }} />
            <p style={{ marginTop: '1rem' }}>Loading orders...</p>
          </div>
        ) : (
          <>
            {/* Header Banner */}
            <div style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', padding: '1.5rem', borderRadius: '16px', marginBottom: '1.5rem', boxShadow: '0 8px 32px rgba(34,197,94,0.25)' }}>
              <h2 style={{ color: 'white', fontSize: '1.75rem', fontWeight: '700', marginBottom: '0.75rem' }}>
                <FontAwesomeIcon icon={faShoppingCart} style={{ marginRight: '0.75rem' }} />
                Order Management
              </h2>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                  <strong style={{ color: 'white', fontSize: '1.1rem' }}>{orders.length}</strong> total
                </span>
                <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                  <strong style={{ color: 'white', fontSize: '1.1rem' }}>{orders.filter(o => o.status === 'pending').length}</strong> pending
                </span>
                <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                  <strong style={{ color: 'white', fontSize: '1.1rem' }}>{orders.filter(o => ['processing', 'shipped'].includes(o.status)).length}</strong> in progress
                </span>
                <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                  <strong style={{ color: 'white', fontSize: '1.1rem' }}>{orders.filter(o => ['delivered', 'completed'].includes(o.status)).length}</strong> completed
                </span>
                <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                  <strong style={{ color: 'white', fontSize: '1.1rem' }}>{orders.filter(o => o.status === 'cancelled').length}</strong> cancelled
                </span>
                {/* models.OrderStatusDisputed is actively set (handlers/escrow.go
                    RequestRefund) but had no counter here — a disputed order was
                    only visible under "All", exactly where support investigating
                    a payment complaint wouldn't think to look. */}
                <span style={{ color: orders.some(o => o.status === 'disputed') ? '#FEF08A' : 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                  <strong style={{ color: 'white', fontSize: '1.1rem' }}>{orders.filter(o => o.status === 'disputed').length}</strong> disputed
                </span>
              </div>
            </div>

            {/* Search + Refresh */}
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <div className="AutoboyExpress-dash-search-bar" style={{ flex: '1 1 280px' }}>
                <FontAwesomeIcon icon={faSearch} className="AutoboyExpress-dash-search-icon" />
                <input
                  type="text"
                  placeholder="Search by order ID, customer, seller..."
                  value={orderSearch}
                  /* Debounced: this fired a full orders fetch on every
                     keystroke, so typing a 12-character order number meant
                     12 round trips — a meaningful share of the requests
                     that were tripping the rate limiter. */
                  onChange={(e) => { setOrderSearch(e.target.value); debouncedFetchOrders(orderStatusFilter, e.target.value); }}
                />
              </div>
              <button
                onClick={() => fetchOrders(orderStatusFilter, orderSearch)}
                style={{ padding: '0.6rem 1.1rem', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.3)', background: 'rgba(34,197,94,0.08)', color: '#16A34A', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}
              >
                <FontAwesomeIcon icon={faSync} /> Refresh
              </button>
            </div>

            {/* Status Filter Tabs */}
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
              {ORDER_STATUSES.map(s => {
                const count = s === 'all' ? orders.length : orders.filter(o => (o.status || 'pending') === s).length;
                const isActive = orderStatusFilter === s;
                return (
                  <button key={s} onClick={() => { setOrderStatusFilter(s); fetchOrders(s, orderSearch); }} style={{
                    padding: '0.375rem 0.85rem', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                    border: isActive ? 'none' : `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                    background: isActive ? '#22C55E' : (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
                    color: isActive ? 'white' : (isDarkMode ? '#94a3b8' : '#64748b'),
                    transition: 'all 0.18s ease', display: 'flex', alignItems: 'center', gap: '0.35rem'
                  }}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                    <span style={{
                      background: isActive ? 'rgba(255,255,255,0.25)' : 'rgba(34,197,94,0.15)',
                      color: isActive ? 'white' : '#22C55E',
                      padding: '0.05rem 0.45rem', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700
                    }}>{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Table */}
            <AdminTableCard>
              <AdminTable
                minWidth="1040px"
                columns={[
                  { width: '16%' }, { width: '15%' }, { width: '13%' }, { width: '10%' },
                  { width: '9%' }, { width: '9%' }, { width: '8%' }, { width: '20%' },
                ]}
              >
                <AdminTableHead>
                  <AdminTh>Order</AdminTh>
                  <AdminTh>Customer</AdminTh>
                  <AdminTh>Seller</AdminTh>
                  <AdminTh>Amount</AdminTh>
                  <AdminTh>Status</AdminTh>
                  <AdminTh>Payment</AdminTh>
                  <AdminTh>Date</AdminTh>
                  <AdminTh align="center">Actions</AdminTh>
                </AdminTableHead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <AdminTableEmpty
                      colSpan={8}
                      message={orderStatusFilter !== 'all' ? `No ${orderStatusFilter} orders` : 'Orders will appear here when customers make purchases'}
                    />
                  ) : filteredOrders.map((order) => {
                    const customerName = getOrderCustomerName(order);
                    const itemsLabel = getOrderItemsLabel(order);
                    const itemCount = (order.items || order.products || []).length;
                    return (
                      <AdminTr key={order._id || order.id}>
                        <AdminTd>
                          <strong className="tw-text-green-500 tw-text-[0.85rem] tw-font-mono">
                            #{(order.order_number || order._id || order.id || '').toString().slice(-8).toUpperCase()}
                          </strong>
                          {itemsLabel && (
                            <div className="tw-text-[0.75rem] tw-opacity-70 tw-mt-0.5 tw-truncate" title={itemsLabel}>
                              {itemsLabel}{itemCount > 1 ? ` (${itemCount} items)` : ''}
                            </div>
                          )}
                        </AdminTd>
                        <AdminTd>
                          <div className="tw-flex tw-items-center tw-gap-2">
                            <div className="tw-w-8 tw-h-8 tw-rounded-full tw-flex-shrink-0 tw-bg-gradient-to-br tw-from-green-500 tw-to-green-600 tw-flex tw-items-center tw-justify-center tw-text-white tw-text-[0.8rem] tw-font-bold">
                              {(customerName || 'C')[0].toUpperCase()}
                            </div>
                            <div className="tw-min-w-0">
                              <div className="tw-text-[0.875rem] tw-font-semibold tw-truncate">{customerName}</div>
                              {order.buyer_email && <div className="tw-text-[0.72rem] tw-opacity-55 tw-truncate">{order.buyer_email}</div>}
                            </div>
                          </div>
                        </AdminTd>
                        <AdminTd>
                          <div className="tw-flex tw-items-center tw-gap-2">
                            <div className="tw-w-7 tw-h-7 tw-rounded-full tw-flex-shrink-0 tw-bg-green-500/15 tw-flex tw-items-center tw-justify-center tw-text-green-600 tw-text-[0.75rem] tw-font-bold">
                              {(order.seller_name || order.seller_email || 'S')[0].toUpperCase()}
                            </div>
                            <div className="tw-min-w-0">
                              <div className="tw-text-[0.875rem] tw-truncate">{order.seller_name || 'Seller'}</div>
                              {order.seller_email && <div className="tw-text-[0.72rem] tw-opacity-55 tw-truncate">{order.seller_email}</div>}
                            </div>
                          </div>
                        </AdminTd>
                        <AdminTd>
                          <strong className="tw-text-green-500 tw-text-[0.95rem]">{formatCurrency(order.total_amount || 0)}</strong>
                        </AdminTd>
                        <AdminTd>
                          <span
                            className="tw-inline-block tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold tw-capitalize"
                            style={{ color: getStatusColor(order.status), background: `${getStatusColor(order.status)}18` }}
                          >
                            {order.status || 'pending'}
                          </span>
                        </AdminTd>
                        <AdminTd>
                          <span
                            className="tw-inline-block tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold"
                            style={{
                              color: getPaymentStatusColor(order.payment_status || order.paymentStatus || 'pending'),
                              background: `${getPaymentStatusColor(order.payment_status || order.paymentStatus || 'pending')}18`,
                            }}
                          >
                            {getPaymentStatusText(order.payment_status || order.paymentStatus || 'pending', order)}
                          </span>
                        </AdminTd>
                        <AdminTd className="tw-whitespace-nowrap tw-opacity-80">
                          {order.created_at ? format(new Date(order.created_at), 'MMM dd, yyyy') : 'N/A'}
                        </AdminTd>
                        <AdminTd align="center">
                          <div className="tw-flex tw-items-center tw-gap-1.5 tw-justify-center tw-flex-nowrap">
                            <AdminIconBtn onClick={() => handleViewOrder(order)} title="View order" tone="green">
                              <FontAwesomeIcon icon={faEye} />
                            </AdminIconBtn>
                            <AdminIconBtn onClick={() => setIMEIViewerTarget({ type: 'order', id: order._id || order.id })} title="View IMEI (admin only)" tone="green">
                              <FontAwesomeIcon icon={faMobileAlt} />
                            </AdminIconBtn>
                            <AdminIconBtn onClick={() => handleUpdateOrderStatus(order._id || order.id, order.status)} title="Update status" tone="green">
                              <FontAwesomeIcon icon={faEdit} />
                            </AdminIconBtn>
                            {!['completed', 'cancelled', 'refunded', 'delivered'].includes(order.status) && (
                              <AdminIconBtn onClick={() => handleCancelOrder(order._id || order.id)} title="Cancel order" tone="red">
                                <FontAwesomeIcon icon={faTimes} />
                              </AdminIconBtn>
                            )}
                          </div>
                        </AdminTd>
                      </AdminTr>
                    );
                  })}
                </tbody>
              </AdminTable>
            </AdminTableCard>
            <AdminPagination page={orderPage} totalPages={orderTotalPages} onPageChange={(p) => fetchOrders(orderStatusFilter, orderSearch, p)} />
          </>
        )}
      </div>
    );
  };

  const renderEscrowContent = () => {
    const ESCROW_STATUSES = ['all', 'held', 'released', 'refunded', 'disputed'];
    const statusMeta = {
      held:     { label: 'Held',     bg: 'rgba(245,158,11,0.13)',  color: '#F59E0B', border: 'rgba(245,158,11,0.3)',  icon: faLock   },
      released: { label: 'Released', bg: 'rgba(34,197,94,0.13)',   color: '#22C55E', border: 'rgba(34,197,94,0.3)',   icon: faCheckCircle },
      refunded: { label: 'Refunded', bg: 'rgba(239,68,68,0.13)',   color: '#EF4444', border: 'rgba(239,68,68,0.3)',   icon: faTimes  },
      disputed: { label: 'Disputed', bg: 'rgba(168,85,247,0.13)',  color: '#A855F7', border: 'rgba(168,85,247,0.3)', icon: faFlag   },
    };
    const getCountdown = (releaseDateStr) => {
      if (!releaseDateStr) return null;
      const now = Date.now();
      const target = new Date(releaseDateStr).getTime();
      const diffMs = target - now;
      if (diffMs <= 0) return { label: 'Overdue', overdue: true };
      const diffH = Math.floor(diffMs / 3600000);
      if (diffH < 24) return { label: `${diffH}h left`, overdue: false };
      const diffD = Math.floor(diffH / 24);
      return { label: `${diffD}d left`, overdue: false };
    };
    const filtered = escrows.filter(e => {
      const q = escrowSearch.toLowerCase();
      const matchSearch = !q ||
        (e._id || e.id || '').toLowerCase().includes(q) ||
        (e.escrow_number || '').toLowerCase().includes(q) ||
        (e.order_id || '').toLowerCase().includes(q) ||
        (e.buyer_name || e.buyer_email || '').toLowerCase().includes(q) ||
        (e.seller_name || e.seller_email || '').toLowerCase().includes(q);
      const matchStatus = escrowStatusFilter === 'all' || (e.status || 'held') === escrowStatusFilter;
      return matchSearch && matchStatus;
    });
    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#22C55E' }} />
            <p style={{ marginTop: '1rem' }}>Loading escrow data...</p>
          </div>
        ) : (
          <>
            <EscrowAnalytics />
            <div className="AEX-AdminFeature-panel">
              <div className="AEX-AdminFeature-panel-header">
                <div>
                  <h3 className="AEX-AdminFeature-panel-title">
                    Escrow Transactions
                  </h3>
                  <p className="AEX-AdminFeature-panel-subtitle">
                    {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
                    {escrowStatusFilter !== 'all' ? ` · ${escrowStatusFilter}` : ''}
                  </p>
                </div>
                <button onClick={() => fetchEscrows(escrowStatusFilter, escrowSearch)}
                  className="AEX-AdminFeature-btn-sm AEX-AdminFeature-btn-sm--teal">
                  Refresh
                </button>
              </div>
              <div className="AEX-AdminFeature-panel-toolbar">
                <div className="tw-relative tw-flex-1 tw-min-w-[220px]">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="tw-absolute tw-left-3.5 tw-top-1/2 tw--translate-y-1/2 tw-text-xs tw-text-zinc-400 dark:tw-text-zinc-500 tw-pointer-events-none"
                  />
                  <input
                    type="text"
                    placeholder="Search by ESC number, buyer, seller..."
                    value={escrowSearch}
                    onChange={e => setEscrowSearch(e.target.value)}
                    className="tw-w-full tw-pl-9 tw-pr-4 tw-py-2.5 tw-rounded-xl tw-border tw-border-black/10 dark:tw-border-white/10 tw-bg-white dark:tw-bg-zinc-900/70 tw-text-zinc-800 dark:tw-text-zinc-100 placeholder:tw-text-zinc-400 dark:placeholder:tw-text-zinc-500 tw-text-sm tw-outline-none tw-transition-colors focus:tw-border-green-500/50 focus:tw-ring-2 focus:tw-ring-green-500/15"
                  />
                </div>
                <select
                  value={escrowStatusFilter}
                  onChange={e => setEscrowStatusFilter(e.target.value)}
                  className="tw-px-4 tw-py-2.5 tw-rounded-xl tw-border tw-border-black/10 dark:tw-border-white/10 tw-bg-white dark:tw-bg-zinc-900/70 tw-text-zinc-800 dark:tw-text-zinc-100 tw-text-sm tw-font-medium tw-cursor-pointer tw-outline-none tw-transition-colors focus:tw-border-green-500/50 focus:tw-ring-2 focus:tw-ring-green-500/15"
                >
                  {ESCROW_STATUSES.map(s => (
                    <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </div>
              {filtered.length === 0 ? (
                <div className="AEX-AdminFeature-empty">
                  <p style={{ margin: 0, fontSize: '0.9rem' }}>No escrow transactions found</p>
                </div>
              ) : (
                <div className="tw-flex tw-flex-col tw-gap-3">
                  {filtered.map((escrow, idx) => {
                    const eid = escrow._id || escrow.id || '';
                    const status = escrow.status || 'held';
                    const meta = statusMeta[status] || statusMeta.held;
                    const canAct = status === 'held' || status === 'disputed';
                    const countdown = getCountdown(escrow.auto_release_date);
                    const buyerName = escrow.buyer_name || escrow.payer_name || 'Buyer';
                    const sellerName = escrow.seller_name || escrow.payee_name || 'Seller';
                    const amount = escrow.amount || escrow.net_amount || 0;
                    const fee = escrow.escrow_fee || 0;
                    return (
                      <div
                        key={eid || idx}
                        className="tw-rounded-2xl tw-p-5 tw-bg-white/95 tw-border tw-border-green-600/10 tw-shadow-[0_4px_20px_rgba(0,0,0,0.06)] tw-transition-shadow hover:tw-shadow-[0_8px_28px_rgba(0,0,0,0.1)] dark:tw-bg-zinc-900/70 dark:tw-border-green-500/15"
                      >
                        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3 tw-pb-4 tw-mb-4 tw-border-b tw-border-black/5 dark:tw-border-white/5">
                          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                            <div className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-bg-blue-500/10 tw-text-blue-600 dark:tw-text-blue-400 tw-text-sm tw-font-semibold">{buyerName}</div>
                            <FontAwesomeIcon icon={faArrowRight} className="tw-text-xs tw-opacity-40" />
                            <div className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-bg-green-500/10 tw-text-green-600 dark:tw-text-green-400 tw-text-sm tw-font-bold">{formatCurrency(amount)}</div>
                            <FontAwesomeIcon icon={faArrowRight} className="tw-text-xs tw-opacity-40" />
                            <div className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-bg-violet-500/10 tw-text-violet-600 dark:tw-text-violet-400 tw-text-sm tw-font-semibold">{sellerName}</div>
                          </div>
                          <div className="tw-flex tw-items-center tw-gap-2">
                            {countdown && (
                              <span className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold ${countdown.overdue ? 'tw-bg-red-500/15 tw-text-red-500' : 'tw-bg-amber-500/15 tw-text-amber-600 dark:tw-text-amber-400'}`}>
                                {countdown.label}
                              </span>
                            )}
                            <span
                              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold"
                              style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}
                            >
                              <FontAwesomeIcon icon={meta.icon} className="tw-text-[0.65rem]" />
                              {meta.label}
                            </span>
                          </div>
                        </div>
                        <div className="tw-flex tw-items-end tw-justify-between tw-flex-wrap tw-gap-4">
                          <div className="tw-flex tw-flex-wrap tw-gap-6">
                            <div>
                              <div className="tw-text-[0.68rem] tw-font-bold tw-uppercase tw-tracking-wider tw-opacity-50 tw-mb-1">Escrow</div>
                              <div className="tw-font-mono tw-text-sm tw-font-semibold">
                                {escrow.escrow_number || `#${eid.slice(-8).toUpperCase()}`}
                              </div>
                            </div>
                            {escrow.order_id && (
                              <div>
                                <div className="tw-text-[0.68rem] tw-font-bold tw-uppercase tw-tracking-wider tw-opacity-50 tw-mb-1">Order</div>
                                <div className="tw-font-mono tw-text-sm tw-font-semibold">{String(escrow.order_id).slice(-8).toUpperCase()}</div>
                              </div>
                            )}
                            {fee > 0 && (
                              <div>
                                <div className="tw-text-[0.68rem] tw-font-bold tw-uppercase tw-tracking-wider tw-opacity-50 tw-mb-1">Fee</div>
                                <div className="tw-text-sm tw-font-semibold">{formatCurrency(fee)}</div>
                              </div>
                            )}
                            <div>
                              <div className="tw-text-[0.68rem] tw-font-bold tw-uppercase tw-tracking-wider tw-opacity-50 tw-mb-1">Held</div>
                              <div className="tw-text-sm tw-font-semibold">
                                {escrow.created_at ? formatDate(escrow.created_at) : '-'}
                              </div>
                            </div>
                          </div>
                          <div className="tw-flex tw-gap-2 tw-flex-wrap">
                            <button
                              onClick={() => handleViewEscrowDetails(eid)}
                              className="tw-px-4 tw-py-2 tw-rounded-lg tw-border tw-border-black/10 dark:tw-border-white/10 tw-bg-black/[0.03] dark:tw-bg-white/5 tw-text-zinc-700 dark:tw-text-zinc-200 tw-font-semibold tw-text-xs tw-cursor-pointer tw-transition-colors hover:tw-bg-black/[0.06] dark:hover:tw-bg-white/10"
                            >
                              Details
                            </button>
                            {canAct && (
                              <>
                                <button
                                  onClick={() => handleReleaseEscrow(escrow)}
                                  className="tw-px-4 tw-py-2 tw-rounded-lg tw-border-0 tw-bg-green-500 tw-text-white tw-font-semibold tw-text-xs tw-cursor-pointer tw-transition-colors hover:tw-bg-green-600"
                                >
                                  Release
                                </button>
                                <button
                                  onClick={() => handleRefundEscrow(escrow)}
                                  className="tw-px-4 tw-py-2 tw-rounded-lg tw-border tw-border-red-500/30 tw-bg-red-500/10 tw-text-red-500 tw-font-semibold tw-text-xs tw-cursor-pointer tw-transition-colors hover:tw-bg-red-500/20"
                                >
                                  Refund
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <AdminPagination page={escrowPage} totalPages={escrowTotalPages} onPageChange={(p) => fetchEscrows(escrowStatusFilter, escrowSearch, p)} />
            <div style={{ marginTop: '2rem' }}>
              <EscrowSettingsPanel />
            </div>
          </>
        )}
      </div>
    );
  };

  const renderWalletsContent = () => {
    const dark = isDarkMode;
    const card = { background: dark ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)', border: `1px solid ${dark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)'}`, borderRadius: '20px', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: dark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(34,197,94,0.08)' };
    const th = { padding: '1rem 1.25rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: dark ? '#4ADE80' : '#16A34A', background: dark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.05)', borderBottom: `1px solid ${dark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)'}` };
    const td = { padding: '0.9rem 1.25rem', fontSize: '0.85rem', color: dark ? '#d1d5db' : '#374151', borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` };
    const txTypeColor = { credit: '#22C55E', debit: '#ef4444', refund: '#f59e0b', adjustment: '#f59e0b' };

    const filteredWallets = wallets.filter(w => {
      const q = walletSearch.toLowerCase();
      return !q ||
        w.user_name?.toLowerCase().includes(q) ||
        w.user_email?.toLowerCase().includes(q) ||
        w.user_id?.toLowerCase().includes(q) ||
        w.currency?.toLowerCase().includes(q);
    });

    return (
    <div style={{ padding: '0 0 2rem 0', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '5rem', color: '#22C55E' }}>
          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2.5rem' }} />
          <p style={{ marginTop: '1rem', color: dark ? '#9ca3af' : '#6b7280', fontSize: '0.9rem' }}>Loading wallets…</p>
        </div>
      ) : (
        <>
          {/* Header */}
          <div style={{ ...card, padding: '1.75rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, background: 'linear-gradient(135deg, #22C55E, #16A34A)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                <FontAwesomeIcon icon={faWallet} style={{ WebkitTextFillColor: '#22C55E', marginRight: '0.6rem' }} />Wallet Management
              </h2>
              <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: dark ? '#6b7280' : '#9ca3af' }}>
                {wallets.length} wallets · {transactions.length} transactions
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button onClick={() => { fetchWallets(); fetchTransactions(); }} style={{ padding: '0.6rem 1.2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <FontAwesomeIcon icon={faSync} /> Refresh
              </button>
            </div>
          </div>

          {/* Stat strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: '1rem' }}>
            {[
              { label: 'Total Wallets', value: wallets.length, icon: faWallet },
              { label: 'Total Balance', value: formatCurrency(wallets.reduce((s, w) => s + (w.balance || 0), 0)), icon: faNairaSign },
              { label: 'Held Funds', value: formatCurrency(wallets.reduce((s, w) => s + (w.held_balance || 0), 0)), icon: faExclamationTriangle },
              { label: 'Transactions', value: transactions.length, icon: faExchangeAlt },
            ].map(s => (
              <div key={s.label} style={{ ...card, padding: '1.2rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: dark ? '#4ADE80' : '#16A34A' }}>{s.label}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: dark ? '#f0fdf4' : '#0f172a' }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.5rem', background: dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', borderRadius: '14px', padding: '0.35rem', width: 'fit-content' }}>
            {[{ k: 'wallets', label: 'Wallets', icon: faWallet }, { k: 'transactions', label: 'Transactions', icon: faExchangeAlt }].map(t => (
              <button key={t.k} onClick={() => setWalletSubTab(t.k)} style={{ padding: '0.55rem 1.2rem', borderRadius: '10px', border: 'none', fontWeight: 600, fontSize: '0.83rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'all 0.2s', background: walletSubTab === t.k ? 'linear-gradient(135deg, #22C55E, #16A34A)' : 'transparent', color: walletSubTab === t.k ? '#fff' : (dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'), boxShadow: walletSubTab === t.k ? '0 4px 12px rgba(34,197,94,0.3)' : 'none' }}>
                <FontAwesomeIcon icon={t.icon} style={{ fontSize: '0.75rem' }} />{t.label}
              </button>
            ))}
          </div>

          {/* Wallets Table */}
          {walletSubTab === 'wallets' && (
            <div style={{ ...card }}>
              <div style={{ padding: '1rem 1.25rem', display: 'flex', gap: '0.75rem', alignItems: 'center', borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', borderRadius: '10px', padding: '0.5rem 0.9rem' }}>
                  <FontAwesomeIcon icon={faSearch} style={{ color: dark ? '#6b7280' : '#9ca3af', fontSize: '0.8rem' }} />
                  <input value={walletSearch} onChange={e => setWalletSearch(e.target.value)} placeholder="Search by name, email, user ID or currency…" style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '0.85rem', color: dark ? '#d1d5db' : '#374151', width: '100%' }} />
                </div>
              </div>
              <AdminTable minWidth="760px" columns={[{ width: '28%' }, { width: '9%' }, { width: '17%' }, { width: '14%' }, { width: '16%' }, { width: '16%' }]}>
                <AdminTableHead>
                  <AdminTh>User</AdminTh>
                  <AdminTh>Currency</AdminTh>
                  <AdminTh>Balance</AdminTh>
                  <AdminTh>Held</AdminTh>
                  <AdminTh>Created</AdminTh>
                  <AdminTh align="center">Actions</AdminTh>
                </AdminTableHead>
                <tbody>
                  {filteredWallets.length === 0 ? (
                    <AdminTableEmpty colSpan={6} message="No wallets found" />
                  ) : filteredWallets.map((wallet, i) => (
                    <AdminTr key={wallet.id || wallet._id || i}>
                      <AdminTd>
                        <div className="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
                          <span className="tw-font-semibold tw-truncate" title={wallet.user_name}>{wallet.user_name || 'Unknown user'}</span>
                          <span className="tw-text-xs tw-text-zinc-500 dark:tw-text-zinc-400 tw-truncate" title={wallet.user_email}>{wallet.user_email || '—'}</span>
                        </div>
                      </AdminTd>
                      <AdminTd><span className="tw-font-bold tw-text-green-500">{wallet.currency || 'NGN'}</span></AdminTd>
                      <AdminTd className="tw-font-bold">{formatCurrency(wallet.balance || 0)}</AdminTd>
                      <AdminTd>
                        <span className={wallet.held_balance > 0 ? 'tw-text-amber-500' : 'tw-opacity-60'}>{formatCurrency(wallet.held_balance || 0)}</span>
                      </AdminTd>
                      <AdminTd className="tw-opacity-80">
                        {wallet.created_at ? new Date(wallet.created_at).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </AdminTd>
                      <AdminTd align="center">
                        <button onClick={() => handleViewWalletDetail(wallet)} className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-green-500/25 tw-bg-green-500/[0.08] tw-text-green-600 tw-font-semibold tw-text-xs tw-cursor-pointer">
                          <FontAwesomeIcon icon={faEye} className="tw-mr-1" />Details
                        </button>
                      </AdminTd>
                    </AdminTr>
                  ))}
                </tbody>
              </AdminTable>
            </div>
          )}

          {/* Transactions Table */}
          {walletSubTab === 'transactions' && (
            <div style={{ ...card }}>
              <AdminTable minWidth="750px" columns={[{ width: '15%' }, { width: '12%' }, { width: '18%' }, { width: '13%' }, { width: '27%' }, { width: '15%' }]}>
                <AdminTableHead>
                  <AdminTh>Tx #</AdminTh>
                  <AdminTh>Type</AdminTh>
                  <AdminTh>Amount</AdminTh>
                  <AdminTh>Status</AdminTh>
                  <AdminTh>Description</AdminTh>
                  <AdminTh>Date</AdminTh>
                </AdminTableHead>
                <tbody>
                  {transactions.length === 0 ? (
                    <AdminTableEmpty colSpan={6} message="No transactions found" />
                  ) : transactions.map((tx, i) => {
                    const tcolor = txTypeColor[tx.type] || '#6b7280';
                    return (
                      <AdminTr key={tx._id || tx.id || i}>
                        <AdminTd className="tw-font-mono tw-text-[0.78rem] tw-opacity-70">{tx.transaction_number || (tx._id || '').substring(0, 10)}</AdminTd>
                        <AdminTd>
                          <span className="tw-px-2 tw-py-1 tw-rounded-md tw-text-[0.72rem] tw-font-bold tw-uppercase" style={{ background: `${tcolor}18`, color: tcolor }}>{tx.type || '—'}</span>
                        </AdminTd>
                        <AdminTd className="tw-font-bold" style={{ color: tx.type === 'credit' ? '#22C55E' : tx.type === 'debit' ? '#ef4444' : undefined }}>
                          {tx.type === 'debit' ? '-' : '+'}{formatCurrency(Math.abs(tx.amount || 0))}
                        </AdminTd>
                        <AdminTd>
                          <span className="tw-text-[0.78rem] tw-font-bold" style={{ color: getStatusColor(tx.status) }}>{(tx.status || '—').toUpperCase()}</span>
                        </AdminTd>
                        <AdminTd className="tw-opacity-75">{tx.description || tx.metadata?.reason || '—'}</AdminTd>
                        <AdminTd className="tw-opacity-80">
                          {tx.created_at ? new Date(tx.created_at).toLocaleString('en-NG', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </AdminTd>
                      </AdminTr>
                    );
                  })}
                </tbody>
              </AdminTable>
            </div>
          )}
        </>
      )}
    </div>
    );
  };
  const renderSwapsContent = () => {
    const SUB_TABS = [
      { key: 'pending_approval', label: 'Pending Approval' },
      { key: 'inspection', label: 'Pending Inspection' },
      { key: 'active', label: 'Active' },
      { key: 'history', label: 'History' },
    ];

    const getFilteredSwaps = () => {
      switch (swapSubTab) {
        case 'pending_approval':
          return swaps.filter(s => s.status === 'pending_approval');
        case 'inspection':
          return swaps.filter(s => s.status === 'pending_inspection');
        case 'active':
          return swaps.filter(s => ['active', 'in_transit', 'in_escrow'].includes(s.status));
        case 'history':
          return swaps.filter(s => ['completed', 'cancelled', 'rejected'].includes(s.status));
        default:
          return swaps;
      }
    };

    const getSwapStatusColor = (status) => {
      const map = {
        pending_approval: '#F59E0B',
        pending_inspection: '#10B981',
        active: '#22C55E',
        in_transit: '#16A34A',
        in_escrow: '#4ADE80',
        completed: '#22C55E',
        cancelled: '#6B7280',
        rejected: '#EF4444',
      };
      return map[status] || '#6B7280';
    };

    const getConditionLabel = (rating) => {
      const map = { 5: 'Excellent', 4: 'Good', 3: 'Fair', 2: 'Poor', 1: 'Damaged' };
      return map[Math.round(rating)] || rating || 'N/A';
    };

    const tabCounts = {
      pending_approval: swaps.filter(s => s.status === 'pending_approval').length,
      inspection: swaps.filter(s => s.status === 'pending_inspection').length,
      active: swaps.filter(s => ['active', 'in_transit', 'in_escrow'].includes(s.status)).length,
      history: swaps.filter(s => ['completed', 'cancelled', 'rejected'].includes(s.status)).length,
    };

    const filteredSwaps = getFilteredSwaps();

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {/* Header Banner */}
        <div className="tw-bg-gradient-to-br tw-from-green-500 tw-to-green-600 tw-rounded-[20px] tw-px-8 tw-py-7 tw-mb-5 tw-shadow-[0_8px_32px_rgba(34,197,94,0.3)] tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-4">
          <div>
            <h2 className="tw-text-white tw-text-[1.75rem] tw-font-extrabold tw-m-0">
              <FontAwesomeIcon icon={faExchangeAlt} className="tw-mr-3 tw-opacity-90" />
              Swap Management
            </h2>
            <p className="tw-text-white/85 tw-text-[0.9rem] tw-mt-[0.4rem] tw-mb-0">
              Monitor and manage all swap deals and device exchange offers across the platform
            </p>
          </div>
          <button
            onClick={fetchSwapData}
            className="tw-bg-white/20 tw-border tw-border-white/35 tw-text-white tw-px-5 tw-py-[0.65rem] tw-rounded-full tw-cursor-pointer tw-font-bold tw-text-sm tw-flex tw-items-center tw-gap-2 tw-transition-colors hover:tw-bg-white/30"
          >
            <FontAwesomeIcon icon={faSync} /> Refresh
          </button>
        </div>

        {/* Empty state */}
        {swaps.length === 0 && !loading && (
          !swapEndpointReady ? (
            <div className="tw-bg-amber-500/[0.06] dark:tw-bg-amber-500/[0.08] tw-border tw-border-amber-500/30 tw-rounded-2xl tw-px-5 tw-py-3.5 tw-mb-5 tw-flex tw-items-start tw-gap-3">
              <FontAwesomeIcon icon={faInfoCircle} className="tw-text-amber-500 tw-text-base tw-flex-shrink-0 tw-mt-[0.15rem]" />
              <span className="tw-text-sm tw-text-amber-800 dark:tw-text-amber-300 tw-font-medium tw-leading-relaxed">
                Platform-wide swap management is awaiting server support. Swap offers initiated by buyers and sellers
                (including premium users) will appear here automatically once the admin swap endpoint is enabled on the
                backend — this view intentionally does not show your own swaps.
              </span>
            </div>
          ) : (
            <div className="tw-bg-green-500/5 dark:tw-bg-green-500/[0.06] tw-border tw-border-green-500/20 tw-rounded-2xl tw-px-5 tw-py-3.5 tw-mb-5 tw-flex tw-items-center tw-gap-3">
              <FontAwesomeIcon icon={faInfoCircle} className="tw-text-green-500 tw-text-base tw-flex-shrink-0" />
              <span className="tw-text-sm tw-text-green-800 dark:tw-text-green-300 tw-font-medium">
                No swap offers yet. Swaps initiated by buyers and sellers will appear here for approval and inspection.
              </span>
            </div>
          )
        )}

        {/* Sub-tab Navigation */}
        <div className="tw-flex tw-gap-3 tw-mb-7 tw-flex-wrap">
          {SUB_TABS.map(tab => (
            <button
              key={tab.key}
              className={`AutoboyExpress-dash-btn ${swapSubTab === tab.key ? 'AutoboyExpress-dash-btn-primary' : 'AutoboyExpress-dash-btn-outline'}`}
              onClick={() => setSwapSubTab(tab.key)}
            >
              {tab.label}
              <span className={`tw-ml-[0.4rem] tw-text-xs tw-font-bold tw-px-2 tw-py-[0.1rem] tw-rounded-[10px] ${swapSubTab === tab.key ? 'tw-bg-green-500/15 tw-text-green-500' : 'tw-bg-black/[0.06] dark:tw-bg-white/10 tw-text-inherit'}`}>
                {tabCounts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Card Grid */}
        {loading ? (
          <div className="tw-text-center tw-py-16">
            <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-green-500/10 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-5 tw-border tw-border-green-500/25">
              <FontAwesomeIcon icon={faSpinner} spin className="tw-text-2xl tw-text-green-500" />
            </div>
            <p className="tw-text-gray-500 dark:tw-text-gray-400 tw-font-medium">Loading swap requests...</p>
          </div>
        ) : filteredSwaps.length === 0 ? (
          <div className="tw-text-center tw-py-20 tw-px-8 tw-bg-white/90 dark:tw-bg-[rgba(15,15,20,0.6)] tw-rounded-3xl tw-border tw-border-dashed tw-border-green-500/30 dark:tw-border-green-500/20 tw-backdrop-blur-xl">
            <div className="tw-w-[88px] tw-h-[88px] tw-rounded-full tw-bg-gradient-to-br tw-from-green-500/15 tw-to-green-600/[0.08] tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-6 tw-border tw-border-green-500/20 tw-shadow-[0_0_40px_rgba(34,197,94,0.08)]">
              <FontAwesomeIcon icon={faExchangeAlt} className="tw-text-[2.25rem] tw-text-green-500 tw-opacity-70" />
            </div>
            <div className="tw-font-bold tw-text-lg tw-text-gray-800 dark:tw-text-gray-100 tw-mb-2">No Swap Requests Found</div>
            <p className="tw-text-gray-500 dark:tw-text-gray-400 tw-text-sm tw-max-w-[320px] tw-mx-auto">
              No swap deals exist in this category yet. They will appear here once users submit swap offers.
            </p>
          </div>
        ) : (
          <div className="tw-grid tw-grid-cols-[repeat(auto-fill,minmax(320px,1fr))] tw-gap-5">
            {filteredSwaps.map(swap => {
              const swapId = swap.id || swap._id;
              const statusColor = getSwapStatusColor(swap.status);
              const initials = (swap.user_name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
              const conditionOutColor = swap.device_out_condition >= 4 ? '#22C55E' : swap.device_out_condition >= 3 ? '#f59e0b' : '#ef4444';
              const conditionInColor = swap.device_in_condition >= 4 ? '#22C55E' : swap.device_in_condition >= 3 ? '#f59e0b' : '#ef4444';
              return (
                <div
                  key={swapId}
                  className="tw-rounded-[20px] tw-border tw-border-green-500/15 tw-backdrop-blur-xl tw-overflow-hidden tw-transition-all tw-duration-200 tw-bg-white/[0.98] dark:tw-bg-[rgba(15,15,20,0.85)] tw-shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:tw-shadow-[0_8px_32px_rgba(0,0,0,0.4)] hover:tw--translate-y-1 hover:tw-shadow-[0_20px_60px_rgba(34,197,94,0.18)]"
                >
                  {/* Status accent bar */}
                  <div style={{ height: '3px', background: `linear-gradient(90deg, ${statusColor}, ${statusColor}66)` }} />

                  <div className="tw-p-5">
                    {/* Card Header */}
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                      <span className="tw-font-bold tw-text-[0.82rem] tw-font-mono tw-text-green-600 dark:tw-text-green-400 tw-tracking-wide">
                        #{swap.swap_number || swapId.substring(0, 8).toUpperCase()}
                      </span>
                      <span
                        className="tw-px-[0.7rem] tw-py-1 tw-rounded-full tw-text-[0.65rem] tw-font-extrabold tw-tracking-wide tw-uppercase"
                        style={{ background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}35` }}
                      >
                        {swap.status?.replace(/_/g, ' ')}
                      </span>
                    </div>

                    {/* User Row */}
                    <div className="tw-flex tw-items-center tw-gap-3 tw-mb-5 tw-p-3 tw-bg-green-500/[0.04] dark:tw-bg-white/[0.04] tw-rounded-xl tw-border tw-border-green-500/[0.08] dark:tw-border-white/[0.06]">
                      <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-gradient-to-br tw-from-green-500 tw-to-green-600 tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-extrabold tw-text-white tw-flex-shrink-0 tw-shadow-[0_4px_12px_rgba(34,197,94,0.35)]">
                        {initials}
                      </div>
                      <div className="tw-flex-1 tw-min-w-0">
                        <div className="tw-font-bold tw-text-gray-800 dark:tw-text-gray-100 tw-text-sm tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
                          {swap.user_name || 'Unknown User'}
                        </div>
                        {swap.user_email && (
                          <div className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">{swap.user_email}</div>
                        )}
                      </div>
                      <span className="tw-text-[0.72rem] tw-text-gray-400 dark:tw-text-gray-500 tw-flex-shrink-0">
                        {formatDate(swap.created_at)}
                      </span>
                    </div>

                    {/* Device Bridge — stacks on narrow screens, side-by-side from sm: up.
                        tw-min-w-0 on both device boxes is load-bearing: without it, a grid
                        item's default min-width is its content's intrinsic size, so a tall/
                        wide device photo can force its whole column wider than the other
                        side's 1fr track instead of shrinking to fit — that's what was pushing
                        one card out of alignment with the other. */}
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-[1fr_auto_1fr] tw-gap-2 tw-items-stretch sm:tw-items-center tw-mb-5">
                      {/* Device Out */}
                      <div className="tw-min-w-0 tw-overflow-hidden tw-bg-green-500/5 dark:tw-bg-green-500/[0.06] tw-rounded-xl tw-p-3 tw-border tw-border-green-500/[0.12]">
                        <div className="tw-text-[0.65rem] tw-font-bold tw-uppercase tw-tracking-wide tw-text-gray-400 dark:tw-text-gray-500 tw-mb-[0.35rem]">Gives</div>
                        {swap.device_out_images?.[0] ? (
                          <div className="tw-relative tw-mb-2">
                            <img
                              src={swap.device_out_images[0]}
                              alt={swap.device_out_name || 'Device'}
                              className="tw-w-full tw-h-20 tw-object-cover tw-rounded-lg tw-cursor-pointer hover:tw-opacity-80 tw-transition-opacity"
                              onClick={(e) => { e.stopPropagation(); openSwapImageViewer(swap.device_out_images, 0); }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            {swap.device_out_images.length > 1 && (
                              <span className="tw-absolute tw-bottom-1 tw-right-1 tw-bg-black/70 tw-text-white tw-text-[0.6rem] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded">
                                +{swap.device_out_images.length - 1}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="tw-w-full tw-h-20 tw-rounded-lg tw-mb-2 tw-flex tw-items-center tw-justify-center tw-bg-black/[0.04] dark:tw-bg-white/[0.04]">
                            <FontAwesomeIcon icon={faImage} className="tw-text-gray-400 dark:tw-text-gray-600 tw-text-lg" />
                          </div>
                        )}
                        <div className="tw-font-bold tw-text-gray-800 dark:tw-text-gray-100 tw-text-[0.82rem] tw-leading-snug tw-mb-[0.3rem] tw-truncate" title={swap.device_out_name || swap.device_out?.name}>
                          {swap.device_out_name || swap.device_out?.name || 'N/A'}
                        </div>
                        {swap.device_out_condition && (
                          <span
                            className="tw-text-[0.65rem] tw-font-bold tw-px-[0.45rem] tw-py-[0.1rem] tw-rounded tw-inline-block tw-max-w-full tw-truncate tw-align-bottom"
                            style={{ color: conditionOutColor, background: `${conditionOutColor}18`, border: `1px solid ${conditionOutColor}30` }}
                          >
                            {getConditionLabel(swap.device_out_condition)}
                          </span>
                        )}
                        {(swap.device_out_battery != null || swap.device_out_face_id || swap.device_out_repair_history) && (
                          <div className="tw-mt-1.5 tw-space-y-0.5">
                            {swap.device_out_battery != null && (
                              <div className="tw-text-[0.65rem] tw-text-gray-500 dark:tw-text-gray-400 tw-truncate">Battery: {swap.device_out_battery}%</div>
                            )}
                            {swap.device_out_face_id && (
                              <div className="tw-text-[0.65rem] tw-text-gray-500 dark:tw-text-gray-400 tw-truncate">Face ID: {swap.device_out_face_id}</div>
                            )}
                            {swap.device_out_repair_history && (
                              <div className="tw-text-[0.65rem] tw-text-gray-500 dark:tw-text-gray-400 tw-truncate" title={swap.device_out_repair_history}>Repairs: {swap.device_out_repair_history}</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Arrow — rotates to point down when the two sides are stacked */}
                      <div className="tw-flex sm:tw-flex-col tw-flex-row tw-items-center tw-justify-center tw-gap-[0.2rem] tw-py-1 sm:tw-py-0">
                        <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gradient-to-br tw-from-green-500 tw-to-green-600 tw-flex tw-items-center tw-justify-center tw-shadow-[0_4px_12px_rgba(34,197,94,0.35)] tw-flex-shrink-0">
                          <FontAwesomeIcon icon={faExchangeAlt} className="tw-text-white tw-text-xs tw-rotate-90 sm:tw-rotate-0" />
                        </div>
                        {swap.value_difference !== undefined && (
                          <span className="tw-text-[0.6rem] tw-font-bold tw-text-green-500 tw-whitespace-nowrap tw-ml-1.5 sm:tw-ml-0">
                            +{formatCurrency(swap.value_difference)}
                          </span>
                        )}
                      </div>

                      {/* Device In */}
                      <div className="tw-min-w-0 tw-overflow-hidden tw-bg-green-500/5 dark:tw-bg-green-500/[0.06] tw-rounded-xl tw-p-3 tw-border tw-border-green-500/[0.12] sm:tw-text-right">
                        <div className="tw-text-[0.65rem] tw-font-bold tw-uppercase tw-tracking-wide tw-text-gray-400 dark:tw-text-gray-500 tw-mb-[0.35rem]">Gets</div>
                        {swap.device_in_image ? (
                          <img
                            src={swap.device_in_image}
                            alt={swap.device_in_name || 'Product'}
                            className="tw-w-full tw-h-20 tw-object-cover tw-rounded-lg tw-mb-2 tw-cursor-pointer hover:tw-opacity-80 tw-transition-opacity"
                            onClick={(e) => { e.stopPropagation(); openSwapImageViewer(swap.device_in_image, 0); }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="tw-w-full tw-h-20 tw-rounded-lg tw-mb-2 tw-flex tw-items-center tw-justify-center tw-bg-black/[0.04] dark:tw-bg-white/[0.04]">
                            <FontAwesomeIcon icon={faImage} className="tw-text-gray-400 dark:tw-text-gray-600 tw-text-lg" />
                          </div>
                        )}
                        <div className="tw-font-bold tw-text-gray-800 dark:tw-text-gray-100 tw-text-[0.82rem] tw-leading-snug tw-mb-[0.3rem] tw-truncate" title={swap.device_in_name || swap.device_in?.name}>
                          {swap.device_in_name || swap.device_in?.name || 'N/A'}
                        </div>
                        {swap.device_in_price && (
                          <div className="tw-text-[0.7rem] tw-font-semibold tw-text-green-500 tw-mb-[0.3rem] tw-truncate">
                            {formatCurrency(swap.device_in_price)}
                          </div>
                        )}
                        {swap.device_in_condition && (
                          <span
                            className="tw-text-[0.65rem] tw-font-bold tw-px-[0.45rem] tw-py-[0.1rem] tw-rounded tw-inline-block tw-max-w-full tw-truncate tw-align-bottom"
                            style={{ color: conditionInColor, background: `${conditionInColor}18`, border: `1px solid ${conditionInColor}30` }}
                          >
                            {getConditionLabel(swap.device_in_condition)}
                          </span>
                        )}
                        {swap.seller_topup_amount != null && (
                          <div className="tw-text-[0.65rem] tw-text-gray-500 dark:tw-text-gray-400 tw-mt-1.5 tw-truncate">
                            Seller top-up: {formatCurrency(swap.seller_topup_amount)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Bar */}
                    <div className="tw-flex tw-gap-2 tw-items-center">
                      {swap.status === 'pending_approval' && (
                        <>
                          <button
                            title="Approve"
                            onClick={() => handleApproveSwap(swapId)}
                            className="tw-flex-1 tw-px-3 tw-py-[0.55rem] tw-rounded-[10px] tw-border-none tw-bg-gradient-to-br tw-from-green-500 tw-to-green-600 tw-text-white tw-font-bold tw-text-[0.78rem] tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-gap-[0.35rem] tw-shadow-[0_4px_12px_rgba(34,197,94,0.35)] tw-transition-transform active:tw-scale-[0.98]"
                          >
                            <FontAwesomeIcon icon={faCheck} /> Approve
                          </button>
                          <button
                            title="Reject"
                            onClick={() => { setRejectSwapTargetId(swapId); setRejectSwapReason(''); setShowRejectSwapModal(true); }}
                            className="tw-flex-1 tw-px-3 tw-py-[0.55rem] tw-rounded-[10px] tw-border tw-border-red-500/30 tw-bg-red-500/[0.08] tw-text-red-500 tw-font-bold tw-text-[0.78rem] tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-gap-[0.35rem] tw-transition-transform active:tw-scale-[0.98]"
                          >
                            <FontAwesomeIcon icon={faTimes} /> Reject
                          </button>
                        </>
                      )}
                      {swap.status === 'pending_inspection' && (
                        <button
                          title="Inspect Device"
                          onClick={() => {
                            setSelectedSwapForInspection(swap);
                            setInspectionStep(1);
                            setInspectionData({ battery_percent: '', face_id_ok: false, storage_confirmed: false, screen_ok: false, battery_health_ok: false, connectivity_ok: false, buttons_ok: false, technician_notes: '', final_decision: 'pass' });
                            setShowInspectionModal(true);
                          }}
                          className="tw-flex-1 tw-px-3 tw-py-[0.55rem] tw-rounded-[10px] tw-border tw-border-green-500/35 tw-bg-green-500/[0.08] tw-text-green-500 tw-font-bold tw-text-[0.78rem] tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-gap-[0.35rem] tw-transition-transform active:tw-scale-[0.98]"
                        >
                          <FontAwesomeIcon icon={faSearch} /> Inspect Device
                        </button>
                      )}
                      <button
                        title="View IMEI (admin only)"
                        onClick={() => setIMEIViewerTarget({ type: 'swap', id: swapId })}
                        className="tw-px-3 tw-py-[0.55rem] tw-rounded-[10px] tw-border tw-border-black/10 dark:tw-border-white/10 tw-bg-transparent tw-text-gray-500 dark:tw-text-gray-400 tw-font-semibold tw-text-[0.78rem] tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-gap-[0.35rem] tw-transition-transform active:tw-scale-[0.98]"
                      >
                        <FontAwesomeIcon icon={faMobileAlt} />
                      </button>
                      <button
                        title="View Details"
                        onClick={() => { setSelectedSwapForDetails(swap); setSwapLogisticsEvents([]); setShowSwapDetailsModal(true); if (['active', 'in_transit'].includes(swap.status) && swap.tracking_id) { handleFetchSwapLogistics(swap.tracking_id); } }}
                        className="tw-px-3 tw-py-[0.55rem] tw-rounded-[10px] tw-border tw-border-black/10 dark:tw-border-white/10 tw-bg-transparent tw-text-gray-500 dark:tw-text-gray-400 tw-font-semibold tw-text-[0.78rem] tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-gap-[0.35rem] tw-transition-transform active:tw-scale-[0.98]"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <AdminPagination page={swapPage} totalPages={swapTotalPages} onPageChange={(p) => fetchSwapData(p)} />
      </div>
    );
  };

  const renderDisputesContent = () => {
    const dark = isDarkMode;
    const card = { background: dark ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)', border: `1px solid ${dark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)'}`, borderRadius: '20px', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: dark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(34,197,94,0.08)' };
    const th = { padding: '1rem 1.25rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: dark ? '#4ADE80' : '#16A34A', background: dark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.05)', borderBottom: `1px solid ${dark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)'}` };
    const td = { padding: '0.9rem 1.25rem', fontSize: '0.85rem', color: dark ? '#d1d5db' : '#374151', borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` };
    const statusColor = { open: '#ef4444', investigating: '#f59e0b', resolved: '#22C55E', closed: '#6b7280', escalated: '#a855f7' };
    const filteredDisputes = disputeFilter === 'all' ? disputes : disputes.filter(d => d.status === disputeFilter);
    const counts = ['all','open','investigating','resolved','closed'].reduce((acc, k) => { acc[k] = k === 'all' ? disputes.length : disputes.filter(d => d.status === k).length; return acc; }, {});

    return (
      <div style={{ padding: '0 0 2rem 0', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '5rem', color: '#22C55E' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2.5rem' }} />
            <p style={{ marginTop: '1rem', color: dark ? '#9ca3af' : '#6b7280' }}>Loading disputes…</p>
          </div>
        ) : (
          <>
            <div style={{ ...card, padding: '1.75rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, background: 'linear-gradient(135deg, #ef4444, #f97316)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ WebkitTextFillColor: '#ef4444', marginRight: '0.6rem' }} />Resolution Centre
                </h2>
                <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: dark ? '#6b7280' : '#9ca3af' }}>
                  {disputes.length} total · {counts.open || 0} open · {counts.resolved || 0} resolved
                </p>
              </div>
              <button onClick={fetchDisputes} style={{ padding: '0.6rem 1.2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <FontAwesomeIcon icon={faSync} /> Refresh
              </button>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {[{ k: 'all', label: 'All' }, { k: 'open', label: 'Open' }, { k: 'investigating', label: 'Investigating' }, { k: 'resolved', label: 'Resolved' }, { k: 'closed', label: 'Closed' }].map(f => (
                <button key={f.k} onClick={() => setDisputeFilter(f.k)} style={{ padding: '0.5rem 1.1rem', borderRadius: '10px', border: `1px solid ${disputeFilter === f.k ? '#22C55E' : (dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)')}`, background: disputeFilter === f.k ? 'linear-gradient(135deg, #22C55E, #16A34A)' : 'transparent', color: disputeFilter === f.k ? '#fff' : (dark ? '#9ca3af' : '#6b7280'), fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  {f.label}
                  <span style={{ background: disputeFilter === f.k ? 'rgba(255,255,255,0.2)' : (dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'), padding: '0.05rem 0.45rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700 }}>{counts[f.k] || 0}</span>
                </button>
              ))}
            </div>

            <AdminTableCard>
              <AdminTable minWidth="750px" columns={[{ width: '16%' }, { width: '13%' }, { width: '25%' }, { width: '13%' }, { width: '12%' }, { width: '13%' }, { width: '13%' }]}>
                <AdminTableHead>
                  <AdminTh>Dispute #</AdminTh>
                  <AdminTh>Order</AdminTh>
                  <AdminTh>Reason</AdminTh>
                  <AdminTh>Amount</AdminTh>
                  <AdminTh>Status</AdminTh>
                  <AdminTh>Date</AdminTh>
                  <AdminTh align="center">Actions</AdminTh>
                </AdminTableHead>
                <tbody>
                  {filteredDisputes.length === 0 ? (
                    <AdminTableEmpty colSpan={7} message={disputes.length === 0 ? 'No disputes on the platform yet' : 'No disputes match this filter'} />
                  ) : filteredDisputes.map((dispute, i) => {
                    const did = dispute._id || dispute.id;
                    const sc = statusColor[dispute.status] || '#6b7280';
                    const ageHrs = dispute.created_at ? (Date.now() - new Date(dispute.created_at)) / 3600000 : 0;
                    return (
                      <AdminTr key={did || i}>
                        <AdminTd>
                          <span className="tw-font-mono tw-text-[0.78rem] tw-font-bold">#{(dispute.dispute_number || did || '').toString().slice(-8).toUpperCase()}</span>
                          {ageHrs > 48 && <span className="tw-ml-2 tw-bg-red-500/15 tw-text-red-500 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[0.65rem] tw-font-bold">URGENT</span>}
                        </AdminTd>
                        <AdminTd className="tw-font-mono tw-text-[0.78rem] tw-opacity-70">{dispute.order_id ? dispute.order_id.substring(0, 10) + '…' : '—'}</AdminTd>
                        <AdminTd className="tw-text-[0.8rem]">{(dispute.reason || 'No reason provided').substring(0, 50)}{(dispute.reason?.length || 0) > 50 ? '…' : ''}</AdminTd>
                        <AdminTd className="tw-font-bold tw-text-green-500">{dispute.order_amount ? formatCurrency(dispute.order_amount) : '—'}</AdminTd>
                        <AdminTd>
                          <span className="tw-px-2.5 tw-py-1 tw-rounded-lg tw-text-[0.72rem] tw-font-bold tw-uppercase" style={{ background: `${sc}18`, color: sc }}>{dispute.status || '—'}</span>
                        </AdminTd>
                        <AdminTd className="tw-opacity-80">{dispute.created_at ? new Date(dispute.created_at).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</AdminTd>
                        <AdminTd align="center">
                          <button onClick={() => handleReviewDispute(dispute)} className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-green-500/25 tw-bg-green-500/[0.08] tw-text-green-600 tw-font-semibold tw-text-xs tw-cursor-pointer">
                            <FontAwesomeIcon icon={faEye} className="tw-mr-1" />Review
                          </button>
                        </AdminTd>
                      </AdminTr>
                    );
                  })}
                </tbody>
              </AdminTable>
            </AdminTableCard>
            <AdminPagination page={disputePage} totalPages={disputeTotalPages} onPageChange={(p) => fetchDisputes(p)} />
          </>
        )}
      </div>
    );
  };

  const renderReviewsContent = () => {
    const dark = isDarkMode;
    const card = { background: dark ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)', border: `1px solid ${dark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)'}`, borderRadius: '20px', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: dark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(34,197,94,0.08)' };
    const th = { padding: '1rem 1.25rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: dark ? '#4ADE80' : '#16A34A', background: dark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.05)', borderBottom: `1px solid ${dark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)'}` };
    const td = { padding: '0.9rem 1.25rem', fontSize: '0.85rem', color: dark ? '#d1d5db' : '#374151', borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` };
    const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1) : '—';
    const filteredReviews = reviews.filter(r => {
      const q = reviewSearch.toLowerCase();
      const ok = !q || (r.reviewer_name || r.reviewer_email || '').toLowerCase().includes(q) || (r.comment || '').toLowerCase().includes(q);
      if (!ok) return false;
      if (reviewFilter === '5-star') return r.rating === 5;
      if (reviewFilter === 'critical') return r.rating <= 2;
      return true;
    });
    const starColor = (n) => n >= 4 ? '#22C55E' : n >= 3 ? '#f59e0b' : '#ef4444';

    return (
      <div style={{ padding: '0 0 2rem 0', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '5rem', color: '#22C55E' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2.5rem' }} />
            <p style={{ marginTop: '1rem', color: dark ? '#9ca3af' : '#6b7280' }}>Loading reviews…</p>
          </div>
        ) : (
          <>
            <div style={{ ...card, padding: '1.75rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, background: 'linear-gradient(135deg, #22C55E, #16A34A)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  <FontAwesomeIcon icon={faStar} style={{ WebkitTextFillColor: '#f59e0b', marginRight: '0.6rem' }} />Review Moderation
                </h2>
                <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: dark ? '#6b7280' : '#9ca3af' }}>
                  {reviews.length} reviews · avg rating {avgRating}
                </p>
              </div>
              <button onClick={fetchReviews} style={{ padding: '0.6rem 1.2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <FontAwesomeIcon icon={faSync} /> Refresh
              </button>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: '220px', display: 'flex', alignItems: 'center', gap: '0.5rem', background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', borderRadius: '12px', padding: '0.55rem 0.9rem', border: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}` }}>
                <FontAwesomeIcon icon={faSearch} style={{ color: dark ? '#6b7280' : '#9ca3af', fontSize: '0.8rem' }} />
                <input value={reviewSearch} onChange={e => setReviewSearch(e.target.value)} placeholder="Search by reviewer or comment…" style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '0.85rem', color: dark ? '#d1d5db' : '#374151', width: '100%' }} />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[{ k: 'all', label: 'All' }, { k: '5-star', label: '5 Stars' }, { k: 'critical', label: 'Critical' }].map(f => (
                  <button key={f.k} onClick={() => setReviewFilter(f.k)} style={{ padding: '0.5rem 1rem', borderRadius: '10px', border: `1px solid ${reviewFilter === f.k ? '#22C55E' : (dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)')}`, background: reviewFilter === f.k ? 'linear-gradient(135deg, #22C55E, #16A34A)' : 'transparent', color: reviewFilter === f.k ? '#fff' : (dark ? '#9ca3af' : '#6b7280'), fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <AdminTableCard>
              <AdminTable minWidth="750px" columns={[{ width: '18%' }, { width: '16%' }, { width: '12%' }, { width: '30%' }, { width: '12%' }, { width: '12%' }]}>
                <AdminTableHead>
                  <AdminTh>Reviewer</AdminTh>
                  <AdminTh>Product</AdminTh>
                  <AdminTh>Rating</AdminTh>
                  <AdminTh>Comment</AdminTh>
                  <AdminTh>Date</AdminTh>
                  <AdminTh align="center">Actions</AdminTh>
                </AdminTableHead>
                <tbody>
                  {filteredReviews.length === 0 ? (
                    <AdminTableEmpty colSpan={6} message={reviews.length === 0 ? 'No reviews on the platform yet' : 'No reviews match this filter'} />
                  ) : filteredReviews.map((review, i) => {
                    const rid = review._id || review.id;
                    const rating = review.rating || 0;
                    return (
                      <AdminTr key={rid || i}>
                        <AdminTd>
                          <div className="tw-font-semibold tw-text-[0.85rem]">{review.reviewer_name || 'User'}</div>
                          <div className="tw-text-[0.72rem] tw-opacity-60">{review.reviewer_email || ''}</div>
                        </AdminTd>
                        <AdminTd className="tw-text-[0.82rem] tw-opacity-75">{review.product_title || review.product_name || '—'}</AdminTd>
                        <AdminTd>
                          <div className="tw-flex tw-items-center tw-gap-1">
                            <span className="tw-font-extrabold tw-text-base" style={{ color: starColor(rating) }}>{rating}</span>
                            <div className="tw-flex tw-gap-px">{[...Array(5)].map((_, j) => <FontAwesomeIcon key={j} icon={faStar} className="tw-text-[0.65rem]" style={{ color: j < rating ? starColor(rating) : (dark ? '#374151' : '#e5e7eb') }} />)}</div>
                          </div>
                        </AdminTd>
                        <AdminTd className="tw-text-[0.8rem] tw-opacity-75">{(review.comment || review.review || '—').substring(0, 60)}{(review.comment?.length || 0) > 60 ? '…' : ''}</AdminTd>
                        <AdminTd className="tw-opacity-80">{review.created_at ? new Date(review.created_at).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</AdminTd>
                        <AdminTd align="center">
                          <button onClick={() => handleOpenReviewResponse(review, 'delete')} className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-red-500/30 tw-bg-red-500/[0.08] tw-text-red-500 tw-font-semibold tw-text-xs tw-cursor-pointer">
                            <FontAwesomeIcon icon={faTrash} className="tw-mr-1" />Delete
                          </button>
                        </AdminTd>
                      </AdminTr>
                    );
                  })}
                </tbody>
              </AdminTable>
            </AdminTableCard>
          </>
        )}
      </div>
    );
  };


  const renderPremiumUsersContent = () => {
    const daysUntilExpiry = (endDate) => {
      if (!endDate) return -1;
      const end = new Date(endDate);
      if (isNaN(end.getTime())) return -1;
      const now = new Date();
      return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    };

    const isExpiringSoon = (endDate) => {
      const days = daysUntilExpiry(endDate);
      return days <= 7 && days > 0;
    };

    const getSubscriptionPercentage = (startDate, endDate) => {
      if (!startDate || !endDate) return 0;
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
      const now = new Date();
      const total = end - start;
      const elapsed = now - start;
      return Math.max(0, Math.min(100, (elapsed / total) * 100));
    };

    const cardBg = isDarkMode ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)';
    const cardBorder = isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)';
    const textPrimary = isDarkMode ? '#F3F4F6' : '#1F2937';
    const textSecondary = isDarkMode ? '#9CA3AF' : '#6B7280';
    const inputBg = isDarkMode ? 'rgba(0,0,0,0.3)' : '#f9fafb';
    const inputBorder = isDarkMode ? 'rgba(34,197,94,0.25)' : 'rgba(34,197,94,0.2)';

    const tabConfig = [
      { id: 'active', label: 'Active Members', icon: faUsers },
      { id: 'analytics', label: 'Analytics', icon: faChartLine },
      { id: 'settings', label: 'Settings', icon: faCog },
    ];

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#22C55E' }} />
            <p style={{ marginTop: '1rem', color: textSecondary }}>Loading premium data...</p>
          </div>
        ) : (
          <>
            {/* Header Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              borderRadius: '20px',
              padding: '1.75rem 2rem',
              marginBottom: '1.5rem',
              boxShadow: '0 8px 32px rgba(34,197,94,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div>
                <h2 style={{ color: '#fff', fontSize: '1.75rem', fontWeight: '800', margin: 0, letterSpacing: '-0.5px' }}>
                  <FontAwesomeIcon icon={faCrown} style={{ marginRight: '0.75rem', opacity: 0.9 }} />
                  VIP & Subscription Manager
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', margin: '0.4rem 0 0' }}>
                  Manage premium tiers, memberships, and subscription analytics
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  onClick={handleOpenGrantPremium}
                  style={{
                    background: '#fff',
                    border: '1px solid rgba(255,255,255,0.35)',
                    color: '#16A34A',
                    padding: '0.6rem 1.25rem',
                    borderRadius: '50px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
                    transition: 'all 0.2s'
                  }}
                >
                  <FontAwesomeIcon icon={faCrown} /> Grant Premium
                </button>
                <button
                  onClick={() => exportRowsToCSV(premiumUsers, 'premium-subscriptions')}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.35)',
                    color: '#fff',
                    padding: '0.6rem 1.25rem',
                    borderRadius: '50px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s'
                  }}
                >
                  <FontAwesomeIcon icon={faDownload} /> Export
                </button>
              </div>
            </div>

            {/* Pill Sub-tabs */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.75rem', flexWrap: 'wrap' }}>
              {tabConfig.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setPremiumSubTab(tab.id)}
                  style={{
                    padding: '0.6rem 1.5rem',
                    borderRadius: '50px',
                    border: premiumSubTab === tab.id ? 'none' : `1px solid ${isDarkMode ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.25)'}`,
                    background: premiumSubTab === tab.id ? 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' : 'transparent',
                    color: premiumSubTab === tab.id ? '#fff' : isDarkMode ? '#9CA3AF' : '#6B7280',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s',
                    boxShadow: premiumSubTab === tab.id ? '0 4px 15px rgba(34,197,94,0.35)' : 'none'
                  }}
                >
                  <FontAwesomeIcon icon={tab.icon} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ACTIVE MEMBERS TAB */}
            {premiumSubTab === 'active' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.25rem' }}>
                {premiumUsers.length === 0 ? (
                  <div style={{
                    gridColumn: '1/-1', textAlign: 'center', padding: '4rem 2rem',
                    background: cardBg, borderRadius: '20px', border: `1px solid ${cardBorder}`,
                    backdropFilter: 'blur(20px)'
                  }}>
                    <div style={{
                      width: '80px', height: '80px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 1.5rem', boxShadow: '0 8px 32px rgba(34,197,94,0.35)'
                    }}>
                      <FontAwesomeIcon icon={faCrown} style={{ color: '#fff', fontSize: '2rem' }} />
                    </div>
                    <h3 style={{ color: textPrimary, fontWeight: '700', marginBottom: '0.5rem' }}>No Premium Members Yet</h3>
                    <p style={{ color: textSecondary, maxWidth: '360px', margin: '0 auto' }}>
                      Once users subscribe to VIP, Pro, or Basic plans, their memberships will appear here for management.
                    </p>
                  </div>
                ) : (
                  premiumUsers.map(premium => {
                    const daysLeft = daysUntilExpiry(premium.end_date);
                    const isSoon = isExpiringSoon(premium.end_date);
                    const percentage = getSubscriptionPercentage(premium.start_date, premium.end_date);
                    const isSeller = premium.user_type === 'seller';
                    const tierColor = isSeller ? '#f59e0b' : '#22C55E';
                    const tierBg = isSeller ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.1)';
                    const initials = (premium.user_name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

                    return (
                      <div
                        key={premium.user_id || premium._id || premium.id}
                        style={{
                          background: cardBg, borderRadius: '20px',
                          border: `1px solid ${isSoon ? 'rgba(245,158,11,0.4)' : cardBorder}`,
                          backdropFilter: 'blur(20px)',
                          boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`,
                          overflow: 'hidden', transition: 'transform 0.2s, box-shadow 0.2s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 60px rgba(34,197,94,0.2)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`; }}
                      >
                        <div style={{ height: '4px', background: `linear-gradient(90deg, ${tierColor}, ${tierColor}88)` }} />
                        <div style={{ padding: '1.25rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                            <div style={{
                              width: '48px', height: '48px', borderRadius: '50%',
                              background: `linear-gradient(135deg, ${tierColor}, ${tierColor}aa)`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '1rem', fontWeight: '800', color: '#fff', flexShrink: 0,
                              boxShadow: `0 4px 15px ${tierColor}44`
                            }}>
                              {initials}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: '700', color: textPrimary, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {premium.user_name || 'User'}
                              </div>
                              <div style={{ fontSize: '0.78rem', color: textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {premium.user_email || 'N/A'}
                              </div>
                            </div>
                            <span style={{
                              padding: '0.3rem 0.75rem', borderRadius: '50px', fontSize: '0.7rem',
                              fontWeight: '800', letterSpacing: '0.05em', textTransform: 'uppercase',
                              background: tierBg, color: tierColor, border: `1px solid ${tierColor}33`, flexShrink: 0
                            }}>
                              {premium.plan_name || 'Premium'}
                            </span>
                          </div>

                          <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '0.75rem', marginBottom: '1rem',
                            background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(34,197,94,0.05)',
                            borderRadius: '12px', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(34,197,94,0.1)'}`
                          }}>
                            <span style={{ fontSize: '0.8rem', color: textSecondary }}>Monthly Amount</span>
                            <strong style={{ color: tierColor, fontSize: '1.05rem', fontWeight: '800' }}>
                              {formatCurrency(premium.amount || 0)}
                            </strong>
                          </div>

                          <div style={{ marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: textSecondary, marginBottom: '0.4rem' }}>
                              <span>Subscription Health</span>
                              <span style={{ fontWeight: '700', color: textPrimary }}>{percentage.toFixed(0)}%</span>
                            </div>
                            <div style={{ height: '6px', background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)', borderRadius: '99px', overflow: 'hidden' }}>
                              <div style={{ width: `${percentage}%`, height: '100%', background: `linear-gradient(90deg, ${tierColor}, ${tierColor}cc)`, borderRadius: '99px', transition: 'width 0.6s ease' }} />
                            </div>
                            <div style={{ fontSize: '0.75rem', marginTop: '0.4rem', color: isSoon ? '#f59e0b' : textSecondary, fontWeight: isSoon ? '600' : '400' }}>
                              {premium.end_date ? (
                                isSoon ? (
                                  <>
                                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.25rem' }} />
                                    Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                                  </>
                                ) : (
                                  `Expires ${format(new Date(premium.end_date), 'MMM dd, yyyy')}`
                                )
                              ) : (
                                'No expiry date'
                              )}
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => handleOpenExtendPremium(premium)}
                              style={{
                                flex: 1, padding: '0.55rem', borderRadius: '10px', border: 'none',
                                background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                                color: '#fff', fontWeight: '600', fontSize: '0.78rem', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem'
                              }}
                            >
                              <FontAwesomeIcon icon={faCalendarPlus} /> Extend
                            </button>
                            <button
                              onClick={() => handleOpenGiftPremium(premium)}
                              style={{
                                flex: 1, padding: '0.55rem', borderRadius: '10px',
                                border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.4)' : 'rgba(34,197,94,0.35)'}`,
                                background: 'transparent', color: '#22C55E', fontWeight: '600',
                                fontSize: '0.78rem', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem'
                              }}
                            >
                              <FontAwesomeIcon icon={faGift} /> Gift
                            </button>
                            <button
                              onClick={() => handleOpenRevokePremium(premium)}
                              style={{
                                padding: '0.55rem 0.75rem', borderRadius: '10px',
                                border: '1px solid rgba(239,68,68,0.3)',
                                background: 'rgba(239,68,68,0.08)', color: '#ef4444',
                                fontWeight: '600', fontSize: '0.78rem', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem'
                              }}
                            >
                              <FontAwesomeIcon icon={faTimesCircle} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* ANALYTICS TAB */}
            {premiumSubTab === 'analytics' && (
              <div>
                {premiumAnalytics ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '1.25rem' }}>
                    {[
                      { icon: faCoins, label: 'Total Revenue', value: formatCurrency(premiumAnalytics.total_revenue || 0), sub: 'From all subscriptions', color: '#22C55E' },
                      { icon: faUsers, label: 'Active Subscriptions', value: premiumAnalytics.active_count || 0, sub: 'Current members', color: '#16A34A' },
                      { icon: faClock, label: 'Avg Subscription Length', value: `${(Number(premiumAnalytics.avg_duration_days) || 0).toFixed(2)}d`, sub: 'Days per subscription', color: '#4ADE80' },
                      { icon: faExclamationTriangle, label: 'Expiring Soon', value: premiumUsers.filter(p => isExpiringSoon(p.end_date)).length, sub: 'Within 7 days', color: '#f59e0b' },
                    ].map((card, i) => (
                      <div
                        key={i}
                        style={{
                          background: cardBg, borderRadius: '20px', padding: '1.5rem',
                          border: `1px solid ${cardBorder}`, backdropFilter: 'blur(20px)',
                          boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`,
                          transition: 'transform 0.2s, box-shadow 0.2s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 60px rgba(34,197,94,0.2)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`; }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: `${card.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FontAwesomeIcon icon={card.icon} style={{ color: card.color, fontSize: '1rem' }} />
                          </div>
                          <span style={{ fontSize: '0.8rem', color: textSecondary, fontWeight: '600' }}>{card.label}</span>
                        </div>
                        <div style={{ fontSize: '2rem', fontWeight: '800', color: card.color, letterSpacing: '-1px', lineHeight: 1 }}>{card.value}</div>
                        <div style={{ fontSize: '0.78rem', color: textSecondary, marginTop: '0.5rem' }}>{card.sub}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '4rem', color: textSecondary }}>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#22C55E', fontSize: '2rem' }} />
                    <p style={{ marginTop: '1rem' }}>Loading analytics...</p>
                  </div>
                )}
              </div>
            )}

            {/* SETTINGS TAB */}
            {premiumSubTab === 'settings' && editingPremiumSettings && (
              <div style={{
                background: cardBg, borderRadius: '20px', border: `1px solid ${cardBorder}`,
                backdropFilter: 'blur(20px)', overflow: 'hidden',
                boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`
              }}>
                <div style={{
                  padding: '1.25rem 1.75rem',
                  borderBottom: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.1)'}`,
                  background: isDarkMode ? 'rgba(34,197,94,0.06)' : 'rgba(240,253,244,0.6)',
                  display: 'flex', alignItems: 'center', gap: '0.75rem'
                }}>
                  <FontAwesomeIcon icon={faCog} style={{ color: '#22C55E' }} />
                  <h3 style={{ margin: 0, fontWeight: '700', color: textPrimary }}>Subscription Tier Configuration</h3>
                </div>
                <div style={{ padding: '1.75rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.75rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>VAT Percentage</label>
                      <input
                        type="number" step="0.1"
                        value={editingPremiumSettings.vat_percentage ?? 0}
                        onChange={(e) => setEditingPremiumSettings(prev => ({ ...prev, vat_percentage: parseFloat(e.target.value) || 0 }))}
                        style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: `1px solid ${inputBorder}`, background: inputBg, color: textPrimary, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Currency</label>
                      <input
                        type="text"
                        value={editingPremiumSettings.currency || 'NGN'}
                        onChange={(e) => setEditingPremiumSettings(prev => ({ ...prev, currency: e.target.value.toUpperCase() }))}
                        style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: `1px solid ${inputBorder}`, background: inputBg, color: textPrimary, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {(editingPremiumSettings.plans || []).map((plan, idx) => (
                    <div key={plan.id || idx} style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                          <span style={{
                            padding: '0.25rem 0.7rem', borderRadius: '50px', fontSize: '0.7rem', fontWeight: '800',
                            textTransform: 'uppercase', letterSpacing: '0.05em',
                            background: plan.user_type === 'seller' ? 'rgba(245,158,11,0.15)' : 'rgba(34,197,94,0.15)',
                            color: plan.user_type === 'seller' ? '#f59e0b' : '#22C55E',
                          }}>
                            {plan.user_type || 'buyer'}
                          </span>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: textSecondary, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={plan.is_active !== false}
                              onChange={(e) => setEditingPremiumSettings(prev => {
                                const plans = [...prev.plans];
                                plans[idx] = { ...plans[idx], is_active: e.target.checked };
                                return { ...prev, plans };
                              })}
                            />
                            Active
                          </label>
                        </div>
                        <button
                          onClick={() => setEditingPremiumSettings(prev => ({ ...prev, plans: prev.plans.filter((_, i) => i !== idx) }))}
                          disabled={premiumActionLoading}
                          style={{ padding: '0.35rem 0.7rem', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: '#ef4444', fontWeight: '600', fontSize: '0.75rem', cursor: 'pointer' }}
                        >
                          <FontAwesomeIcon icon={faTimes} /> Remove Plan
                        </button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Plan Name</label>
                          <input
                            type="text"
                            value={plan.name || ''}
                            onChange={(e) => setEditingPremiumSettings(prev => {
                              const plans = [...prev.plans];
                              plans[idx] = { ...plans[idx], name: e.target.value };
                              return { ...prev, plans };
                            })}
                            style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: `1px solid ${inputBorder}`, background: inputBg, color: textPrimary, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Applies To</label>
                          <select
                            value={plan.user_type || 'buyer'}
                            onChange={(e) => setEditingPremiumSettings(prev => {
                              const plans = [...prev.plans];
                              plans[idx] = { ...plans[idx], user_type: e.target.value };
                              return { ...prev, plans };
                            })}
                            style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: `1px solid ${inputBorder}`, background: inputBg, color: textPrimary, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                          >
                            <option value="buyer">Buyers</option>
                            <option value="seller">Sellers</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Price</label>
                          <input
                            type="number" step="0.01"
                            value={plan.price || 0}
                            onChange={(e) => setEditingPremiumSettings(prev => {
                              const plans = [...prev.plans];
                              plans[idx] = { ...plans[idx], price: parseFloat(e.target.value) || 0 };
                              return { ...prev, plans };
                            })}
                            style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: `1px solid ${inputBorder}`, background: inputBg, color: textPrimary, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Duration (months)</label>
                          <input
                            type="number" min="1"
                            value={plan.duration_months || 1}
                            onChange={(e) => setEditingPremiumSettings(prev => {
                              const plans = [...prev.plans];
                              plans[idx] = { ...plans[idx], duration_months: parseInt(e.target.value, 10) || 1 };
                              return { ...prev, plans };
                            })}
                            style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: `1px solid ${inputBorder}`, background: inputBg, color: textPrimary, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Features (comma-separated)</label>
                        <textarea
                          value={(plan.features || []).join(', ')}
                          onChange={(e) => setEditingPremiumSettings(prev => {
                            const plans = [...prev.plans];
                            plans[idx] = { ...plans[idx], features: e.target.value.split(',').map(p => p.trim()).filter(Boolean) };
                            return { ...prev, plans };
                          })}
                          rows={2}
                          style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: `1px solid ${inputBorder}`, background: inputBg, color: textPrimary, fontSize: '0.9rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: '1.6' }}
                        />
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={() => setEditingPremiumSettings(prev => ({
                      ...prev,
                      plans: [...(prev.plans || []), {
                        id: `plan_${Date.now()}`, name: 'New Plan', price: 0, duration_months: 1,
                        currency: prev.currency || 'NGN', user_type: 'buyer', features: [], is_active: true,
                      }],
                    }))}
                    style={{ padding: '0.6rem 1.1rem', borderRadius: '10px', border: `1px dashed ${isDarkMode ? 'rgba(34,197,94,0.4)' : 'rgba(34,197,94,0.35)'}`, background: 'transparent', color: '#22C55E', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer', marginBottom: '1.5rem' }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add Plan
                  </button>

                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                    <button
                      onClick={() => setEditingPremiumSettings(JSON.parse(JSON.stringify(premiumSettings)))}
                      disabled={premiumActionLoading}
                      style={{ padding: '0.7rem 1.5rem', borderRadius: '12px', border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.25)'}`, background: 'transparent', color: '#22C55E', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSavePremiumSettings}
                      disabled={premiumActionLoading}
                      style={{ padding: '0.7rem 1.5rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#fff', fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 15px rgba(34,197,94,0.35)' }}
                    >
                      {premiumActionLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />}
                      Save Settings
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };


  const renderModerationContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#22C55E' }} />
          <p style={{ marginTop: '1rem' }}>Loading...</p>
        </div>
      ) : (
        <>
          <div style={{
            background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
            padding: '1.5rem',
            borderRadius: '16px',
            marginBottom: '1.5rem',
            boxShadow: '0 8px 32px rgba(34, 197, 94, 0.25)'
          }}>
            <h2 style={{ color: 'white', fontSize: '1.75rem', fontWeight: '700', marginBottom: '0.5rem' }}>
              <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: '0.75rem' }} />
              Reports & Support
            </h2>
            <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.95rem' }}>
              Manage reports, respond to support tickets, and maintain platform integrity
            </p>
          </div>

          {/* Sub-tab switcher */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <button
              className={`AutoboyExpress-dash-btn ${moderationSubTab === 'support' ? 'AutoboyExpress-dash-btn-primary' : 'AutoboyExpress-dash-btn-outline'}`}
              onClick={() => setModerationSubTab('support')}
            >
              <FontAwesomeIcon icon={faComment} /> Support Tickets ({supportTickets.length})
            </button>
            <button
              className={`AutoboyExpress-dash-btn ${moderationSubTab === 'prohibited' ? 'AutoboyExpress-dash-btn-primary' : 'AutoboyExpress-dash-btn-outline'}`}
              onClick={() => setModerationSubTab('prohibited')}
            >
              <FontAwesomeIcon icon={faShieldAlt} /> Prohibited Items
            </button>
            <button
              className={`AutoboyExpress-dash-btn ${moderationSubTab === 'reports' ? 'AutoboyExpress-dash-btn-primary' : 'AutoboyExpress-dash-btn-outline'}`}
              onClick={() => setModerationSubTab('reports')}
            >
              <FontAwesomeIcon icon={faFlag} /> Reports ({reports.length})
            </button>
          </div>

          {moderationSubTab === 'reports' ? (
            /* ===== REPORTS TABLE ===== */
            <div style={{
              background: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.95)',
              border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(22, 163, 74, 0.15)'}`,
              borderRadius: '16px',
              padding: '1.5rem'
            }}>
              {reports.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', opacity: 0.7 }}>
                  <FontAwesomeIcon icon={faFlag} size="3x" style={{ color: '#22C55E', marginBottom: '1rem' }} />
                  <p>No reports found</p>
                </div>
              ) : (
                <AdminTable minWidth="800px" columns={[{ width: '18%' }, { width: '13%' }, { width: '24%' }, { width: '13%' }, { width: '13%' }, { width: '19%' }]}>
                  <AdminTableHead>
                    <AdminTh>Reporter</AdminTh>
                    <AdminTh>Type</AdminTh>
                    <AdminTh>Reason</AdminTh>
                    <AdminTh>Status</AdminTh>
                    <AdminTh>Date</AdminTh>
                    <AdminTh>Actions</AdminTh>
                  </AdminTableHead>
                  <tbody>
                    {reports.map((report) => (
                      <AdminTr key={report._id || report.id}>
                        <AdminTd className="tw-font-semibold">
                          {report.reporter_name || report.reporter_email || report.user_name || 'Unknown'}
                        </AdminTd>
                        <AdminTd>
                          <AdminBadge tone="green">{report.report_type || report.type || 'general'}</AdminBadge>
                        </AdminTd>
                        <AdminTd className="tw-truncate" title={report.reason || report.description || report.message || ''}>
                          {report.reason || report.description || report.message || 'N/A'}
                        </AdminTd>
                        <AdminTd>
                          <span className="tw-font-semibold" style={{ color: getStatusColor(report.status) }}>{report.status || 'pending'}</span>
                        </AdminTd>
                        <AdminTd>{report.created_at ? format(new Date(report.created_at), 'MMM dd, yyyy') : 'N/A'}</AdminTd>
                        <AdminTd>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            {report.status !== 'resolved' && report.status !== 'dismissed' ? (
                              <>
                                <button
                                  className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-success"
                                  style={{ padding: '0.3rem 0.6rem', fontSize: '0.78rem' }}
                                  onClick={() => handleUpdateReportStatus(report._id || report.id, 'resolved')}
                                >
                                  Resolve
                                </button>
                                {report.reported_user_id && (
                                  <>
                                    <button
                                      className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-warning"
                                      style={{ padding: '0.3rem 0.6rem', fontSize: '0.78rem' }}
                                      onClick={() => handleTakeReportAction(report._id || report.id, 'warn_user')}
                                    >
                                      Warn
                                    </button>
                                    <button
                                      className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-danger"
                                      style={{ padding: '0.3rem 0.6rem', fontSize: '0.78rem' }}
                                      onClick={() => handleTakeReportAction(report._id || report.id, 'ban_user')}
                                    >
                                      Suspend
                                    </button>
                                  </>
                                )}
                                {report.type === 'product' && report.reported_item_id && (
                                  <button
                                    className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-danger"
                                    style={{ padding: '0.3rem 0.6rem', fontSize: '0.78rem' }}
                                    onClick={() => handleTakeReportAction(report._id || report.id, 'remove_content')}
                                  >
                                    Remove Listing
                                  </button>
                                )}
                                <button
                                  className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
                                  style={{ padding: '0.3rem 0.6rem', fontSize: '0.78rem' }}
                                  onClick={() => handleUpdateReportStatus(report._id || report.id, 'dismissed')}
                                >
                                  Dismiss
                                </button>
                              </>
                            ) : (
                              <span style={{ opacity: 0.6, fontSize: '0.85rem', textTransform: 'capitalize' }}>
                                {report.status}
                                {report.action_taken ? ` · ${report.action_taken.replace(/_/g, ' ')}` : ''}
                              </span>
                            )}
                          </div>
                        </AdminTd>
                      </AdminTr>
                    ))}
                  </tbody>
                </AdminTable>
              )}
            </div>
          ) : moderationSubTab === 'prohibited' ? (
            <div className={`AutoboyExpress-dash-card ${isDarkMode ? 'dark' : ''}`} style={{ padding: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <FontAwesomeIcon icon={faShieldAlt} style={{ color: '#ec4899' }} /> Prohibited Items Management
                </h3>
                <button className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary" onClick={handleUpdateProhibitedItems}>
                  <FontAwesomeIcon icon={faSave} /> Save Rules
                </button>
              </div>
              <p style={{ opacity: 0.7, marginBottom: '1.5rem' }}>
                Enter keywords or items that should be restricted on the platform, separated by commas.
              </p>
              <textarea
                value={prohibitedItemsText}
                onChange={(e) => setProhibitedItemsText(e.target.value)}
                placeholder="e.g. drugs, weapons, stolen goods..."
                style={{
                  width: '100%',
                  minHeight: '200px',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.8)',
                  border: `2px solid ${isDarkMode ? 'rgba(34,197,94,0.2)' : 'rgba(22,163,74,0.2)'}`,
                  color: isDarkMode ? '#fff' : '#000',
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  resize: 'vertical'
                }}
              />
              <div style={{ marginTop: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {prohibitedItems.map((item, idx) => (
                  <span key={idx} style={{
                    padding: '0.4rem 0.8rem',
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    borderRadius: '20px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    border: '1px solid rgba(239, 68, 68, 0.2)'
                  }}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            /* ===== SUPPORT TICKETS (existing layout) ===== */
            <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '1.5rem', height: 'calc(100vh - 240px)', minHeight: '600px' }}>
              {/* Support Tickets Sidebar */}
              <div style={{
                background: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.95)',
                border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(22, 163, 74, 0.15)'}`,
                borderRadius: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{
                  padding: '1rem',
                  borderBottom: `2px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(22, 163, 74, 0.2)'}`,
                  background: isDarkMode ? 'rgba(34, 197, 94, 0.08)' : 'rgba(240, 253, 244, 0.5)'
                }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Support Tickets ({supportTickets.length})
                  </h3>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
                  {supportTickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      onClick={() => handleSelectTicket(ticket)}
                      style={{
                        padding: '1rem',
                        marginBottom: '0.5rem',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        border: `1px solid ${selectedTicket?.id === ticket.id ? '#22C55E' : (isDarkMode ? 'rgba(34, 197, 94, 0.1)' : 'rgba(22, 163, 74, 0.1)')}`,
                        background: selectedTicket?.id === ticket.id
                          ? (isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(240, 253, 244, 0.8)')
                          : (isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.5)')
                      }}
                      onMouseEnter={(e) => {
                        if (selectedTicket?.id !== ticket.id) {
                          e.currentTarget.style.background = isDarkMode ? 'rgba(34, 197, 94, 0.08)' : 'rgba(240, 253, 244, 0.5)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedTicket?.id !== ticket.id) {
                          e.currentTarget.style.background = isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.5)';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: '600', margin: 0 }}>{ticket.subject}</h4>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '6px',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          background: getStatusColor(ticket.status) + '20',
                          color: getStatusColor(ticket.status)
                        }}>
                          {ticket.status}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.8rem', opacity: 0.7, margin: '0.25rem 0' }}>
                        {ticket.category} � {ticket.created_at ? formatDate(ticket.created_at) : 'Recently'}
                      </p>
                    </div>
                  ))}
                  {supportTickets.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '2rem', opacity: 0.7 }}>
                      <FontAwesomeIcon icon={faComment} size="2x" style={{ color: '#22C55E', marginBottom: '0.5rem' }} />
                      <p>No support tickets</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Chat/Messages Area */}
              <div style={{
                background: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.95)',
                border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(22, 163, 74, 0.15)'}`,
                borderRadius: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {selectedTicket ? (
                  <>
                    <div style={{
                      padding: '1rem 1.5rem',
                      borderBottom: `2px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(22, 163, 74, 0.2)'}`,
                      background: isDarkMode ? 'rgba(34, 197, 94, 0.08)' : 'rgba(240, 253, 244, 0.5)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                          {selectedTicket.subject}
                        </h3>
                        <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                          {selectedTicket.ticket_number || `Ticket #${selectedTicket.id}`} · {selectedTicket.category} · {selectedTicket.status?.replace(/_/g, ' ')}
                        </p>
                      </div>
                      {selectedTicket.status !== 'closed' && (
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {selectedTicket.status === 'open' && (
                            <button
                              onClick={() => handleSetTicketStatus(selectedTicket.id, 'in_progress')}
                              style={{
                                padding: '0.5rem 1rem',
                                borderRadius: '8px',
                                border: '1px solid rgba(59, 130, 246, 0.35)',
                                background: 'rgba(59, 130, 246, 0.12)',
                                color: '#2563eb',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              Mark In Progress
                            </button>
                          )}
                          {selectedTicket.status !== 'resolved' && (
                            <button
                              onClick={() => handleSetTicketStatus(selectedTicket.id, 'resolved')}
                              style={{
                                padding: '0.5rem 1rem',
                                borderRadius: '8px',
                                border: '1px solid rgba(34, 197, 94, 0.35)',
                                background: 'rgba(34, 197, 94, 0.12)',
                                color: '#16a34a',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              Mark Resolved
                            </button>
                          )}
                          <button
                            onClick={() => handleSetTicketStatus(selectedTicket.id, 'closed')}
                            style={{
                              padding: '0.5rem 1rem',
                              borderRadius: '8px',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              background: 'rgba(239, 68, 68, 0.1)',
                              color: '#dc2626',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Close Ticket
                          </button>
                        </div>
                      )}
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
                      {ticketMessages.map((msg, index) => (
                        <div
                          key={msg.id || index}
                          style={{
                            display: 'flex',
                            justifyContent: msg.sender === 'support' ? 'flex-start' : 'flex-end',
                            marginBottom: '1rem'
                          }}
                        >
                          <div style={{
                            maxWidth: '70%',
                            padding: '1rem',
                            borderRadius: '12px',
                            background: msg.sender === 'support'
                              ? (isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(240, 253, 244, 0.8)')
                              : (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)')
                          }}>
                            <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.5rem', fontWeight: '600' }}>
                              {msg.sender === 'support' ? 'Support Agent' : 'User'} · {formatDate(msg.created_at)}
                            </div>
                            <p style={{ margin: 0 }}>{msg.message}</p>
                          </div>
                        </div>
                      ))}
                      {ticketMessages.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '3rem', opacity: 0.7 }}>
                          <FontAwesomeIcon icon={faComment} size="3x" style={{ color: '#22C55E', marginBottom: '0.5rem' }} />
                          <p>No messages yet</p>
                        </div>
                      )}
                    </div>

                    {selectedTicket.status !== 'closed' && (
                      <form onSubmit={handleReplyToTicket} style={{
                        padding: '1rem',
                        borderTop: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(22, 163, 74, 0.2)'}`,
                        display: 'flex',
                        gap: '0.75rem'
                      }}>
                        <input
                          type="text"
                          value={ticketReplyMessage}
                          onChange={(e) => setTicketReplyMessage(e.target.value)}
                          placeholder="Type your reply..."
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            borderRadius: '8px',
                            border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.3)' : 'rgba(22, 163, 74, 0.3)'}`,
                            background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.9)',
                            fontSize: '1rem'
                          }}
                        />
                        <button
                          type="submit"
                          style={{
                            padding: '0.75rem 1.5rem',
                            borderRadius: '8px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                            color: 'white',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          <FontAwesomeIcon icon={faPaperPlane} /> Send
                        </button>
                      </form>
                    )}
                  </>
                ) : (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem' }}>
                    <div style={{ textAlign: 'center', opacity: 0.7 }}>
                      <FontAwesomeIcon icon={faComment} size="4x" style={{ color: '#22C55E', marginBottom: '1rem' }} />
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                        Select a ticket
                      </h3>
                      <p>Choose a support ticket from the list to view and respond</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderCategoriesContent = () => {
    const cardBg = isDarkMode ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)';
    const cardBorder = isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)';
    const textPrimary = isDarkMode ? '#F3F4F6' : '#1F2937';
    const textSecondary = isDarkMode ? '#9CA3AF' : '#6B7280';
    const inputBg = isDarkMode ? 'rgba(0,0,0,0.3)' : '#f9fafb';
    const inputBorder = `1px solid ${isDarkMode ? 'rgba(34,197,94,0.25)' : 'rgba(34,197,94,0.2)'}`;

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#22C55E' }} />
            <p style={{ marginTop: '1rem', color: textSecondary }}>Loading categories...</p>
          </div>
        ) : (
          <>
            {/* Header Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              borderRadius: '20px', padding: '1.75rem 2rem', marginBottom: '1.5rem',
              boxShadow: '0 8px 32px rgba(34,197,94,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem'
            }}>
              <div>
                <h2 style={{ color: '#fff', fontSize: '1.75rem', fontWeight: '800', margin: 0 }}>
                  <FontAwesomeIcon icon={faTags} style={{ marginRight: '0.75rem', opacity: 0.9 }} />
                  Product Categories
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', margin: '0.4rem 0 0' }}>
                  Organise your marketplace with clear product category hierarchies
                </p>
              </div>
              <button
                onClick={handleCreateCategory}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)',
                  color: '#fff', padding: '0.65rem 1.25rem', borderRadius: '50px', cursor: 'pointer',
                  fontWeight: '700', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
                }}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Category
              </button>
            </div>

            {/* Category Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.25rem' }}>
              {categories.length === 0 ? (
                <div style={{
                  gridColumn: '1/-1', textAlign: 'center', padding: '4rem 2rem',
                  background: cardBg, borderRadius: '20px', border: `1px solid ${cardBorder}`,
                  backdropFilter: 'blur(20px)'
                }}>
                  <div style={{
                    width: '72px', height: '72px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 1.25rem', boxShadow: '0 8px 32px rgba(34,197,94,0.3)'
                  }}>
                    <FontAwesomeIcon icon={faTags} style={{ color: '#fff', fontSize: '1.75rem' }} />
                  </div>
                  <h3 style={{ color: textPrimary, fontWeight: '700', marginBottom: '0.5rem' }}>No Categories Yet</h3>
                  <p style={{ color: textSecondary, maxWidth: '320px', margin: '0 auto' }}>
                    Create your first product category to help buyers browse your marketplace efficiently.
                  </p>
                </div>
              ) : categories.map(category => (
                <div
                  key={category._id || category.id}
                  style={{
                    background: cardBg, borderRadius: '20px', padding: '1.5rem',
                    border: `1px solid ${cardBorder}`, backdropFilter: 'blur(20px)',
                    boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`,
                    transition: 'transform 0.2s, box-shadow 0.2s', textAlign: 'center'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 60px rgba(34,197,94,0.2)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`; }}
                >
                  <div style={{
                    width: '60px', height: '60px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 1rem', boxShadow: '0 4px 15px rgba(34,197,94,0.35)'
                  }}>
                    <FontAwesomeIcon icon={faTags} style={{ color: '#fff', fontSize: '1.25rem' }} />
                  </div>
                  <h4 style={{ color: textPrimary, fontWeight: '700', margin: '0 0 0.35rem', fontSize: '1rem' }}>
                    {category.name || 'Category'}
                  </h4>
                  <span style={{
                    display: 'inline-block', padding: '0.25rem 0.75rem', borderRadius: '50px',
                    fontSize: '0.75rem', fontWeight: '700',
                    background: 'rgba(34,197,94,0.12)', color: '#22C55E',
                    border: '1px solid rgba(34,197,94,0.2)', marginBottom: '1.25rem'
                  }}>
                    {category.product_count || 0} products
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleEditCategory(category)}
                      style={{
                        flex: 1, padding: '0.55rem', borderRadius: '10px',
                        border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.35)' : 'rgba(34,197,94,0.3)'}`,
                        background: 'transparent', color: '#22C55E', fontWeight: '600',
                        fontSize: '0.8rem', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem'
                      }}
                    >
                      <FontAwesomeIcon icon={faEdit} /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category._id || category.id)}
                      style={{
                        padding: '0.55rem 0.75rem', borderRadius: '10px',
                        border: '1px solid rgba(239,68,68,0.3)',
                        background: 'rgba(239,68,68,0.08)', color: '#ef4444',
                        cursor: 'pointer', fontSize: '0.8rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Category Form Modal */}
            {showCategoryModal && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
              }} onClick={() => setShowCategoryModal(false)}>
                <div style={{
                  background: isDarkMode ? 'rgba(15,15,20,0.98)' : '#fff',
                  borderRadius: '24px', overflow: 'hidden',
                  maxWidth: '500px', width: '90%',
                  boxShadow: '0 30px 80px rgba(0,0,0,0.4)',
                  border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)'}`
                }} onClick={(e) => e.stopPropagation()}>
                  <div style={{ height: '4px', background: 'linear-gradient(90deg, #22C55E, #16A34A)' }} />
                  <div style={{ padding: '2rem' }}>
                    <h2 style={{ marginBottom: '1.5rem', fontSize: '1.4rem', fontWeight: '800', color: textPrimary }}>
                      <FontAwesomeIcon icon={editingCategory ? faEdit : faPlus} style={{ color: '#22C55E', marginRight: '0.75rem' }} />
                      {editingCategory ? 'Edit Category' : 'Create Category'}
                    </h2>
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      {[
                        { label: 'Name *', key: 'name', type: 'text', placeholder: 'e.g. Smartphones' },
                        { label: 'Icon (FontAwesome class)', key: 'icon', type: 'text', placeholder: 'e.g. fa-mobile-alt' },
                      ].map(field => (
                        <div key={field.key}>
                          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{field.label}</label>
                          <input
                            type={field.type}
                            placeholder={field.placeholder}
                            value={categoryFormData[field.key]}
                            onChange={(e) => setCategoryFormData({ ...categoryFormData, [field.key]: e.target.value })}
                            style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: inputBorder, background: inputBg, color: textPrimary, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                          />
                        </div>
                      ))}
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description</label>
                        <textarea
                          placeholder="Category description..."
                          value={categoryFormData.description}
                          onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                          style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: inputBorder, background: inputBg, color: textPrimary, fontSize: '0.95rem', outline: 'none', minHeight: '80px', resize: 'vertical', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setShowCategoryModal(false)}
                        style={{ padding: '0.7rem 1.5rem', borderRadius: '12px', border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.25)'}`, background: 'transparent', color: '#22C55E', fontWeight: '600', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveCategory}
                        style={{ padding: '0.7rem 1.5rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#fff', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 15px rgba(34,197,94,0.35)' }}
                      >
                        <FontAwesomeIcon icon={faSave} /> {editingCategory ? 'Update' : 'Create'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Per-model color options powering the seller listing form's
                Color dropdown — product taxonomy, so it lives alongside
                categories rather than in general settings. */}
            <DeviceColorManager isDarkMode={isDarkMode} showToast={showToast} />
          </>
        )}
      </div>
    );
  };

  const renderAnalyticsContent = () => {
    const cardBg = isDarkMode ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)';
    const cardBorder = isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)';
    const textPrimary = isDarkMode ? '#F3F4F6' : '#1F2937';
    const textSecondary = isDarkMode ? '#9CA3AF' : '#6B7280';
    const rowEven = isDarkMode ? 'rgba(34,197,94,0.03)' : 'rgba(34,197,94,0.03)';

    // Backend returns platform totals only (no time-series breakdowns)
    const totalsCards = [
      { label: 'Total Users', icon: faUsers, color: '#22C55E', value: analyticsData.totalUsers || 0, sub: 'Registered accounts' },
      { label: 'Total Orders', icon: faChartLine, color: '#16A34A', value: analyticsData.totalOrders || 0, sub: 'Lifetime orders' },
      { label: 'Total Products', icon: faTags, color: '#4ADE80', value: analyticsData.totalProducts || 0, sub: 'Active listings' },
    ];

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#22C55E' }} />
            <p style={{ marginTop: '1rem', color: textSecondary }}>Loading analytics...</p>
          </div>
        ) : (
          <>
            {/* Header Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              borderRadius: '20px', padding: '1.75rem 2rem', marginBottom: '1.5rem',
              boxShadow: '0 8px 32px rgba(34,197,94,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem'
            }}>
              <div>
                <h2 style={{ color: '#fff', fontSize: '1.75rem', fontWeight: '800', margin: 0 }}>
                  <FontAwesomeIcon icon={faChartLine} style={{ marginRight: '0.75rem', opacity: 0.9 }} />
                  Analytics Overview
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', margin: '0.4rem 0 0' }}>
                  Real-time platform performance metrics and growth indicators
                </p>
              </div>
              <button
                onClick={() => exportRowsToCSV([analyticsData], 'analytics-overview')}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)',
                  color: '#fff', padding: '0.65rem 1.25rem', borderRadius: '50px', cursor: 'pointer',
                  fontWeight: '700', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
                }}
              >
                <FontAwesomeIcon icon={faDownload} /> Export Report
              </button>
            </div>

            {/* Platform Totals */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.25rem', marginBottom: '1.5rem' }}>
              {totalsCards.map((card, i) => (
                <div
                  key={i}
                  style={{
                    background: cardBg, borderRadius: '20px', padding: '1.75rem',
                    border: `1px solid ${cardBorder}`, backdropFilter: 'blur(20px)',
                    boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`,
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 60px rgba(34,197,94,0.2)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '14px', background: `${card.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={card.icon} style={{ color: card.color, fontSize: '1.15rem' }} />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontWeight: '800', color: textPrimary, fontSize: '0.95rem' }}>{card.label}</h3>
                      <span style={{ fontSize: '0.75rem', color: textSecondary }}>{card.sub}</span>
                    </div>
                  </div>
                  <div style={{ fontSize: '3rem', fontWeight: '900', color: card.color, letterSpacing: '-2px', lineHeight: 1 }}>
                    {card.value.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>

            {/* Top Lists */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem' }}>
              {[
                { title: 'Top Categories', icon: faTags, data: analyticsData.topCategories || [], valueKey: 'count', valueSuffix: ' products', nameKey: 'name' },
                { title: 'Top Sellers', icon: faCrown, data: analyticsData.topSellers || [], valueKey: 'revenue', isRevenue: true, nameKey: 'name' },
              ].map((list, i) => (
                <div key={i} style={{
                  background: cardBg, borderRadius: '20px', overflow: 'hidden',
                  border: `1px solid ${cardBorder}`, backdropFilter: 'blur(20px)',
                  boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`
                }}>
                  <div style={{
                    padding: '1.1rem 1.5rem',
                    borderBottom: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.08)'}`,
                    background: isDarkMode ? 'rgba(34,197,94,0.06)' : 'rgba(240,253,244,0.5)',
                    display: 'flex', alignItems: 'center', gap: '0.75rem'
                  }}>
                    <FontAwesomeIcon icon={list.icon} style={{ color: '#22C55E' }} />
                    <h3 style={{ margin: 0, fontWeight: '800', color: textPrimary, fontSize: '1rem' }}>{list.title}</h3>
                  </div>
                  <div style={{ padding: '0.75rem' }}>
                    {list.data.length === 0 ? (
                      <p style={{ textAlign: 'center', padding: '2rem', color: textSecondary, fontSize: '0.875rem' }}>No data available</p>
                    ) : list.data.map((item, j) => (
                      <div key={j} style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '0.65rem 0.75rem', borderRadius: '10px',
                        background: j % 2 === 0 ? rowEven : 'transparent',
                        marginBottom: '0.25rem'
                      }}>
                        <div style={{
                          width: '28px', height: '28px', borderRadius: '8px',
                          background: j === 0 ? 'linear-gradient(135deg, #22C55E, #16A34A)' : isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.75rem', fontWeight: '800',
                          color: j === 0 ? '#fff' : textSecondary, flexShrink: 0
                        }}>
                          {j + 1}
                        </div>
                        <span style={{ flex: 1, color: textPrimary, fontWeight: '600', fontSize: '0.875rem' }}>{item[list.nameKey]}</span>
                        <strong style={{ color: '#22C55E', fontSize: '0.875rem' }}>
                          {list.isRevenue ? formatCurrency(item[list.valueKey]) : `${item[list.valueKey]}${list.valueSuffix}`}
                        </strong>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderSystemHealthContent = () => {
    const cardBg = isDarkMode ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)';
    const cardBorder = isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)';
    const textPrimary = isDarkMode ? '#F3F4F6' : '#1F2937';
    const textSecondary = isDarkMode ? '#9CA3AF' : '#6B7280';

    const dbConnected = systemMetrics.dbConnected;
    const dbCollections = systemMetrics.dbCollections || {};
    const environment = systemMetrics.environment || {};
    const lastChecked = systemMetrics.lastChecked;
    const statusColor = dbConnected ? '#22C55E' : '#ef4444';

    const collectionItems = [
      { label: 'Users', icon: faUsers, key: 'users' },
      { label: 'Orders', icon: faChartLine, key: 'orders' },
      { label: 'Products', icon: faTags, key: 'products' },
      { label: 'Categories', icon: faDatabase, key: 'categories' },
    ];

    const formatUptime = (seconds) => {
      const s = Number(seconds) || 0;
      const days = Math.floor(s / 86400);
      const hours = Math.floor((s % 86400) / 3600);
      const minutes = Math.floor((s % 3600) / 60);
      if (days > 0) return `${days}d ${hours}h`;
      if (hours > 0) return `${hours}h ${minutes}m`;
      return `${minutes}m`;
    };

    const usageColor = (pct) => pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#22C55E';

    const liveMetrics = [
      { label: 'CPU Usage', value: `${(systemMetrics.cpu ?? 0).toFixed(1)}%`, icon: faServer, color: usageColor(systemMetrics.cpu ?? 0), sub: `${systemMetrics.cpuCores || 0} cores` },
      { label: 'Memory Usage', value: `${(systemMetrics.memory ?? 0).toFixed(1)}%`, icon: faDatabase, color: usageColor(systemMetrics.memory ?? 0), sub: null },
      { label: 'Storage Usage', value: `${(systemMetrics.storage ?? 0).toFixed(1)}%`, icon: faBox, color: usageColor(systemMetrics.storage ?? 0), sub: null },
      { label: 'DB Latency', value: `${(systemMetrics.apiLatency ?? 0).toFixed(1)}ms`, icon: faBolt, color: systemMetrics.dbReachable ? '#22C55E' : '#ef4444', sub: systemMetrics.dbReachable ? 'Reachable' : 'Unreachable' },
      { label: 'Uptime', value: formatUptime(systemMetrics.uptime), icon: faHistory, color: '#22C55E', sub: null },
      { label: 'Active Connections', value: systemMetrics.activeConnections ?? 0, icon: faUsers, color: '#22C55E', sub: null },
      { label: 'Queued Jobs', value: systemMetrics.queuedJobs ?? 0, icon: faClipboardList, color: (systemMetrics.queuedJobs ?? 0) > 0 ? '#f59e0b' : '#22C55E', sub: null },
    ];

    const handleExport = async (type) => {
      setExportingType(type);
      try {
        const res = await adminAPI.exportSystemData({ type });
        const payload = res?.data || res;
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}-export-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        showToast(`${type[0].toUpperCase()}${type.slice(1)} export downloaded`, 'success');
      } catch (error) {
        showToast('Failed to export data', 'error');
      } finally {
        setExportingType(null);
      }
    };

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#22C55E' }} />
            <p style={{ marginTop: '1rem', color: textSecondary }}>Loading system status...</p>
          </div>
        ) : (
          <>
            {/* Header Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              borderRadius: '20px', padding: '1.75rem 2rem', marginBottom: '1.5rem',
              boxShadow: '0 8px 32px rgba(34,197,94,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem'
            }}>
              <div>
                <h2 style={{ color: '#fff', fontSize: '1.75rem', fontWeight: '800', margin: 0 }}>
                  <FontAwesomeIcon icon={faServer} style={{ marginRight: '0.75rem', opacity: 0.9 }} />
                  System Health
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', margin: '0.4rem 0 0' }}>
                  Live CPU/memory/storage load, database connectivity, collection stats, and environment configuration
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {['users', 'products', 'orders'].map(type => (
                  <button
                    key={type}
                    onClick={() => handleExport(type)}
                    disabled={exportingType === type}
                    style={{
                      background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                      color: '#fff', padding: '0.6rem 1rem', borderRadius: '50px', cursor: 'pointer',
                      fontWeight: '600', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem',
                      opacity: exportingType === type ? 0.6 : 1
                    }}
                  >
                    <FontAwesomeIcon icon={exportingType === type ? faSpinner : faHistory} spin={exportingType === type} />
                    Export {type[0].toUpperCase()}{type.slice(1)}
                  </button>
                ))}
                <button
                  onClick={fetchSystemMetrics}
                  style={{
                    background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)',
                    color: '#fff', padding: '0.65rem 1.25rem', borderRadius: '50px', cursor: 'pointer',
                    fontWeight: '700', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
                  }}
                >
                  <FontAwesomeIcon icon={faSync} /> Refresh
                </button>
              </div>
            </div>

            {/* Live Metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
              {liveMetrics.map((metric, i) => (
                <div key={i} style={{
                  background: cardBg, borderRadius: '16px', padding: '1.1rem',
                  border: `1px solid ${cardBorder}`, backdropFilter: 'blur(20px)',
                  boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <FontAwesomeIcon icon={metric.icon} style={{ color: metric.color, fontSize: '0.85rem' }} />
                    <span style={{ fontSize: '0.7rem', fontWeight: '700', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{metric.label}</span>
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '900', color: metric.color, letterSpacing: '-0.5px' }}>{metric.value}</div>
                  {metric.sub && <div style={{ fontSize: '0.72rem', color: textSecondary, marginTop: '0.2rem' }}>{metric.sub}</div>}
                </div>
              ))}
            </div>

            {/* DB Connection Status */}
            <div style={{
              background: cardBg, borderRadius: '20px', padding: '1.5rem',
              border: `1px solid ${dbConnected ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
              backdropFilter: 'blur(20px)', marginBottom: '1.25rem',
              boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  width: '52px', height: '52px', borderRadius: '16px',
                  background: `${statusColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <FontAwesomeIcon icon={faDatabase} style={{ color: statusColor, fontSize: '1.4rem' }} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontWeight: '800', color: textPrimary, fontSize: '1.1rem' }}>MongoDB</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColor, boxShadow: `0 0 8px ${statusColor}` }} />
                    <span style={{ fontSize: '0.875rem', color: statusColor, fontWeight: '700' }}>
                      {dbConnected ? 'Connected' : 'Disconnected'}
                    </span>
                  </div>
                </div>
              </div>
              {lastChecked && (
                <span style={{ fontSize: '0.78rem', color: textSecondary }}>
                  Last checked: {new Date(lastChecked).toLocaleTimeString()}
                </span>
              )}
            </div>

            {/* Collection Counts */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1.25rem', marginBottom: '1.25rem' }}>
              {collectionItems.map((col, i) => (
                <div key={i} style={{
                  background: cardBg, borderRadius: '20px', padding: '1.5rem',
                  border: `1px solid ${cardBorder}`, backdropFilter: 'blur(20px)',
                  boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`,
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 60px rgba(34,197,94,0.2)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={col.icon} style={{ color: '#22C55E', fontSize: '0.9rem' }} />
                    </div>
                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{col.label}</span>
                  </div>
                  <div style={{ fontSize: '2.75rem', fontWeight: '900', color: '#22C55E', letterSpacing: '-2px', lineHeight: 1 }}>
                    {(dbCollections[col.key] ?? 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: textSecondary, marginTop: '0.4rem' }}>documents</div>
                </div>
              ))}
            </div>

            {/* Environment Info */}
            {Object.keys(environment).length > 0 && (
              <div style={{
                background: cardBg, borderRadius: '20px', overflow: 'hidden',
                border: `1px solid ${cardBorder}`, backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`
              }}>
                <div style={{
                  padding: '1rem 1.5rem',
                  borderBottom: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.08)'}`,
                  background: isDarkMode ? 'rgba(34,197,94,0.06)' : 'rgba(240,253,244,0.5)',
                  display: 'flex', alignItems: 'center', gap: '0.6rem'
                }}>
                  <FontAwesomeIcon icon={faServer} style={{ color: '#22C55E' }} />
                  <h3 style={{ margin: 0, fontWeight: '800', color: textPrimary, fontSize: '0.95rem' }}>Environment</h3>
                </div>
                <div style={{ padding: '1.25rem', display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                  {Object.entries(environment).map(([k, v]) => (
                    <div key={k} style={{
                      padding: '0.6rem 1rem', borderRadius: '10px',
                      background: isDarkMode ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.06)',
                      border: `1px solid rgba(34,197,94,0.15)`
                    }}>
                      <span style={{ fontSize: '0.75rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: '0.5rem' }}>{k.replace(/_/g, ' ')}:</span>
                      <strong style={{ color: textPrimary, fontSize: '0.875rem' }}>{String(v)}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderServiceChargesContent = () => {
    const cardBg = isDarkMode ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)';
    const cardBorder = isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)';
    const textPrimary = isDarkMode ? '#F3F4F6' : '#1F2937';
    const textSecondary = isDarkMode ? '#9CA3AF' : '#6B7280';
    const inputStyle = {
      padding: '0.65rem 0.9rem', borderRadius: '10px', fontSize: '0.9rem', width: '100%',
      background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', color: 'inherit',
      border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)', outline: 'none',
    };

    const finalRate = (basePercent) => (basePercent * (1 + vatPercentage / 100));

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {/* Header Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
          borderRadius: '20px', padding: '1.75rem 2rem', marginBottom: '1.5rem',
          boxShadow: '0 8px 32px rgba(34,197,94,0.3)'
        }}>
          <h2 style={{ color: '#fff', fontSize: '1.75rem', fontWeight: '800', margin: 0 }}>
            <FontAwesomeIcon icon={faPercentage} style={{ marginRight: '0.75rem', opacity: 0.9 }} />
            Service Charges
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', margin: '0.4rem 0 0' }}>
            Set the seller commission rate card and VAT — sellers only ever see the combined "X% (VAT included)" figure; the two components stay separately recorded for accounting.
          </p>
        </div>

        {loadingCommission ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#22C55E' }} />
          </div>
        ) : (
          <>
            {/* VAT rate card */}
            <div style={{
              background: cardBg, borderRadius: '18px', padding: '1.5rem',
              border: `1px solid ${cardBorder}`, backdropFilter: 'blur(20px)', marginBottom: '1.5rem',
              display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap'
            }}>
              <div style={{ flex: '1 1 260px' }}>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.85rem', color: textPrimary, marginBottom: '0.4rem' }}>
                  VAT Rate (applied on top of every tier's base charge)
                </label>
                <p style={{ margin: '0 0 0.6rem', fontSize: '0.8rem', color: textSecondary }}>
                  Final Rate = Base Service Charge × (1 + VAT/100). Currently active: <strong>{vatPercentage}%</strong>
                </p>
                <div style={{ display: 'flex', gap: '0.6rem' }}>
                  <input
                    type="number" min="0" max="100" step="0.01" value={vatInput}
                    onChange={e => setVatInput(e.target.value)}
                    style={{ ...inputStyle, maxWidth: '160px' }}
                  />
                  <button
                    onClick={handleSaveVat}
                    disabled={savingVat || parseFloat(vatInput) === vatPercentage}
                    style={{
                      padding: '0.65rem 1.25rem', borderRadius: '10px', border: 'none', fontWeight: '700', cursor: 'pointer',
                      background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#fff',
                      opacity: (savingVat || parseFloat(vatInput) === vatPercentage) ? 0.5 : 1,
                    }}
                  >
                    {savingVat ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Save VAT Rate'}
                  </button>
                </div>
              </div>
            </div>

            {/* Tiers table */}
            <div style={{
              background: cardBg, borderRadius: '18px', overflow: 'hidden',
              border: `1px solid ${cardBorder}`, backdropFilter: 'blur(20px)',
            }}>
              <div style={{ padding: '1.25rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${cardBorder}` }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: textPrimary }}>Seller Commission Tiers</h3>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: textSecondary }}>Applies to every escrow-held order, based on the transaction amount</p>
                </div>
                <button
                  onClick={() => openTierModal(null)}
                  style={{
                    padding: '0.6rem 1.1rem', borderRadius: '10px', border: 'none', fontWeight: '700', cursor: 'pointer',
                    background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#fff',
                    display: 'flex', alignItems: 'center', gap: '0.5rem'
                  }}
                >
                  <FontAwesomeIcon icon={faCoins} /> Add Tier
                </button>
              </div>

              {commissionTiers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: textSecondary }}>
                  No commission tiers configured yet. Click "Add Tier" to create the rate card.
                </div>
              ) : (
                <AdminTable minWidth="680px" columns={[{ width: '28%' }, { width: '16%' }, { width: '26%' }, { width: '12%' }, { width: '18%' }]}>
                  <AdminTableHead>
                    <AdminTh>Transaction Amount</AdminTh>
                    <AdminTh>Base Charge</AdminTh>
                    <AdminTh>Final Rate (VAT incl.)</AdminTh>
                    <AdminTh align="center">Active</AdminTh>
                    <AdminTh align="right">Actions</AdminTh>
                  </AdminTableHead>
                  <tbody>
                    {commissionTiers.map((tier, i) => (
                      <AdminTr key={tier.id || tier._id || i}>
                        <AdminTd className="tw-font-semibold">
                          {formatCurrency(tier.min_price)} {tier.max_price < 0 ? 'and above' : `– ${formatCurrency(tier.max_price)}`}
                        </AdminTd>
                        <AdminTd>{(tier.percentage * 100).toFixed(2)}%</AdminTd>
                        <AdminTd>
                          <AdminBadge tone="green">{finalRate(tier.percentage * 100).toFixed(3).replace(/\.?0+$/, '')}% (VAT included)</AdminBadge>
                        </AdminTd>
                        <AdminTd align="center">
                          <button
                            onClick={() => handleToggleTierActive(tier)}
                            title={tier.active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                            className="tw-w-[38px] tw-h-[22px] tw-rounded-full tw-border-0 tw-cursor-pointer tw-relative tw-transition-colors"
                            style={{ background: tier.active ? '#22C55E' : (isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)') }}
                          >
                            <span className="tw-absolute tw-top-0.5 tw-w-[18px] tw-h-[18px] tw-rounded-full tw-bg-white tw-shadow tw-transition-all" style={{ left: tier.active ? '18px' : '2px' }} />
                          </button>
                        </AdminTd>
                        <AdminTd align="right">
                          <div className="tw-inline-flex tw-gap-1.5">
                            <AdminIconBtn onClick={() => openTierModal(tier)} title="Edit">
                              <FontAwesomeIcon icon={faEdit} />
                            </AdminIconBtn>
                            <AdminIconBtn onClick={() => handleDeleteTier(tier)} title="Delete" tone="red">
                              <FontAwesomeIcon icon={faTrash} />
                            </AdminIconBtn>
                          </div>
                        </AdminTd>
                      </AdminTr>
                    ))}
                  </tbody>
                </AdminTable>
              )}
            </div>

          </>
        )}

        {/* Add/Edit Tier Modal */}
        {showTierModal && (
          <div
            onClick={() => setShowTierModal(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '1rem' }}
          >
            <div onClick={e => e.stopPropagation()} style={{ background: isDarkMode ? '#17171f' : '#fff', borderRadius: '18px', padding: '1.75rem', width: '100%', maxWidth: '420px', border: `1px solid ${cardBorder}` }}>
              <h3 style={{ margin: '0 0 1.2rem', fontSize: '1.15rem', fontWeight: '800', color: textPrimary }}>
                {editingTier ? 'Edit Tier' : 'Add Tier'}
              </h3>

              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: textSecondary, marginBottom: '0.3rem' }}>Minimum Amount (₦)</label>
              <input type="number" value={tierForm.min_price} onChange={e => setTierForm(f => ({ ...f, min_price: e.target.value }))} style={{ ...inputStyle, marginBottom: '0.9rem' }} placeholder="e.g. 10000" />

              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: textSecondary, marginBottom: '0.3rem' }}>Maximum Amount (₦) — leave blank for "and above"</label>
              <input type="number" value={tierForm.max_price} onChange={e => setTierForm(f => ({ ...f, max_price: e.target.value }))} style={{ ...inputStyle, marginBottom: '0.9rem' }} placeholder="e.g. 99000" />

              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: textSecondary, marginBottom: '0.3rem' }}>Base Service Charge (%) — before VAT</label>
              <input type="number" step="0.01" value={tierForm.percentage} onChange={e => setTierForm(f => ({ ...f, percentage: e.target.value }))} style={{ ...inputStyle, marginBottom: '0.9rem' }} placeholder="e.g. 30" />

              {tierForm.percentage !== '' && !isNaN(parseFloat(tierForm.percentage)) && (
                <p style={{ margin: '0 0 1.1rem', fontSize: '0.85rem', color: textSecondary }}>
                  Seller will see: <strong style={{ color: '#22C55E' }}>{finalRate(parseFloat(tierForm.percentage)).toFixed(3).replace(/\.?0+$/, '')}% (VAT included)</strong>
                </p>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.4rem', fontSize: '0.85rem', color: textPrimary, cursor: 'pointer' }}>
                <input type="checkbox" checked={tierForm.active} onChange={e => setTierForm(f => ({ ...f, active: e.target.checked }))} />
                Active
              </label>

              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowTierModal(false)} style={{ padding: '0.65rem 1.1rem', borderRadius: '10px', border: `1px solid ${cardBorder}`, background: 'transparent', color: 'inherit', cursor: 'pointer', fontWeight: '600' }}>
                  Cancel
                </button>
                <button
                  onClick={handleSaveTier}
                  disabled={savingTier}
                  style={{ padding: '0.65rem 1.1rem', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#fff', cursor: 'pointer', fontWeight: '700', opacity: savingTier ? 0.6 : 1 }}
                >
                  {savingTier ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLogsContent = () => {
    const cardBg = isDarkMode ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)';
    const cardBorder = isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)';
    const textPrimary = isDarkMode ? '#F3F4F6' : '#1F2937';
    const textSecondary = isDarkMode ? '#9CA3AF' : '#6B7280';

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {/* Header Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
          borderRadius: '20px', padding: '1.75rem 2rem', marginBottom: '1.5rem',
          boxShadow: '0 8px 32px rgba(34,197,94,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem'
        }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: '1.75rem', fontWeight: '800', margin: 0 }}>
              <FontAwesomeIcon icon={faServer} style={{ marginRight: '0.75rem', opacity: 0.9 }} />
              Admin Audit Log
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', margin: '0.4rem 0 0' }}>
              Admin actions audit trail — user management, approvals, status changes
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={fetchSystemActivity}
              style={{
                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                color: '#fff', padding: '0.6rem 1.1rem', borderRadius: '50px', cursor: 'pointer',
                fontWeight: '600', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem'
              }}
            >
              <FontAwesomeIcon icon={faSync} /> Refresh
            </button>
          </div>
        </div>

        {/* Terminal Card */}
        <div style={{
          background: isDarkMode ? '#0A0A0A' : '#111111',
          borderRadius: '20px', overflow: 'hidden',
          border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)'}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
        }}>
          {/* Terminal title bar */}
          <div style={{
            padding: '0.75rem 1.25rem',
            borderBottom: '1px solid rgba(34,197,94,0.15)',
            background: 'rgba(34,197,94,0.06)',
            display: 'flex', alignItems: 'center', gap: '0.5rem'
          }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ef4444' }} />
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#f59e0b' }} />
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#22C55E' }} />
            <span style={{ marginLeft: '0.75rem', fontSize: '0.8rem', color: '#22C55E', fontFamily: 'monospace', fontWeight: '600' }}>
              system.log — live stream
            </span>
          </div>

          {/* Log entries — sourced from /admin/system/activity (admin audit trail) */}
          <div style={{ padding: '1.25rem', minHeight: '300px', maxHeight: '600px', overflowY: 'auto', fontFamily: 'monospace' }}>
            {systemLogs.length > 0 ? (
              systemLogs.map((log, index) => {
                const actionColor = log.action?.includes('delete') || log.action?.includes('reject') ? '#ef4444'
                  : log.action?.includes('suspend') || log.action?.includes('ban') ? '#f59e0b'
                  : '#22C55E';
                const actionLabel = (log.action || 'INFO').replace(/_/g, '_').toUpperCase();
                return (
                  <div key={index} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                    padding: '0.5rem 0.25rem', borderBottom: '1px solid rgba(255,255,255,0.04)'
                  }}>
                    <span style={{
                      padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.65rem', fontWeight: '800',
                      letterSpacing: '0.04em', textTransform: 'uppercase', flexShrink: 0,
                      backgroundColor: actionColor + '22', color: actionColor,
                      border: `1px solid ${actionColor}44`, whiteSpace: 'nowrap'
                    }}>
                      {actionLabel}
                    </span>
                    <span style={{ color: '#4ADE80', fontSize: '0.78rem', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {log.timestamp ? format(new Date(log.timestamp), 'MMM d HH:mm:ss') : 'N/A'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ color: '#E5E7EB', fontSize: '0.82rem', lineHeight: '1.5' }}>
                        Admin <span style={{ color: '#4ADE80' }}>{log.admin_id?.substring(0, 12)}…</span>
                        {log.target_user_id ? <> → user <span style={{ color: '#4ADE80' }}>{log.target_user_id?.substring(0, 12)}…</span></> : ''}
                        {log.details && Object.keys(log.details).length > 0 && (
                          <span style={{ color: '#9CA3AF' }}> — {JSON.stringify(log.details)}</span>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <div style={{ color: '#22C55E', fontSize: '1rem', fontFamily: 'monospace', marginBottom: '0.75rem' }}>
                  $ tail -f /var/log/audit.log
                </div>
                <div style={{ color: '#4ADE80', fontSize: '0.875rem', fontFamily: 'monospace', opacity: 0.7 }}>
                  -- No admin activity logged yet --
                </div>
                <div style={{ color: '#6B7280', fontSize: '0.8rem', fontFamily: 'monospace', marginTop: '0.5rem' }}>
                  Admin actions (status changes, approvals, etc.) appear here.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderAdminsContent = () => {
    const cardBg = isDarkMode ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)';
    const cardBorder = isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)';
    const textPrimary = isDarkMode ? '#F3F4F6' : '#1F2937';
    const textSecondary = isDarkMode ? '#9CA3AF' : '#6B7280';
    const inputBg = isDarkMode ? 'rgba(0,0,0,0.3)' : '#f9fafb';
    const inputBorder = `1px solid ${isDarkMode ? 'rgba(34,197,94,0.25)' : 'rgba(34,197,94,0.2)'}`;
    const thBg = isDarkMode ? 'rgba(34,197,94,0.08)' : 'rgba(240,253,244,0.6)';
    const rowHover = isDarkMode ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.03)';

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#22C55E' }} />
            <p style={{ marginTop: '1rem', color: textSecondary }}>Loading admin users...</p>
          </div>
        ) : (
          <>
            {/* Header Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              borderRadius: '20px', padding: '1.75rem 2rem', marginBottom: '1.5rem',
              boxShadow: '0 8px 32px rgba(34,197,94,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem'
            }}>
              <div>
                <h2 style={{ color: '#fff', fontSize: '1.75rem', fontWeight: '800', margin: 0 }}>
                  <FontAwesomeIcon icon={faUserShield} style={{ marginRight: '0.75rem', opacity: 0.9 }} />
                  Admin Management
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', margin: '0.4rem 0 0' }}>
                  Manage administrator accounts, roles, and permissions
                </p>
              </div>
              <button
                onClick={handleCreateAdmin}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)',
                  color: '#fff', padding: '0.65rem 1.25rem', borderRadius: '50px', cursor: 'pointer',
                  fontWeight: '700', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
                }}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Admin
              </button>
            </div>

            {/* Admins Table Card */}
            <div style={{
              background: cardBg, borderRadius: '20px', overflow: 'hidden',
              border: `1px solid ${cardBorder}`, backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`
            }}>
              {admins.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                  <div style={{
                    width: '72px', height: '72px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 1.25rem', boxShadow: '0 8px 32px rgba(34,197,94,0.3)'
                  }}>
                    <FontAwesomeIcon icon={faUserShield} style={{ color: '#fff', fontSize: '1.75rem' }} />
                  </div>
                  <h3 style={{ color: textPrimary, fontWeight: '700', marginBottom: '0.5rem' }}>No Admins Added Yet</h3>
                  <p style={{ color: textSecondary }}>Click "Add Admin" to invite a team member.</p>
                </div>
              ) : (
                <AdminTable minWidth="720px" columns={[{ width: '26%' }, { width: '20%' }, { width: '13%' }, { width: '17%' }, { width: '12%' }, { width: '12%' }]}>
                  <AdminTableHead>
                    <AdminTh>Admin</AdminTh>
                    <AdminTh>Email</AdminTh>
                    <AdminTh>Role</AdminTh>
                    <AdminTh>Last Login</AdminTh>
                    <AdminTh>Status</AdminTh>
                    <AdminTh>Actions</AdminTh>
                  </AdminTableHead>
                  <tbody>
                    {admins.map((admin, idx) => {
                      const fullName = `${admin.profile?.first_name || ''} ${admin.profile?.last_name || ''}`.trim() || admin.username || 'Admin';
                      const initials = fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                      const isActive = !admin.status || admin.status === 'active';
                      return (
                        <AdminTr key={admin._id || admin.id}>
                          <AdminTd>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <div style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.8rem', fontWeight: '800', color: '#fff', flexShrink: 0
                              }}>
                                {initials}
                              </div>
                              <div>
                                <div className="tw-font-bold tw-text-[0.9rem]">{fullName}</div>
                                <div className="tw-text-[0.75rem] tw-opacity-60">{admin.phone || 'N/A'}</div>
                              </div>
                            </div>
                          </AdminTd>
                          <AdminTd className="tw-opacity-75 tw-text-[0.875rem]">{admin.email || 'N/A'}</AdminTd>
                          <AdminTd>
                            <AdminBadge tone="green">{admin.admin_role || 'Admin'}</AdminBadge>
                          </AdminTd>
                          <AdminTd className="tw-opacity-75 tw-text-[0.875rem]">
                            {admin.last_login ? format(new Date(admin.last_login), 'MMM dd, yyyy HH:mm') : 'Never'}
                          </AdminTd>
                          <AdminTd>
                            <AdminBadge tone={isActive ? 'green' : 'red'}>{admin.status || 'active'}</AdminBadge>
                          </AdminTd>
                          <AdminTd>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <AdminIconBtn onClick={() => handleEditAdmin(admin)} title="Edit admin" tone="green">
                                <FontAwesomeIcon icon={faEdit} />
                              </AdminIconBtn>
                              <AdminIconBtn onClick={() => handleDeactivateAdmin(admin._id || admin.id)} title="Deactivate admin" tone="red">
                                <FontAwesomeIcon icon={faTrash} />
                              </AdminIconBtn>
                            </div>
                          </AdminTd>
                        </AdminTr>
                      );
                    })}
                  </tbody>
                </AdminTable>
              )}
            </div>

            {/* Admin Form Modal */}
            {showAdminModal && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
              }} onClick={() => setShowAdminModal(false)}>
                <div style={{
                  background: isDarkMode ? 'rgba(15,15,20,0.98)' : '#fff',
                  borderRadius: '24px', overflow: 'hidden',
                  maxWidth: '500px', width: '90%',
                  boxShadow: '0 30px 80px rgba(0,0,0,0.4)',
                  border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)'}`
                }} onClick={(e) => e.stopPropagation()}>
                  <div style={{ height: '4px', background: 'linear-gradient(90deg, #22C55E, #16A34A)' }} />
                  <div style={{ padding: '2rem' }}>
                    <h2 style={{ marginBottom: '1.5rem', fontSize: '1.4rem', fontWeight: '800', color: textPrimary }}>
                      <FontAwesomeIcon icon={editingAdmin ? faEdit : faPlus} style={{ color: '#22C55E', marginRight: '0.75rem' }} />
                      {editingAdmin ? 'Edit Admin Role' : 'Create Admin'}
                    </h2>
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      {!editingAdmin && (
                        <>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email *</label>
                            <input type="email" placeholder="admin@autoboy.com" value={adminFormData.email}
                              onChange={(e) => setAdminFormData({ ...adminFormData, email: e.target.value })}
                              style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: inputBorder, background: inputBg, color: textPrimary, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                            />
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>First Name *</label>
                              <input type="text" placeholder="John" value={adminFormData.first_name}
                                onChange={(e) => setAdminFormData({ ...adminFormData, first_name: e.target.value })}
                                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: inputBorder, background: inputBg, color: textPrimary, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last Name *</label>
                              <input type="text" placeholder="Doe" value={adminFormData.last_name}
                                onChange={(e) => setAdminFormData({ ...adminFormData, last_name: e.target.value })}
                                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: inputBorder, background: inputBg, color: textPrimary, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                              />
                            </div>
                          </div>
                          <p style={{ fontSize: '0.78rem', color: textSecondary, margin: 0 }}>
                            A temporary password will be generated automatically and shown once after creation.
                          </p>
                        </>
                      )}
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role *</label>
                        <select value={adminFormData.admin_role}
                          onChange={(e) => setAdminFormData({ ...adminFormData, admin_role: e.target.value })}
                          style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '12px', border: inputBorder, background: inputBg, color: textPrimary, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}>
                          <option value="moderator">Moderator</option>
                          <option value="support_admin">Support Admin</option>
                          <option value="staff_admin">Staff Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setShowAdminModal(false)}
                        style={{ padding: '0.7rem 1.5rem', borderRadius: '12px', border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.25)'}`, background: 'transparent', color: '#22C55E', fontWeight: '600', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveAdmin}
                        disabled={savingAdmin}
                        style={{ padding: '0.7rem 1.5rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#fff', fontWeight: '700', cursor: savingAdmin ? 'not-allowed' : 'pointer', opacity: savingAdmin ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 15px rgba(34,197,94,0.35)' }}
                      >
                        <FontAwesomeIcon icon={savingAdmin ? faSpinner : faSave} spin={savingAdmin} /> {editingAdmin ? 'Update' : 'Create'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Temp Password Reveal Modal */}
            {tempAdminPassword && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100
              }}>
                <div style={{
                  background: isDarkMode ? 'rgba(15,15,20,0.98)' : '#fff',
                  borderRadius: '24px', overflow: 'hidden', maxWidth: '440px', width: '90%',
                  boxShadow: '0 30px 80px rgba(0,0,0,0.4)',
                  border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)'}`
                }}>
                  <div style={{ height: '4px', background: 'linear-gradient(90deg, #22C55E, #16A34A)' }} />
                  <div style={{ padding: '2rem' }}>
                    <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', fontWeight: '800', color: textPrimary }}>
                      <FontAwesomeIcon icon={faShieldAlt} style={{ color: '#22C55E', marginRight: '0.6rem' }} />
                      Admin Created
                    </h2>
                    <p style={{ fontSize: '0.85rem', color: textSecondary, marginBottom: '1.25rem' }}>
                      Share this temporary password with <strong>{tempAdminPassword.email}</strong> securely — it will not be shown again. They should change it after signing in.
                    </p>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.9rem 1rem', borderRadius: '12px',
                      background: isDarkMode ? 'rgba(34,197,94,0.08)' : 'rgba(240,253,244,0.8)',
                      border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.25)' : 'rgba(34,197,94,0.2)'}`,
                      fontFamily: 'monospace', fontSize: '1.05rem', fontWeight: '700', color: '#16A34A',
                      justifyContent: 'space-between'
                    }}>
                      <span>{tempAdminPassword.password}</span>
                      <button
                        onClick={() => { navigator.clipboard?.writeText(tempAdminPassword.password); showToast('Password copied', 'success'); }}
                        style={{ padding: '0.4rem 0.7rem', borderRadius: '8px', border: 'none', background: '#22C55E', color: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700' }}
                      >
                        Copy
                      </button>
                    </div>
                    <button
                      onClick={() => setTempAdminPassword(null)}
                      style={{ width: '100%', marginTop: '1.5rem', padding: '0.7rem 1.5rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#fff', fontWeight: '700', cursor: 'pointer' }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderSettingsContent = () => {
    const cardBg = isDarkMode ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)';
    const cardBorder = isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)';
    const textPrimary = isDarkMode ? '#F3F4F6' : '#1F2937';
    const textSecondary = isDarkMode ? '#9CA3AF' : '#6B7280';
    const inputBg = isDarkMode ? 'rgba(0,0,0,0.3)' : '#f9fafb';
    const inputBorderStyle = `1px solid ${isDarkMode ? 'rgba(34,197,94,0.25)' : 'rgba(34,197,94,0.2)'}`;
    const thBg = isDarkMode ? 'rgba(34,197,94,0.08)' : 'rgba(240,253,244,0.6)';

    const inputStyle = { width: '100%', padding: '0.7rem 1rem', borderRadius: '12px', border: inputBorderStyle, background: inputBg, color: textPrimary, fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' };
    const selectStyle = { ...inputStyle };
    const labelStyle = { display: 'block', fontSize: '0.78rem', fontWeight: '700', marginBottom: '0.4rem', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' };

    const ToggleSwitch = ({ checked, onChange }) => (
      <div
        onClick={() => onChange({ target: { checked: !checked } })}
        style={{
          width: '48px', height: '26px', borderRadius: '13px', cursor: 'pointer', position: 'relative', flexShrink: 0,
          background: checked ? 'linear-gradient(135deg, #22C55E, #16A34A)' : isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
          transition: 'background 0.3s', boxShadow: checked ? '0 2px 8px rgba(34,197,94,0.4)' : 'none'
        }}
      >
        <div style={{
          position: 'absolute', top: '3px', left: checked ? '25px' : '3px',
          width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
          transition: 'left 0.3s', boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
        }} />
      </div>
    );

    const SectionCard = ({ icon, title, children }) => (
      <div style={{
        background: cardBg, borderRadius: '20px', overflow: 'hidden',
        border: `1px solid ${cardBorder}`, backdropFilter: 'blur(20px)',
        boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`
      }}>
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.08)'}`,
          background: isDarkMode ? 'rgba(34,197,94,0.06)' : 'rgba(240,253,244,0.5)',
          display: 'flex', alignItems: 'center', gap: '0.6rem'
        }}>
          <FontAwesomeIcon icon={icon} style={{ color: '#22C55E' }} />
          <h3 style={{ margin: 0, fontWeight: '800', color: textPrimary, fontSize: '0.95rem' }}>{title}</h3>
        </div>
        <div style={{ padding: '1.25rem', display: 'grid', gap: '1rem' }}>{children}</div>
      </div>
    );

    const SettingsRow = ({ label, children }) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <span style={{ fontSize: '0.875rem', color: textPrimary, fontWeight: '500', flex: 1 }}>{label}</span>
        <div style={{ flexShrink: 0 }}>{children}</div>
      </div>
    );

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {/* Header Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
          borderRadius: '20px', padding: '1.75rem 2rem', marginBottom: '1.5rem',
          boxShadow: '0 8px 32px rgba(34,197,94,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem'
        }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: '1.75rem', fontWeight: '800', margin: 0 }}>
              <FontAwesomeIcon icon={faCog} style={{ marginRight: '0.75rem', opacity: 0.9 }} />
              System Settings
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', margin: '0.4rem 0 0' }}>
              Configure platform behaviour, transaction fees, security, and logistics
            </p>
          </div>
          <button
            onClick={handleSaveSettings}
            disabled={settingsLoading}
            style={{
              background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)',
              color: '#fff', padding: '0.65rem 1.25rem', borderRadius: '50px', cursor: 'pointer',
              fontWeight: '700', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
            }}
          >
            <FontAwesomeIcon icon={settingsLoading ? faSpinner : faSave} spin={settingsLoading} />
            {settingsLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Settings Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem', marginBottom: '1.5rem' }}>
          <SectionCard icon={faCog} title="General Settings">
            <div>
              <label style={labelStyle}>Platform Name</label>
              <input type="text" value={settingsData.general.platform_name}
                onChange={(e) => setSettingsData({ ...settingsData, general: { ...settingsData.general, platform_name: e.target.value } })}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Support Email</label>
              <input type="email" value={settingsData.general.support_email}
                onChange={(e) => setSettingsData({ ...settingsData, general: { ...settingsData.general, support_email: e.target.value } })}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Support Phone</label>
              <input type="text" value={settingsData.general.support_phone}
                onChange={(e) => setSettingsData({ ...settingsData, general: { ...settingsData.general, support_phone: e.target.value } })}
                style={inputStyle} />
            </div>
            <SettingsRow label="Maintenance Mode">
              <ToggleSwitch checked={settingsData.general.maintenance_mode}
                onChange={(e) => setSettingsData({ ...settingsData, general: { ...settingsData.general, maintenance_mode: e.target.checked } })} />
            </SettingsRow>
            {settingsData.general.maintenance_mode && (
              <div>
                <label style={labelStyle}>Maintenance Message</label>
                <input type="text" placeholder="We'll be back shortly" value={settingsData.general.maintenance_message}
                  onChange={(e) => setSettingsData({ ...settingsData, general: { ...settingsData.general, maintenance_message: e.target.value } })}
                  style={inputStyle} />
              </div>
            )}
          </SectionCard>

          <SectionCard icon={faDollarSign} title="Payment Settings">
            <div>
              <label style={labelStyle}>Transaction Fee (%)</label>
              <input type="number" step="0.1" value={settingsData.payment.commission_rate}
                onChange={(e) => setSettingsData({ ...settingsData, payment: { ...settingsData.payment, commission_rate: parseFloat(e.target.value) || 0 } })}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Escrow Fee (%)</label>
              <input type="number" step="0.1" value={settingsData.payment.escrow_fee}
                onChange={(e) => setSettingsData({ ...settingsData, payment: { ...settingsData.payment, escrow_fee: parseFloat(e.target.value) || 0 } })}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Minimum Transaction (₦)</label>
              <input type="number" value={settingsData.payment.minimum_transaction}
                onChange={(e) => setSettingsData({ ...settingsData, payment: { ...settingsData.payment, minimum_transaction: parseInt(e.target.value) || 0 } })}
                style={inputStyle} />
            </div>
          </SectionCard>

          <SectionCard icon={faShieldAlt} title="Security Settings">
            <SettingsRow label="2FA Required for Admins">
              <ToggleSwitch checked={settingsData.security.two_fa_required}
                onChange={(e) => setSettingsData({ ...settingsData, security: { ...settingsData.security, two_fa_required: e.target.checked } })} />
            </SettingsRow>
            <div>
              <label style={labelStyle}>Session Timeout (minutes)</label>
              <input type="number" value={settingsData.security.session_timeout}
                onChange={(e) => setSettingsData({ ...settingsData, security: { ...settingsData.security, session_timeout: parseInt(e.target.value) || 30 } })}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>IP Whitelist (comma-separated)</label>
              <input type="text" placeholder="e.g. 41.58.0.1, 102.89.0.1" value={settingsData.security.ip_whitelist}
                onChange={(e) => setSettingsData({ ...settingsData, security: { ...settingsData.security, ip_whitelist: e.target.value } })}
                style={inputStyle} />
            </div>
          </SectionCard>

          <SectionCard icon={faTruck} title="Logistics Settings">
            <div>
              <label style={labelStyle}>FabsTech API Key</label>
              <input type="password" value={settingsData.logistics.fabstech_api_key}
                onChange={(e) => setSettingsData({ ...settingsData, logistics: { ...settingsData.logistics, fabstech_api_key: e.target.value } })}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>FabsTech Base URL</label>
              <input type="text" value={settingsData.logistics.fabstech_base_url}
                onChange={(e) => setSettingsData({ ...settingsData, logistics: { ...settingsData.logistics, fabstech_base_url: e.target.value } })}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Base Shipping Rate (₦)</label>
              <input type="number" value={settingsData.logistics.base_shipping_rate}
                onChange={(e) => setSettingsData({ ...settingsData, logistics: { ...settingsData.logistics, base_shipping_rate: parseFloat(e.target.value) || 0 } })}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Price Per KM (₦)</label>
              <input type="number" value={settingsData.logistics.price_per_km}
                onChange={(e) => setSettingsData({ ...settingsData, logistics: { ...settingsData.logistics, price_per_km: parseFloat(e.target.value) || 0 } })}
                style={inputStyle} />
            </div>
          </SectionCard>
        </div>

        {/* 2FA Section */}
        <div style={{
          background: cardBg, borderRadius: '20px', overflow: 'hidden',
          border: `1px solid ${cardBorder}`, backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px rgba(0,0,0,${isDarkMode ? '0.4' : '0.08'})`,
          marginBottom: '1.5rem'
        }}>
          <div style={{
            padding: '1rem 1.5rem',
            borderBottom: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.08)'}`,
            background: isDarkMode ? 'rgba(34,197,94,0.06)' : 'rgba(240,253,244,0.5)',
            display: 'flex', alignItems: 'center', gap: '0.6rem'
          }}>
            <FontAwesomeIcon icon={faShieldAlt} style={{ color: '#22C55E' }} />
            <h3 style={{ margin: 0, fontWeight: '800', color: textPrimary, fontSize: '0.95rem' }}>Two-Factor Authentication</h3>
          </div>
          <div style={{ padding: '1.25rem' }}>
            <TwoFactorAuth isDarkMode={isDarkMode} />
          </div>
        </div>

        {/* Danger Zone */}
        <div style={{ padding: '1.75rem', background: 'rgba(239,68,68,0.05)', borderRadius: '20px', border: '1px solid rgba(239,68,68,0.12)' }}>
          <h3 style={{ color: '#dc2626', marginBottom: '0.75rem', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} /> Danger Zone
          </h3>
          <p style={{ marginBottom: '1.25rem', color: textSecondary, fontSize: '0.9rem' }}>
            Seeds default categories, a starter admin account, and default settings — but only on a fresh database. It safely no-ops and does nothing if an admin account already exists, so it will not touch or reset your existing data.
          </p>
          <button
            onClick={handleInitDatabase}
            disabled={initializingSystem}
            style={{
              padding: '0.7rem 1.5rem', borderRadius: '12px', border: '1px solid rgba(239,68,68,0.3)',
              background: 'rgba(239,68,68,0.1)', color: '#dc2626', fontWeight: '700', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem'
            }}
          >
            <FontAwesomeIcon icon={initializingSystem ? faSpinner : faHistory} spin={initializingSystem} />
            {initializingSystem ? 'Initializing...' : 'Initialize System Database'}
          </button>
        </div>
      </div>
    );
  };

  // ===== MARKETING HANDLER FUNCTIONS =====
  const handleCreateBanner = () => {
    setBannerForm({ type: 'hero', title: '', subtitle: '', cta_text: '', cta_link: '', image: '', is_active: true });
    setShowBannerModal(true);
  };

  const handleBannerImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const valid = validateImageFile(file);
    if (!valid.valid) { showToast(valid.error, 'error'); return; }
    setBannerUploading(true);
    try {
      // Direct Cloudinary upload (preset autoboy_uploads) with ImgBB/Imgur
      // fallbacks — the same robust path used for product images. The backend
      // /upload route was failing, so we bypass it.
      const { url } = await uploadImage(file);
      if (!url) throw new Error('No URL returned');
      setBannerForm(prev => ({ ...prev, image: url }));
      showToast('Image uploaded', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to upload image', 'error');
    } finally {
      setBannerUploading(false);
    }
  };

  const submitBanner = async () => {
    if (!bannerForm.title.trim()) return showToast('Banner title is required', 'error');
    if (!bannerForm.image) return showToast('Please upload a banner image', 'error');
    setMarketingSaving(true);
    try {
      await adminAPI.createBanner({
        type: bannerForm.type,
        title: bannerForm.title.trim(),
        subtitle: bannerForm.subtitle.trim(),
        cta_text: bannerForm.cta_text.trim(),
        cta_link: bannerForm.cta_link.trim(),
        image: bannerForm.image,
        is_active: bannerForm.is_active,
      });
      showToast('Banner created successfully', 'success');
      setShowBannerModal(false);
      fetchMarketingData();
    } catch (e) { showToast('Failed to create banner', 'error'); }
    finally { setMarketingSaving(false); }
  };

  const handleDeleteBanner = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await adminAPI.deleteBanner(id);
      showToast('Banner deleted', 'success');
      fetchMarketingData();
    } catch (e) { showToast('Failed to delete banner', 'error'); }
  };

  const handleCreateFlashSale = () => {
    setFlashForm({ title: '', description: '', discount_percentage: '', start_time: '', end_time: '', early_access_hours: '24', image: '', is_active: true });
    setShowFlashModal(true);
  };

  const handleFlashImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const valid = validateImageFile(file);
    if (!valid.valid) { showToast(valid.error, 'error'); return; }
    setFlashUploading(true);
    try {
      const { url } = await uploadImage(file);
      if (!url) throw new Error('No URL returned');
      setFlashForm(prev => ({ ...prev, image: url }));
      showToast('Image uploaded', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to upload image', 'error');
    } finally {
      setFlashUploading(false);
    }
  };

  const submitFlashSale = async () => {
    if (!flashForm.title.trim()) return showToast('Campaign title is required', 'error');
    const pct = parseFloat(flashForm.discount_percentage);
    if (isNaN(pct) || pct <= 0) return showToast('Enter a valid discount percentage', 'error');
    if (!flashForm.start_time || !flashForm.end_time) return showToast('Start and end time are required', 'error');
    setMarketingSaving(true);
    try {
      await adminAPI.createFlashSale({
        title: flashForm.title.trim(),
        description: flashForm.description.trim(),
        // Backend ExclusiveDeal model uses discount_percent + start_date/end_date
        // (verified against the live API — NOT discount_percentage/start_time/end_time).
        discount_percent: pct,
        start_date: new Date(flashForm.start_time).toISOString(),
        end_date: new Date(flashForm.end_time).toISOString(),
        // Premium members see this banner from start_date; everyone else only
        // after start_date + early_access_hours ("a day or two" head start).
        early_access_hours: parseInt(flashForm.early_access_hours) || 0,
        banner_image: flashForm.image,
        is_active: flashForm.is_active,
      });
      showToast('Sales campaign created', 'success');
      setShowFlashModal(false);
      fetchMarketingData();
    } catch (e) { showToast('Failed to create sales campaign', 'error'); }
    finally { setMarketingSaving(false); }
  };

  const handleDeleteFlashSale = async (id) => {
    if (!window.confirm('Delete this sales campaign?')) return;
    try {
      await adminAPI.deleteFlashSale(id);
      showToast('Sales campaign deleted', 'success');
      fetchMarketingData();
    } catch (e) { showToast('Failed to delete sales campaign', 'error'); }
  };

  const handleCreatePromoCode = () => {
    setPromoForm({ code: '', type: 'percentage', discount_value: '', min_order_amount: '', max_uses: '', max_uses_per_user: '', start_date: '', end_date: '', is_active: true });
    setShowPromoModal(true);
  };

  const submitPromoCode = async () => {
    if (!promoForm.code.trim()) return showToast('Promo code is required', 'error');
    const val = parseFloat(promoForm.discount_value);
    if (isNaN(val) || val <= 0) return showToast('Enter a valid discount value', 'error');
    setMarketingSaving(true);
    try {
      {
        const maxUses = parseInt(promoForm.max_uses) || 0;
        const perUser = parseInt(promoForm.max_uses_per_user) || 0;
        await adminAPI.createPromoCode({
          code: promoForm.code.trim().toUpperCase(),
          // models.PromoCode.Name is a required field the form never
          // collects a separate value for — the code itself is a reasonable
          // default rather than saving every promo with an empty name.
          name: promoForm.code.trim().toUpperCase(),
          type: promoForm.type,
          discount_value: val,
          min_order_amount: parseFloat(promoForm.min_order_amount) || 0,
          // The actual model fields are usage_limit/user_usage_limit
          // (models/promo.go) — this used to send max_total_uses/max_uses/
          // max_uses_per_user, none of which bind to anything, so every
          // promo code was silently created with no usage cap at all
          // regardless of what an admin entered here.
          usage_limit: maxUses,
          user_usage_limit: perUser,
          start_date: promoForm.start_date ? new Date(promoForm.start_date).toISOString() : null,
          end_date: promoForm.end_date ? new Date(promoForm.end_date).toISOString() : null,
          is_active: promoForm.is_active,
        });
      }
      showToast('Promo code created', 'success');
      setShowPromoModal(false);
      fetchMarketingData();
    } catch (e) { showToast('Failed to create promo code', 'error'); }
    finally { setMarketingSaving(false); }
  };

  const handleTogglePromo = async (id, currentStatus) => {
    try {
      await adminAPI.togglePromoStatus(id, !currentStatus);
      showToast('Promo code status updated', 'success');
      fetchMarketingData();
    } catch (e) { showToast('Failed to update promo code', 'error'); }
  };

  const handleDeletePromoCode = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      await adminAPI.deletePromoCode(id);
      showToast('Promo code deleted', 'success');
      fetchMarketingData();
    } catch (e) { showToast('Failed to delete promo code', 'error'); }
  };

  const handleSendBulkNotification = async (e) => {
    e.preventDefault();
    const form = e.target;
    const title = form.notifTitle.value.trim();
    const message = form.notifMessage.value.trim();
    const user_type = form.notifTarget.value || 'all';
    if (!title || !message) return showToast('Title and message are required', 'error');
    try {
      const result = await adminAPI.sendBulkNotification({ title, message, user_type });
      const sentTo = result?.data?.sent_to ?? result?.sent_to ?? 0;
      showToast(`Notification sent to ${sentTo} user${sentTo !== 1 ? 's' : ''}`, 'success');
      form.reset();
      fetchCommunicationData();
    } catch (err) { showToast('Failed to send notification', 'error'); }
  };

  const renderMarketingContent = () => {
    const cardBg = isDarkMode ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.92)';
    const cardBorder = isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(22,163,74,0.12)';
    const textPrimary = isDarkMode ? '#f0fdf4' : '#14532d';
    const textSecondary = isDarkMode ? 'rgba(240,253,244,0.6)' : 'rgba(20,83,45,0.6)';
    const rowBg = isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(34,197,94,0.04)';
    const theadBg = isDarkMode ? 'rgba(34,197,94,0.08)' : 'rgba(22,163,74,0.06)';
    const inputBorder = isDarkMode ? 'rgba(34,197,94,0.3)' : 'rgba(22,163,74,0.3)';
    const inputBg = isDarkMode ? 'rgba(0,0,0,0.25)' : '#fff';

    const tabs = [
      { id: 'banners', label: 'Banners', icon: faImage },
      { id: 'flash-sales', label: 'Sales Campaigns', icon: faBolt },
      { id: 'promo-codes', label: 'Promo Codes', icon: faPercentage },
    ];

    const EmptyState = ({ icon, title, hint }) => (
      <div style={{ textAlign: 'center', padding: '4rem 2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
        <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(22,163,74,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(34,197,94,0.2)' }}>
          <FontAwesomeIcon icon={icon} style={{ fontSize: '1.75rem', color: '#22C55E' }} />
        </div>
        <div>
          <div style={{ fontWeight: '700', fontSize: '1.05rem', color: textPrimary, marginBottom: '0.35rem' }}>{title}</div>
          <div style={{ fontSize: '0.875rem', color: textSecondary }}>{hint}</div>
        </div>
      </div>
    );

    return (
      <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
        {/* ── Header Banner ── */}
        <div style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', borderRadius: '20px', padding: '2rem 2.5rem', marginBottom: '2rem', boxShadow: '0 12px 40px rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', minHeight: '90px' }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: '1.875rem', fontWeight: '800', margin: 0, letterSpacing: '-0.02em' }}>
              <FontAwesomeIcon icon={faBullhorn} style={{ marginRight: '0.75rem', opacity: 0.9 }} />
              Marketing &amp; Promotions
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', margin: '0.4rem 0 0' }}>Manage banners, sales campaigns, and promotional codes</p>
          </div>
          <button onClick={marketingSubTab === 'banners' ? handleCreateBanner : marketingSubTab === 'flash-sales' ? handleCreateFlashSale : handleCreatePromoCode}
            style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', borderRadius: '50px', padding: '0.65rem 1.5rem', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', backdropFilter: 'blur(10px)' }}>
            <FontAwesomeIcon icon={faPlus} /> Create New
          </button>
        </div>

        {/* ── Pill Tabs ── */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.75rem', flexWrap: 'wrap', padding: '0.5rem', background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', borderRadius: '50px', width: 'fit-content' }}>
          {tabs.map(tab => {
            const active = marketingSubTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setMarketingSubTab(tab.id)}
                style={{ padding: '0.6rem 1.4rem', borderRadius: '50px', border: active ? 'none' : `1px solid ${cardBorder}`, background: active ? 'linear-gradient(135deg, #22C55E, #16A34A)' : 'transparent', color: active ? '#fff' : textSecondary, fontWeight: active ? '700' : '500', fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.2s ease', boxShadow: active ? '0 4px 15px rgba(34,197,94,0.3)' : 'none' }}>
                <FontAwesomeIcon icon={tab.icon} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: textSecondary }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2.5rem', color: '#22C55E', marginBottom: '1rem' }} />
            <p style={{ marginTop: '1rem', fontWeight: '500' }}>Loading marketing data...</p>
          </div>
        ) : (
          <>
            {/* ===== BANNERS ===== */}
            {marketingSubTab === 'banners' && (
              <div style={{ background: cardBg, backdropFilter: 'blur(20px)', borderRadius: '20px', border: `1px solid ${cardBorder}`, padding: '1.75rem', boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(34,197,94,0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: textPrimary }}>Active Banners</h3>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: textSecondary }}>{banners.length} banner{banners.length !== 1 ? 's' : ''} configured</p>
                  </div>
                  <button onClick={handleCreateBanner}
                    style={{ background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', border: 'none', borderRadius: '50px', padding: '0.6rem 1.25rem', fontWeight: '700', fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 15px rgba(34,197,94,0.3)' }}>
                    <FontAwesomeIcon icon={faPlus} /> Add Banner
                  </button>
                </div>
                {banners.length === 0 ? (
                  <EmptyState icon={faImage} title="No banners configured" hint="Add your first promotional banner to boost visibility on the storefront." />
                ) : (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {banners.map(banner => (
                      <div key={banner.id || banner._id}
                        style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', padding: '1.1rem 1.25rem', background: rowBg, borderRadius: '14px', border: `1px solid ${cardBorder}`, transition: 'all 0.2s ease' }}>
                        {(banner.image_url || banner.image)
                          ? <img src={banner.image_url || banner.image} alt={banner.title} style={{ width: '90px', height: '56px', objectFit: 'cover', borderRadius: '10px', flexShrink: 0, border: '1px solid rgba(255,255,255,0.08)' }} onError={e => { e.target.style.display = 'none'; }} />
                          : <div style={{ width: '90px', height: '56px', borderRadius: '10px', background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(22,163,74,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FontAwesomeIcon icon={faImage} style={{ color: '#22C55E', fontSize: '1.25rem' }} /></div>
                        }
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '700', fontSize: '0.95rem', color: textPrimary, marginBottom: '0.25rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{banner.title}</div>
                          {banner.cta_link && <div style={{ fontSize: '0.78rem', color: textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{banner.cta_link}</div>}
                        </div>
                        <span style={{ padding: '0.3rem 0.8rem', borderRadius: '50px', fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.05em', background: banner.is_active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.1)', color: banner.is_active ? '#22C55E' : '#ef4444', border: `1px solid ${banner.is_active ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.2)'}`, flexShrink: 0 }}>
                          {banner.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                        <button onClick={() => handleDeleteBanner(banner.id || banner._id)} title="Delete banner"
                          style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', borderRadius: '10px', padding: '0.5rem 0.75rem', cursor: 'pointer', flexShrink: 0, transition: 'all 0.2s ease' }}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ===== SALES CAMPAIGNS (sitewide/category promotions — NOT tied to a
                 specific product; a promotional banner shown on the homepage,
                 not a product card. To feature a discounted PRODUCT, use
                 Products -> Flash Deal instead.) ===== */}
            {marketingSubTab === 'flash-sales' && (
              <div style={{ background: cardBg, backdropFilter: 'blur(20px)', borderRadius: '20px', border: `1px solid ${cardBorder}`, padding: '1.75rem', boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(34,197,94,0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: textPrimary }}>Sales Campaigns</h3>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: textSecondary }}>{flashSales.length} campaign{flashSales.length !== 1 ? 's' : ''} total &middot; a promotional banner, not a specific product</p>
                  </div>
                  <button onClick={handleCreateFlashSale}
                    style={{ background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', border: 'none', borderRadius: '50px', padding: '0.6rem 1.25rem', fontWeight: '700', fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 15px rgba(34,197,94,0.3)' }}>
                    <FontAwesomeIcon icon={faPlus} /> Create Sales Campaign
                  </button>
                </div>
                {flashSales.length === 0 ? (
                  <EmptyState icon={faBolt} title="No sales campaigns running" hint="Create a time-limited promotional banner to drive urgency and boost conversions. To discount a specific product instead, use Products -> Flash Deal." />
                ) : (
                  <AdminTable minWidth="820px" columns={[{ width: '26%' }, { width: '12%' }, { width: '15%' }, { width: '15%' }, { width: '14%' }, { width: '9%' }, { width: '9%' }]}>
                    <AdminTableHead>
                      <AdminTh>Title</AdminTh>
                      <AdminTh>Discount</AdminTh>
                      <AdminTh>Starts</AdminTh>
                      <AdminTh>Ends</AdminTh>
                      <AdminTh>Early Access</AdminTh>
                      <AdminTh>Status</AdminTh>
                      <AdminTh>Actions</AdminTh>
                    </AdminTableHead>
                    <tbody>
                      {flashSales.map(sale => (
                        <AdminTr key={sale.id || sale._id}>
                          <AdminTd className="tw-font-semibold">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                              {sale.banner_image
                                ? <img src={sale.banner_image} alt="" style={{ width: '44px', height: '32px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                                : <div style={{ width: '44px', height: '32px', borderRadius: '6px', background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(22,163,74,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FontAwesomeIcon icon={faBolt} style={{ color: '#22C55E', fontSize: '0.85rem' }} /></div>}
                              <span>{sale.title}</span>
                            </div>
                          </AdminTd>
                          <AdminTd><span className="tw-text-green-500 tw-font-extrabold">{sale.discount_percent ?? sale.discount_percentage ?? 0}% OFF</span></AdminTd>
                          <AdminTd className="tw-text-[0.85rem] tw-opacity-75">{(() => { const d = sale.start_date || sale.starts_at; return d && new Date(d).getFullYear() > 1 ? new Date(d).toLocaleString() : 'N/A'; })()}</AdminTd>
                          <AdminTd className="tw-text-[0.85rem] tw-opacity-75">{(() => { const d = sale.end_date || sale.ends_at; return d && new Date(d).getFullYear() > 1 ? new Date(d).toLocaleString() : 'N/A'; })()}</AdminTd>
                          <AdminTd className="tw-text-[0.85rem] tw-opacity-75">{sale.early_access_hours > 0 ? `${sale.early_access_hours}h for premium` : 'None'}</AdminTd>
                          <AdminTd>
                            <AdminBadge tone={sale.is_active ? 'green' : 'red'}>{sale.is_active ? 'ACTIVE' : 'ENDED'}</AdminBadge>
                          </AdminTd>
                          <AdminTd>
                            <AdminIconBtn onClick={() => handleDeleteFlashSale(sale.id || sale._id)} title="Delete" tone="red">
                              <FontAwesomeIcon icon={faTrash} />
                            </AdminIconBtn>
                          </AdminTd>
                        </AdminTr>
                      ))}
                    </tbody>
                  </AdminTable>
                )}
              </div>
            )}

            {/* ===== PROMO CODES ===== */}
            {marketingSubTab === 'promo-codes' && (
              <div style={{ background: cardBg, backdropFilter: 'blur(20px)', borderRadius: '20px', border: `1px solid ${cardBorder}`, padding: '1.75rem', boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(34,197,94,0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: textPrimary }}>Promo Codes</h3>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: textSecondary }}>{promoCodes.length} code{promoCodes.length !== 1 ? 's' : ''} configured</p>
                  </div>
                  <button onClick={handleCreatePromoCode}
                    style={{ background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', border: 'none', borderRadius: '50px', padding: '0.6rem 1.25rem', fontWeight: '700', fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 15px rgba(34,197,94,0.3)' }}>
                    <FontAwesomeIcon icon={faPlus} /> New Promo Code
                  </button>
                </div>
                {promoCodes.length === 0 ? (
                  <EmptyState icon={faPercentage} title="No promo codes yet" hint="Create discount codes to reward loyal customers and drive new sign-ups." />
                ) : (
                  <AdminTable minWidth="720px" columns={[{ width: '18%' }, { width: '18%' }, { width: '16%' }, { width: '16%' }, { width: '16%' }, { width: '16%' }]}>
                    <AdminTableHead>
                      <AdminTh>Code</AdminTh>
                      <AdminTh>Discount</AdminTh>
                      <AdminTh>Uses</AdminTh>
                      <AdminTh>Expires</AdminTh>
                      <AdminTh>Status</AdminTh>
                      <AdminTh>Actions</AdminTh>
                    </AdminTableHead>
                    <tbody>
                      {promoCodes.map(promo => (
                        <AdminTr key={promo.id || promo._id}>
                          <AdminTd>
                            <code className="tw-font-extrabold tw-text-green-500 tw-bg-green-500/10 tw-px-2 tw-py-1 tw-rounded-lg tw-tracking-wider tw-text-[0.875rem] tw-border tw-border-green-500/20">{promo.code}</code>
                          </AdminTd>
                          <AdminTd className="tw-font-bold">{promo.type === 'fixed' ? formatCurrency(promo.discount_value ?? 0) : `${promo.discount_value ?? promo.discount_percentage ?? 0}% OFF`}</AdminTd>
                          <AdminTd className="tw-opacity-75 tw-text-[0.875rem]">{(promo.usage_count ?? 0)}{(promo.usage_limit || 0) > 0 ? ` / ${promo.usage_limit}` : ' / ∞'}</AdminTd>
                          <AdminTd className="tw-opacity-75 tw-text-[0.875rem]">{(() => { const d = promo.end_date || promo.expires_at; return d && new Date(d).getFullYear() > 1 ? new Date(d).toLocaleDateString() : 'Never'; })()}</AdminTd>
                          <AdminTd>
                            <button onClick={() => handleTogglePromo(promo.id || promo._id, promo.is_active)} title="Toggle status"
                              className="tw-px-3 tw-py-1 tw-rounded-full tw-text-[0.72rem] tw-font-bold tw-cursor-pointer"
                              style={{ border: `1px solid ${promo.is_active ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.2)'}`, background: promo.is_active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.1)', color: promo.is_active ? '#22C55E' : '#ef4444' }}>
                              {promo.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </button>
                          </AdminTd>
                          <AdminTd>
                            <AdminIconBtn onClick={() => handleDeletePromoCode(promo.id || promo._id)} title="Delete" tone="red">
                              <FontAwesomeIcon icon={faTrash} />
                            </AdminIconBtn>
                          </AdminTd>
                        </AdminTr>
                      ))}
                    </tbody>
                  </AdminTable>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderCommunicationContent = () => {
    const cardBg = isDarkMode ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.92)';
    const cardBorder = isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(22,163,74,0.12)';
    const textPrimary = isDarkMode ? '#f0fdf4' : '#14532d';
    const textSecondary = isDarkMode ? 'rgba(240,253,244,0.6)' : 'rgba(20,83,45,0.6)';
    const rowBg = isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(34,197,94,0.04)';
    const theadBg = isDarkMode ? 'rgba(34,197,94,0.08)' : 'rgba(22,163,74,0.06)';
    const inputBorder = isDarkMode ? 'rgba(34,197,94,0.3)' : 'rgba(22,163,74,0.3)';
    const inputBg = isDarkMode ? 'rgba(0,0,0,0.25)' : '#fff';
    const labelStyle = { display: 'block', fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.875rem', color: textPrimary };
    const inputStyle = { width: '100%', padding: '0.85rem 1rem', borderRadius: '12px', border: `1px solid ${inputBorder}`, background: inputBg, fontSize: '0.95rem', color: textPrimary, outline: 'none', boxSizing: 'border-box' };

    const tabs = [
      { id: 'send', label: 'Send Notification', icon: faPaperPlane },
      { id: 'templates', label: 'Templates', icon: faFileAlt },
      { id: 'stats', label: 'Stats', icon: faChartLine },
    ];

    return (
      <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
        {/* ── Header Banner ── */}
        <div style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', borderRadius: '20px', padding: '2rem 2.5rem', marginBottom: '2rem', boxShadow: '0 12px 40px rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', minHeight: '90px' }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: '1.875rem', fontWeight: '800', margin: 0, letterSpacing: '-0.02em' }}>
              <FontAwesomeIcon icon={faBell} style={{ marginRight: '0.75rem', opacity: 0.9 }} />
              Communication &amp; Notifications
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', margin: '0.4rem 0 0' }}>Send notifications, manage templates, and view delivery stats</p>
          </div>
        </div>

        {/* ── Pill Tabs ── */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.75rem', flexWrap: 'wrap', padding: '0.5rem', background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', borderRadius: '50px', width: 'fit-content' }}>
          {tabs.map(tab => {
            const active = communicationSubTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setCommunicationSubTab(tab.id)}
                style={{ padding: '0.6rem 1.4rem', borderRadius: '50px', border: active ? 'none' : `1px solid ${cardBorder}`, background: active ? 'linear-gradient(135deg, #22C55E, #16A34A)' : 'transparent', color: active ? '#fff' : textSecondary, fontWeight: active ? '700' : '500', fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.2s ease', boxShadow: active ? '0 4px 15px rgba(34,197,94,0.3)' : 'none' }}>
                <FontAwesomeIcon icon={tab.icon} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: textSecondary }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2.5rem', color: '#22C55E', marginBottom: '1rem' }} />
            <p style={{ marginTop: '1rem', fontWeight: '500' }}>Loading communication data...</p>
          </div>
        ) : (
          <>
            {/* ===== SEND BULK NOTIFICATION ===== */}
            {communicationSubTab === 'send' && (() => {
              const NotifSendForm = () => {
                const [previewTitle, setPreviewTitle] = React.useState('');
                const [previewMsg, setPreviewMsg] = React.useState('');
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px,1fr) 280px', gap: '1.5rem', alignItems: 'start' }}>
                    {/* Form */}
                    <div style={{ background: cardBg, backdropFilter: 'blur(20px)', borderRadius: '20px', border: `1px solid ${cardBorder}`, padding: '2rem', boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(34,197,94,0.08)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.75rem' }}>
                        <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, #22C55E, #16A34A)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 15px rgba(34,197,94,0.35)' }}>
                          <FontAwesomeIcon icon={faPaperPlane} style={{ color: '#fff', fontSize: '1.1rem' }} />
                        </div>
                        <div>
                          <h3 style={{ margin: 0, fontWeight: '800', fontSize: '1.15rem', color: textPrimary }}>Send Bulk Notification</h3>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: textSecondary }}>Sends push notifications to all registered devices</p>
                        </div>
                      </div>
                      <form onSubmit={handleSendBulkNotification}>
                        <div style={{ marginBottom: '1.25rem' }}>
                          <label style={labelStyle}>Target Audience</label>
                          <select name="notifTarget" style={inputStyle}>
                            <option value="all">All Users</option>
                            <option value="buyers">Buyers</option>
                            <option value="sellers">Sellers</option>
                            <option value="premium">Premium Users</option>
                          </select>
                        </div>
                        <div style={{ marginBottom: '1.25rem' }}>
                          <label style={labelStyle}>Notification Title *</label>
                          <input type="text" name="notifTitle" required placeholder="e.g. New Feature Available!"
                            style={inputStyle} onChange={e => setPreviewTitle(e.target.value)} />
                        </div>
                        <div style={{ marginBottom: '1.75rem' }}>
                          <label style={labelStyle}>Message *</label>
                          <textarea name="notifMessage" required rows="4" placeholder="Write your notification message here..."
                            style={{ ...inputStyle, resize: 'vertical', minHeight: '100px', fontFamily: 'inherit' }}
                            onChange={e => setPreviewMsg(e.target.value)} />
                        </div>
                        <button type="submit"
                          style={{ width: '100%', padding: '0.9rem', borderRadius: '14px', border: 'none', background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', fontWeight: '800', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', boxShadow: '0 6px 20px rgba(34,197,94,0.4)' }}>
                          <FontAwesomeIcon icon={faPaperPlane} /> Send Notification
                        </button>
                      </form>
                    </div>

                    {/* Phone Preview */}
                    <div style={{ position: 'sticky', top: '1rem' }}>
                      <p style={{ fontSize: '0.78rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: textSecondary, marginBottom: '0.75rem', textAlign: 'center' }}>Preview</p>
                      <div style={{ width: '220px', margin: '0 auto', background: isDarkMode ? '#1a1a2e' : '#f1f5f9', borderRadius: '32px', padding: '2rem 0.75rem 1.5rem', border: `2px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                        {/* Status bar */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 0.5rem', marginBottom: '1rem', fontSize: '0.65rem', color: isDarkMode ? '#9CA3AF' : '#6B7280', fontWeight: '600' }}>
                          <span>9:41</span><span>●●●</span>
                        </div>
                        {/* Notification card */}
                        <div style={{ background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.95)', borderRadius: '16px', padding: '0.85rem', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                            <div style={{ width: '20px', height: '20px', borderRadius: '6px', background: 'linear-gradient(135deg, #22C55E, #16A34A)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <FontAwesomeIcon icon={faBell} style={{ color: '#fff', fontSize: '0.55rem' }} />
                            </div>
                            <span style={{ fontSize: '0.6rem', fontWeight: '700', color: isDarkMode ? '#9CA3AF' : '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>AutoBoy</span>
                            <span style={{ fontSize: '0.55rem', color: isDarkMode ? '#6B7280' : '#9CA3AF', marginLeft: 'auto' }}>now</span>
                          </div>
                          <div style={{ fontSize: '0.72rem', fontWeight: '800', color: isDarkMode ? '#F3F4F6' : '#1F2937', marginBottom: '0.2rem', lineHeight: 1.3 }}>
                            {previewTitle || 'Notification Title'}
                          </div>
                          <div style={{ fontSize: '0.65rem', color: isDarkMode ? '#9CA3AF' : '#6B7280', lineHeight: 1.4 }}>
                            {previewMsg || 'Your message will appear here as users will see it on their devices.'}
                          </div>
                        </div>
                        {/* Home bar */}
                        <div style={{ width: '60px', height: '4px', background: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)', borderRadius: '2px', margin: '1.25rem auto 0' }} />
                      </div>
                      <p style={{ fontSize: '0.72rem', color: textSecondary, textAlign: 'center', marginTop: '0.75rem', lineHeight: 1.4 }}>
                        Delivered to users' devices via push notifications
                      </p>
                    </div>
                  </div>
                );
              };
              return <NotifSendForm />;
            })()}

            {/* ===== TEMPLATES ===== */}
            {communicationSubTab === 'templates' && (
              <div style={{ background: cardBg, backdropFilter: 'blur(20px)', borderRadius: '20px', border: `1px solid ${cardBorder}`, padding: '1.75rem', boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(34,197,94,0.08)' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: textPrimary }}>Notification Templates</h3>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: textSecondary }}>Reusable message templates for common notification types</p>
                </div>
                {notificationTemplates.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '4rem 2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(22,163,74,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(34,197,94,0.2)' }}>
                      <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: '1.75rem', color: '#22C55E' }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '1.05rem', color: textPrimary, marginBottom: '0.35rem' }}>No templates created yet</div>
                      <div style={{ fontSize: '0.875rem', color: textSecondary }}>Create reusable notification templates to speed up your messaging workflow.</div>
                    </div>
                  </div>
                ) : (
                  <AdminTable minWidth="680px" columns={[{ width: '22%' }, { width: '38%' }, { width: '20%' }, { width: '20%' }]}>
                    <AdminTableHead>
                      <AdminTh>Template Name</AdminTh>
                      <AdminTh>Description</AdminTh>
                      <AdminTh>Last Used</AdminTh>
                      <AdminTh>Actions</AdminTh>
                    </AdminTableHead>
                    <tbody>
                      {notificationTemplates.map(template => (
                        <AdminTr key={template.id}>
                          <AdminTd className="tw-font-bold">{template.name}</AdminTd>
                          <AdminTd className="tw-opacity-75 tw-text-[0.875rem]">{template.description || 'No description'}</AdminTd>
                          <AdminTd className="tw-opacity-75 tw-text-[0.85rem]">{template.last_used ? new Date(template.last_used).toLocaleString() : 'Never used'}</AdminTd>
                          <AdminTd>
                            <button className="tw-bg-green-500/10 tw-border tw-border-green-500/25 tw-text-green-500 tw-rounded-lg tw-px-3 tw-py-1.5 tw-cursor-pointer tw-font-semibold tw-text-xs tw-inline-flex tw-items-center tw-gap-1.5">
                              <FontAwesomeIcon icon={faEdit} /> Edit
                            </button>
                          </AdminTd>
                        </AdminTr>
                      ))}
                    </tbody>
                  </AdminTable>
                )}
              </div>
            )}

            {/* ===== STATS ===== */}
            {communicationSubTab === 'stats' && (
              <>
                {/* Explanation note */}
                <div style={{
                  background: isDarkMode ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.04)',
                  border: '1px solid rgba(34,197,94,0.2)', borderRadius: '14px',
                  padding: '0.85rem 1.25rem', marginBottom: '1.25rem',
                  display: 'flex', alignItems: 'flex-start', gap: '0.75rem'
                }}>
                  <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#22C55E', marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.85rem', color: textSecondary, lineHeight: 1.5 }}>
                    <strong style={{ color: textPrimary }}>About these stats:</strong> "Total Sent" includes all platform notifications — order status updates, badge achievements, system alerts, and admin bulk sends. These are stored in each user's in-app notification bell and represent real delivery counts from the backend.
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
                  {[
                    { label: 'Total Sent', value: notificationStats.sent || 0, icon: faPaperPlane, accent: '#22C55E', bg: 'rgba(34,197,94,0.12)', borderColor: 'rgba(34,197,94,0.25)', sub: 'All platform notifications' },
                    { label: 'Read', value: notificationStats.read || 0, icon: faBell, accent: '#16A34A', bg: 'rgba(22,163,74,0.12)', borderColor: 'rgba(22,163,74,0.25)', sub: 'Opened by users' },
                    { label: 'Unread', value: notificationStats.failed || 0, icon: faTimesCircle, accent: '#f59e0b', bg: 'rgba(245,158,11,0.1)', borderColor: 'rgba(245,158,11,0.2)', sub: 'Pending user read' },
                    { label: 'Read Rate', value: notificationStats.read_rate || 0, display: `${(notificationStats.read_rate || 0).toFixed ? (notificationStats.read_rate || 0).toFixed(1) : notificationStats.read_rate}%`, icon: faCheckCircle, accent: '#4ADE80', bg: 'rgba(74,222,128,0.12)', borderColor: 'rgba(74,222,128,0.25)', sub: 'Share of sent that were read' },
                  ].map(stat => (
                    <div key={stat.label} style={{ background: cardBg, backdropFilter: 'blur(20px)', borderRadius: '20px', border: `1px solid ${stat.borderColor}`, padding: '1.75rem', boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 24px rgba(34,197,94,0.06)' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: stat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem', border: `1px solid ${stat.borderColor}` }}>
                        <FontAwesomeIcon icon={stat.icon} style={{ fontSize: '1.25rem', color: stat.accent }} />
                      </div>
                      <div style={{ fontSize: '2.75rem', fontWeight: '900', color: stat.accent, lineHeight: 1, marginBottom: '0.35rem', letterSpacing: '-0.03em' }}>{stat.display || stat.value.toLocaleString()}</div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '700', color: textPrimary, marginBottom: '0.2rem' }}>{stat.label}</div>
                      <div style={{ fontSize: '0.75rem', color: textSecondary }}>{stat.sub}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  const getContentForSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboardContent();
      case 'users':
        return renderUsersContent();
      case 'kyc-verification':
        return renderKYCVerificationContent();
      case 'products':
        return renderProductsContent();
      case 'orders':
        return renderOrdersContent();
      case 'escrow':
        return renderEscrowContent();
      case 'wallets':
        return renderWalletsContent();
      case 'swaps':
        return renderSwapsContent();
      case 'disputes':
        return renderDisputesContent();
      case 'reviews':
        return renderReviewsContent();
      case 'premium':
        return renderPremiumUsersContent();
      case 'marketing':
        return renderMarketingContent();
      case 'communication':
        return renderCommunicationContent();
      case 'moderation':
        return renderModerationContent();
      case 'categories':
        return renderCategoriesContent();
      case 'analytics':
        return renderAnalyticsContent();
      case 'system-health':
        return renderSystemHealthContent();
      case 'service-charges':
        return renderServiceChargesContent();
      case 'logs':
        return renderLogsContent();
      case 'admins':
        return renderAdminsContent();
      case 'settings':
        return renderSettingsContent();
      default:
        return renderDashboardContent();
    }
  };

  const renderDashboardContent = () => {
    const dark = isDarkMode;
    const card = {
      background: dark ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)',
      border: `1px solid ${dark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)'}`,
      borderRadius: '20px',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      boxShadow: dark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(34,197,94,0.08)',
      padding: '1.5rem',
    };
    const label = { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: dark ? '#4ADE80' : '#16A34A', marginBottom: '0.3rem' };
    const val = { fontSize: '2.2rem', fontWeight: 800, lineHeight: 1, color: dark ? '#f0fdf4' : '#0f172a' };
    const sub = { fontSize: '0.78rem', color: dark ? '#6b7280' : '#9ca3af', marginTop: '0.25rem' };
    const quickActions = [
      { label: 'KYC Queue', icon: faUserCheck, section: 'kyc-verification', accent: '#22C55E' },
      { label: 'Users', icon: faUsers, section: 'users', accent: '#22C55E' },
      { label: 'Products', icon: faBox, section: 'products', accent: '#22C55E' },
      { label: 'Orders', icon: faExchangeAlt, section: 'orders', accent: '#22C55E' },
      { label: 'Settings', icon: faCog, section: 'settings', accent: '#22C55E' },
      { label: 'Analytics', icon: faChartLine, section: 'analytics', accent: '#22C55E' },
    ];
    const dbCollections = stats.dbCollections || {};
    const nowStr = new Date().toLocaleDateString('en-NG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    return (
      <div style={{ padding: '0 0 2rem 0', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '5rem', color: '#22C55E' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2.5rem' }} />
            <p style={{ marginTop: '1rem', color: dark ? '#9ca3af' : '#6b7280', fontSize: '0.9rem' }}>Loading dashboard…</p>
          </div>
        ) : (
          <>
            {/* Hero Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #166534 0%, #15803d 40%, #16a34a 70%, #22c55e 100%)',
              borderRadius: '24px', padding: '2rem 2.5rem', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem',
              boxShadow: '0 12px 40px rgba(34,197,94,0.3)', position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
              <div style={{ position: 'absolute', bottom: '-60px', right: '80px', width: '160px', height: '160px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{nowStr}</div>
                <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>Welcome back, Admin</h1>
                <p style={{ margin: '0.4rem 0 0', color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem' }}>AutoBoy Express control centre — {stats.dbConnected ? 'All systems operational' : 'DB offline'}</p>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button onClick={fetchDashboardData} style={{
                  padding: '0.6rem 1.2rem', borderRadius: '12px', border: '1.5px solid rgba(255,255,255,0.4)',
                  background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: '0.85rem',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                >
                  <FontAwesomeIcon icon={faSync} /> Refresh
                </button>
                <div style={{ padding: '0.6rem 1rem', borderRadius: '12px', background: 'rgba(0,0,0,0.2)', color: '#fff', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: stats.dbConnected ? '#4ade80' : '#f87171', display: 'inline-block', boxShadow: stats.dbConnected ? '0 0 8px #4ade80' : '0 0 8px #f87171' }} />
                  DB {stats.dbConnected ? 'Connected' : 'Offline'}
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              {[
                { label: 'Total Users', value: (stats.totalUsers || 0).toLocaleString(), icon: faUsers, sub: `${dbCollections.users || 0} in DB`, accent: '#22C55E' },
                { label: 'Total Products', value: (stats.activeListings || 0).toLocaleString(), icon: faBox, sub: `${dbCollections.products || 0} in DB`, accent: '#22C55E' },
                { label: 'Total Orders', value: (stats.totalTransactions || 0).toLocaleString(), icon: faExchangeAlt, sub: `${dbCollections.orders || 0} in DB`, accent: '#22C55E' },
                { label: 'Categories', value: (dbCollections.categories || 0).toLocaleString(), icon: faChartBar, sub: 'Active categories', accent: '#22C55E' },
              ].map(s => (
                <div key={s.label} style={{ ...card, display: 'flex', flexDirection: 'column', gap: '0.5rem', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: '-10px', right: '-10px', width: '70px', height: '70px', borderRadius: '50%', background: `${s.accent}10` }} />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={label}>{s.label}</span>
                    <div style={{ width: '38px', height: '38px', borderRadius: '12px', background: `linear-gradient(135deg, ${s.accent}22, ${s.accent}44)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.accent, fontSize: '1rem' }}>
                      <FontAwesomeIcon icon={s.icon} />
                    </div>
                  </div>
                  <div style={val}>{s.value}</div>
                  <div style={sub}>{s.sub}</div>
                  <div style={{ marginTop: '0.5rem', height: '3px', borderRadius: '99px', background: dark ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.1)' }}>
                    <div style={{ height: '100%', width: '100%', borderRadius: '99px', background: `linear-gradient(90deg, ${s.accent}, #4ade80)` }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div style={card}>
              <div style={{ ...label, marginBottom: '1rem' }}>Quick Actions</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {quickActions.map(a => (
                  <button key={a.section} onClick={() => setActiveSection(a.section)} style={{
                    padding: '0.65rem 1.2rem', borderRadius: '12px', border: `1.5px solid ${dark ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.18)'}`,
                    background: dark ? 'rgba(34,197,94,0.07)' : 'rgba(34,197,94,0.05)', color: dark ? '#4ADE80' : '#16A34A',
                    fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem',
                    transition: 'all 0.2s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(34,197,94,0.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = dark ? 'rgba(34,197,94,0.07)' : 'rgba(34,197,94,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    <FontAwesomeIcon icon={a.icon} style={{ fontSize: '0.8rem' }} /> {a.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Two-column: Recent Orders + Recent Activity */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
              {/* Recent Orders */}
              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem' }}>
                  <div style={{ ...label, marginBottom: 0 }}>Recent Orders</div>
                  <button onClick={() => setActiveSection('orders')} style={{
                    padding: '0.35rem 0.85rem', borderRadius: '8px', border: 'none', fontSize: '0.75rem', fontWeight: 600,
                    background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', cursor: 'pointer',
                  }}>View All</button>
                </div>
                {orders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: dark ? '#4b5563' : '#9ca3af', fontSize: '0.85rem' }}>
                    <FontAwesomeIcon icon={faBox} style={{ fontSize: '1.5rem', marginBottom: '0.5rem', display: 'block' }} />
                    No recent orders
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {orders.slice(0, 5).map(order => (
                      <div key={order._id || order.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.75rem 1rem', borderRadius: '12px',
                        background: dark ? 'rgba(34,197,94,0.05)' : 'rgba(34,197,94,0.04)',
                        border: `1px solid ${dark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.08)'}`,
                      }}>
                        <div style={{ minWidth: 0, flex: 1, marginRight: '0.75rem' }}>
                          <div style={{ fontWeight: 700, fontSize: '0.85rem', color: dark ? '#e2e8f0' : '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {getOrderItemsLabel(order) || `#${(order.order_number || order.id || '—').toString().slice(-8)}`}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: dark ? '#6b7280' : '#9ca3af', marginTop: '0.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <i className="fas fa-user" style={{ marginRight: '0.3rem', opacity: 0.7 }} />{getOrderCustomerName(order)}
                            <span style={{ opacity: 0.6 }}> · #{(order.order_number || order.id || '—').toString().slice(-8)}</span>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#22C55E' }}>{formatCurrency(order.total_amount || 0)}</div>
                          <div style={{ fontSize: '0.72rem', fontWeight: 600, marginTop: '0.15rem', color: getStatusColor(order.status) }}>
                            {(order.status || 'pending').toUpperCase()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Admin Activity */}
              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem' }}>
                  <div style={{ ...label, marginBottom: 0 }}>Recent Admin Activity</div>
                  <button onClick={() => setActiveSection('logs')} style={{
                    padding: '0.35rem 0.85rem', borderRadius: '8px', border: 'none', fontSize: '0.75rem', fontWeight: 600,
                    background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', cursor: 'pointer',
                  }}>View Logs</button>
                </div>
                {systemLogs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: dark ? '#4b5563' : '#9ca3af', fontSize: '0.85rem' }}>
                    <FontAwesomeIcon icon={faServer} style={{ fontSize: '1.5rem', marginBottom: '0.5rem', display: 'block' }} />
                    No recent activity
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    {systemLogs.slice(0, 6).map((log, i) => {
                      const isWarn = log.type === 'warn' || log.type === 'error';
                      return (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                          padding: '0.65rem 0.9rem', borderRadius: '10px',
                          background: isWarn ? (dark ? 'rgba(239,68,68,0.06)' : 'rgba(239,68,68,0.04)') : (dark ? 'rgba(34,197,94,0.05)' : 'rgba(34,197,94,0.04)'),
                          border: `1px solid ${isWarn ? 'rgba(239,68,68,0.12)' : (dark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.08)')}`,
                        }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', marginTop: '0.3rem', flexShrink: 0, background: isWarn ? '#ef4444' : '#22C55E', boxShadow: `0 0 6px ${isWarn ? '#ef4444' : '#22C55E'}` }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: dark ? '#d1d5db' : '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {(log.message || log.action || 'Activity').replace(/_/g, ' ')}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: dark ? '#6b7280' : '#9ca3af', marginTop: '0.1rem' }}>
                              {log.timestamp ? new Date(log.timestamp).toLocaleString('en-NG', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* DB Collections + Environment strip */}
            <div style={{ ...card, padding: '1.2rem 1.5rem' }}>
              <div style={{ ...label, marginBottom: '1rem' }}>System Snapshot</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                {Object.entries(dbCollections).map(([col, count]) => (
                  <div key={col} style={{
                    padding: '0.45rem 0.9rem', borderRadius: '10px',
                    background: dark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)',
                    border: `1px solid ${dark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)'}`,
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                  }}>
                    <span style={{ fontWeight: 700, fontSize: '1rem', color: '#22C55E' }}>{count}</span>
                    <span style={{ fontSize: '0.75rem', color: dark ? '#9ca3af' : '#6b7280', textTransform: 'capitalize' }}>{col}</span>
                  </div>
                ))}
                {stats.environment && Object.entries(stats.environment).slice(0, 3).map(([k, v]) => (
                  <div key={k} style={{
                    padding: '0.45rem 0.9rem', borderRadius: '10px',
                    background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                    border: `1px solid ${dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                    fontSize: '0.75rem', color: dark ? '#6b7280' : '#9ca3af',
                  }}>
                    <span style={{ fontWeight: 600, color: dark ? '#9ca3af' : '#6b7280' }}>{k}: </span>{v}
                  </div>
                ))}
                <div style={{ marginLeft: 'auto', fontSize: '0.72rem', color: dark ? '#4b5563' : '#9ca3af' }}>
                  Last synced: {stats.lastChecked ? new Date(stats.lastChecked).toLocaleTimeString() : '—'}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Continue with the rest of the component structure...
  // For brevity, I'll include the main structure and key sections

  return (
    <>
      <div className="AutoboyExpress-dash-container">
        {/* Custom Toast Notification - Inside Dashboard */}
        {toast && (
          <div className={`AutoboyExpress-admin-toast AutoboyExpress-admin-toast-${toast.type}`}>
            <div className="AutoboyExpress-admin-toast-icon">
              <FontAwesomeIcon icon={
                toast.type === 'success' ? faCheckCircle :
                  toast.type === 'error' ? faExclamationTriangle :
                    toast.type === 'warning' ? faExclamationTriangle :
                      faBell
              } />
            </div>
            <div className="AutoboyExpress-admin-toast-content">
              <p className="AutoboyExpress-admin-toast-message">{toast.message}</p>
            </div>
            <button
              className="AutoboyExpress-admin-toast-close"
              onClick={() => setToast(null)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}
        <div className={`AutoboyExpress-dash-sidebar ${isMobileSidebarOpen ? 'AutoboyExpress-dash-sidebar-open' : ''}`}>
          <div className="AutoboyExpress-dash-logo"><img src={isDarkMode ? darkLogo : lightLogo} alt="AutoboyExpress" /></div>
          <div className="AutoboyExpress-dash-profile">
            <div className="AutoboyExpress-dash-profile-avatar"><FontAwesomeIcon icon={faUserShield} /></div>
            <div className="AutoboyExpress-dash-profile-info">
              <h3 className="AutoboyExpress-dash-seller-name">Admin Panel</h3>
              <p className="AutoboyExpress-dash-seller-status">Super Administrator</p>
            </div>
          </div>

          <ul className="AutoboyExpress-dash-nav">
            <li className="AutoboyExpress-dash-nav-item">
              <button
                className="AutoboyExpress-dash-nav-link"
                onClick={() => { navigate('/homepage'); }}
                style={{
                  background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                  color: 'white',
                  fontWeight: '600',
                  marginBottom: '0.5rem'
                }}
              >
                <FontAwesomeIcon icon={faBox} className="AutoboyExpress-dash-nav-icon" />
                Home
              </button>
            </li>
            {[
              { id: 'dashboard', icon: faChartBar, label: 'Dashboard' },
              { id: 'users', icon: faUsers, label: 'User Management' },
              { id: 'kyc-verification', icon: faUserCheck, label: 'KYC Verification' },
              { id: 'products', icon: faBox, label: 'Product Management' },
              { id: 'orders', icon: faShoppingCart, label: 'Order Management' },
              { id: 'escrow', icon: faLock, label: 'Escrow Management' },
              { id: 'wallets', icon: faWallet, label: 'Wallet Management' },
              { id: 'swaps', icon: faExchangeAlt, label: 'Swap Management' },
              { id: 'disputes', icon: faGavel, label: 'Disputes' },
              { id: 'reviews', icon: faStar, label: 'Reviews' },
              { id: 'premium', icon: faCrown, label: 'Premium Users' },
              { id: 'marketing', icon: faBullhorn, label: 'Marketing' },
              { id: 'communication', icon: faEnvelope, label: 'Communication' },
              { id: 'moderation', icon: faShieldAlt, label: 'Reports & Support' },
              { id: 'categories', icon: faTags, label: 'Categories' },
              { id: 'analytics', icon: faChartLine, label: 'Analytics' },
              { id: 'system-health', icon: faServer, label: 'System Health' },
              { id: 'service-charges', icon: faPercentage, label: 'Service Charges' },
              { id: 'logs', icon: faClipboardList, label: 'System Logs' },
              { id: 'admins', icon: faUserShield, label: 'Admin Management' },
              { id: 'settings', icon: faCog, label: 'Settings' }
            ].map(item => (
              <li key={item.id} className="AutoboyExpress-dash-nav-item">
                <button
                  className={`AutoboyExpress-dash-nav-link ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => { setActiveSection(item.id); setIsMobileSidebarOpen(false); }}
                >
                  <FontAwesomeIcon icon={item.icon} className="AutoboyExpress-dash-nav-icon" />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {isMobileSidebarOpen && <div className="AutoboyExpress-dash-mobile-overlay" onClick={() => setIsMobileSidebarOpen(false)} />}

        <div className="AutoboyExpress-dash-main">
          <div className="AutoboyExpress-dash-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button className="AutoboyExpress-dash-mobile-toggle" onClick={toggleMobileSidebar}>
                <FontAwesomeIcon icon={faBars} />
              </button>
              <h1 className="AutoboyExpress-dash-title">
                {activeSection.charAt(0).toUpperCase() + activeSection.slice(1).replace('-', ' ')}
              </h1>
            </div>
            <div className="AutoboyExpress-dash-header-actions">
              <div style={{ position: 'relative' }}>
                <button
                  className="AutoboyExpress-dash-theme-toggle"
                  onClick={() => setShowNotifications(!showNotifications)}
                  style={{ position: 'relative' }}
                >
                  <FontAwesomeIcon icon={faBell} />
                  {notifications.length > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      background: '#EF4444',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.65rem',
                      fontWeight: '700'
                    }}>
                      {notifications.length > 9 ? '9+' : notifications.length}
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <div className="aex-notif-pop">
                    <div className="aex-notif-pop-head">
                      <h3><FontAwesomeIcon icon={faBell} /> Notifications</h3>
                      <button className="aex-notif-pop-close" onClick={() => setShowNotifications(false)} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                    <div className="aex-notif-pop-body">
                      {notifications.length === 0 ? (
                        <div className="aex-notif-empty">
                          <FontAwesomeIcon icon={faBell} size="2x" style={{ color: '#22C55E', marginBottom: '0.5rem' }} />
                          <p>No new notifications</p>
                        </div>
                      ) : (
                        notifications.map((notification, index) => {
                          const unread = !(notification.is_read || notification.read);
                          return (
                            <div
                              key={notification._id || notification.id || index}
                              className={`aex-notif-row ${unread ? 'unread' : ''}`}
                              onClick={() => handleOpenNotif(notification)}
                            >
                              <div className="aex-notif-ic"><FontAwesomeIcon icon={faBell} /></div>
                              <div className="aex-notif-txt">
                                <p className="t">
                                  {notification.title || 'Notification'}
                                  {unread && <span className="aex-notif-dot" />}
                                </p>
                                <p className="m">{notification.message || notification.body}</p>
                                <p className="d">{notification.created_at ? formatDate(notification.created_at) : 'Just now'}</p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Notification detail modal — portaled to body so it centres on the viewport
                  (the header is a containing block for position:fixed, which broke centering) */}
              {selectedNotif && createPortal(
                <div className="aex-notif-overlay" onClick={() => setSelectedNotif(null)}>
                  <div className="aex-notif-modal" onClick={e => e.stopPropagation()}>
                    <div className="aex-notif-modal-head">
                      <h3><FontAwesomeIcon icon={faBell} /> Notification</h3>
                      <button className="aex-notif-pop-close" onClick={() => setSelectedNotif(null)} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                    <div className="aex-notif-modal-body">
                      <h4 style={{ margin: '0 0 0.6rem', fontSize: '1.05rem', fontWeight: 700 }}>{selectedNotif.title || 'Notification'}</h4>
                      {(selectedNotif.type || selectedNotif.priority) && (
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.9rem', flexWrap: 'wrap' }}>
                          {selectedNotif.type && <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '0.2rem 0.6rem', borderRadius: '20px', background: 'rgba(34,197,94,0.15)', color: '#22C55E' }}>{String(selectedNotif.type).replace(/_/g, ' ')}</span>}
                          {selectedNotif.priority ? <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '0.2rem 0.6rem', borderRadius: '20px', background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>Priority {selectedNotif.priority}</span> : null}
                        </div>
                      )}
                      <p style={{ margin: '0 0 1rem', fontSize: '0.925rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {selectedNotif.message || selectedNotif.body || 'No additional details.'}
                      </p>
                      <div style={{ fontSize: '0.78rem', opacity: 0.6, borderTop: `1px solid rgba(148,163,184,0.25)`, paddingTop: '0.85rem' }}>
                        {selectedNotif.created_at ? formatDate(selectedNotif.created_at) : 'Just now'}
                        {selectedNotif.related_id && <span> · Ref #{String(selectedNotif.related_id).slice(-8).toUpperCase()}</span>}
                      </div>
                    </div>
                    <div className="aex-notif-modal-foot">
                      <button onClick={async () => { const nid = selectedNotif._id || selectedNotif.id; if (nid) { try { await notificationsAPI.delete(nid); setNotifications(prev => prev.filter(x => (x._id || x.id) !== nid)); } catch {} } setSelectedNotif(null); }}
                        style={{ padding: '9px 16px', borderRadius: '8px', border: `1px solid rgba(148,163,184,0.35)`, background: 'transparent', color: '#EF4444', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
                        <FontAwesomeIcon icon={faTrash} style={{ marginRight: '0.4rem' }} /> Delete
                      </button>
                      <button onClick={() => setSelectedNotif(null)} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>Close</button>
                    </div>
                  </div>
                </div>,
                document.body
              )}

              <button className="AutoboyExpress-dash-theme-toggle" onClick={toggleTheme}>
                <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} />
              </button>
              <button className="AutoboyExpress-dash-settings-btn" onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} />
              </button>
            </div>
          </div>

          <div className="AutoboyExpress-dash-content-area">
            {getContentForSection()}
          </div>
        </div>
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '1rem',
            animation: 'fadeIn 0.3s ease'
          }}
          onClick={() => setShowUserModal(false)}
        >
          <div
            style={{
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(10, 10, 15, 0.98) 0%, rgba(20, 20, 25, 0.98) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 253, 244, 0.98) 100%)',
              borderRadius: '24px',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: isDarkMode
                ? '0 20px 60px rgba(0, 0, 0, 0.5)'
                : '0 20px 60px rgba(0, 0, 0, 0.2)',
              border: isDarkMode
                ? '1px solid rgba(34, 197, 94, 0.2)'
                : '1px solid rgba(22, 163, 74, 0.2)',
              animation: 'slideUp 0.3s ease'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '2rem',
                borderBottom: isDarkMode
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: isDarkMode
                  ? 'rgba(34, 197, 94, 0.15)'
                  : 'rgba(22, 163, 74, 0.08)'
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: '1.75rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}
              >
                <FontAwesomeIcon icon={faUserCircle} />
                User Details
              </h2>
              <button
                onClick={() => setShowUserModal(false)}
                style={{
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  transition: 'all 0.3s ease',
                  color: 'inherit'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(220, 53, 69, 0.2)';
                  e.currentTarget.style.transform = 'rotate(90deg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.transform = 'rotate(0deg)';
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '2rem' }}>
              {/* User Profile Section */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                  marginBottom: '2rem',
                  padding: '1.5rem',
                  background: isDarkMode
                    ? 'rgba(34, 197, 94, 0.12)'
                    : 'rgba(22, 163, 74, 0.08)',
                  borderRadius: '16px',
                  border: isDarkMode
                    ? '1px solid rgba(34, 197, 94, 0.25)'
                    : '1px solid rgba(22, 163, 74, 0.15)'
                }}
              >
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    color: 'white',
                    fontWeight: '700',
                    boxShadow: '0 8px 24px rgba(34, 197, 94, 0.45)'
                  }}
                >
                  {(selectedUser.full_name || selectedUser.username || 'U').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', fontWeight: '700' }}>
                    {selectedUser.full_name || selectedUser.username || 'N/A'}
                  </h3>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <span
                      style={{
                        padding: '0.4rem 0.8rem',
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        background:
                          selectedUser.role === 'seller'
                            ? 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)'
                            : selectedUser.role === 'admin'
                              ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'
                              : 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
                        color: 'white'
                      }}
                    >
                      {selectedUser.role || 'buyer'}
                    </span>
                    <span
                      style={{
                        padding: '0.4rem 0.8rem',
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        background:
                          selectedUser.status === 'active'
                            ? 'linear-gradient(135deg, #28a745 0%, #218838 100%)'
                            : selectedUser.status === 'suspended'
                              ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'
                              : 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)',
                        color: 'white'
                      }}
                    >
                      {selectedUser.status || 'active'}
                    </span>
                  </div>
                </div>
              </div>

              {/* User Information Grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1.5rem',
                  marginBottom: '2rem'
                }}
              >
                <div
                  style={{
                    padding: '1.25rem',
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    borderRadius: '12px',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.85rem',
                      opacity: 0.7,
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: '600'
                    }}
                  >
                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '0.5rem' }} />
                    Email
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: '600', wordBreak: 'break-all' }}>
                    {selectedUser.email || 'N/A'}
                  </div>
                </div>

                <div
                  style={{
                    padding: '1.25rem',
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    borderRadius: '12px',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.85rem',
                      opacity: 0.7,
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: '600'
                    }}
                  >
                    <FontAwesomeIcon icon={faPhone} style={{ marginRight: '0.5rem' }} />
                    Phone
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: '600' }}>
                    {selectedUser.phone || 'N/A'}
                  </div>
                </div>

                <div
                  style={{
                    padding: '1.25rem',
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    borderRadius: '12px',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.85rem',
                      opacity: 0.7,
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: '600'
                    }}
                  >
                    <FontAwesomeIcon icon={faHistory} style={{ marginRight: '0.5rem' }} />
                    Joined Date
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: '600' }}>
                    {selectedUser.created_at ? format(new Date(selectedUser.created_at), 'MMM dd, yyyy') : 'N/A'}
                  </div>
                </div>

                <div
                  style={{
                    padding: '1.25rem',
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    borderRadius: '12px',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.85rem',
                      opacity: 0.7,
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: '600'
                    }}
                  >
                    <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.5rem' }} />
                    User ID
                  </div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '600', fontFamily: 'Geist', monoSpace: 'true' }}>
                    {selectedUser._id || selectedUser.id || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)' }}>
                {selectedUser.status === 'suspended' ? (
                  <button
                    onClick={() => {
                      handleReactivateUser(selectedUser._id || selectedUser.id);
                      setShowUserModal(false);
                    }}
                    style={{
                      padding: '0.875rem 1.5rem',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
                      color: 'white',
                      fontWeight: '700',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(40, 167, 69, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(40, 167, 69, 0.3)';
                    }}
                  >
                    <FontAwesomeIcon icon={faUserCheck} />
                    Reactivate User
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      handleSuspendUser(selectedUser._id || selectedUser.id);
                      setShowUserModal(false);
                    }}
                    style={{
                      padding: '0.875rem 1.5rem',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                      color: 'white',
                      fontWeight: '700',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(220, 53, 69, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(220, 53, 69, 0.3)';
                    }}
                  >
                    <FontAwesomeIcon icon={faBan} />
                    Suspend User
                  </button>
                )}
                <button
                  onClick={() => setShowUserModal(false)}
                  style={{
                    padding: '0.875rem 1.5rem',
                    borderRadius: '12px',
                    border: 'none',
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    color: 'inherit',
                    fontWeight: '700',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Create/Edit Modal */}
      {showProductModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '1rem'
          }}
          onClick={() => setShowProductModal(false)}
        >
          <div
            style={{
              background: isDarkMode ? 'rgba(10, 10, 15, 0.98)' : 'rgba(255, 255, 255, 0.98)',
              borderRadius: '20px',
              maxWidth: '720px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              padding: '1.5rem',
              borderRadius: '20px 20px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>
                {editingProduct ? 'Edit Product' : 'Create New Product'}
              </h2>
              <button
                onClick={() => setShowProductModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                �
              </button>
            </div>

            <form onSubmit={handleSubmitProduct} style={{ padding: '2rem' }}>

              {/* shared styles */}
              {(() => {
                const lbl = { display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem', color: isDarkMode ? '#f8fafc' : '#1e293b' };
                const inp = { width: '100%', padding: '0.75rem', borderRadius: '8px', border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.3)' : 'rgba(22,163,74,0.3)'}`, background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)', color: isDarkMode ? '#f8fafc' : '#1e293b', fontSize: '0.95rem', boxSizing: 'border-box', outline: 'none' };
                const mb = { marginBottom: '1.25rem' };
                return (
                  <>
                    {/* ── Title ── */}
                    <div style={mb}>
                      <label style={lbl}>Product Title *</label>
                      <input type="text" value={productFormData.title} onChange={e => setProductFormData(p => ({ ...p, title: e.target.value }))} required style={inp} placeholder="e.g. iPhone 15 Pro Max" />
                    </div>

                    {/* ── Description ── */}
                    <div style={mb}>
                      <label style={lbl}>Description *</label>
                      <textarea value={productFormData.description} onChange={e => setProductFormData(p => ({ ...p, description: e.target.value }))} required rows={3} style={{ ...inp, resize: 'vertical' }} placeholder="Provide detailed product specifications and condition details..." />
                    </div>

                    {/* ── Price + Seller Price ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', ...mb }}>
                      <div>
                        <label style={lbl}>Retail Price (₦) *</label>
                        <input type="number" value={productFormData.price} onChange={e => setProductFormData(p => ({ ...p, price: e.target.value }))} required min="0" step="0.01" style={inp} placeholder="0.00" />
                      </div>
                      <div>
                        <label style={lbl}>Wholesale Price (₦)</label>
                        <input type="number" value={productFormData.seller_price} onChange={e => setProductFormData(p => ({ ...p, seller_price: e.target.value }))} min="0" step="0.01" style={inp} placeholder="Optional wholesale price" />
                      </div>
                    </div>

                    {/* ── Brand + Model ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', ...mb }}>
                      <div>
                        <label style={lbl}>Brand</label>
                        <input type="text" value={productFormData.brand} onChange={e => setProductFormData(p => ({ ...p, brand: e.target.value }))} style={inp} placeholder="e.g. Apple" />
                      </div>
                      <div>
                        <label style={lbl}>Model</label>
                        <input type="text" value={productFormData.model} onChange={e => setProductFormData(p => ({ ...p, model: e.target.value }))} style={inp} placeholder="e.g. iPhone 15 Pro" />
                      </div>
                    </div>

                    {/* ── Quantity + Condition ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', ...mb }}>
                      <div>
                        <label style={lbl}>Quantity</label>
                        <input type="number" value={productFormData.quantity} onChange={e => setProductFormData(p => ({ ...p, quantity: e.target.value }))} min="1" style={inp} placeholder="1" />
                      </div>
                      <div>
                        <label style={lbl}>Condition</label>
                        <select value={productFormData.condition} onChange={e => setProductFormData(p => ({ ...p, condition: e.target.value }))} style={inp}>
                          <option value="new">Brand New</option>
                          <option value="uk_used">UK Used</option>
                          <option value="nigeria_used">Nigerian Used</option>
                          <option value="good_deal">Good Deal</option>
                          <option value="swap_deal">Swap Deal</option>
                          <option value="for_sale">For Sale</option>
                          <option value="refurbished">Refurbished</option>
                        </select>
                      </div>
                    </div>

                    {/* ── Category + Currency ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', ...mb }}>
                      <div>
                        <label style={lbl}>Category *</label>
                        <select value={productFormData.category_id} onChange={e => setProductFormData(p => ({ ...p, category_id: e.target.value }))} required style={inp}>
                          <option value="">Select category…</option>
                          {(categories || []).map(c => (
                            <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={lbl}>Currency *</label>
                        <select value={productFormData.currency} onChange={e => setProductFormData(p => ({ ...p, currency: e.target.value }))} required style={inp}>
                          <option value="NGN">NGN (₦)</option>
                          <option value="USD">USD ($)</option>
                          <option value="GBP">GBP (£)</option>
                          <option value="EUR">EUR (€)</option>
                        </select>
                      </div>
                    </div>

                    {/* ── State / Feature Toggles (Good Deal = is_featured → Homepage, Trending, Hot) ── */}
                    <div style={{ ...mb, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem' }}>
                      <button type="button" onClick={() => setProductFormData(p => ({ ...p, is_featured: !p.is_featured }))} style={{ ...inp, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', background: productFormData.is_featured ? 'rgba(16,185,129,0.15)' : inp.background, color: productFormData.is_featured ? '#10B981' : inp.color, borderColor: productFormData.is_featured ? '#10B981' : inp.borderColor }}>
                        <FontAwesomeIcon icon={faTag} /> Good Deal
                      </button>
                      <button type="button" onClick={() => setProductFormData(p => ({ ...p, is_trending: !p.is_trending }))} style={{ ...inp, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', background: productFormData.is_trending ? 'rgba(245,158,11,0.15)' : inp.background, color: productFormData.is_trending ? '#F59E0B' : inp.color, borderColor: productFormData.is_trending ? '#F59E0B' : inp.borderColor }}>
                        <FontAwesomeIcon icon={faChartLine} /> Trending
                      </button>
                      <button type="button" onClick={() => setProductFormData(p => ({ ...p, is_hot: !p.is_hot }))} style={{ ...inp, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', background: productFormData.is_hot ? 'rgba(239,68,68,0.15)' : inp.background, color: productFormData.is_hot ? '#EF4444' : inp.color, borderColor: productFormData.is_hot ? '#EF4444' : inp.borderColor }}>
                        <FontAwesomeIcon icon={faFlag} /> Hot Deal
                      </button>
                    </div>

                    {/* ── Location ── */}
                    <div style={{ ...mb, padding: '1rem', borderRadius: '10px', border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(22,163,74,0.15)'}`, background: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(240,253,244,0.4)' }}>
                      <label style={{ ...lbl, marginBottom: '0.75rem', color: '#22C55E', fontSize: '0.825rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Location *</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                        <div>
                          <label style={{ ...lbl, fontSize: '0.78rem', marginBottom: '0.3rem' }}>City</label>
                          <input type="text" value={productFormData.location?.city || ''} onChange={e => setProductFormData(p => ({ ...p, location: { ...p.location, city: e.target.value } }))} required style={{ ...inp, padding: '0.55rem 0.7rem', fontSize: '0.85rem' }} placeholder="e.g. Lagos" />
                        </div>
                        <div>
                          <label style={{ ...lbl, fontSize: '0.78rem', marginBottom: '0.3rem' }}>State</label>
                          <input type="text" value={productFormData.location?.state || ''} onChange={e => setProductFormData(p => ({ ...p, location: { ...p.location, state: e.target.value } }))} required style={{ ...inp, padding: '0.55rem 0.7rem', fontSize: '0.85rem' }} placeholder="e.g. Lagos" />
                        </div>
                        <div>
                          <label style={{ ...lbl, fontSize: '0.78rem', marginBottom: '0.3rem' }}>Country</label>
                          <input type="text" value={productFormData.location?.country || ''} onChange={e => setProductFormData(p => ({ ...p, location: { ...p.location, country: e.target.value } }))} required style={{ ...inp, padding: '0.55rem 0.7rem', fontSize: '0.85rem' }} placeholder="e.g. Nigeria" />
                        </div>
                      </div>
                    </div>

                    {/* ── Specifications ── */}
                    <div style={{ ...mb, padding: '1rem', borderRadius: '10px', border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(22,163,74,0.15)'}`, background: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(240,253,244,0.4)' }}>
                      <label style={{ ...lbl, marginBottom: '0.75rem', color: '#22C55E', fontSize: '0.825rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Specifications</label>
                      {Object.entries(productFormData.specifications || {}).map(([k, v]) => (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.35rem 0.6rem', borderRadius: '6px', marginBottom: '0.35rem', background: isDarkMode ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)', fontSize: '0.85rem' }}>
                          <span style={{ color: isDarkMode ? '#f8fafc' : '#1e293b' }}><strong style={{ color: '#22C55E' }}>{k}:</strong> {v}</span>
                          <button type="button" onClick={() => removeProductSpec(k)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 0.25rem', fontSize: '1rem', lineHeight: 1 }}>×</button>
                        </div>
                      ))}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <input value={productSpecInput.key} onChange={e => setProductSpecInput(p => ({ ...p, key: e.target.value }))} style={{ ...inp, padding: '0.55rem 0.7rem', fontSize: '0.85rem' }} placeholder="Key (e.g. Storage)" />
                        <input value={productSpecInput.value} onChange={e => setProductSpecInput(p => ({ ...p, value: e.target.value }))} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addProductSpec())} style={{ ...inp, padding: '0.55rem 0.7rem', fontSize: '0.85rem' }} placeholder="Value (e.g. 256GB)" />
                        <button type="button" onClick={addProductSpec} style={{ padding: '0.55rem 0.9rem', borderRadius: '8px', border: 'none', background: '#22C55E', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>+ Add</button>
                      </div>
                    </div>

                    {/* ── Images ── */}
                    <div style={{ ...mb, padding: '1rem', borderRadius: '10px', border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(22,163,74,0.15)'}`, background: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(240,253,244,0.4)' }}>
                      <label style={{ ...lbl, marginBottom: '0.75rem', color: '#22C55E', fontSize: '0.825rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Product Images ({(productFormData.images || []).length}/10)
                      </label>

                      {/* File upload */}
                      <input type="file" id="admin-product-images" multiple accept="image/*" onChange={handleProductImageUpload} style={{ display: 'none' }} disabled={productUploadingImages || (productFormData.images || []).length >= 10} />
                      <label htmlFor="admin-product-images" style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.6rem',
                        padding: '1.5rem 1rem', border: `2px dashed ${isDarkMode ? 'rgba(34,197,94,0.35)' : 'rgba(34,197,94,0.4)'}`,
                        borderRadius: '10px', cursor: (productUploadingImages || (productFormData.images || []).length >= 10) ? 'not-allowed' : 'pointer',
                        opacity: (productUploadingImages || (productFormData.images || []).length >= 10) ? 0.6 : 1,
                        transition: 'border-color 0.2s', background: isDarkMode ? 'rgba(34,197,94,0.04)' : 'rgba(34,197,94,0.03)'
                      }}>
                        <FontAwesomeIcon icon={productUploadingImages ? faSpinner : faUpload} spin={productUploadingImages} style={{ fontSize: '1.75rem', color: '#22C55E' }} />
                        <span style={{ fontSize: '0.875rem', color: isDarkMode ? '#94a3b8' : '#64748b', textAlign: 'center' }}>
                          {productUploadingImages
                            ? `Uploading ${productUploadProgress.current}/${productUploadProgress.total}… ${productUploadProgress.fileName || ''}`
                            : (productFormData.images || []).length >= 10
                            ? 'Maximum 10 images reached'
                            : 'Click to upload images (max 10MB each)'}
                        </span>
                      </label>

                      {/* URL input */}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                        <input type="text" value={productImageUrlInput} onChange={e => setProductImageUrlInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddProductImageUrl())} style={{ ...inp, padding: '0.55rem 0.7rem', fontSize: '0.85rem', flex: 1 }} placeholder="Or paste an image URL…" />
                        <button type="button" onClick={handleAddProductImageUrl} disabled={(productFormData.images || []).length >= 10} style={{ padding: '0.55rem 0.9rem', borderRadius: '8px', border: 'none', background: '#22C55E', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Add URL</button>
                      </div>

                      {/* Preview grid */}
                      {(productFormData.images || []).length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '0.6rem', marginTop: '0.75rem' }}>
                          {(productFormData.images || []).map((img, idx) => (
                            <div key={idx} style={{ position: 'relative', aspectRatio: '1', borderRadius: '8px', overflow: 'hidden', border: `2px solid ${isDarkMode ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.25)'}` }}>
                              <img src={img} alt={`img-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              <button type="button" onClick={() => removeProductImage(idx)} style={{ position: 'absolute', top: '3px', right: '3px', width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(239,68,68,0.9)', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Admin Controls - Status Change & Delete */}
              {editingProduct && (
                <div style={{
                  marginTop: '2rem',
                  padding: '1.5rem',
                  background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(240, 253, 244, 0.5)',
                  borderRadius: '12px',
                  border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(22, 163, 74, 0.2)'}`
                }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    marginBottom: '1rem',
                    color: '#22C55E'
                  }}>
                    <FontAwesomeIcon icon={faUserShield} style={{ marginRight: '0.5rem' }} />
                    Admin Controls
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem', color: isDarkMode ? '#f8fafc' : '#1e293b' }}>
                        Product Status
                      </label>
                      <select
                        value={editingProduct.status || 'pending'}
                        onChange={(e) => {
                          if (window.confirm(`Change product status to "${e.target.value}"?`)) {
                            handleChangeProductStatus(editingProduct._id || editingProduct.id, e.target.value);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.3)' : 'rgba(22, 163, 74, 0.3)'}`,
                          background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.9)',
                          fontSize: '1rem',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="pending">Pending Review</option>
                        <option value="active">Active</option>
                        <option value="draft">Draft</option>
                        <option value="rejected">Rejected</option>
                        <option value="sold">Sold</option>
                      </select>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
                            handleDeleteProduct(editingProduct._id || editingProduct.id);
                            setShowProductModal(false);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: 'none',
                          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                          color: 'white',
                          fontSize: '1rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        Delete Product
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button
                  type="button"
                  onClick={() => setShowProductModal(false)}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    borderRadius: '8px',
                    border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.3)' : 'rgba(22, 163, 74, 0.3)'}`,
                    background: 'transparent',
                    color: '#22C55E',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? 'Saving...' : (editingProduct ? 'Update Product' : 'Create Product')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Suspend User Modal ─────────────────────────────────── */}
      {showSuspendModal && (
        <div onClick={() => setShowSuspendModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: isDarkMode ? '#1e293b' : '#fff', borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>Suspend User</h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Please provide a reason for this suspension.</p>
            <textarea
              autoFocus
              rows={3}
              placeholder="e.g. Violated terms of service..."
              value={suspendReason}
              onChange={e => setSuspendReason(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0a0a0a' : '#f8fafc', color: isDarkMode ? '#f1f5f9' : '#1e293b', outline: 'none' }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowSuspendModal(false)} style={{ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: 'transparent', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitSuspendUser} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Suspend</button>
            </div>
          </div>
        </div>
      )}

      {/* ── IMEI Inspection (admin-only) ────────────────────────── */}
      {imeiViewerTarget && (
        <AdminIMEIViewer
          type={imeiViewerTarget.type}
          recordId={imeiViewerTarget.id}
          isDarkMode={isDarkMode}
          onClose={() => setIMEIViewerTarget(null)}
        />
      )}

      {/* ── Update Perks Modal ─────────────────────────────────── */}
      {showPerksModal && (
        <div onClick={() => setShowPerksModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: isDarkMode ? '#1e293b' : '#fff', borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>Update User Perks</h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Pre-filled with this user's current perks — edit the list below. Saving replaces the full list, so remove a perk by deleting it here rather than leaving it out.</p>
            <input
              autoFocus
              type="text"
              placeholder="e.g. priority_support, fee_discount"
              value={perksInput}
              onChange={e => setPerksInput(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0a0a0a' : '#f8fafc', color: isDarkMode ? '#f1f5f9' : '#1e293b', outline: 'none' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: isDarkMode ? '#64748b' : '#94a3b8' }}>
              Common: <code>priority_support</code>, <code>fee_discount</code>, <code>early_access</code>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowPerksModal(false)} style={{ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: 'transparent', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitUpdateUserPerks} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #4b5563 0%, #374151 100%)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Save Perks</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Product Modal ──────────────────────────────── */}
      {reviewingProduct && (
        <ProductReviewModal
          product={reviewingProduct}
          categories={categories}
          isDarkMode={isDarkMode}
          onClose={() => setReviewingProduct(null)}
          onApprove={handleApproveProduct}
          onReject={handleRejectProduct}
        />
      )}

      {showRejectProductModal && (
        <div onClick={() => setShowRejectProductModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: isDarkMode ? '#1e293b' : '#fff', borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>Reject Product</h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Please provide a reason for rejecting this listing.</p>
            <textarea
              autoFocus
              rows={3}
              placeholder="e.g. Prohibited item, misleading description..."
              value={rejectProductReason}
              onChange={e => setRejectProductReason(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0a0a0a' : '#f8fafc', color: isDarkMode ? '#f1f5f9' : '#1e293b', outline: 'none' }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowRejectProductModal(false)} style={{ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: 'transparent', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitRejectProduct} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Reject Listing</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Update Order Status Modal ────────────────────────── */}
      {showOrderStatusModal && (
        <div onClick={() => setShowOrderStatusModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: isDarkMode ? '#1e293b' : '#fff', borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 0.4rem', fontSize: '1.1rem', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>Update Order Status</h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Select the new status for this order.</p>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '0.35rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>New Status</label>
            <select
              value={orderStatusValue}
              onChange={e => setOrderStatusValue(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '1rem', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0a0a0a' : '#f8fafc', color: isDarkMode ? '#f1f5f9' : '#1e293b', outline: 'none' }}
            >
              {['pending', 'confirmed', 'paid', 'processing', 'shipped', 'delivered', 'completed', 'cancelled', 'refunded'].map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '0.35rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Note (optional)</label>
            <textarea
              rows={2}
              placeholder="Add a note about this status change..."
              value={orderStatusNote}
              onChange={e => setOrderStatusNote(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0a0a0a' : '#f8fafc', color: isDarkMode ? '#f1f5f9' : '#1e293b', outline: 'none' }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowOrderStatusModal(false)} style={{ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: 'transparent', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitUpdateOrderStatus} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Update Status</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Order Detail Modal ────────────────────────────────── */}
      {showOrderDetailModal && selectedOrder && (
        <div onClick={() => setShowOrderDetailModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: isDarkMode ? '#1e293b' : '#fff', color: isDarkMode ? '#e2e8f0' : '#1e293b', borderRadius: '18px', padding: '2rem', width: '100%', maxWidth: '560px', boxShadow: '0 20px 60px rgba(0,0,0,0.45)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>Order Details</h3>
                <p style={{ margin: '0.2rem 0 0', fontSize: '13px', color: '#22C55E', fontFamily: 'monospace', fontWeight: 600 }}>
                  #{(selectedOrder.order_number || selectedOrder._id || selectedOrder.id || '').toString().slice(-8).toUpperCase()}
                </p>
              </div>
              <button onClick={() => setShowOrderDetailModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '1.2rem', padding: '0.25rem' }}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
              {[
                { label: 'Status', value: <span style={{ color: getStatusColor(selectedOrder.status), fontWeight: 700, textTransform: 'capitalize' }}>{selectedOrder.status || 'pending'}</span> },
                { label: 'Payment', value: <span style={{ fontWeight: 600 }}>{getPaymentStatusText(selectedOrder.payment_status || selectedOrder.paymentStatus || 'pending', selectedOrder)}</span> },
                { label: 'Amount', value: <strong style={{ color: '#22C55E' }}>{formatCurrency(selectedOrder.total_amount || 0)}</strong> },
                { label: 'Date', value: selectedOrder.created_at ? format(new Date(selectedOrder.created_at), 'MMM dd, yyyy HH:mm') : 'N/A' },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(240,253,244,0.5)', borderRadius: '10px', padding: '0.75rem 1rem', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(22,163,74,0.1)'}` }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.55, marginBottom: '0.2rem' }}>{label}</div>
                  <div style={{ fontSize: '0.9rem' }}>{value}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
              <div style={{ background: isDarkMode ? 'rgba(34,197,94,0.06)' : 'rgba(240,253,244,0.6)', borderRadius: '10px', padding: '0.875rem 1rem', border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.12)' : 'rgba(22,163,74,0.12)'}` }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#22C55E', marginBottom: '0.4rem' }}>Customer</div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{getOrderCustomerName(selectedOrder)}</div>
                {/* Admin is the one party allowed to see both sides' contact
                    details — buyer and seller never see each other's, but
                    support needs them for dispute handling. Phone was
                    already being joined server-side, just never rendered
                    here. */}
                {selectedOrder.buyer_email && <div style={{ fontSize: '0.775rem', opacity: 0.6, marginTop: '0.15rem' }}>{selectedOrder.buyer_email}</div>}
                {selectedOrder.buyer_phone && <div style={{ fontSize: '0.775rem', opacity: 0.6, marginTop: '0.1rem' }}>{selectedOrder.buyer_phone}</div>}
              </div>
              <div style={{ background: isDarkMode ? 'rgba(34,197,94,0.06)' : 'rgba(240,253,244,0.6)', borderRadius: '10px', padding: '0.875rem 1rem', border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.12)' : 'rgba(22,163,74,0.12)'}` }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#22C55E', marginBottom: '0.4rem' }}>Seller</div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{selectedOrder.seller_name || 'N/A'}</div>
                {selectedOrder.seller_email && <div style={{ fontSize: '0.775rem', opacity: 0.6, marginTop: '0.15rem' }}>{selectedOrder.seller_email}</div>}
                {typeof selectedOrder.seller_payout === 'number' && (
                  <div style={{ fontSize: '0.775rem', color: '#22C55E', fontWeight: 600, marginTop: '0.3rem' }}>
                    Payout: {formatCurrency(selectedOrder.seller_payout)}
                  </div>
                )}
              </div>
            </div>

            {selectedOrder.shipping_address && (
              <div style={{ background: isDarkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc', borderRadius: '10px', padding: '0.875rem 1rem', marginBottom: '1.25rem', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}` }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.55, marginBottom: '0.35rem' }}>Shipping Address</div>
                <div style={{ fontSize: '0.875rem' }}>
                  {typeof selectedOrder.shipping_address === 'string'
                    ? selectedOrder.shipping_address
                    : `${selectedOrder.shipping_address.street || ''} ${selectedOrder.shipping_address.city || ''} ${selectedOrder.shipping_address.state || ''}`.trim() || 'N/A'}
                </div>
              </div>
            )}

            {(selectedOrder.items || selectedOrder.products)?.length > 0 && (
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.55, marginBottom: '0.5rem' }}>Items</div>
                {(selectedOrder.items || selectedOrder.products).map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: i < (selectedOrder.items || selectedOrder.products).length - 1 ? `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}` : 'none' }}>
                    <div>
                      <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{item.product_title || item.product_snapshot?.title || item.title || item.name || item.product_name || 'Item'}</div>
                      {item.quantity && <div style={{ fontSize: '0.75rem', opacity: 0.55 }}>Qty: {item.quantity}</div>}
                    </div>
                    <strong style={{ color: '#22C55E', fontSize: '0.875rem' }}>{formatCurrency(item.total_price || item.price || item.unit_price || 0)}</strong>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '0.5rem' }}>
              <button onClick={() => { setShowOrderDetailModal(false); handleUpdateOrderStatus(selectedOrder._id || selectedOrder.id, selectedOrder.status); }} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}>
                <FontAwesomeIcon icon={faEdit} style={{ marginRight: '0.4rem' }} /> Update Status
              </button>
              <button onClick={() => setShowOrderDetailModal(false)} style={{ padding: '9px 18px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: 'transparent', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Boost Product Modal ───────────────────────────────── */}
      {showBoostModal && (
        <div onClick={() => setShowBoostModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: isDarkMode ? '#1e293b' : '#fff', borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>Boost Product</h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Set boost level (1–5) and duration in days.</p>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '0.35rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Boost Level (1–5)</label>
            <input
              type="number" min="1" max="5" value={boostLevel}
              onChange={e => setBoostLevel(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '1rem', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0a0a0a' : '#f8fafc', color: isDarkMode ? '#f1f5f9' : '#1e293b', outline: 'none' }}
            />
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '0.35rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Duration (days)</label>
            <input
              type="number" min="1" value={boostDays}
              onChange={e => setBoostDays(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0a0a0a' : '#f8fafc', color: isDarkMode ? '#f1f5f9' : '#1e293b', outline: 'none' }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowBoostModal(false)} style={{ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: 'transparent', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitBoostProduct} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Apply Boost</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Flash Deal Modal ──────────────────────────────────────────── */}
      {showFlashDealModal && flashDealTarget && (
        <div onClick={() => setShowFlashDealModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: isDarkMode ? '#1e293b' : '#fff', borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
              <FontAwesomeIcon icon={faBolt} style={{ color: '#EA580C', marginRight: '0.4rem' }} /> Set Flash Deal
            </h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
              {flashDealTarget.title || flashDealTarget.name} — regular price {formatCurrency(flashDealTarget.price || 0)}
            </p>

            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '0.35rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Flash Deal Price (₦)</label>
            <input
              type="number" min="0" value={flashDealForm.price}
              onChange={e => setFlashDealForm(p => ({ ...p, price: e.target.value }))}
              placeholder="e.g. 45000"
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '1rem', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0a0a0a' : '#f8fafc', color: isDarkMode ? '#f1f5f9' : '#1e293b', outline: 'none' }}
            />

            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '0.35rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Ends</label>
            <input
              type="datetime-local" value={flashDealForm.end_time}
              onChange={e => setFlashDealForm(p => ({ ...p, end_time: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '1rem', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0a0a0a' : '#f8fafc', color: isDarkMode ? '#f1f5f9' : '#1e293b', outline: 'none' }}
            />

            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '0.35rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Early access for premium members (hours)</label>
            <input
              type="number" min="0" max="72" value={flashDealForm.early_access_hours}
              onChange={e => setFlashDealForm(p => ({ ...p, early_access_hours: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0a0a0a' : '#f8fafc', color: isDarkMode ? '#f1f5f9' : '#1e293b', outline: 'none' }}
            />
            <p style={{ fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#64748b', margin: '0.4rem 0 0' }}>
              Premium buyers &amp; sellers see this deal as soon as it's set. Everyone else sees it this many hours later — 24-48 (1-2 days) is typical.
            </p>

            <div style={{ display: 'flex', gap: '10px', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowFlashDealModal(false)} style={{ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: 'transparent', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitFlashDeal} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Set Flash Deal</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Banner Modal ──────────────────────────────────────────── */}
      {(() => {
        // Green-themed modal tokens to match the admin Marketing aesthetic.
        const mGreenBorder = isDarkMode ? 'rgba(34,197,94,0.25)' : 'rgba(22,163,74,0.2)';
        const mLabel = { display: 'block', fontSize: '13px', fontWeight: 700, marginBottom: '0.4rem', color: isDarkMode ? '#86efac' : '#15803d', letterSpacing: '0.01em' };
        const mInput = { width: '100%', padding: '10px 12px', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '1rem', border: `1px solid ${mGreenBorder}`, background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(240,253,244,0.5)', color: isDarkMode ? '#f0fdf4' : '#14532d', outline: 'none' };
        const mOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' };
        const mCard = { background: isDarkMode ? 'rgba(15,17,21,0.98)' : '#fff', border: `1px solid ${mGreenBorder}`, borderRadius: '18px', padding: '2rem', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto', boxShadow: isDarkMode ? '0 24px 70px rgba(0,0,0,0.6), 0 0 0 1px rgba(34,197,94,0.08)' : '0 24px 70px rgba(22,163,74,0.18)' };
        const mTitle = { margin: '0 0 1.25rem', fontSize: '1.15rem', fontWeight: 800, color: isDarkMode ? '#f0fdf4' : '#14532d', display: 'flex', alignItems: 'center', gap: '0.5rem' };
        const mCancel = { padding: '9px 20px', borderRadius: '10px', border: `1px solid ${mGreenBorder}`, background: 'transparent', color: isDarkMode ? '#86efac' : '#15803d', fontWeight: 600, cursor: 'pointer' };
        const mSave = { padding: '9px 22px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: marketingSaving ? 0.6 : 1, boxShadow: '0 6px 18px rgba(34,197,94,0.35)' };
        return (
          <>
            {showBannerModal && (
              <div onClick={() => setShowBannerModal(false)} style={mOverlay}>
                <div onClick={e => e.stopPropagation()} style={mCard}>
                  <h3 style={mTitle}>Create Banner</h3>
                  <label style={mLabel}>Type</label>
                  <select value={bannerForm.type} onChange={e => setBannerForm(p => ({ ...p, type: e.target.value }))} style={mInput}>
                    <option value="hero">Hero</option>
                    <option value="promo">Promo</option>
                  </select>
                  <label style={mLabel}>Title *</label>
                  <input type="text" value={bannerForm.title} onChange={e => setBannerForm(p => ({ ...p, title: e.target.value }))} placeholder="Swap Your Device Today" style={mInput} />
                  <label style={mLabel}>Subtitle</label>
                  <input type="text" value={bannerForm.subtitle} onChange={e => setBannerForm(p => ({ ...p, subtitle: e.target.value }))} placeholder="Trade in your old phone" style={mInput} />
                  <label style={mLabel}>Banner Image *</label>
                  <input type="file" accept="image/*" onChange={handleBannerImageUpload} style={{ ...mInput, padding: '8px' }} />
                  {bannerUploading && <p style={{ fontSize: '12px', color: '#22C55E', margin: '-0.5rem 0 1rem' }}>Uploading…</p>}
                  {bannerForm.image && <img src={bannerForm.image} alt="preview" style={{ width: '100%', borderRadius: '8px', marginBottom: '1rem', maxHeight: '120px', objectFit: 'cover' }} />}
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={mLabel}>CTA Text</label>
                      <input type="text" value={bannerForm.cta_text} onChange={e => setBannerForm(p => ({ ...p, cta_text: e.target.value }))} placeholder="Swap Now" style={mInput} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={mLabel}>CTA Link</label>
                      <input type="text" value={bannerForm.cta_link} onChange={e => setBannerForm(p => ({ ...p, cta_link: e.target.value }))} placeholder="/swap" style={mInput} />
                    </div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#64748b', marginBottom: '0.5rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={bannerForm.is_active} onChange={e => setBannerForm(p => ({ ...p, is_active: e.target.checked }))} /> Active
                  </label>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '1rem', justifyContent: 'flex-end' }}>
                    <button onClick={() => setShowBannerModal(false)} style={mCancel}>Cancel</button>
                    <button onClick={submitBanner} disabled={marketingSaving || bannerUploading} style={mSave}>Create Banner</button>
                  </div>
                </div>
              </div>
            )}

            {showFlashModal && (
              <div onClick={() => setShowFlashModal(false)} style={mOverlay}>
                <div onClick={e => e.stopPropagation()} style={mCard}>
                  <h3 style={mTitle}>Create Sales Campaign</h3>
                  <p style={{ fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#64748b', margin: '-0.75rem 0 1rem' }}>
                    A promotional banner (title, description, discount headline) — not tied to a specific product. To feature a discounted product, use Products -&gt; Flash Deal instead.
                  </p>
                  <label style={mLabel}>Title *</label>
                  <input type="text" value={flashForm.title} onChange={e => setFlashForm(p => ({ ...p, title: e.target.value }))} placeholder="Weekend Sale" style={mInput} />
                  <label style={mLabel}>Description</label>
                  <input type="text" value={flashForm.description} onChange={e => setFlashForm(p => ({ ...p, description: e.target.value }))} placeholder="Massive discounts this weekend only" style={mInput} />
                  <label style={mLabel}>Discount Percentage (%) *</label>
                  <input type="number" min="1" max="100" value={flashForm.discount_percentage} onChange={e => setFlashForm(p => ({ ...p, discount_percentage: e.target.value }))} placeholder="30" style={mInput} />
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={mLabel}>Starts *</label>
                      <input type="datetime-local" value={flashForm.start_time} onChange={e => setFlashForm(p => ({ ...p, start_time: e.target.value }))} style={mInput} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={mLabel}>Ends *</label>
                      <input type="datetime-local" value={flashForm.end_time} onChange={e => setFlashForm(p => ({ ...p, end_time: e.target.value }))} style={mInput} />
                    </div>
                  </div>
                  <label style={mLabel}>Early access for premium members (hours)</label>
                  <input type="number" min="0" max="72" value={flashForm.early_access_hours} onChange={e => setFlashForm(p => ({ ...p, early_access_hours: e.target.value }))} placeholder="24" style={mInput} />
                  <p style={{ fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#64748b', margin: '-0.5rem 0 1rem' }}>
                    Premium buyers &amp; sellers see this deal as soon as it starts. Everyone else sees it this many hours later — 24-48 (1-2 days) is typical.
                  </p>
                  <label style={mLabel}>Deal Image</label>
                  <input type="file" accept="image/*" onChange={handleFlashImageUpload} style={{ ...mInput, padding: '8px' }} />
                  {flashUploading && <p style={{ fontSize: '12px', color: '#22C55E', margin: '-0.5rem 0 1rem' }}>Uploading…</p>}
                  {flashForm.image && <img src={flashForm.image} alt="preview" style={{ width: '100%', borderRadius: '8px', marginBottom: '1rem', maxHeight: '120px', objectFit: 'cover' }} />}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#64748b', marginBottom: '0.5rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={flashForm.is_active} onChange={e => setFlashForm(p => ({ ...p, is_active: e.target.checked }))} /> Active
                  </label>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '1rem', justifyContent: 'flex-end' }}>
                    <button onClick={() => setShowFlashModal(false)} style={mCancel}>Cancel</button>
                    <button onClick={submitFlashSale} disabled={marketingSaving || flashUploading} style={mSave}>Create Sales Campaign</button>
                  </div>
                </div>
              </div>
            )}

            {showPromoModal && (
              <div onClick={() => setShowPromoModal(false)} style={mOverlay}>
                <div onClick={e => e.stopPropagation()} style={mCard}>
                  <h3 style={mTitle}>Create Promo Code</h3>
                  <label style={mLabel}>Code *</label>
                  <input type="text" value={promoForm.code} onChange={e => setPromoForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="SAVE20" style={mInput} />
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={mLabel}>Type</label>
                      <select value={promoForm.type} onChange={e => setPromoForm(p => ({ ...p, type: e.target.value }))} style={mInput}>
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed (₦)</option>
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={mLabel}>Discount Value *</label>
                      <input type="number" min="1" value={promoForm.discount_value} onChange={e => setPromoForm(p => ({ ...p, discount_value: e.target.value }))} placeholder={promoForm.type === 'percentage' ? '20' : '5000'} style={mInput} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={mLabel}>Min Order (₦)</label>
                      <input type="number" min="0" value={promoForm.min_order_amount} onChange={e => setPromoForm(p => ({ ...p, min_order_amount: e.target.value }))} placeholder="0" style={mInput} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={mLabel}>Max Uses (0 = ∞)</label>
                      <input type="number" min="0" value={promoForm.max_uses} onChange={e => setPromoForm(p => ({ ...p, max_uses: e.target.value }))} placeholder="500" style={mInput} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={mLabel}>Per User</label>
                      <input type="number" min="0" value={promoForm.max_uses_per_user} onChange={e => setPromoForm(p => ({ ...p, max_uses_per_user: e.target.value }))} placeholder="1" style={mInput} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={mLabel}>Starts</label>
                      <input type="datetime-local" value={promoForm.start_date} onChange={e => setPromoForm(p => ({ ...p, start_date: e.target.value }))} style={mInput} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={mLabel}>Expires</label>
                      <input type="datetime-local" value={promoForm.end_date} onChange={e => setPromoForm(p => ({ ...p, end_date: e.target.value }))} style={mInput} />
                    </div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#64748b', marginBottom: '0.5rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={promoForm.is_active} onChange={e => setPromoForm(p => ({ ...p, is_active: e.target.checked }))} /> Active
                  </label>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '1rem', justifyContent: 'flex-end' }}>
                    <button onClick={() => setShowPromoModal(false)} style={mCancel}>Cancel</button>
                    <button onClick={submitPromoCode} disabled={marketingSaving} style={mSave}>Create Promo Code</button>
                  </div>
                </div>
              </div>
            )}
          </>
        );
      })()}


      {/* ── Reject Swap Modal ──────────────────────────────────────────────── */}
      {showRejectSwapModal && (
        <div className="AEX-AdminFeature-overlay" onClick={() => setShowRejectSwapModal(false)}>
          <div className="AEX-AdminFeature-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="AEX-AdminFeature-modal-header">
              <div>
                <div className="AEX-AdminFeature-badge-red">
                  <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.7rem' }} />
                  Reject Swap
                </div>
                <h3 className="AEX-AdminFeature-modal-title">Rejection Reason</h3>
              </div>
              <button className="AEX-AdminFeature-close-btn" onClick={() => setShowRejectSwapModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <label className="AEX-AdminFeature-label">Reason (Required)</label>
            <textarea
              className="AEX-AdminFeature-textarea"
              value={rejectSwapReason}
              onChange={e => setRejectSwapReason(e.target.value)}
              placeholder="e.g. Device condition does not match the listing"
              rows={3}
            />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button className="AEX-AdminFeature-btn-ghost" onClick={() => setShowRejectSwapModal(false)}>Cancel</button>
              <button className="AEX-AdminFeature-btn-danger" onClick={handleRejectSwap}>
                <FontAwesomeIcon icon={faTimes} style={{ marginRight: '0.4rem' }} /> Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Swap Inspection Modal ─────────────────────────────────────────── */}
      {showInspectionModal && selectedSwapForInspection && (
        <div className="AEX-AdminFeature-overlay" onClick={() => setShowInspectionModal(false)}>
          <div className="AEX-AdminFeature-modal-lg" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="AEX-AdminFeature-modal-header">
              <div>
                <div className="AEX-AdminFeature-badge-red" style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E', borderColor: 'rgba(34,197,94,0.25)' }}>
                  <FontAwesomeIcon icon={faSearch} style={{ fontSize: '0.7rem' }} />
                  Device Inspection
                </div>
                <h3 className="AEX-AdminFeature-modal-title">
                  Swap #{selectedSwapForInspection.swap_number || (selectedSwapForInspection.id || selectedSwapForInspection._id).substring(0, 8).toUpperCase()}
                </h3>
              </div>
              <button className="AEX-AdminFeature-close-btn" onClick={() => setShowInspectionModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Phase Indicator */}
            <div className="AEX-AdminFeature-swap-inspection-phase">
              {[
                { step: 1, label: 'Verification' },
                { step: 2, label: 'Functionality' },
                { step: 3, label: 'Final Action' }
              ].map(({ step, label }) => (
                <div key={step} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem',
                  flex: 1, position: 'relative'
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem',
                    background: inspectionStep >= step ? '#22C55E' : (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'),
                    color: inspectionStep >= step ? '#fff' : (isDarkMode ? '#94a3b8' : '#64748b'),
                    transition: 'background 0.2s'
                  }}>
                    {inspectionStep > step ? <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.65rem' }} /> : step}
                  </div>
                  <span style={{ fontSize: '0.72rem', fontWeight: 600, color: inspectionStep >= step ? '#22C55E' : (isDarkMode ? '#94a3b8' : '#64748b') }}>
                    {label}
                  </span>
                  {step < 3 && (
                    <div style={{
                      position: 'absolute', top: 14, left: '60%', right: '-40%', height: 2,
                      background: inspectionStep > step ? '#22C55E' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')
                    }} />
                  )}
                </div>
              ))}
            </div>

            {/* Phase 1: Verification */}
            {inspectionStep === 1 && (
              <div className="AEX-AdminFeature-modal-body" style={{ flexDirection: 'column', gap: '1rem' }}>
                <p style={{ fontSize: '0.85rem', color: isDarkMode ? '#94a3b8' : '#64748b', margin: 0 }}>
                  Verify the physical details submitted by the user for: <strong>{selectedSwapForInspection.device_out_name || 'Device'}</strong>
                </p>

                {/* Battery % */}
                <div>
                  <label className="AEX-AdminFeature-label">Battery Health (%)</label>
                  <input
                    className="AEX-AdminFeature-input"
                    type="number"
                    min="0" max="100"
                    value={inspectionData.battery_percent}
                    onChange={e => setInspectionData(d => ({ ...d, battery_percent: e.target.value }))}
                    placeholder="e.g. 87"
                  />
                </div>

                {/* Toggle rows */}
                {[
                  { key: 'face_id_ok', label: 'Face ID / Biometrics working' },
                  { key: 'storage_confirmed', label: 'Storage capacity confirmed' },
                ].map(({ key, label }) => (
                  <div key={key} className="AEX-AdminFeature-swap-toggle-row">
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{label}</span>
                    <button
                      onClick={() => setInspectionData(d => ({ ...d, [key]: !d[key] }))}
                      style={{
                        padding: '0.3rem 1rem', borderRadius: '20px', border: 'none', cursor: 'pointer',
                        fontWeight: 700, fontSize: '0.78rem', fontFamily: 'inherit',
                        background: inspectionData[key] ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.1)',
                        color: inspectionData[key] ? '#22C55E' : '#EF4444',
                      }}
                    >
                      {inspectionData[key] ? 'PASS' : 'FAIL'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Phase 2: Functionality */}
            {inspectionStep === 2 && (
              <div className="AEX-AdminFeature-modal-body" style={{ flexDirection: 'column', gap: '0.75rem' }}>
                <p style={{ fontSize: '0.85rem', color: isDarkMode ? '#94a3b8' : '#64748b', margin: 0 }}>
                  Check each functional component of the device.
                </p>
                {[
                  { key: 'screen_ok', label: 'Screen — No cracks, dead pixels, or burn-in' },
                  { key: 'battery_health_ok', label: 'Battery — Holds charge, no swelling' },
                  { key: 'connectivity_ok', label: 'Connectivity — WiFi, Bluetooth, cellular working' },
                  { key: 'buttons_ok', label: 'Buttons — Power, volume, home all responsive' },
                ].map(({ key, label }) => (
                  <label key={key} className="AEX-AdminFeature-swap-checklist-item">
                    <input
                      type="checkbox"
                      checked={inspectionData[key]}
                      onChange={e => setInspectionData(d => ({ ...d, [key]: e.target.checked }))}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Phase 3: Final Action */}
            {inspectionStep === 3 && (
              <div className="AEX-AdminFeature-modal-body" style={{ flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label className="AEX-AdminFeature-label">Final Decision</label>
                  <select
                    className="AEX-AdminFeature-select"
                    value={inspectionData.final_decision}
                    onChange={e => setInspectionData(d => ({ ...d, final_decision: e.target.value }))}
                  >
                    <option value="pass">Pass — Device meets swap criteria</option>
                    <option value="fail">Fail — Device does not meet criteria</option>
                  </select>
                </div>
                <div>
                  <label className="AEX-AdminFeature-label">Technician Notes</label>
                  <textarea
                    className="AEX-AdminFeature-textarea"
                    rows={4}
                    value={inspectionData.technician_notes}
                    onChange={e => setInspectionData(d => ({ ...d, technician_notes: e.target.value }))}
                    placeholder="Describe findings, anomalies, or any additional notes..."
                  />
                </div>
              </div>
            )}

            {/* Footer Navigation */}
            <div className="AEX-AdminFeature-modal-footer">
              <button
                className="AEX-AdminFeature-btn-ghost"
                onClick={() => {
                  if (inspectionStep === 1) setShowInspectionModal(false);
                  else setInspectionStep(s => s - 1);
                }}
              >
                {inspectionStep === 1 ? 'Cancel' : 'Back'}
              </button>
              {inspectionStep < 3 ? (
                <button className="AEX-AdminFeature-btn-success" onClick={() => setInspectionStep(s => s + 1)}>
                  Next <FontAwesomeIcon icon={faCheck} />
                </button>
              ) : (
                <button className="AEX-AdminFeature-btn-success" onClick={handleInspectSwap}>
                  <FontAwesomeIcon icon={faSave} style={{ marginRight: '0.4rem' }} /> Submit Inspection
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ── Swap Details Modal ─────────────────────────────────────────────── */}
      {showSwapDetailsModal && selectedSwapForDetails && (() => {
        const swap = selectedSwapForDetails;
        const swapId = swap.id || swap._id;
        const statusColor = (() => {
          const map = {
            pending_approval: '#F59E0B', pending_inspection: '#3B82F6',
            active: '#22C55E', in_transit: '#6366F1', in_escrow: '#8B5CF6',
            completed: '#22C55E', cancelled: '#6B7280', rejected: '#EF4444'
          };
          return map[swap.status] || '#6B7280';
        })();
        const isActive = ['active', 'in_transit', 'in_escrow'].includes(swap.status);

        return (
          <div className="AEX-AdminFeature-overlay" onClick={() => setShowSwapDetailsModal(false)}>
            <div className="AEX-AdminFeature-modal-lg" onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div className="AEX-AdminFeature-modal-header--padded">
                <div className="AEX-AdminFeature-user-info">
                  <div className="AEX-AdminFeature-user-avatar-lg">
                    {(swap.user_name || 'U')[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="AEX-AdminFeature-user-name">{swap.user_name || 'Unknown User'}</div>
                    <div className="AEX-AdminFeature-user-email">
                      Swap #{swap.swap_number || swapId.substring(0, 8).toUpperCase()}
                    </div>
                  </div>
                  <span style={{
                    padding: '0.25rem 0.7rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700,
                    background: `${statusColor}18`, color: statusColor,
                    border: `1px solid ${statusColor}30`, marginLeft: '0.25rem'
                  }}>
                    {swap.status?.replace(/_/g, ' ').toUpperCase()}
                  </span>
                </div>
                <button className="AEX-AdminFeature-close-btn" onClick={() => setShowSwapDetailsModal(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {/* Body */}
              <div className="AEX-AdminFeature-modal-body" style={{ flexDirection: 'column', gap: '1.25rem' }}>

                {/* Device Bridge */}
                <div className="AEX-AdminFeature-swap-device-bridge" style={{ marginBottom: 0 }}>
                  <div className="AEX-AdminFeature-swap-device-side">
                    <div className="AEX-AdminFeature-meta-label">Device Out (User gives)</div>
                    {swap.device_out_images?.length > 0 ? (
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                        {swap.device_out_images.slice(0, 5).map((img, i) => (
                          <img key={i} src={img} alt={`Device ${i + 1}`}
                            style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, cursor: 'pointer' }}
                            onClick={() => openSwapImageViewer(swap.device_out_images, i)}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ))}
                        {swap.device_out_images.length > 5 && (
                          <div style={{ width: '56px', height: '56px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', fontSize: '0.75rem', fontWeight: 700 }}>
                            +{swap.device_out_images.length - 5}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ width: '56px', height: '56px', borderRadius: '8px', marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}>
                        <FontAwesomeIcon icon={faImage} style={{ opacity: 0.4 }} />
                      </div>
                    )}
                    <div className="AEX-AdminFeature-meta-value" style={{ fontSize: '1rem', marginTop: '0.5rem' }}>
                      {swap.device_out_name || swap.device_out?.name || 'N/A'}
                    </div>
                    {swap.device_out_condition && (
                      <div style={{ fontSize: '0.8rem', color: '#F59E0B', fontWeight: 600, marginTop: '0.25rem' }}>
                        Condition: {swap.device_out_condition}/10
                      </div>
                    )}
                    {swap.device_out_battery != null && (
                      <div style={{ fontSize: '0.78rem', opacity: 0.75, marginTop: '0.2rem' }}>Battery: {swap.device_out_battery}%</div>
                    )}
                    {swap.device_out_face_id && (
                      <div style={{ fontSize: '0.78rem', opacity: 0.75, marginTop: '0.15rem' }}>Face ID: {swap.device_out_face_id}</div>
                    )}
                    {swap.device_out_repair_history && (
                      <div style={{ fontSize: '0.78rem', opacity: 0.75, marginTop: '0.15rem' }}>Repair history: {swap.device_out_repair_history}</div>
                    )}
                  </div>
                  <div className="AEX-AdminFeature-swap-bridge-arrow">
                    <FontAwesomeIcon icon={faExchangeAlt} />
                  </div>
                  <div className="AEX-AdminFeature-swap-device-side">
                    <div className="AEX-AdminFeature-meta-label">Device In (User gets)</div>
                    {swap.device_in_image ? (
                      <img src={swap.device_in_image} alt={swap.device_in_name || 'Product'}
                        style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', marginTop: '0.5rem', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, cursor: 'pointer' }}
                        onClick={() => openSwapImageViewer(swap.device_in_image, 0)}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div style={{ width: '56px', height: '56px', borderRadius: '8px', marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}>
                        <FontAwesomeIcon icon={faImage} style={{ opacity: 0.4 }} />
                      </div>
                    )}
                    <div className="AEX-AdminFeature-meta-value" style={{ fontSize: '1rem', marginTop: '0.5rem' }}>
                      {swap.device_in_name || swap.device_in?.name || 'N/A'}
                    </div>
                    {swap.device_in_price != null && (
                      <div style={{ fontSize: '0.85rem', color: '#22C55E', fontWeight: 700, marginTop: '0.25rem' }}>
                        {formatCurrency(swap.device_in_price)}
                      </div>
                    )}
                    {swap.seller_topup_amount != null && (
                      <div style={{ fontSize: '0.78rem', opacity: 0.75, marginTop: '0.2rem' }}>Seller top-up: {formatCurrency(swap.seller_topup_amount)}</div>
                    )}
                  </div>
                </div>

                {/* Meta */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  {[
                    { label: 'Value Difference', value: formatCurrency(swap.value_difference || 0), color: '#22C55E' },
                    { label: 'Created', value: formatDate(swap.created_at), color: 'inherit' },
                    { label: 'Updated', value: formatDate(swap.updated_at), color: 'inherit' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="AEX-AdminFeature-metric-card">
                      <div className="AEX-AdminFeature-metric-label">{label}</div>
                      <div className="AEX-AdminFeature-metric-value" style={{ color, fontSize: '0.9rem' }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Inspection Report (if present) */}
                {swap.inspection_report && (
                  <div style={{ background: isDarkMode ? 'rgba(34,197,94,0.07)' : 'rgba(240,253,244,0.8)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 12, padding: '1rem' }}>
                    <div className="AEX-AdminFeature-tx-section-title">Inspection Report</div>
                    <div style={{ fontSize: '0.85rem', color: isDarkMode ? '#cbd5e1' : '#334155', lineHeight: 1.6 }}>
                      {swap.inspection_report.technician_notes || 'No notes.'}
                    </div>
                    {swap.inspection_report.final_decision && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <span style={{
                          padding: '0.2rem 0.65rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700,
                          background: swap.inspection_report.final_decision === 'pass' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.1)',
                          color: swap.inspection_report.final_decision === 'pass' ? '#22C55E' : '#EF4444'
                        }}>
                          {swap.inspection_report.final_decision.toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Logistics Timeline (active swaps only) */}
                {isActive && (
                  <div>
                    <div className="AEX-AdminFeature-tx-section-title" style={{ marginBottom: '0.85rem' }}>
                      <FontAwesomeIcon icon={faBox} style={{ marginRight: '0.4rem' }} />
                      Delivery Tracking
                      {swap.tracking_id && (
                        <span className="AEX-AdminFeature-meta-label" style={{ marginLeft: '0.5rem', textTransform: 'none' }}>
                          ({swap.tracking_id})
                        </span>
                      )}
                    </div>
                    {loadingSwapLogistics ? (
                      <div className="AEX-AdminFeature-loading">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <p>Loading tracking events...</p>
                      </div>
                    ) : swapLogisticsEvents.length === 0 ? (
                      <p style={{ fontSize: '0.85rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                        {swap.tracking_id ? 'No tracking events available yet.' : 'No tracking ID on this swap.'}
                      </p>
                    ) : (
                      <div className="AEX-AdminFeature-logistics-timeline">
                        {swapLogisticsEvents.map((event, i) => (
                          <div key={i} className="AEX-AdminFeature-logistics-event">
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <div className="AEX-AdminFeature-logistics-event-dot" style={{
                                background: i === 0 ? '#22C55E' : (isDarkMode ? '#334155' : '#cbd5e1')
                              }} />
                              {i < swapLogisticsEvents.length - 1 && (
                                <div className="AEX-AdminFeature-logistics-event-line" />
                              )}
                            </div>
                            <div style={{ paddingBottom: i < swapLogisticsEvents.length - 1 ? '1rem' : 0 }}>
                              <div className="AEX-AdminFeature-tx-type">{event.event || event.description || 'Event'}</div>
                              {event.location && (
                                <div className="AEX-AdminFeature-tx-desc">{event.location}</div>
                              )}
                              <div className="AEX-AdminFeature-tx-date">
                                {event.timestamp ? format(new Date(event.timestamp), 'MMM dd, yyyy · HH:mm') : 'Time unknown'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="AEX-AdminFeature-modal-footer">
                <button className="AEX-AdminFeature-btn-ghost" onClick={() => setShowSwapDetailsModal(false)}>Close</button>
              </div>

            </div>
          </div>
        );
      })()}

      {/* ── Swap Image Lightbox ────────────────────────────────────────────── */}
      {swapImageViewer && (() => {
        const { images, index } = swapImageViewer;
        const hasMultiple = images.length > 1;
        const goTo = (i) => setSwapImageViewer({ images, index: (i + images.length) % images.length });
        return (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              // Must sit above .AEX-AdminFeature-overlay (z-index: 9999, used by
              // the Swap Details Modal this viewer opens from) and its --top
              // variant (10000) — otherwise the lightbox renders underneath the
              // modal instead of over it.
              background: 'rgba(0,0,0,0.9)', zIndex: 10050,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem'
            }}
            onClick={() => setSwapImageViewer(null)}
          >
            <button
              onClick={() => setSwapImageViewer(null)}
              style={{
                position: 'absolute', top: '1.25rem', right: '1.25rem', width: '40px', height: '40px',
                borderRadius: '50%', border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.1)',
                color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem'
              }}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>

            {hasMultiple && (
              <button
                onClick={(e) => { e.stopPropagation(); goTo(index - 1); }}
                style={{
                  position: 'absolute', left: '1.25rem', top: '50%', transform: 'translateY(-50%)',
                  width: '44px', height: '44px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.3)',
                  background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', fontSize: '1.1rem'
                }}
                title="Previous"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}

            <img
              src={images[index]}
              alt={`Photo ${index + 1} of ${images.length}`}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
            />

            {hasMultiple && (
              <button
                onClick={(e) => { e.stopPropagation(); goTo(index + 1); }}
                style={{
                  position: 'absolute', right: '1.25rem', top: '50%', transform: 'translateY(-50%)',
                  width: '44px', height: '44px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.3)',
                  background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', fontSize: '1.1rem'
                }}
                title="Next"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            )}

            {hasMultiple && (
              <div style={{
                position: 'absolute', bottom: '1.5rem', left: '50%', transform: 'translateX(-50%)',
                color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem', fontWeight: 600,
                background: 'rgba(255,255,255,0.1)', padding: '0.35rem 0.9rem', borderRadius: '50px'
              }}>
                {index + 1} / {images.length}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Dispute Resolution Modal ───────────────────────────────────────── */}
      {showDisputeModal && selectedDispute && (() => {
        const dispute = selectedDispute;
        const disputeId = dispute._id || dispute.id;
        const disputeStatusColor = {
          open: '#EF4444', investigating: '#F59E0B', 'in-review': '#F59E0B',
          resolved: '#22C55E', refunded: '#06B6D4', closed: '#6B7280', escalated: '#8B5CF6'
        };
        const statusColor = disputeStatusColor[dispute.status] || '#6B7280';
        const urgencyMs = 48 * 60 * 60 * 1000;
        const isUrgent = dispute.created_at && (Date.now() - new Date(dispute.created_at)) > urgencyMs;
        const evidence = dispute.evidence || dispute.attachments || [];

        return (
          <div className="AEX-AdminFeature-overlay AEX-AdminFeature-overlay--blur" onClick={() => setShowDisputeModal(false)}>
            <div className="AEX-AdminFeature-dispute-modal" onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div className="AEX-AdminFeature-modal-header--padded">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b', marginBottom: '0.25rem' }}>
                      Dispute #{dispute.dispute_number || disputeId.substring(0, 8).toUpperCase()}
                    </div>
                    <h3 className="AEX-AdminFeature-modal-title" style={{ margin: 0 }}>Resolution Control Center</h3>
                  </div>
                  {isUrgent && (
                    <span className="AEX-AdminFeature-dispute-urgency">
                      <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '0.65rem', marginRight: '0.3rem' }} />
                      Urgent
                    </span>
                  )}
                  <span className="AEX-AdminFeature-status-badge" style={{
                    background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}30`
                  }}>
                    {dispute.status?.toUpperCase()}
                  </span>
                </div>
                <button className="AEX-AdminFeature-close-btn" onClick={() => setShowDisputeModal(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {/* Conflict Summary */}
              <div className="AEX-AdminFeature-dispute-conflict-grid">
                <div className="AEX-AdminFeature-dispute-conflict-side AEX-AdminFeature-dispute-conflict-side--buyer">
                  <div className="AEX-AdminFeature-meta-label" style={{ color: '#3B82F6' }}>Buyer's Complaint</div>
                  <div className="AEX-AdminFeature-user-name" style={{ marginTop: '0.5rem' }}>{dispute.raised_by || 'Buyer'}</div>
                  <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: 1.5, color: isDarkMode ? '#cbd5e1' : '#334155' }}>
                    {dispute.reason || dispute.description || 'No reason provided'}
                  </div>
                </div>
                <div className="AEX-AdminFeature-dispute-conflict-side AEX-AdminFeature-dispute-conflict-side--seller">
                  <div className="AEX-AdminFeature-meta-label" style={{ color: '#22C55E' }}>Seller's Response</div>
                  <div className="AEX-AdminFeature-user-name" style={{ marginTop: '0.5rem' }}>{dispute.against || 'Seller'}</div>
                  <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: 1.5, color: isDarkMode ? '#cbd5e1' : '#334155' }}>
                    {dispute.seller_response || 'No response submitted yet'}
                  </div>
                </div>
              </div>

              {/* Two-Column Body */}
              <div className="AEX-AdminFeature-dispute-modal-body-grid">
                {/* Evidence Carousel */}
                <div>
                  <div className="AEX-AdminFeature-tx-section-title" style={{ marginBottom: '0.85rem' }}>Evidence</div>
                  {evidence.length === 0 ? (
                    <p style={{ fontSize: '0.85rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>No evidence uploaded</p>
                  ) : (
                    <div className="AEX-AdminFeature-dispute-evidence-carousel">
                      <img
                        src={evidence[evidenceIndex]?.url || evidence[evidenceIndex]}
                        alt={`Evidence ${evidenceIndex + 1}`}
                        className="AEX-AdminFeature-dispute-evidence-img"
                        onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23e5e7eb" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%236b7280" font-size="14"%3ENo image%3C/text%3E%3C/svg%3E'; }}
                      />
                      {evidence.length > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem', gap: '0.5rem' }}>
                          <button
                            className="AEX-AdminFeature-btn-sm AEX-AdminFeature-btn-sm--ghost"
                            onClick={() => setEvidenceIndex(Math.max(0, evidenceIndex - 1))}
                            disabled={evidenceIndex === 0}
                          >
                            <FontAwesomeIcon icon={faChevronLeft} />
                          </button>
                          <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            {evidenceIndex + 1} / {evidence.length}
                          </span>
                          <button
                            className="AEX-AdminFeature-btn-sm AEX-AdminFeature-btn-sm--ghost"
                            onClick={() => setEvidenceIndex(Math.min(evidence.length - 1, evidenceIndex + 1))}
                            disabled={evidenceIndex === evidence.length - 1}
                          >
                            <FontAwesomeIcon icon={faChevronRight} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Admin Notes Feed */}
                <div>
                  <div className="AEX-AdminFeature-tx-section-title" style={{ marginBottom: '0.85rem' }}>Internal Notes</div>
                  <div className="AEX-AdminFeature-dispute-notes-feed">
                    {(dispute.admin_notes || []).length === 0 ? (
                      <p style={{ fontSize: '0.85rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>No notes yet</p>
                    ) : (
                      (dispute.admin_notes || []).map((note, i) => (
                        <div key={i} className="AEX-AdminFeature-dispute-note-item">
                          <div className="AEX-AdminFeature-user-name" style={{ fontSize: '0.8rem' }}>
                            {note.admin_name || 'Admin'}
                          </div>
                          <div style={{ fontSize: '0.85rem', marginTop: '0.2rem', color: isDarkMode ? '#cbd5e1' : '#334155' }}>
                            {note.text || note.note || note}
                          </div>
                          <div className="AEX-AdminFeature-tx-date" style={{ marginTop: '0.2rem' }}>
                            {note.created_at ? format(new Date(note.created_at), 'MMM dd, yyyy · HH:mm') : 'Unknown time'}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Note Input */}
                  <div style={{ marginTop: '0.75rem', borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, paddingTop: '0.75rem' }}>
                    <textarea
                      className="AEX-AdminFeature-textarea"
                      value={disputeNoteText}
                      onChange={e => setDisputeNoteText(e.target.value)}
                      placeholder="Add an internal note..."
                      rows={2}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    <button
                      className="AEX-AdminFeature-btn-sm AEX-AdminFeature-btn-sm--ghost"
                      onClick={() => handleAddDisputeNote(disputeId)}
                      disabled={addingNote}
                      style={{ width: '100%' }}
                    >
                      {addingNote ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Add Note'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Financial Actions */}
              <div style={{ paddingTop: '1.25rem', borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
                <div className="AEX-AdminFeature-tx-section-title" style={{ marginBottom: '0.85rem' }}>Update Status</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
                  {['open', 'investigating', 'resolved', 'closed', 'escalated'].map(status => (
                    <button
                      key={status}
                      className={`AEX-AdminFeature-btn-sm ${dispute.status === status ? 'AEX-AdminFeature-btn-sm--green' : 'AEX-AdminFeature-btn-sm--ghost'}`}
                      onClick={() => handleUpdateDisputeStatus(disputeId, status)}
                      style={{ textTransform: 'capitalize' }}
                    >
                      {status}
                    </button>
                  ))}
                </div>

                <div className="AEX-AdminFeature-tx-section-title" style={{ marginBottom: '0.85rem', marginTop: '1rem' }}>Resolution</div>

                {/* Reason Input */}
                <label className="AEX-AdminFeature-label">Reason (Required)</label>
                <textarea
                  className="AEX-AdminFeature-textarea"
                  value={resolutionPayload.reason}
                  onChange={e => setResolutionPayload(p => ({ ...p, reason: e.target.value }))}
                  placeholder="Explain the resolution rationale..."
                  rows={2}
                  style={{ marginBottom: '1rem' }}
                />

                {/* Partial Refund Amount Input */}
                {showPartialInput && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label className="AEX-AdminFeature-label">Refund Amount</label>
                    <input
                      className="AEX-AdminFeature-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={resolutionPayload.amount}
                      onChange={e => setResolutionPayload(p => ({ ...p, amount: e.target.value }))}
                      placeholder="Enter amount"
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="AEX-AdminFeature-dispute-actions-grid">
                  <button
                    className="AEX-AdminFeature-btn-sm AEX-AdminFeature-btn-sm--green"
                    disabled={resolutionLoading}
                    onClick={() => {
                      setResolutionPayload(p => ({ ...p, resolution: 'refund_buyer' }));
                      setTimeout(() => handleResolveDispute(), 0);
                    }}
                  >
                    {resolutionLoading && resolutionPayload.resolution === 'refund_buyer' ? (
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.4rem' }} />
                    ) : null}
                    Refund Buyer Full
                  </button>
                  <button
                    className="AEX-AdminFeature-btn-sm"
                    style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)' }}
                    disabled={resolutionLoading}
                    onClick={() => {
                      setResolutionPayload(p => ({ ...p, resolution: 'release_seller' }));
                      setTimeout(() => handleResolveDispute(), 0);
                    }}
                  >
                    {resolutionLoading && resolutionPayload.resolution === 'release_seller' ? (
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.4rem' }} />
                    ) : null}
                    Release to Seller
                  </button>
                  <button
                    className="AEX-AdminFeature-btn-sm"
                    style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }}
                    disabled={resolutionLoading}
                    onClick={() => {
                      if (!showPartialInput) {
                        setShowPartialInput(true);
                      } else {
                        setResolutionPayload(p => ({ ...p, resolution: 'partial_refund' }));
                        setTimeout(() => handleResolveDispute(), 0);
                      }
                    }}
                  >
                    {resolutionLoading && resolutionPayload.resolution === 'partial_refund' ? (
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.4rem' }} />
                    ) : null}
                    {showPartialInput ? 'Confirm Partial Refund' : 'Partial Refund'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Wallet Details Modal ──────────────────────────────────────────── */}
      {showWalletModal && (() => {
        const w = selectedWalletDetails;
        const isFrozen = (w?.status || '').toLowerCase() === 'frozen';
        const statusColor = isFrozen ? '#EF4444' : '#22C55E';
        const statusBg = isFrozen ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)';
        const statusBorder = isFrozen ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)';
        const txTypeColor = (type) => {
          if (!type) return '#64748b';
          const t = type.toLowerCase();
          if (t.includes('credit') || t.includes('release') || t.includes('topup')) return '#22C55E';
          if (t.includes('debit') || t.includes('withdrawal') || t.includes('hold')) return '#EF4444';
          return '#3B82F6';
        };

        return (
          <div className="AEX-AdminFeature-overlay" onClick={() => setShowWalletModal(false)}>
            <div className="AEX-AdminFeature-modal-lg" onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div className="AEX-AdminFeature-modal-header--padded">
                <div className="AEX-AdminFeature-user-info">
                  <div className="AEX-AdminFeature-user-avatar-lg">
                    {(w?.user_name || 'U')[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="AEX-AdminFeature-user-name">{w?.user_name || 'User'}</div>
                    <div className="AEX-AdminFeature-user-email">{w?.user_email || ''}</div>
                  </div>
                  {w && (
                    <span style={{ padding: '0.25rem 0.7rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, background: statusBg, color: statusColor, border: `1px solid ${statusBorder}`, marginLeft: '0.25rem' }}>
                      {(w.status || 'active').toUpperCase()}
                    </span>
                  )}
                </div>
                <button className="AEX-AdminFeature-close-btn" onClick={() => setShowWalletModal(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {/* Body */}
              <div className="AEX-AdminFeature-modal-body">
                {walletDetailsLoading ? (
                  <div className="AEX-AdminFeature-loading">
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#22C55E' }} />
                    <p>Loading wallet details…</p>
                  </div>
                ) : w ? (
                  <>
                    {/* Metrics grid */}
                    <div className="AEX-AdminFeature-metrics-grid">
                      {[
                        { label: 'Balance', value: formatCurrency(w.balance || 0), color: '#22C55E' },
                        { label: 'Held Balance', value: formatCurrency(w.escrow_balance || w.held_balance || 0), color: '#F59E0B' },
                        { label: 'Transactions', value: w.total_transactions || (w.recent_transactions || []).length || 0, color: '#3B82F6' },
                      ].map(({ label, value, color }) => (
                        <div key={label} className="AEX-AdminFeature-metric-card">
                          <div className="AEX-AdminFeature-metric-label">{label}</div>
                          <div className="AEX-AdminFeature-metric-value" style={{ color }}>{value}</div>
                        </div>
                      ))}
                    </div>

                    {/* Action row */}
                    <div className="AEX-AdminFeature-action-row">
                      {isFrozen ? (
                        <button className="AEX-AdminFeature-btn-success" onClick={handleUnfreezeWallet}>
                          <FontAwesomeIcon icon={faCheckCircle} /> Unfreeze Wallet
                        </button>
                      ) : (
                        <button className="AEX-AdminFeature-btn-freeze" onClick={handleFreezeWallet}>
                          <FontAwesomeIcon icon={faLock} /> Freeze Wallet
                        </button>
                      )}
                      <button
                        className={`AEX-AdminFeature-btn-adjust${showAdjustPanel ? ' active' : ''}`}
                        onClick={() => { setShowAdjustPanel(v => !v); setAdjustAmount(''); setAdjustReason(''); setAdjustType('credit'); }}>
                        <FontAwesomeIcon icon={faEdit} /> Adjust Balance
                      </button>
                    </div>

                    {/* Adjust panel */}
                    {showAdjustPanel && (
                      <div className="AEX-AdminFeature-adjust-panel">
                        <div className="AEX-AdminFeature-adjust-panel-title">Adjust Balance</div>
                        <div className="AEX-AdminFeature-adjust-grid">
                          <div>
                            <label className="AEX-AdminFeature-label" style={{ fontSize: '0.78rem', marginBottom: '0.3rem' }}>Amount (₦)</label>
                            <input type="number" min="0" step="0.01" value={adjustAmount}
                              onChange={e => setAdjustAmount(e.target.value)}
                              placeholder="e.g. 5000"
                              className="AEX-AdminFeature-input" />
                          </div>
                          <div>
                            <label className="AEX-AdminFeature-label" style={{ fontSize: '0.78rem', marginBottom: '0.3rem' }}>Type</label>
                            <select value={adjustType} onChange={e => setAdjustType(e.target.value)}
                              className="AEX-AdminFeature-select">
                              <option value="credit">Credit (Add funds)</option>
                              <option value="debit">Debit (Remove funds)</option>
                            </select>
                          </div>
                        </div>
                        <div style={{ marginBottom: '0.85rem' }}>
                          <label className="AEX-AdminFeature-label" style={{ fontSize: '0.78rem', marginBottom: '0.3rem' }}>Reason (Required)</label>
                          <input type="text" value={adjustReason} onChange={e => setAdjustReason(e.target.value)}
                            placeholder="e.g. Compensation for delayed order"
                            className="AEX-AdminFeature-input" />
                        </div>
                        <div className="AEX-AdminFeature-adjust-footer">
                          <button className="AEX-AdminFeature-btn-ghost" style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                            onClick={() => setShowAdjustPanel(false)}>Cancel</button>
                          <button onClick={submitAdjustWallet}
                            style={{ padding: '8px 18px', borderRadius: '7px', border: 'none', background: adjustType === 'credit' ? 'linear-gradient(135deg,#22C55E,#16A34A)' : 'linear-gradient(135deg,#EF4444,#DC2626)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit' }}>
                            <FontAwesomeIcon icon={adjustType === 'credit' ? faPlus : faTimes} style={{ marginRight: '0.35rem' }} />
                            {adjustType === 'credit' ? 'Credit' : 'Debit'} Wallet
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Recent transactions */}
                    {(w.recent_transactions || []).length > 0 && (
                      <div>
                        <div className="AEX-AdminFeature-tx-section-title">
                          Recent Transactions ({w.recent_transactions.length})
                        </div>
                        <div className="AEX-AdminFeature-tx-list">
                          {w.recent_transactions.slice(0, 20).map((tx, i) => (
                            <div key={tx._id || tx.id || i} className="AEX-AdminFeature-tx-row">
                              <div className="AEX-AdminFeature-tx-left">
                                <div className="AEX-AdminFeature-tx-dot" style={{ background: txTypeColor(tx.type) }} />
                                <div>
                                  <div className="AEX-AdminFeature-tx-type">{(tx.type || 'transaction').replace(/_/g, ' ')}</div>
                                  {tx.description && <div className="AEX-AdminFeature-tx-desc">{tx.description}</div>}
                                </div>
                              </div>
                              <div className="AEX-AdminFeature-tx-right">
                                <div className="AEX-AdminFeature-tx-amount" style={{ color: txTypeColor(tx.type) }}>{formatCurrency(tx.amount || 0)}</div>
                                <div className="AEX-AdminFeature-tx-date">{tx.created_at ? formatDate(tx.created_at) : ''}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </div>

              {/* Footer */}
              <div className="AEX-AdminFeature-modal-footer">
                <button className="AEX-AdminFeature-btn-ghost" onClick={() => setShowWalletModal(false)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Review Response Modal ───────────────────────────────────────────── */}
      {showReviewResponseModal && selectedReviewForResponse && (() => {
        const sentiment = selectedReviewForResponse.rating <= 2 ? 'Critical' : selectedReviewForResponse.rating >= 4 ? 'Positive' : 'Neutral';
        return (
          <div className="AEX-AdminFeature-overlay AEX-AdminFeature-overlay--blur" onClick={() => setShowReviewResponseModal(false)}>
            <div className="AEX-AdminFeature-modal-md" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="AEX-AdminFeature-modal-header--padded">
                <div>
                  <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: 600 }}>
                    Review Moderation
                  </h3>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Rating:</span>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      {[...Array(5)].map((_, i) => (
                        <FontAwesomeIcon
                          key={i}
                          icon={faStar}
                          style={{ color: i < (selectedReviewForResponse.rating || 0) ? '#f59e0b' : '#d1d5db', fontSize: '0.875rem' }}
                        />
                      ))}
                    </div>
                    <span style={{ fontSize: '0.75rem', background: 'rgba(245,158,11,0.12)', color: '#f59e0b', padding: '0.25rem 0.75rem', borderRadius: '0.375rem', marginLeft: '0.5rem' }}>
                      {sentiment}
                    </span>
                  </div>
                </div>
                <button className="AEX-AdminFeature-close-btn" onClick={() => setShowReviewResponseModal(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {/* Reviewer & Product Info */}
              <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>Reviewer</div>
                  <h4 style={{ margin: 0, marginBottom: '0.125rem' }}>{selectedReviewForResponse.reviewer_name || 'User'}</h4>
                  <p style={{ margin: 0, fontSize: '0.875rem', opacity: 0.7 }}>{selectedReviewForResponse.reviewer_email}</p>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>Product</div>
                  <p style={{ margin: 0, fontWeight: 500 }}>{selectedReviewForResponse.product_title || selectedReviewForResponse.product_name}</p>
                </div>
              </div>

              {/* Review Comment */}
              <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)', maxHeight: '150px', overflowY: 'auto' }}>
                <p style={{ margin: 0, lineHeight: '1.6', color: '#E5E7EB' }}>
                  "{selectedReviewForResponse.comment || selectedReviewForResponse.review || 'No comment'}"
                </p>
              </div>

              {/* Conditional Content Based on Action Type */}
              <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                {reviewResponseType === 'reject' && (
                  <div>
                    <label className="AEX-AdminFeature-label">Rejection Reason</label>
                    <textarea
                      className="AEX-AdminFeature-textarea"
                      value={reviewRejectReason}
                      onChange={e => setReviewRejectReason(e.target.value)}
                      placeholder="Explain why you're rejecting this review..."
                      rows={3}
                      style={{ marginBottom: '1rem' }}
                    />
                  </div>
                )}
                {reviewResponseType === 'delete' && (
                  <div style={{ padding: '1rem', background: 'rgba(239,68,68,0.1)', borderRadius: '0.5rem', color: '#FCA5A5' }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem' }} />
                    This action will permanently delete the review and cannot be undone.
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="AEX-AdminFeature-modal-footer" style={{ padding: '1.5rem', display: 'flex', gap: '0.75rem' }}>
                <button
                  className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-outline"
                  onClick={() => setShowReviewResponseModal(false)}
                  style={{ flex: 1 }}
                  disabled={reviewResponseLoading}
                >
                  Cancel
                </button>
                {reviewResponseType === 'approve' && (
                  <button
                    className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                    onClick={handleApproveReview}
                    style={{ flex: 1 }}
                    disabled={reviewResponseLoading}
                  >
                    {reviewResponseLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />}
                    {' '}Approve Review
                  </button>
                )}
                {reviewResponseType === 'reject' && (
                  <button
                    className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-outline"
                    onClick={handleRejectReview}
                    style={{ flex: 1 }}
                    disabled={reviewResponseLoading || !reviewRejectReason.trim()}
                  >
                    {reviewResponseLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTimes} />}
                    {' '}Reject Review
                  </button>
                )}
                {reviewResponseType === 'flag' && (
                  <button
                    className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-warning"
                    onClick={handleFlagReview}
                    style={{ flex: 1, background: '#f59e0b', color: '#000' }}
                    disabled={reviewResponseLoading}
                  >
                    {reviewResponseLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faFlag} />}
                    {' '}Flag Review
                  </button>
                )}
                {reviewResponseType === 'delete' && (
                  <button
                    className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-danger"
                    onClick={handleDeleteReview}
                    style={{ flex: 1 }}
                    disabled={reviewResponseLoading}
                  >
                    {reviewResponseLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTrash} />}
                    {' '}Delete Review
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Premium Extend Modal ───────────────────────────────────────────── */}
      {/* ── Grant Premium Modal ────────────────────────────────────────────── */}
      {showGrantPremiumModal && (
        <div className="AEX-AdminFeature-overlay AEX-AdminFeature-overlay--blur" onClick={() => setShowGrantPremiumModal(false)}>
          <div className="AEX-AdminFeature-modal-md" onClick={e => e.stopPropagation()}>
            <div className="AEX-AdminFeature-modal-header--padded">
              <div>
                <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: 600 }}>
                  Grant Premium Subscription
                </h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#9CA3AF' }}>
                  Give a buyer or seller premium access directly
                </p>
              </div>
              <button className="AEX-AdminFeature-close-btn" onClick={() => setShowGrantPremiumModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              {!grantPremiumSelectedUser ? (
                <>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Search buyer or seller
                  </label>
                  <input
                    type="text"
                    autoFocus
                    placeholder="Search by name or email..."
                    value={grantPremiumSearch}
                    onChange={(e) => setGrantPremiumSearch(e.target.value)}
                    style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.25)', background: 'rgba(0,0,0,0.2)', color: '#F3F4F6', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', marginBottom: '0.75rem' }}
                  />
                  {grantPremiumSearching ? (
                    <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#22C55E' }} />
                    </div>
                  ) : grantPremiumSearch.trim().length >= 2 && grantPremiumSearchResults.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: '0.85rem', padding: '1rem 0' }}>No matching users found</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '260px', overflowY: 'auto' }}>
                      {grantPremiumSearchResults.map(u => {
                        const uName = `${u.profile?.first_name || ''} ${u.profile?.last_name || ''}`.trim() || u.username;
                        return (
                          <button
                            key={u.id || u._id}
                            onClick={() => setGrantPremiumSelectedUser(u)}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
                              padding: '0.6rem 0.85rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)',
                              background: 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', width: '100%',
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: '#F3F4F6', fontSize: '0.875rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{uName}</div>
                              <div style={{ color: '#9CA3AF', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
                              {(u.isPremium || u.is_premium) && (
                                <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase' }}>Premium</span>
                              )}
                              <span style={{ fontSize: '0.7rem', color: '#6B7280', textTransform: 'capitalize' }}>{u.user_type}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: 'rgba(34,197,94,0.08)', borderRadius: '10px', marginBottom: '1.25rem' }}>
                    <div>
                      <div style={{ color: '#F3F4F6', fontWeight: 600, fontSize: '0.9rem' }}>
                        {`${grantPremiumSelectedUser.profile?.first_name || ''} ${grantPremiumSelectedUser.profile?.last_name || ''}`.trim() || grantPremiumSelectedUser.username}
                      </div>
                      <div style={{ color: '#9CA3AF', fontSize: '0.78rem' }}>{grantPremiumSelectedUser.email}</div>
                    </div>
                    <button
                      onClick={() => { setGrantPremiumSelectedUser(null); setGrantPremiumSearch(''); }}
                      style={{ background: 'transparent', border: 'none', color: '#22C55E', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Change
                    </button>
                  </div>

                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Plan</label>
                  <select
                    value={grantPremiumPlanId}
                    onChange={(e) => setGrantPremiumPlanId(e.target.value)}
                    style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.25)', background: 'rgba(0,0,0,0.2)', color: '#F3F4F6', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', marginBottom: '1.25rem' }}
                  >
                    <option value="">Select a plan...</option>
                    {(premiumSettings?.plans || []).map(p => (
                      <option key={p.id} value={p.id}>{p.name} — {formatCurrency(p.price)} ({p.user_type})</option>
                    ))}
                  </select>

                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Duration</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.25rem' }}>
                    {[30, 90, 365].map(days => (
                      <button
                        key={days}
                        onClick={() => setGrantPremiumDurationDays(days)}
                        className={grantPremiumDurationDays === days ? 'AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary' : 'AutoboyExpress-dash-btn AutoboyExpress-dash-btn-outline'}
                        style={{ fontSize: '0.875rem', padding: '0.75rem' }}
                      >
                        {days} day{days !== 1 ? 's' : ''}
                      </button>
                    ))}
                  </div>

                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.5rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reason (optional)</label>
                  <textarea
                    value={grantPremiumReason}
                    onChange={(e) => setGrantPremiumReason(e.target.value)}
                    rows={2}
                    placeholder="E.g. goodwill gesture, promo, manual payment received..."
                    style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.25)', background: 'rgba(0,0,0,0.2)', color: '#F3F4F6', fontSize: '0.875rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </>
              )}
            </div>

            <div className="AEX-AdminFeature-modal-footer" style={{ padding: '1.5rem', display: 'flex', gap: '0.75rem' }}>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-outline"
                onClick={() => setShowGrantPremiumModal(false)}
                disabled={premiumActionLoading}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                onClick={handleGrantPremium}
                disabled={premiumActionLoading || !grantPremiumSelectedUser || !grantPremiumPlanId}
                style={{ flex: 1 }}
              >
                {premiumActionLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCrown} />}
                {' '}Grant Premium
              </button>
            </div>
          </div>
        </div>
      )}

      {showPremiumExtendModal && selectedPremiumUser && (
        <div className="AEX-AdminFeature-overlay AEX-AdminFeature-overlay--blur" onClick={() => setShowPremiumExtendModal(false)}>
          <div className="AEX-AdminFeature-modal-md" onClick={e => e.stopPropagation()}>
            <div className="AEX-AdminFeature-modal-header--padded">
              <div>
                <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: 600 }}>
                  Extend Premium Subscription
                </h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#9CA3AF' }}>
                  {selectedPremiumUser.user_name || 'User'}
                </p>
              </div>
              <button className="AEX-AdminFeature-close-btn" onClick={() => setShowPremiumExtendModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              {(() => {
                // Extension always counts from the subscription's own current
                // expiry, even if that's already in the past — never silently
                // from today. Matches the backend (ExtendPremium).
                const currentExpiry = selectedPremiumUser.end_date ? new Date(selectedPremiumUser.end_date) : null;
                const alreadyExpired = currentExpiry && currentExpiry.getTime() < Date.now();
                const base = currentExpiry ? currentExpiry.getTime() : Date.now();
                const newEndDate = premiumExtensionDays ? new Date(base + premiumExtensionDays * 24 * 60 * 60 * 1000) : null;
                const stillExpired = newEndDate && newEndDate.getTime() < Date.now();

                return (
                  <>
                    <div style={{ padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.05)', borderRadius: '0.5rem', fontSize: '0.85rem', color: '#D1D5DB', marginBottom: '1.25rem' }}>
                      Current expiry: <strong style={{ color: alreadyExpired ? '#F59E0B' : '#F3F4F6' }}>
                        {currentExpiry ? format(currentExpiry, 'MMM dd, yyyy') : 'No active subscription'}
                      </strong>
                      {alreadyExpired && <span style={{ color: '#F59E0B' }}> (already expired)</span>}
                    </div>

                    <p style={{ margin: 0, marginBottom: '1rem', fontSize: '0.875rem', color: '#D1D5DB' }}>
                      Select extension duration:
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
                      {[7, 30, 365].map(days => (
                        <button
                          key={days}
                          onClick={() => setPremiumExtensionDays(days)}
                          className={premiumExtensionDays === days ? 'AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary' : 'AutoboyExpress-dash-btn AutoboyExpress-dash-btn-outline'}
                          style={{ fontSize: '0.875rem', padding: '0.75rem' }}
                        >
                          +{days} day{days !== 1 ? 's' : ''}
                        </button>
                      ))}
                    </div>

                    <div style={{ padding: '1rem', background: 'rgba(34,197,94,0.1)', borderRadius: '0.5rem', fontSize: '0.875rem', color: '#22C55E' }}>
                      <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '0.5rem' }} />
                      New end date: {newEndDate ? format(newEndDate, 'MMM dd, yyyy') : 'Select duration'}
                    </div>
                    {stillExpired && (
                      <div style={{ padding: '0.75rem 1rem', marginTop: '0.75rem', background: 'rgba(245,158,11,0.1)', borderRadius: '0.5rem', fontSize: '0.8rem', color: '#F59E0B' }}>
                        <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '0.5rem' }} />
                        This subscription expired a while ago — the new end date above is still in the past. Increase the duration if you want the account active again immediately.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="AEX-AdminFeature-modal-footer" style={{ padding: '1.5rem', display: 'flex', gap: '0.75rem' }}>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-outline"
                onClick={() => setShowPremiumExtendModal(false)}
                disabled={premiumActionLoading}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                onClick={handleExtendPremium}
                disabled={premiumActionLoading}
                style={{ flex: 1 }}
              >
                {premiumActionLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCalendarPlus} />}
                {' '}Extend Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Premium Revoke Modal ───────────────────────────────────────────── */}
      {showPremiumRevokeModal && selectedPremiumUser && (
        <div className="AEX-AdminFeature-overlay AEX-AdminFeature-overlay--blur" onClick={() => setShowPremiumRevokeModal(false)}>
          <div className="AEX-AdminFeature-modal-md" onClick={e => e.stopPropagation()}>
            <div className="AEX-AdminFeature-modal-header--padded">
              <div>
                <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: 600, color: '#FCA5A5' }}>
                  Revoke Premium Subscription
                </h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#9CA3AF' }}>
                  {selectedPremiumUser.user_name || 'User'}
                </p>
              </div>
              <button className="AEX-AdminFeature-close-btn" onClick={() => setShowPremiumRevokeModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ padding: '1rem', background: 'rgba(239,68,68,0.1)', borderRadius: '0.5rem', marginBottom: '1rem', color: '#FCA5A5' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem' }} />
                This action will immediately revoke premium access.
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label className="AEX-AdminFeature-label">Revocation Reason</label>
                <textarea
                  className="AEX-AdminFeature-textarea"
                  value={premiumRevocationReason}
                  onChange={e => setPremiumRevocationReason(e.target.value)}
                  placeholder="Explain reason for revocation..."
                  rows={3}
                  style={{ width: '100%' }}
                />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={premiumRefundPartial}
                  onChange={e => setPremiumRefundPartial(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span>Refund partial amount to user wallet</span>
              </label>
            </div>

            <div className="AEX-AdminFeature-modal-footer" style={{ padding: '1.5rem', display: 'flex', gap: '0.75rem' }}>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-outline"
                onClick={() => setShowPremiumRevokeModal(false)}
                disabled={premiumActionLoading}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-danger"
                onClick={handleRevokePremium}
                disabled={premiumActionLoading || !premiumRevocationReason.trim()}
                style={{ flex: 1 }}
              >
                {premiumActionLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTimesCircle} />}
                {' '}Revoke Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Premium Gift Modal ───────────────────────────────────────────── */}
      {showPremiumGiftModal && selectedPremiumUser && (
        <div className="AEX-AdminFeature-overlay AEX-AdminFeature-overlay--blur" onClick={() => setShowPremiumGiftModal(false)}>
          <div className="AEX-AdminFeature-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="AEX-AdminFeature-modal-header--padded">
              <div>
                <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.125rem', fontWeight: 600 }}>
                  Gift 30-Day Premium
                </h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#9CA3AF' }}>
                  {selectedPremiumUser.user_name || 'User'}
                </p>
              </div>
              <button className="AEX-AdminFeature-close-btn" onClick={() => setShowPremiumGiftModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ padding: '1rem', background: 'rgba(245,158,11,0.1)', borderRadius: '0.5rem', color: '#FCD34D', fontSize: '0.875rem' }}>
                <FontAwesomeIcon icon={faGift} style={{ marginRight: '0.5rem' }} />
                Send 30 days of premium at no cost to the user
              </div>
            </div>

            <div className="AEX-AdminFeature-modal-footer" style={{ padding: '1.5rem', display: 'flex', gap: '0.75rem' }}>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-outline"
                onClick={() => setShowPremiumGiftModal(false)}
                disabled={premiumActionLoading}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                onClick={handleGiftPremium}
                disabled={premiumActionLoading}
                style={{ flex: 1, background: '#f59e0b' }}
              >
                {premiumActionLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faGift} />}
                {' '}Send Gift
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Freeze Wallet Modal ───────────────────────────────────────────── */}
      {showFreezeModal && (
        <div className="AEX-AdminFeature-overlay AEX-AdminFeature-overlay--top" onClick={() => setShowFreezeModal(false)}>
          <div className="AEX-AdminFeature-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="AEX-AdminFeature-modal-header">
              <div>
                <div className="AEX-AdminFeature-badge-red">
                  <FontAwesomeIcon icon={faLock} style={{ fontSize: '0.7rem' }} />
                  Freeze Wallet
                </div>
                <h3 className="AEX-AdminFeature-modal-title">
                  Freeze {selectedWalletDetails?.user_name ? `${selectedWalletDetails.user_name}'s` : ''} Wallet
                </h3>
              </div>
              <button className="AEX-AdminFeature-close-btn" onClick={() => setShowFreezeModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <label className="AEX-AdminFeature-label">Reason (Required)</label>
            <textarea className="AEX-AdminFeature-textarea"
              value={freezeReason}
              onChange={e => setFreezeReason(e.target.value)}
              placeholder="e.g. Suspicious activity detected"
              rows={3}
            />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button className="AEX-AdminFeature-btn-ghost" onClick={() => setShowFreezeModal(false)}>Cancel</button>
              <button className="AEX-AdminFeature-btn-danger" onClick={submitFreezeWallet}>
                <FontAwesomeIcon icon={faLock} style={{ marginRight: '0.4rem' }} /> Confirm Freeze
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Escrow Action Modal (Release / Refund) ─────────────────────────── */}
      {showEscrowActionModal && escrowActionTarget && (
        <div className="AEX-AdminFeature-overlay AEX-AdminFeature-overlay--top" onClick={() => setShowEscrowActionModal(false)}>
          <div className="AEX-AdminFeature-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="AEX-AdminFeature-modal-header">
              <div>
                <div className="AEX-AdminFeature-badge-red" style={escrowActionType === 'release' ? { background: 'rgba(34,197,94,0.1)', color: '#22C55E', borderColor: 'rgba(34,197,94,0.25)' } : undefined}>
                  <FontAwesomeIcon icon={escrowActionType === 'release' ? faCheckCircle : faTimes} style={{ fontSize: '0.7rem' }} />
                  {escrowActionType === 'release' ? 'Release Funds' : 'Refund Buyer'}
                </div>
                <h3 className="AEX-AdminFeature-modal-title">
                  {escrowActionType === 'release' ? 'Release Escrow to Seller' : 'Refund Escrow to Buyer'}
                </h3>
              </div>
              <button className="AEX-AdminFeature-close-btn" onClick={() => setShowEscrowActionModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
              {escrowActionTarget.escrow_number || `#${(escrowActionTarget._id || escrowActionTarget.id || '').slice(-8).toUpperCase()}`}
              {' · '}{formatCurrency(escrowActionTarget.amount || escrowActionTarget.net_amount || 0)}
            </p>
            <label className="AEX-AdminFeature-label">
              Reason {escrowActionType === 'refund' ? '(Required)' : '(Optional)'}
            </label>
            <textarea className="AEX-AdminFeature-textarea"
              value={escrowActionNote}
              onChange={e => setEscrowActionNote(e.target.value)}
              placeholder={escrowActionType === 'release' ? 'e.g. Delivery confirmed by buyer' : 'e.g. Item not as described'}
              rows={3}
            />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button className="AEX-AdminFeature-btn-ghost" onClick={() => setShowEscrowActionModal(false)}>Cancel</button>
              <button
                className={escrowActionType === 'release' ? 'AEX-AdminFeature-btn-success' : 'AEX-AdminFeature-btn-danger'}
                onClick={submitEscrowAction}
              >
                <FontAwesomeIcon icon={escrowActionType === 'release' ? faCheckCircle : faTimes} style={{ marginRight: '0.4rem' }} />
                {escrowActionType === 'release' ? 'Confirm Release' : 'Confirm Refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Escrow Details Modal ────────────────────────────────────────────── */}
      {showEscrowDetailModal && (() => {
        const esc = selectedEscrowDetails;
        const escStatus = esc?.status || 'held';
        const escStatusMeta = {
          held: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', label: 'Held' },
          released: { color: '#22C55E', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.3)', label: 'Released' },
          refunded: { color: '#EF4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', label: 'Refunded' },
          disputed: { color: '#A855F7', bg: 'rgba(168,85,247,0.12)', border: 'rgba(168,85,247,0.3)', label: 'Disputed' },
        };
        const escMeta = escStatusMeta[escStatus] || escStatusMeta.held;
        const escCanAct = escStatus === 'held' || escStatus === 'disputed';

        return (
          <div className="AEX-AdminFeature-overlay" onClick={() => setShowEscrowDetailModal(false)}>
            <div className="AEX-AdminFeature-modal-lg" onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div className="AEX-AdminFeature-modal-header--padded">
                <div className="AEX-AdminFeature-user-info">
                  <div className="AEX-AdminFeature-user-avatar-lg">
                    <FontAwesomeIcon icon={faLock} />
                  </div>
                  <div>
                    <div className="AEX-AdminFeature-user-name">
                      {esc?.escrow_number || `#${(esc?._id || esc?.id || '').slice(-8).toUpperCase()}`}
                    </div>
                    <div className="AEX-AdminFeature-user-email">
                      {esc?.provider ? `Provider: ${esc.provider}` : 'Internal escrow'}
                    </div>
                  </div>
                  {esc && (
                    <span style={{ padding: '0.25rem 0.7rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, background: escMeta.bg, color: escMeta.color, border: `1px solid ${escMeta.border}`, marginLeft: '0.25rem' }}>
                      {escMeta.label}
                    </span>
                  )}
                </div>
                <button className="AEX-AdminFeature-close-btn" onClick={() => setShowEscrowDetailModal(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {/* Body */}
              <div className="AEX-AdminFeature-modal-body">
                {escrowDetailsLoading ? (
                  <div className="AEX-AdminFeature-loading">
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#22C55E' }} />
                    <p>Loading escrow details…</p>
                  </div>
                ) : esc ? (
                  <>
                    {/* Metrics grid */}
                    <div className="AEX-AdminFeature-metrics-grid">
                      <div className="AEX-AdminFeature-metric-card">
                        <div className="AEX-AdminFeature-metric-label">Amount Held</div>
                        <div className="AEX-AdminFeature-metric-value" style={{ color: '#22C55E' }}>{formatCurrency(esc.amount || esc.net_amount || 0)}</div>
                      </div>
                      <div className="AEX-AdminFeature-metric-card">
                        <div className="AEX-AdminFeature-metric-label">Escrow Fee</div>
                        <div className="AEX-AdminFeature-metric-value" style={{ color: '#F59E0B' }}>{formatCurrency(esc.escrow_fee || 0)}</div>
                      </div>
                      <div className="AEX-AdminFeature-metric-card">
                        <div className="AEX-AdminFeature-metric-label">Auto-Release</div>
                        <div className="AEX-AdminFeature-metric-value" style={{ color: '#3B82F6' }}>
                          {esc.auto_release_date ? formatDate(esc.auto_release_date) : '—'}
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="AEX-AdminFeature-escrow-meta-row" style={{ marginTop: '1rem' }}>
                      <div className="AEX-AdminFeature-escrow-meta-items">
                        <div>
                          <div className="AEX-AdminFeature-meta-label">Buyer</div>
                          <div className="AEX-AdminFeature-meta-value">{esc.buyer_name || esc.payer_name || 'Buyer'}</div>
                        </div>
                        <div>
                          <div className="AEX-AdminFeature-meta-label">Seller</div>
                          <div className="AEX-AdminFeature-meta-value">{esc.seller_name || esc.payee_name || 'Seller'}</div>
                        </div>
                        {esc.order_id && (
                          <div>
                            <div className="AEX-AdminFeature-meta-label">Order</div>
                            <div className="AEX-AdminFeature-meta-value--mono">{String(esc.order_id).slice(-8).toUpperCase()}</div>
                          </div>
                        )}
                        <div>
                          <div className="AEX-AdminFeature-meta-label">Held Since</div>
                          <div className="AEX-AdminFeature-meta-value">{esc.created_at ? formatDate(esc.created_at) : '—'}</div>
                        </div>
                        {esc.provider && (
                          <div>
                            <div className="AEX-AdminFeature-meta-label">Provider</div>
                            <div className="AEX-AdminFeature-meta-value" style={{ textTransform: 'capitalize' }}>{esc.provider}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions — buttons stay enabled even for Pandascrow-held escrows;
                        the backend rejects manual release/refund for those and the
                        error toast explains why (OTP-gated release, dispute-only refund). */}
                    {escCanAct && (
                      <div className="AEX-AdminFeature-action-row" style={{ marginTop: '1.25rem' }}>
                        <button className="AEX-AdminFeature-btn-success" onClick={() => { setShowEscrowDetailModal(false); handleReleaseEscrow(esc); }}>
                          <FontAwesomeIcon icon={faCheckCircle} /> Release to Seller
                        </button>
                        <button className="AEX-AdminFeature-btn-danger" onClick={() => { setShowEscrowDetailModal(false); handleRefundEscrow(esc); }}>
                          <FontAwesomeIcon icon={faTimes} /> Refund Buyer
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="AEX-AdminFeature-empty">
                    <p style={{ margin: 0 }}>No escrow details available.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

    </>
  );
};

export default AdminDashboard;
